/**
 * Mock API for local development
 * This simulates the backend API when running locally
 */

import { handleLeaveMock } from './leaveMock'
import { Patient, PatientFormData, SmokingStatus, AlcoholStatus } from '../types/patient'
import { User, UserFormData } from '../types/user'
import { Family, FamilyMember } from '../types/patientRelationship'
import { Appointment } from '../types/appointment'
import { ClinicHours } from '../types/clinicHours'
import { PracticeLocation } from '../types/practiceLocation'
import { Consultation, ConsultationArtefact, ConsultationTerminologySystem } from '../types/consultation'
import { LabResult, LabResultSummary } from '../types/labResult'
import { VitalSigns } from '../types/vitalSigns'
import { PatientFile } from '../types/patientFile'
import type { BatchDocument, BatchSummary } from '../types/batchDocument'
import { Task, TaskFormData } from '../types/task'
import { CommunicationLog } from '../types/communicationLog'
import { ChatFormData, Message } from '../types/chat'
import { TemplateFormData, DocumentGenerationRequest, DocumentGenerationResponse, WordTemplate } from '../types/wordTemplate'
import { 
  PatientHistoryEntry, 
  PatientClinicalHistory, 
  PatientCondition, 
  FamilyHistory, 
  PatientConsent,
  ConditionFormData,
  FamilyHistoryFormData,
  ConsentFormData
} from '../types/patientHistory'
import { MedicationReference, PatientMedication } from '../types/medication'
import { Immunisation } from '../types/immunisation'
import { VaccineReference } from '../types/vaccineReference'
import { PracticeVaccine } from '../types/practiceVaccine'
import { Prescription, CreatePrescriptionInput, AllergyCheckResult, InteractionCheckResult } from '../types/prescription'
import { fetchAndParseHPRAXML } from '../utils/xmlParser'
import JSZip from 'jszip'
import { DOMParser } from '@xmldom/xmldom'
import { extractVariablesFromContent, enrichVariables } from '../utils/templateVariables'
import { RolePermission, Permission, PermissionTemplate } from '../types/rolePermission'
import { PERMISSION_TEMPLATES, DEFAULT_ROLE_PERMISSIONS } from '../types/permissionTemplates'
import { UserRole, getRoleCategory } from '../types/user'
import { AuditEntry, AuditHistoryResponse } from '../types/audit'
import { AppointmentTypeConfig, DEFAULT_APPOINTMENT_TYPES } from '../types/appointmentType'
import { AutomationRule, AutomationRuleFormData } from '../types/automationRule'
import { Pregnancy, MaternityVisit, PregnancyFormData, MaternityVisitFormData } from '../types/maternity'
import { calculateDueDate, calculateGestationalAge } from '../utils/dueDateCalculator'
import { buildMaternityClaimFormHtml, encodeUtf8Base64 } from '../utils/maternityClaimFormHtml'
import { Assessment, AssessmentFormData } from '../types/assessment'
import { Referral, ReferralOverview, Provider } from '../types/referral'
import type {
  DashboardRoleGroup,
  DashboardSummary,
  PptQueueSnapshot,
  PptPipeline,
  PptBatchReadiness,
  PptExceptions,
} from '../types/dashboard'
import { BillingAccount, BillableItem, BillingPayment, BillingStatement, BillingPaymentMethod, BillingPosItem } from '../types/billing'

// Local type for STC mock data (avoids dependency on types/stc for CI when stc types are uncommitted)
interface MockStcRecord {
  id: string
  patientId: string
  patientKey?: string
  patientName?: string
  claimNumber?: string | null
  formNumber?: string | null
  lastSubmissionAttemptAt?: string | null
  lastSubmissionResult?: 'success' | 'failed' | null
  lastSubmissionErrorMessage?: string | null
  lastSubmissionHttpStatus?: number | null
  lastSubmissionDocNum?: string | null
  lastSubmissionDocNam?: string | null
  lastSubmissionClaimSummaryUrl?: string | null
  lastInquiryAt?: string | null
  lastInquiryStatusRaw?: string | null
  lastInquiryStatusNormalized?: 'to_pay' | 'paid' | 'rejected' | 'unknown' | null
  lastInquiryProcessedPeriod?: string | null
  gmsNumber?: string | null
  ppsNumber?: string | null
  feeCode?: string | null
  estimatedFee?: number | null
  consultationId: string
  formType: 'STC' | 'MICS'
  status: 'draft' | 'signed' | 'queued' | 'to_pay' | 'paid' | 'failed' | 'rejected'
  rejectionReason?: string | null
  rejectedAt?: string | null
  signatureS3Key?: string | null
  formPngS3Key?: string | null
  formData?: Record<string, unknown>
  createdAt: string
  signedAt?: string | null
  processedAt?: string | null
  createdBy?: string
  lockState?: 'in-batch'
  lockBatchRunId?: string
}

interface MockStcBatchRun {
  id: string
  source: 'pms' | 'ppt'
  status: 'running' | 'completed' | 'completed_with_errors' | 'failed'
  createdAt: string
  startedAt: string
  completedAt?: string
  submitDocNum: string
  submitDocNam: string
  totalItems: number
  processedItems: number
  submittedCount: number
  failedCount: number
  skippedCount: number
}

interface MockPptPatient {
  id: string
  tenantId: string
  firstName?: string
  middleName?: string
  lastName?: string
  displayName: string
  dateOfBirth?: string
  gmsOrDvcNumber?: string
  ppsn?: string
  patientKey?: string
  importSource?: string
  importBatchId?: string
  createdAt: string
  updatedAt: string
}

interface MockPptImportJob {
  id: string
  importBatchId: string
  mode: 'async'
  status: 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed'
  totalRows: number
  processed: number
  inserted: number
  duplicatesSkipped: number
  invalidRows: number
  errorsSample: Array<{ row: number; message: string }>
  createdAt: string
  startedAt?: string
  completedAt?: string
  updatedAt: string
  errorMessage?: string
}

// Tenant interface for mock data
interface MockTenant {
  subdomain: string
  tenantId: string
  clinicName: string
  contactEmail: string
  contactPersonName?: string
  phoneNumber?: string
  status: 'pending' | 'provisioning' | 'provisioned' | 'failed'
  createdAt: string
  updatedAt: string
  stackName?: string
  fullDomain?: string
  provisionedAt?: string
  error?: string
  failedAt?: string
  termsAccepted?: boolean
  termsVersion?: string
  termsAcceptedAt?: string
  privacyAccepted?: boolean
  privacyVersion?: string
  privacyAcceptedAt?: string
  pcrsResponsibilityAccepted?: boolean
  pcrsResponsibilityVersion?: string
  pcrsResponsibilityAcceptedAt?: string
}

// In-memory storage for custom phrases (user-specific)
// Load from localStorage on initialization
const loadCustomPhrases = (): any[] => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('clnicl_custom_phrases')
      if (stored) {
        return JSON.parse(stored)
      }
    }
  } catch (error) {
    console.error('[Mock API] Error loading custom phrases from localStorage:', error)
  }
  return []
}

const saveCustomPhrases = (phrases: any[]) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('clnicl_custom_phrases', JSON.stringify(phrases))
    }
  } catch (error) {
    console.error('[Mock API] Error saving custom phrases to localStorage:', error)
  }
}

let mockCustomPhrases: any[] = loadCustomPhrases()
let mockPcrsCredentialsConfigured = false

const MOCK_STC_FEE_BY_CODE: Record<string, number> = {
  A: 24.8,
  AB: 70.0,
  AC: 50.0,
  AD: 60.0,
  AL: 100.0,
  AM: 100.0,
  B: 50.0,
  C: 24.8,
  CF: 55.0,
  CG: 100.0,
  CH: 160.0,
  CI: 110.0,
  CJ: 50.0,
  CK: 55.0,
  CL: 55.0,
  CM: 160.0,
  CN: 50.0,
  CO: 100.0,
  CQ: 110.0,
  D: 24.8,
  F: 24.8,
  G: 24.8,
  H: 24.8,
  J: 24.8,
  K: 37.21,
  L: 60.0,
  M: 62.02,
  X: 24.8,
  Y: 37.21,
  Z: 24.8,
}

const MOCK_STC_FEE_CODES = Object.keys(MOCK_STC_FEE_BY_CODE)

function normalizeMockFeeCode(value: unknown): string {
  return String(value || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .trim()
}

/** Strip query string and optional leading env segment so /staging/patients/... routes match handlers. */
function stripMockRequestPath(url: string): string {
  const path = url.split('?')[0]
  return path.replace(/^\/(?:staging|dev|development|production|app|test)(?=\/)/, '') || '/'
}

function normalizeMockPptPatientName(rawName: string) {
  const name = String(rawName || '')
    .trim()
    .replace(/^"+|"+$/g, '')
    .replace(/""/g, '"')
    .trim()
  if (!name) return { firstName: '', middleName: '', lastName: '', displayName: '' }
  if (name.includes(',')) {
    const [lastNamePart, firstMiddlePart] = name.split(',', 2).map((value) => value.trim())
    const firstMiddle = String(firstMiddlePart || '').split(/\s+/).filter(Boolean)
    const firstName = firstMiddle[0] || ''
    const middleName = firstMiddle.slice(1).join(' ')
    const lastName = String(lastNamePart || '').trim()
    return {
      firstName,
      middleName,
      lastName,
      displayName: [firstName, middleName, lastName].filter(Boolean).join(' ').trim(),
    }
  }
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return { firstName: '', middleName: '', lastName: parts[0], displayName: parts[0] }
  const firstName = parts[0]
  const lastName = parts[parts.length - 1]
  const middleName = parts.slice(1, -1).join(' ')
  return { firstName, middleName, lastName, displayName: [firstName, middleName, lastName].filter(Boolean).join(' ').trim() }
}

function parseMockCsvLine(line: string): string[] {
  const cells: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current)
      current = ''
      continue
    }
    current += ch
  }
  cells.push(current)
  return cells.map((cell) => cell.trim())
}

function normalizeMockPptDob(rawDob: string): string {
  const dob = String(rawDob || '').trim()
  if (!dob) return ''
  const direct = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (direct) return dob
  const dayFirst = dob.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/)
  if (dayFirst) {
    const day = dayFirst[1]
    const month = dayFirst[2]
    const year = dayFirst[3]
    const iso = `${year}-${month}-${day}`
    const parsedIso = new Date(`${iso}T00:00:00Z`)
    if (!Number.isNaN(parsedIso.getTime()) && parsedIso.toISOString().slice(0, 10) === iso) return iso
  }
  const parsed = new Date(dob)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().slice(0, 10)
}

function normalizeMockPptCard(card: string): string {
  return String(card || '').trim().toUpperCase().replace(/\s+/g, '')
}

function normalizeMockPpsn(value: string): string {
  return String(value || '').trim().toUpperCase().replace(/\s+/g, '')
}

function normalizeMockPptKey(name: string, dob: string): string {
  const normalizedName = String(name || '').toLowerCase().trim().replace(/\s+/g, ' ')
  const normalizedDob = normalizeMockPptDob(dob)
  if (!normalizedName || !normalizedDob) return ''
  return `${normalizedName}#${normalizedDob}`
}

function upsertMockPptPatientFromStc(record: MockStcRecord): void {
  const parsedName = normalizeMockPptPatientName(record.patientName || String(record.formData?.patientName || ''))
  const displayName = parsedName.displayName
  const card = normalizeMockPptCard(String(record.gmsNumber || record.formData?.gmsNumber || ''))
  const ppsn = normalizeMockPpsn(String(record.ppsNumber || record.formData?.ppsNumber || ''))
  const dateOfBirth = normalizeMockPptDob(String(record.formData?.dateOfBirth || record.formData?.dob || ''))
  const patientKey = normalizeMockPptKey(displayName, dateOfBirth)
  if (!displayName && !card) return

  const existing = mockPptPatients.find((patient) => (
    (card && patient.gmsOrDvcNumber === card) ||
    (!card && patientKey && patient.patientKey === patientKey)
  ))
  const now = new Date().toISOString()
  if (existing) {
    existing.firstName = existing.firstName || parsedName.firstName || undefined
    existing.middleName = existing.middleName || parsedName.middleName || undefined
    existing.lastName = existing.lastName || parsedName.lastName || undefined
    existing.displayName = existing.displayName || displayName
    existing.gmsOrDvcNumber = existing.gmsOrDvcNumber || card || undefined
    existing.ppsn = existing.ppsn || ppsn || undefined
    existing.dateOfBirth = existing.dateOfBirth || dateOfBirth || undefined
    existing.patientKey = existing.patientKey || patientKey || undefined
    existing.updatedAt = now
    return
  }

  mockPptPatients.push({
    id: `ppt-auto-${crypto.randomUUID()}`,
    tenantId: 'mock-tenant',
    firstName: parsedName.firstName || undefined,
    middleName: parsedName.middleName || undefined,
    lastName: parsedName.lastName || undefined,
    displayName,
    dateOfBirth: dateOfBirth || undefined,
    gmsOrDvcNumber: card || undefined,
    ppsn: ppsn || undefined,
    patientKey: patientKey || undefined,
    importSource: 'stc-auto',
    importBatchId: undefined,
    createdAt: now,
    updatedAt: now,
  })
}

// Seed STCS/SS claim report with sample records (mix of statuses and form types)
const mockStcRecords: MockStcRecord[] = (() => {
  const now = Date.now()
  const day = 24 * 60 * 60 * 1000
  const iso = (d: Date) => d.toISOString()
  const patients: { id: string; patientName: string; gmsNumber: string }[] = [
    { id: '1', patientName: 'John Doe', gmsNumber: '0032063A' },
    { id: '2', patientName: 'Jane Smith', gmsNumber: '0032064B' },
    { id: '3', patientName: "Michael O'Brien", gmsNumber: '0032065C' },
  ]
  const statuses: Array<MockStcRecord['status']> = ['draft', 'signed', 'queued', 'to_pay', 'paid', 'failed', 'rejected']
  const formTypes: Array<MockStcRecord['formType']> = ['STC', 'MICS']
  const records: MockStcRecord[] = []
  for (let i = 1; i <= 75; i += 1) {
    const patient = patients[(i - 1) % patients.length]
    const status = statuses[(i - 1) % statuses.length]
    const formType = formTypes[(i - 1) % formTypes.length]
    const daysAgo = 1 + (i % 45)
    const created = new Date(now - daysAgo * day)
    const signed = status !== 'draft' ? new Date(now - (daysAgo - 1) * day) : null
    const processed = status === 'paid' ? new Date(now - (daysAgo - 3) * day) : null
    const feeCode = MOCK_STC_FEE_CODES[(i - 1) % MOCK_STC_FEE_CODES.length]
    const estimatedFee = MOCK_STC_FEE_BY_CODE[feeCode] ?? null
    const hasSubmittedToPcrs = status === 'to_pay' || status === 'paid'
    const claimNumber = hasSubmittedToPcrs ? String(112313700 + i) : null
    const formNumber = hasSubmittedToPcrs ? String(741400 + i) : null
    const claimSummaryUrl = hasSubmittedToPcrs
      ? `https://secure.sspcrs.ie/stcss/sec/claim/${1794000 + i}?mode=saved`
      : null
    const failedSubmissionMeta =
      status === 'failed'
        ? {
            lastSubmissionAttemptAt: iso(new Date(now - 2 * 60 * 60 * 1000)),
            lastSubmissionResult: 'failed' as const,
            lastSubmissionErrorMessage: 'Mock: claim submission failed (use S on STC report to return to signed)',
            lastSubmissionHttpStatus: 502,
            lastSubmissionDocNum: patient.gmsNumber,
            lastSubmissionDocNam: 'DR. MOCK PROVIDER',
          }
        : {}
    records.push({
      id: `stc-seed-${i}`,
      patientId: patient.id,
      patientKey: `NAME#${patient.patientName.toLowerCase()}`,
      patientName: patient.patientName,
      gmsNumber: patient.gmsNumber,
      feeCode,
      estimatedFee,
      consultationId: i <= 2 && patient.id === '1' ? String(i) : `c-stc-${i}`,
      formType,
      status,
      claimNumber,
      formNumber,
      ...failedSubmissionMeta,
      lastSubmissionClaimSummaryUrl: claimSummaryUrl,
      signatureS3Key: status !== 'draft' ? `stc-signatures/stc-seed-${i}/signature.png` : null,
      formPngS3Key: status !== 'draft' && i <= 5 ? `stc-forms/stc-seed-${i}/form.png` : null,
      formData: {
        patientName: patient.patientName,
        gmsNumber: patient.gmsNumber,
        ...(formType === 'STC' ? { specialService: feeCode } : { micsService: feeCode }),
      },
      createdAt: iso(created),
      signedAt: signed ? iso(signed) : null,
      processedAt: processed ? iso(processed) : null,
    })
  }
  return records
})()

const mockStcBatchRuns: MockStcBatchRun[] = []
const mockPptImportJobs: MockPptImportJob[] = []
const mockPptPatients: MockPptPatient[] = [
  {
    id: 'ppt-patient-1',
    tenantId: 'mock-tenant',
    firstName: 'John',
    middleName: '',
    lastName: 'Doe',
    displayName: 'John Doe',
    dateOfBirth: '1984-05-10',
    gmsOrDvcNumber: '0032063A',
    patientKey: 'john doe#1984-05-10',
    importSource: 'seed',
    importBatchId: 'seed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
  },
  {
    id: 'ppt-patient-2',
    tenantId: 'mock-tenant',
    firstName: 'Jane',
    middleName: '',
    lastName: 'Smith',
    displayName: 'Jane Smith',
    dateOfBirth: '1979-11-22',
    gmsOrDvcNumber: '0032064B',
    patientKey: 'jane smith#1979-11-22',
    importSource: 'seed',
    importBatchId: 'seed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
  },
  {
    id: 'ppt-patient-3',
    tenantId: 'mock-tenant',
    firstName: 'Niamh',
    middleName: '',
    lastName: 'Byrne',
    displayName: 'Niamh Byrne',
    dateOfBirth: '1991-10-09',
    gmsOrDvcNumber: '',
    ppsn: '9876543N',
    patientKey: 'niamh byrne#1991-10-09',
    importSource: 'seed',
    importBatchId: 'seed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 9).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 9).toISOString(),
  },
  {
    id: 'ppt-patient-4',
    tenantId: 'mock-tenant',
    firstName: 'Mary',
    middleName: '',
    lastName: 'Murphy',
    displayName: 'Mary Murphy',
    dateOfBirth: '1980-01-01',
    gmsOrDvcNumber: '1111111A',
    ppsn: '1234567AA',
    patientKey: 'mary murphy#1980-01-01',
    importSource: 'seed',
    importBatchId: 'seed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
  },
  {
    id: 'ppt-patient-5',
    tenantId: 'mock-tenant',
    firstName: 'Mary',
    middleName: '',
    lastName: 'Murphy',
    displayName: 'Mary Murphy',
    dateOfBirth: '1992-02-02',
    gmsOrDvcNumber: '2222222B',
    ppsn: '2345678BB',
    patientKey: 'mary murphy#1992-02-02',
    importSource: 'seed',
    importBatchId: 'seed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
  },
]

const mockCertificates: any[] = [
  {
    id: 'cert-pcrs-1',
    type: 'PCRS',
    name: 'PCRS Production Certificate',
    description: 'Primary Care Reimbursement Service (production)',
    serialNumber: 'A1B2C3D4E5F6',
    issuer: 'HSE Certificate Authority',
    cipherSummary: 'Shrouded Keybag: pbeWithSHA1And3-KeyTripleDES-CBC; PKCS7 Encrypted data: pbeWithSHA1And3-KeyTripleDES-CBC',
    legacyCipher: false,
    status: 'active',
    isActive: true,
    validFrom: new Date(Date.now() - 1000 * 60 * 60 * 24 * 120).toISOString(),
    validTo: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 120).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
  },
  {
    id: 'cert-healthlink-1',
    type: 'Healthlink',
    name: 'Healthlink Messaging Certificate',
    description: 'Healthlink secure messaging',
    serialNumber: 'HLNK-2026-0001',
    issuer: 'Healthlink CA',
    cipherSummary: 'Shrouded Keybag: pbeWithSHA1And3-KeyTripleDES-CBC',
    legacyCipher: false,
    status: 'pending',
    isActive: false,
    validFrom: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    validTo: new Date(Date.now() + 1000 * 60 * 60 * 24 * 350).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
  },
  {
    id: 'cert-pcrs-expired',
    type: 'PCRS',
    name: 'PCRS Legacy Certificate',
    description: 'Expired legacy certificate',
    serialNumber: 'LEGACY-0003',
    issuer: 'HSE Certificate Authority',
    cipherSummary: 'PKCS7 Encrypted data: pbeWithSHA1And40BitRC2-CBC',
    legacyCipher: true,
    status: 'expired',
    isActive: false,
    validFrom: new Date(Date.now() - 1000 * 60 * 60 * 24 * 900).toISOString(),
    validTo: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 900).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
  },
]

// Log what was loaded on initialization
console.log('[Mock API] Loaded', mockCustomPhrases.length, 'custom phrases from localStorage on init')
if (mockCustomPhrases.length > 0) {
  console.log('[Mock API] Initial phrases:', mockCustomPhrases.map(p => ({ userId: p.userId, trigger: p.trigger })))
}

// Helper to load signature image for dev mode
// In production, signatures are uploaded by users
let devSignatureBase64: string | null = null
async function loadDevSignature(): Promise<string | null> {
  if (devSignatureBase64) return devSignatureBase64
  try {
    const response = await fetch('/signature-placeholder.png')
    if (!response.ok) return null
    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1]
        devSignatureBase64 = base64
        resolve(base64)
      }
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

// In-memory storage for local development
const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    email: 'admin@clnicl.ie',
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    roleCategory: 'Administrative',
    permissions: ['*'],
    isActive: true,
    twoFactorEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
  },
  {
    id: '2',
    username: 'doctor',
    email: 'doctor@clnicl.ie',
    salutation: 'Dr',
    firstName: 'Sarah',
    middleName: 'Marie',
    lastName: 'O\'Brien',
    role: 'doctor',
    roleCategory: 'Clinical',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write'],
    isActive: true,
    twoFactorEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastLoginAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    phone: '+353 1 234 5678',
    mobile: '+353 87 123 4567',
    address: {
      street: '123 Main Street',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D02 XY12',
      country: 'Ireland',
    },
    hpi: 'HPI123456',
    professionalBodyRegistrationNumber: '12345',
    gmsNumber: 'GMS-001',
    contractualArrangements: [
      {
        type: 'full-time',
        startDate: '2020-01-01',
      },
    ],
    signatureS3Key: undefined, // Will be loaded from file in getUsers()
  },
  {
    id: '3',
    username: 'nurse',
    email: 'nurse@clnicl.ie',
    salutation: 'Ms',
    firstName: 'Patricia',
    lastName: 'Murphy',
    role: 'nurse',
    roleCategory: 'Clinical',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write', 'lab-results:read', 'lab-results:write'],
    isActive: true,
    twoFactorEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    phone: '+353 1 234 5679',
    mobile: '+353 87 123 4568',
    address: {
      street: '456 Oak Avenue',
      city: 'Cork',
      state: 'Munster',
      postalCode: 'T12 XY34',
      country: 'Ireland',
    },
    professionalBodyRegistrationNumber: '67890',
    prescriberIdentifierNumber: 'PIN-001',
    contractualArrangements: [
      {
        type: 'part-time',
        startDate: '2021-06-01',
      },
    ],
    signatureS3Key: undefined, // Will be loaded from file in getUsers()
  },
  {
    id: '4',
    username: 'reception',
    email: 'reception@clnicl.ie',
    firstName: 'Reception',
    lastName: 'Staff',
    role: 'receptionist',
    roleCategory: 'Administrative',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write', 'patients:override:emergency'],
    isActive: true,
    twoFactorEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '5',
    username: 'emily.kavanagh',
    email: 'doctor2@clnicl.ie',
    firstName: 'Dr. Emily',
    lastName: 'Kavanagh',
    role: 'doctor',
    roleCategory: 'Clinical',
    permissions: ['patients:read', 'patients:write', 'appointments:read', 'appointments:write', 'templates:read', 'templates:write'],
    isActive: true,
    twoFactorEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    professionalBodyRegistrationNumber: '54321',
    gmsNumber: 'GMS-002',
    signatureS3Key: undefined, // Signatures uploaded separately
  },
]

// In-memory storage for role permissions
const mockRolePermissions: RolePermission[] = (() => {
  const now = new Date().toISOString()
  const allRoles: UserRole[] = [
    'admin',
    'doctor',
    'nurse',
    'receptionist',
    'physician-assistant',
    'therapist',
    'billing',
    'office-manager',
    'system-admin',
    'it-support',
  ]
  
  return allRoles.map((role) => ({
    role,
    roleCategory: getRoleCategory(role),
    permissions: DEFAULT_ROLE_PERMISSIONS[role] || [],
    createdAt: now,
    updatedAt: now,
  }))
})()

// In-memory storage for tasks
const mockTasks: Task[] = [
  {
    id: 'task-1',
    title: 'Review patient lab results',
    description: 'Review and follow up on recent lab results for patient #1',
    status: 'pending',
    priority: 'high',
    assignedTo: '2',
    assignedBy: '1',
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    relatedEntityType: 'lab-result',
    relatedEntityId: '1',
    tags: ['urgent', 'lab-results'],
  },
  {
    id: 'task-2',
    title: 'Schedule follow-up appointment',
    description: 'Patient requested follow-up for next week',
    status: 'in-progress',
    priority: 'medium',
    assignedTo: '4',
    assignedBy: '2',
    dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    relatedEntityType: 'patient',
    relatedEntityId: '1',
    tags: ['appointment'],
  },
  {
    id: 'task-3',
    title: 'Complete consultation notes',
    description: 'Finish documentation for consultation #1',
    status: 'pending',
    priority: 'medium',
    assignedTo: '2',
    assignedBy: '2',
    dueDate: new Date().toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    relatedEntityType: 'consultation',
    relatedEntityId: '1',
    tags: ['documentation'],
  },
  {
    id: 'task-4',
    title: 'Review pending tasks',
    description: 'Review and prioritize pending clinical tasks',
    status: 'assigned',
    priority: 'high',
    assignedTo: '1',
    assignedBy: '1',
    dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tags: ['review', 'priority'],
  },
  {
    id: 'task-5',
    title: 'Update system configuration',
    description: 'Review and update tenant settings',
    status: 'created',
    priority: 'medium',
    assignedTo: '1',
    assignedBy: '1',
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tags: ['admin', 'configuration'],
  },
]

const mockCommunicationLogs: CommunicationLog[] = [
  {
    id: 'comm-1',
    patientId: '1',
    communicationType: 'telephone',
    direction: 'incoming',
    participants: [
      { name: 'John Doe', role: 'patient' },
      { name: 'Sarah O\'Brien', role: 'provider' },
    ],
    dateTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    duration: 12,
    summary: 'Discussed lab results and follow-up plan.',
    notes: 'Patient agreed to schedule appointment next week.',
    recordedBy: '1',
    recordedByName: 'Sarah O\'Brien',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

// In-memory storage for automation rules (mirrors backend DEFAULT_RULES)
let mockAutomationRules: AutomationRule[] = [
  {
    id: 'lab-result-follow-up',
    name: 'Lab Result Follow-Up',
    description: 'Create task when lab result requires review',
    triggerType: 'lab-result-created',
    enabled: true,
    condition: {
      field: 'reviewStatus',
      operator: 'equals',
      value: 'in-review',
    },
    action: {
      type: 'create-task',
      title: 'Follow up on lab result',
      description: 'Lab result requires review and follow-up',
      priority: 'high',
      urgency: 'urgent',
      ehrComponentType: 'lab-result',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'medication-review-due',
    name: 'Medication Review Due',
    description: 'Create task when medication review date is reached',
    triggerType: 'medication-review-due',
    enabled: true,
    condition: {
      field: 'reviewDueDate',
      operator: 'lessThanOrEqual',
      value: 'now',
    },
    action: {
      type: 'create-task',
      title: 'Medication review due',
      description: 'Patient medication requires review',
      priority: 'medium',
      urgency: 'routine',
      ehrComponentType: 'medication',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

// In-memory storage for chat messages
const mockMessages: Message[] = [
  {
    id: 'msg-1',
    content: 'Welcome to the team chat! This is where we can communicate quickly.',
    senderId: '1',
    isTeamMessage: true,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'msg-2',
    content: 'Thanks! Looking forward to working with everyone.',
    senderId: '2',
    isTeamMessage: true,
    createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  },
]

// In-memory medication reference data (sample subset for local dev)
const mockMedicationReferences: MedicationReference[] = [
  // Interchangeable Group IC001 - Paracetamol variants
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG001',
    productName: 'Paracetamol 500mg Tablets',
    strength: '500 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/001/001',
    atcCodes: ['N02BE01'],
    activeSubstances: ['Paracetamol'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC001',
    gmsReimbursable: true,
    gmsCodes: ['GMS001'],
    packSize: 32,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG001A',
    productName: 'Paracetamol 500mg Tablets (Generic)',
    strength: '500 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/001/002',
    atcCodes: ['N02BE01'],
    activeSubstances: ['Paracetamol'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC001',
    gmsReimbursable: true,
    gmsCodes: ['GMS001'],
    packSize: 30,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG001B',
    productName: 'Paracetamol 500mg Tablets (Brand)',
    strength: '500 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/001/003',
    atcCodes: ['N02BE01'],
    activeSubstances: ['Paracetamol'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC001',
    gmsReimbursable: true,
    gmsCodes: ['GMS001'],
    packSize: 50,
  },
  // Interchangeable Group IC002 - Ibuprofen variants
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG002',
    productName: 'Ibuprofen 200mg Tablets',
    strength: '200 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/002/001',
    atcCodes: ['M01AE01'],
    activeSubstances: ['Ibuprofen'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC002',
    gmsReimbursable: true,
    gmsCodes: ['GMS002'],
    packSize: 24,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG002A',
    productName: 'Ibuprofen 200mg Tablets (Generic)',
    strength: '200 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/002/002',
    atcCodes: ['M01AE01'],
    activeSubstances: ['Ibuprofen'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC002',
    gmsReimbursable: true,
    gmsCodes: ['GMS002'],
    packSize: 30,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG002B',
    productName: 'Ibuprofen 400mg Tablets',
    strength: '400 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/002/003',
    atcCodes: ['M01AE01'],
    activeSubstances: ['Ibuprofen'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC002',
    gmsReimbursable: true,
    gmsCodes: ['GMS002'],
    packSize: 28,
  },
  // Interchangeable Group IC003 - Amoxicillin variants
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG003',
    productName: 'Amoxicillin 500mg Capsules',
    strength: '500 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/003/001',
    atcCodes: ['J01CA04'],
    activeSubstances: ['Amoxicillin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC003',
    gmsReimbursable: true,
    gmsCodes: ['GMS003'],
    packSize: 21,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG003A',
    productName: 'Amoxicillin 500mg Capsules (Generic)',
    strength: '500 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/003/002',
    atcCodes: ['J01CA04'],
    activeSubstances: ['Amoxicillin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC003',
    gmsReimbursable: true,
    gmsCodes: ['GMS003'],
    packSize: 20,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG003B',
    productName: 'Amoxicillin 250mg Capsules',
    strength: '250 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/003/003',
    atcCodes: ['J01CA04'],
    activeSubstances: ['Amoxicillin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC003',
    gmsReimbursable: true,
    gmsCodes: ['GMS003'],
    packSize: 21,
  },
  // Interchangeable Group IC004 - Salbutamol inhalers
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG004',
    productName: 'Salbutamol 100mcg Inhaler',
    strength: '100 mcg',
    dosageForm: 'Pressurised Inhalation',
    routesOfAdministration: ['Inhalation'],
    licenceNumber: 'PA1234/004/001',
    atcCodes: ['R03AC02'],
    activeSubstances: ['Salbutamol'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC004',
    gmsReimbursable: true,
    gmsCodes: ['GMS004'],
    packSize: 1,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG004A',
    productName: 'Salbutamol 100mcg Inhaler (Generic)',
    strength: '100 mcg',
    dosageForm: 'Pressurised Inhalation',
    routesOfAdministration: ['Inhalation'],
    licenceNumber: 'PA1234/004/002',
    atcCodes: ['R03AC02'],
    activeSubstances: ['Salbutamol'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: true,
    interchangeableListCode: 'IC004',
    gmsReimbursable: true,
    gmsCodes: ['GMS004'],
    packSize: 1,
  },
  // Non-interchangeable medications
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG005',
    productName: 'Aspirin 75mg Tablets',
    strength: '75 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/005/001',
    atcCodes: ['B01AC06'],
    activeSubstances: ['Acetylsalicylic acid'],
    dispensingLegalStatus: 'OTC',
    supplyLegalStatus: 'Non-prescription',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS005'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG006',
    productName: 'Metformin 500mg Tablets',
    strength: '500 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/006/001',
    atcCodes: ['A10BA02'],
    activeSubstances: ['Metformin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS006'],
    packSize: 56,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG007',
    productName: 'Atorvastatin 20mg Tablets',
    strength: '20 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/007/001',
    atcCodes: ['C10AA05'],
    activeSubstances: ['Atorvastatin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS007'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG008',
    productName: 'Omeprazole 20mg Capsules',
    strength: '20 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/008/001',
    atcCodes: ['A02BC01'],
    activeSubstances: ['Omeprazole'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS008'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG009',
    productName: 'Lisinopril 10mg Tablets',
    strength: '10 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/009/001',
    atcCodes: ['C09AA03'],
    activeSubstances: ['Lisinopril'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS009'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG010',
    productName: 'Amlodipine 5mg Tablets',
    strength: '5 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/010/001',
    atcCodes: ['C08CA01'],
    activeSubstances: ['Amlodipine'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS010'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG011',
    productName: 'Simvastatin 40mg Tablets',
    strength: '40 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/011/001',
    atcCodes: ['C10AA01'],
    activeSubstances: ['Simvastatin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS011'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG012',
    productName: 'Ramipril 5mg Capsules',
    strength: '5 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/012/001',
    atcCodes: ['C09AA05'],
    activeSubstances: ['Ramipril'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS012'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG013',
    productName: 'Bisoprolol 5mg Tablets',
    strength: '5 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/013/001',
    atcCodes: ['C07AB02'],
    activeSubstances: ['Bisoprolol'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS013'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG014',
    productName: 'Furosemide 40mg Tablets',
    strength: '40 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/014/001',
    atcCodes: ['C03CA01'],
    activeSubstances: ['Furosemide'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS014'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG015',
    productName: 'Levothyroxine 50mcg Tablets',
    strength: '50 mcg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/015/001',
    atcCodes: ['H03AA01'],
    activeSubstances: ['Levothyroxine'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS015'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG016',
    productName: 'Atenolol 50mg Tablets',
    strength: '50 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/016/001',
    atcCodes: ['C07AB03'],
    activeSubstances: ['Atenolol'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS016'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG017',
    productName: 'Gliclazide 80mg Tablets',
    strength: '80 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/017/001',
    atcCodes: ['A10BB09'],
    activeSubstances: ['Gliclazide'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS017'],
    packSize: 60,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG018',
    productName: 'Warfarin 3mg Tablets',
    strength: '3 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/018/001',
    atcCodes: ['B01AA03'],
    activeSubstances: ['Warfarin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS018'],
    packSize: 28,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG019',
    productName: 'Prednisolone 5mg Tablets',
    strength: '5 mg',
    dosageForm: 'Tablet',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/019/001',
    atcCodes: ['H02AB06'],
    activeSubstances: ['Prednisolone'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS019'],
    packSize: 30,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG020',
    productName: 'Doxycycline 100mg Capsules',
    strength: '100 mg',
    dosageForm: 'Capsule',
    routesOfAdministration: ['Oral'],
    licenceNumber: 'PA1234/020/001',
    atcCodes: ['J01AA02'],
    activeSubstances: ['Doxycycline'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS020'],
    packSize: 14,
  },
  // Test entries with long product names to test column width fix
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG021',
    productName: 'Tramadol /Paracetamol Rowa 37.5 mg/325 mg Film-coated Tablets',
    strength: '37.5 mg/325 mg',
    dosageForm: 'Film-coated tablet',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/021/001',
    atcCodes: ['N02AJ', 'N02AJ13'],
    activeSubstances: ['Tramadol', 'Paracetamol'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS021'],
    packSize: 30,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG022',
    productName: 'Amoxicillin 125 mg/5 ml powder for oral suspension',
    strength: '125 mg/5 ml',
    dosageForm: 'Powder for oral suspension',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/022/001',
    atcCodes: ['J01CA', 'J01CA04'],
    activeSubstances: ['Amoxicillin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS022'],
    packSize: 100,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG023',
    productName: 'Ibuprofen /Pseudoephedrine Hydrochloride 200 mg/30 mg Modified-release Tablets',
    strength: '200 mg/30 mg',
    dosageForm: 'Modified-release tablet',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/023/001',
    atcCodes: ['M01AE', 'R01BA'],
    activeSubstances: ['Ibuprofen', 'Pseudoephedrine'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS023'],
    packSize: 24,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG024',
    productName: 'Paracetamol /Codeine Phosphate Hemihydrate 500 mg/30 mg Effervescent Tablets',
    strength: '500 mg/30 mg',
    dosageForm: 'Effervescent tablet',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/024/001',
    atcCodes: ['N02AJ', 'N02AJ06'],
    activeSubstances: ['Paracetamol', 'Codeine'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS024'],
    packSize: 20,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG025',
    productName: 'Metformin Hydrochloride 500 mg Prolonged-release Tablets',
    strength: '500 mg',
    dosageForm: 'Prolonged-release tablet',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/025/001',
    atcCodes: ['A10BA', 'A10BA02'],
    activeSubstances: ['Metformin'],
    dispensingLegalStatus: 'Prescription only',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS025'],
    packSize: 56,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG026',
    productName: 'Methadone 1 mg/ml Oral Solution',
    strength: '1 mg/ml',
    dosageForm: 'Oral solution',
    routesOfAdministration: ['Oral use'],
    licenceNumber: 'PA1234/026/001',
    atcCodes: ['N07BC', 'N07BC01'],
    activeSubstances: ['Methadone'],
    dispensingLegalStatus: 'Controlled drug',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS026'],
    packSize: 500,
  },
  {
    tenantId: 'demo-tenant',
    drugIdPK: 'DRUG027',
    productName: 'Buprenorphine /Naloxone 8 mg/2 mg Sublingual Tablets',
    strength: '8 mg/2 mg',
    dosageForm: 'Sublingual tablet',
    routesOfAdministration: ['Sublingual use'],
    licenceNumber: 'PA1234/027/001',
    atcCodes: ['N07BC', 'N07BC51'],
    activeSubstances: ['Buprenorphine', 'Naloxone'],
    dispensingLegalStatus: 'Controlled drug',
    supplyLegalStatus: 'Prescription only',
    isWithdrawn: false,
    isInterchangeable: false,
    gmsReimbursable: true,
    gmsCodes: ['GMS027'],
    packSize: 28,
  },
]

// In-memory patient immunisations list for local development
const mockPatientImmunisations: Immunisation[] = [
  // Patient 1 (adult) - non-childhood vaccines
  {
    id: 'imm-1',
    immunisationId: 'imm-1',
    patientId: '1',
    tenantId: 'demo-tenant',
    date: '2024-09-15',
    type: 'Flu',
    batchNumber: 'FLU2024-001',
    manufacturer: 'GSK',
    expiryDate: '2025-06-30',
    siteGiven: 'Left deltoid',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    practiceVaccineId: 'Flu#FLU2024-001', // Connected to practice inventory
    notes: 'Annual flu vaccination',
    consentGiven: true,
    consentType: 'patient',
    consentDate: '2024-09-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-09-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-09-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-2',
    immunisationId: 'imm-2',
    patientId: '1',
    tenantId: 'demo-tenant',
    date: '2023-10-20',
    type: 'COVID-19',
    batchNumber: 'COVID-2023-ABC',
    manufacturer: 'Pfizer',
    expiryDate: '2024-12-31',
    siteGiven: 'Right deltoid',
    methodOfAdmin: 'IM',
    dose: { value: 0.3, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    notes: 'Booster dose',
    consentGiven: true,
    consentType: 'patient',
    consentDate: '2023-10-20',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2023-10-20').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2023-10-20').toISOString(),
    updatedBy: '2',
  },
  // Patient 2 (child) - childhood schedule vaccines for testing schedule logic
  // DOB: 2023-11-15 (approximately 13 months old as of Dec 2024)
  {
    id: 'imm-3',
    immunisationId: 'imm-3',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-01-15', // ~2 months old
    type: '6 in 1',
    batchNumber: '6IN1-2024-001',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    siteGiven: 'Left thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-2m',
    practiceVaccineId: '6 in 1#6IN1-2024-001', // Connected to practice inventory
    notes: '2 month schedule - first dose',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-01-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-01-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-01-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-4',
    immunisationId: 'imm-4',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-01-15', // ~2 months old
    type: 'MenB',
    batchNumber: 'MENB-2024-001',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    siteGiven: 'Right thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-2m',
    notes: '2 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-01-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-01-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-01-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-5',
    immunisationId: 'imm-5',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-01-15', // ~2 months old
    type: 'PCV',
    batchNumber: 'PCV-2024-001',
    manufacturer: 'Pfizer',
    expiryDate: '2025-12-31',
    siteGiven: 'Left thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-2m',
    notes: '2 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-01-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-01-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-01-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-6',
    immunisationId: 'imm-6',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-03-15', // ~4 months old
    type: '6 in 1',
    batchNumber: '6IN1-2024-002',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    siteGiven: 'Left thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-4m',
    notes: '4 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-03-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-03-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-03-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-7',
    immunisationId: 'imm-7',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-03-15', // ~4 months old
    type: 'MenB',
    batchNumber: 'MENB-2024-002',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    siteGiven: 'Right thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-4m',
    notes: '4 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-03-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-03-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-03-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-8',
    immunisationId: 'imm-8',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-05-15', // ~6 months old
    type: '6 in 1',
    batchNumber: '6IN1-2024-003',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    siteGiven: 'Left thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-6m',
    notes: '6 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-05-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-05-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-05-15').toISOString(),
    updatedBy: '2',
  },
  {
    id: 'imm-9',
    immunisationId: 'imm-9',
    patientId: '2',
    tenantId: 'demo-tenant',
    date: '2024-05-15', // ~6 months old
    type: 'PCV',
    batchNumber: 'PCV-2024-002',
    manufacturer: 'Pfizer',
    expiryDate: '2025-12-31',
    siteGiven: 'Right thigh',
    methodOfAdmin: 'IM',
    dose: { value: 0.5, unit: 'ml' },
    administeredBy: '2',
    administeredByName: 'Dr. Sarah O\'Brien',
    scheduledVaccineId: 'sched-2-6m',
    notes: '6 month schedule',
    consentGiven: true,
    consentType: 'parent',
    consentDate: '2024-05-15',
    expiredVaccineUsed: false,
    adverseReactionChecked: true,
    recommendedByGuideline: true,
    createdAt: new Date('2024-05-15').toISOString(),
    createdBy: '2',
    updatedAt: new Date('2024-05-15').toISOString(),
    updatedBy: '2',
  },
  // Note: 12 month vaccines (MMR, MenB, Chickenpox) are NOT given yet - will show as "due" or "overdue"
  // 13 month vaccines (6 in 1, MenC, PCV) are NOT given yet - will show as "due" or "overdue"
]

// In-memory patient medications list for local development
// Mock vaccine reference data (NIAC guidelines)
// Mock assessments data
const mockAssessments: Assessment[] = []

// Mock care plans data
const mockCarePlans: any[] = [
  // Template: Diabetes Management Plan
  {
    id: 'template-diabetes-001',
    patientId: undefined, // Templates don't have patientId
    title: 'Type 2 Diabetes Management Plan',
    description: 'Comprehensive care plan for managing Type 2 Diabetes including medication, monitoring, and lifestyle interventions.',
    status: 'active',
    isTemplate: true,
    templateCategory: 'diabetes',
    goals: [
      {
        id: 'goal-1',
        description: 'Achieve and maintain HbA1c < 7.0%',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Primary goal for diabetes control',
      },
      {
        id: 'goal-2',
        description: 'Maintain blood pressure < 130/80 mmHg',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Important for cardiovascular health',
      },
      {
        id: 'goal-3',
        description: 'Achieve 5-10% weight loss if BMI > 25',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Weight management improves insulin sensitivity',
      },
    ],
    interventions: [
      {
        id: 'intervention-1',
        type: 'medication',
        description: 'Metformin 500mg twice daily with meals',
        frequency: 'Twice daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'First-line medication for Type 2 Diabetes',
      },
      {
        id: 'intervention-2',
        type: 'monitoring',
        description: 'Self-monitoring of blood glucose (SMBG)',
        frequency: 'Twice daily (fasting and post-prandial)',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Monitor fasting and 2-hour post-meal glucose',
      },
      {
        id: 'intervention-3',
        type: 'lifestyle',
        description: 'Dietary modification - Low glycemic index diet',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Focus on whole grains, vegetables, lean proteins',
      },
      {
        id: 'intervention-4',
        type: 'lifestyle',
        description: 'Regular physical activity - 150 minutes/week moderate intensity',
        frequency: '5 times per week',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Walking, cycling, or swimming recommended',
      },
      {
        id: 'intervention-5',
        type: 'monitoring',
        description: 'Quarterly HbA1c testing',
        frequency: 'Every 3 months',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Track long-term glucose control',
      },
    ],
    outcomes: [
      {
        id: 'outcome-1',
        description: 'HbA1c level',
        measurement: 'Laboratory test',
        targetValue: '< 7.0%',
        status: 'pending',
        notes: 'Primary outcome measure',
      },
      {
        id: 'outcome-2',
        description: 'Blood pressure',
        measurement: 'Clinical measurement',
        targetValue: '< 130/80 mmHg',
        status: 'pending',
        notes: 'Monitor at each visit',
      },
      {
        id: 'outcome-3',
        description: 'Body weight',
        measurement: 'Clinical measurement',
        targetValue: '5-10% reduction if BMI > 25',
        status: 'pending',
        notes: 'Track monthly',
      },
    ],
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'doctor@clnicl.ie',
    updatedBy: 'doctor@clnicl.ie',
  },
  // Template: Cardiac Rehabilitation Plan
  {
    id: 'template-cardiac-001',
    patientId: undefined,
    title: 'Post-MI Cardiac Rehabilitation Plan',
    description: 'Structured rehabilitation plan following myocardial infarction focusing on medication adherence, exercise, and risk factor modification.',
    status: 'active',
    isTemplate: true,
    templateCategory: 'cardiac',
    goals: [
      {
        id: 'goal-1',
        description: 'Complete 12-week cardiac rehabilitation program',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Structured program with exercise and education',
      },
      {
        id: 'goal-2',
        description: 'Achieve LDL cholesterol < 1.8 mmol/L',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Secondary prevention target',
      },
      {
        id: 'goal-3',
        description: 'Maintain blood pressure < 140/90 mmHg',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Cardiovascular risk reduction',
      },
    ],
    interventions: [
      {
        id: 'intervention-1',
        type: 'medication',
        description: 'Dual antiplatelet therapy (DAPT)',
        frequency: 'As prescribed',
        duration: '12 months post-MI',
        status: 'not-started',
        notes: 'Aspirin + P2Y12 inhibitor (e.g., Clopidogrel)',
      },
      {
        id: 'intervention-2',
        type: 'medication',
        description: 'High-intensity statin therapy',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Atorvastatin 80mg or Rosuvastatin 40mg',
      },
      {
        id: 'intervention-3',
        type: 'therapy',
        description: 'Cardiac rehabilitation exercise program',
        frequency: '3 times per week',
        duration: '12 weeks',
        status: 'not-started',
        notes: 'Supervised exercise with gradual progression',
      },
      {
        id: 'intervention-4',
        type: 'lifestyle',
        description: 'Mediterranean diet',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Emphasize fruits, vegetables, whole grains, fish',
      },
      {
        id: 'intervention-5',
        type: 'lifestyle',
        description: 'Smoking cessation support',
        frequency: 'As needed',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Refer to smoking cessation service if applicable',
      },
      {
        id: 'intervention-6',
        type: 'monitoring',
        description: 'Lipid panel every 3 months',
        frequency: 'Every 3 months',
        duration: 'First year, then annually',
        status: 'not-started',
        notes: 'Monitor LDL, HDL, triglycerides',
      },
    ],
    outcomes: [
      {
        id: 'outcome-1',
        description: 'Exercise capacity',
        measurement: '6-minute walk test or stress test',
        targetValue: 'Improvement from baseline',
        status: 'pending',
        notes: 'Assess at start and end of program',
      },
      {
        id: 'outcome-2',
        description: 'LDL cholesterol',
        measurement: 'Laboratory test',
        targetValue: '< 1.8 mmol/L',
        status: 'pending',
        notes: 'Secondary prevention target',
      },
      {
        id: 'outcome-3',
        description: 'Medication adherence',
        measurement: 'Patient self-report and pharmacy records',
        targetValue: '> 80% adherence',
        status: 'pending',
        notes: 'Critical for secondary prevention',
      },
    ],
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'doctor@clnicl.ie',
    updatedBy: 'doctor@clnicl.ie',
  },
  // Template: Hypertension Management Plan
  {
    id: 'template-hypertension-001',
    patientId: undefined,
    title: 'Hypertension Management Plan',
    description: 'Comprehensive plan for managing hypertension through medication, lifestyle modifications, and regular monitoring.',
    status: 'active',
    isTemplate: true,
    templateCategory: 'cardiac',
    goals: [
      {
        id: 'goal-1',
        description: 'Achieve target blood pressure < 140/90 mmHg (or < 130/80 if diabetes/CKD)',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Individualized target based on comorbidities',
      },
      {
        id: 'goal-2',
        description: 'Reduce sodium intake to < 2g/day',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Dietary modification',
      },
    ],
    interventions: [
      {
        id: 'intervention-1',
        type: 'medication',
        description: 'ACE inhibitor or ARB (first-line)',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Enalapril, Lisinopril, or Losartan',
      },
      {
        id: 'intervention-2',
        type: 'lifestyle',
        description: 'DASH diet (Dietary Approaches to Stop Hypertension)',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Rich in fruits, vegetables, low-fat dairy, whole grains',
      },
      {
        id: 'intervention-3',
        type: 'lifestyle',
        description: 'Regular aerobic exercise',
        frequency: '5 times per week, 30 minutes',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Moderate intensity exercise',
      },
      {
        id: 'intervention-4',
        type: 'monitoring',
        description: 'Home blood pressure monitoring',
        frequency: 'Twice daily (morning and evening)',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Record readings in log book',
      },
    ],
    outcomes: [
      {
        id: 'outcome-1',
        description: 'Blood pressure',
        measurement: 'Clinical and home monitoring',
        targetValue: '< 140/90 mmHg',
        status: 'pending',
        notes: 'Monitor weekly initially, then monthly when stable',
      },
      {
        id: 'outcome-2',
        description: 'Medication adherence',
        measurement: 'Patient self-report',
        targetValue: '> 80% adherence',
        status: 'pending',
        notes: 'Assess at each visit',
      },
    ],
    createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'doctor@clnicl.ie',
    updatedBy: 'doctor@clnicl.ie',
  },
  // Template: COPD Management Plan
  {
    id: 'template-respiratory-001',
    patientId: undefined,
    title: 'COPD Management Plan',
    description: 'Comprehensive care plan for Chronic Obstructive Pulmonary Disease focusing on symptom management, medication, and pulmonary rehabilitation.',
    status: 'active',
    isTemplate: true,
    templateCategory: 'respiratory',
    goals: [
      {
        id: 'goal-1',
        description: 'Reduce frequency of exacerbations',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Target < 2 exacerbations per year',
      },
      {
        id: 'goal-2',
        description: 'Improve exercise tolerance',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Increase 6-minute walk distance',
      },
      {
        id: 'goal-3',
        description: 'Maintain FEV1 > 50% predicted',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Monitor lung function',
      },
    ],
    interventions: [
      {
        id: 'intervention-1',
        type: 'medication',
        description: 'Long-acting bronchodilator (LABA or LAMA)',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Salmeterol, Formoterol, or Tiotropium',
      },
      {
        id: 'intervention-2',
        type: 'medication',
        description: 'Inhaled corticosteroid (if frequent exacerbations)',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Budesonide or Fluticasone',
      },
      {
        id: 'intervention-3',
        type: 'therapy',
        description: 'Pulmonary rehabilitation program',
        frequency: '2-3 times per week',
        duration: '8-12 weeks',
        status: 'not-started',
        notes: 'Exercise training and education',
      },
      {
        id: 'intervention-4',
        type: 'lifestyle',
        description: 'Smoking cessation (if applicable)',
        frequency: 'As needed',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Critical intervention',
      },
      {
        id: 'intervention-5',
        type: 'monitoring',
        description: 'Annual spirometry',
        frequency: 'Annually',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Monitor FEV1 decline',
      },
    ],
    outcomes: [
      {
        id: 'outcome-1',
        description: 'FEV1',
        measurement: 'Spirometry',
        targetValue: 'Maintain > 50% predicted',
        status: 'pending',
        notes: 'Annual assessment',
      },
      {
        id: 'outcome-2',
        description: 'Exacerbation frequency',
        measurement: 'Patient report and medical records',
        targetValue: '< 2 per year',
        status: 'pending',
        notes: 'Track episodes requiring antibiotics/steroids',
      },
      {
        id: 'outcome-3',
        description: 'Exercise capacity',
        measurement: '6-minute walk test',
        targetValue: 'Improvement from baseline',
        status: 'pending',
        notes: 'Assess before and after pulmonary rehab',
      },
    ],
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'doctor@clnicl.ie',
    updatedBy: 'doctor@clnicl.ie',
  },
  // Template: General Wellness Plan
  {
    id: 'template-general-001',
    patientId: undefined,
    title: 'General Wellness and Preventive Care Plan',
    description: 'Comprehensive preventive care plan focusing on health maintenance, screening, and lifestyle optimization.',
    status: 'active',
    isTemplate: true,
    templateCategory: 'preventive',
    goals: [
      {
        id: 'goal-1',
        description: 'Maintain healthy weight (BMI 18.5-24.9)',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Regular monitoring and lifestyle support',
      },
      {
        id: 'goal-2',
        description: 'Complete age-appropriate health screenings',
        targetDate: undefined,
        status: 'not-started',
        notes: 'Follow national screening guidelines',
      },
    ],
    interventions: [
      {
        id: 'intervention-1',
        type: 'lifestyle',
        description: 'Balanced diet with emphasis on whole foods',
        frequency: 'Daily',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Mediterranean or DASH diet principles',
      },
      {
        id: 'intervention-2',
        type: 'lifestyle',
        description: 'Regular physical activity',
        frequency: '150 minutes moderate or 75 minutes vigorous per week',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Mix of aerobic and strength training',
      },
      {
        id: 'intervention-3',
        type: 'monitoring',
        description: 'Annual health check-up',
        frequency: 'Annually',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Comprehensive review of health status',
      },
      {
        id: 'intervention-4',
        type: 'education',
        description: 'Health education and self-management',
        frequency: 'As needed',
        duration: 'Ongoing',
        status: 'not-started',
        notes: 'Provide resources and support',
      },
    ],
    outcomes: [
      {
        id: 'outcome-1',
        description: 'BMI',
        measurement: 'Clinical measurement',
        targetValue: '18.5-24.9',
        status: 'pending',
        notes: 'Monitor quarterly',
      },
      {
        id: 'outcome-2',
        description: 'Screening completion rate',
        measurement: 'Medical records review',
        targetValue: '100% of recommended screenings',
        status: 'pending',
        notes: 'Track age-appropriate screenings',
      },
    ],
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'doctor@clnicl.ie',
    updatedBy: 'doctor@clnicl.ie',
  },
]

// Mock protocols data
const mockProtocols: any[] = []
const mockProtocolEnrollments: any[] = []

// Mock drug recalls data
const mockDrugRecalls: any[] = []

// Mock research studies data
const mockResearchStudies: any[] = []
const mockResearchEnrollments: any[] = []

// Mock patient guidance data
const mockPatientGuidance: any[] = []
// const mockGuidanceTemplates: any[] = [] // Reserved for future use

// Mock patient reminders data
const mockPatientReminders: any[] = []
const mockReminderRules: any[] = []

const mockVaccineReferences: VaccineReference[] = [
  {
    id: 'vr-1',
    tenantId: 'demo-tenant',
    vaccineType: '6 in 1',
    manufacturer: 'GSK',
    components: [
      { name: 'Egg', type: 'allergen', description: 'May contain egg protein' },
      { name: 'Gelatin', type: 'allergen', description: 'May contain gelatin' },
    ],
    recommendations: [
      {
        ageRange: { minMonths: 2, maxMonths: 24 },
        rationale: 'Routine childhood immunisation at 2, 4, and 6 months',
        guidelineChapter: 'Chapter 11',
      },
    ],
    contraindications: [
      {
        type: 'absolute',
        description: 'Severe allergic reaction to previous dose',
        guidelineReference: 'Chapter 11.1',
      },
    ],
    standardDose: { value: 0.5, unit: 'ml' },
    standardRoute: 'IM',
    standardSite: 'Thigh',
    guidelineChapter: 'Chapter 11',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'vr-2',
    tenantId: 'demo-tenant',
    vaccineType: 'MMR',
    manufacturer: 'MSD',
    components: [
      { name: 'Egg', type: 'allergen', description: 'Contains egg protein' },
      { name: 'Gelatin', type: 'allergen', description: 'Contains gelatin' },
    ],
    recommendations: [
      {
        ageRange: { minMonths: 12, maxMonths: 18 },
        rationale: 'Routine childhood immunisation at 12 months',
        guidelineChapter: 'Chapter 21',
      },
    ],
    contraindications: [
      {
        type: 'absolute',
        description: 'Severe allergic reaction to egg or gelatin',
        guidelineReference: 'Chapter 21.1',
      },
    ],
    standardDose: { value: 0.5, unit: 'ml' },
    standardRoute: 'SC',
    standardSite: 'Upper arm',
    guidelineChapter: 'Chapter 21',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

// Mock practice vaccine inventory
// Mock formulary medications (in-memory storage)
let mockFormularyMedications: Array<{
  tenantId: string
  medicationId: string
  addedAt: string
  [key: string]: any
}> = []

// Load formulary from localStorage on initialization
const loadFormulary = (): Array<{ tenantId: string; medicationId: string; addedAt: string; [key: string]: any }> => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('clnicl_formulary')
      if (stored) {
        return JSON.parse(stored)
      }
    }
  } catch (error) {
    console.error('[Mock API] Error loading formulary from localStorage:', error)
  }
  return []
}

// Save formulary to localStorage
const saveFormulary = (formulary: Array<{ tenantId: string; medicationId: string; addedAt: string; [key: string]: any }>) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('clnicl_formulary', JSON.stringify(formulary))
    }
  } catch (error) {
    console.error('[Mock API] Error saving formulary to localStorage:', error)
  }
}

// Initialize formulary from localStorage
mockFormularyMedications = loadFormulary()

const mockPracticeVaccines: PracticeVaccine[] = [
  {
    id: 'pv-1',
    tenantId: 'demo-tenant',
    vaccineId: '6 in 1#6IN1-2024-001',
    vaccineType: '6 in 1',
    batchNumber: '6IN1-2024-001',
    manufacturer: 'GSK',
    expiryDate: '2025-12-31',
    quantity: 25,
    isActive: true,
    location: 'Fridge A',
    notes: 'Standard stock',
    createdAt: new Date('2024-01-01').toISOString(),
    createdBy: '1',
    updatedAt: new Date('2024-01-01').toISOString(),
    updatedBy: '1',
  },
  {
    id: 'pv-2',
    tenantId: 'demo-tenant',
    vaccineId: 'MMR#MMR-2024-001',
    vaccineType: 'MMR',
    batchNumber: 'MMR-2024-001',
    manufacturer: 'MSD',
    expiryDate: '2025-06-30',
    quantity: 15,
    isActive: true,
    location: 'Fridge A',
    notes: 'Standard stock',
    createdAt: new Date('2024-01-01').toISOString(),
    createdBy: '1',
    updatedAt: new Date('2024-01-01').toISOString(),
    updatedBy: '1',
  },
  {
    id: 'pv-3',
    tenantId: 'demo-tenant',
    vaccineId: 'Flu#FLU2024-001',
    vaccineType: 'Flu',
    batchNumber: 'FLU2024-001',
    manufacturer: 'GSK',
    expiryDate: '2024-12-31',
    quantity: 50,
    isActive: true,
    location: 'Fridge B',
    notes: 'Seasonal stock',
    createdAt: new Date('2024-09-01').toISOString(),
    createdBy: '1',
    updatedAt: new Date('2024-09-01').toISOString(),
    updatedBy: '1',
  },
]

const mockPatientMedications: PatientMedication[] = [
  {
    tenantId: 'demo-tenant',
    patientId: '1',
    medicationId: 'med-1',
    referenceDrugId: 'DRUG001',
    name: 'Paracetamol 500mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'q6h', text: 'Every 6 hours as needed' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date().toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Headache',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1201',
    referenceDrugId: 'DRUG101',
    name: 'Atorvastatin 20mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hyperlipidaemia',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1202',
    referenceDrugId: 'DRUG102',
    name: 'Amlodipine 5mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypertension',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1203',
    referenceDrugId: 'DRUG103',
    name: 'Metformin 500mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 56, unit: 'tablet' },
    startDate: new Date(Date.now() - 160 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Type 2 diabetes',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1204',
    referenceDrugId: 'DRUG104',
    name: 'Levothyroxine 75mcg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypothyroidism',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1205',
    referenceDrugId: 'DRUG105',
    name: 'Sertraline 50mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 140 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Anxiety',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1206',
    referenceDrugId: 'DRUG106',
    name: 'Omeprazole 20mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'capsule' },
    startDate: new Date(Date.now() - 130 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'GERD',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1207',
    referenceDrugId: 'DRUG107',
    name: 'Salbutamol 100mcg Inhaler',
    dose: { value: 2, unit: 'puff' },
    doseText: '2 puffs',
    route: 'Inhaled',
    frequency: { code: 'prn', text: 'As needed' },
    quantity: { value: 1, unit: 'inhaler' },
    startDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Asthma',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1208',
    referenceDrugId: 'DRUG108',
    name: 'Budesonide/Formoterol 200/6 Inhaler',
    dose: { value: 2, unit: 'puff' },
    doseText: '2 puffs',
    route: 'Inhaled',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 1, unit: 'inhaler' },
    startDate: new Date(Date.now() - 115 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Asthma control',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1209',
    referenceDrugId: 'DRUG109',
    name: 'Ibuprofen 400mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'tid', text: 'Three times daily with food' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Back pain',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1210',
    referenceDrugId: 'DRUG110',
    name: 'Ramipril 5mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'capsule' },
    startDate: new Date(Date.now() - 110 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypertension',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1211',
    referenceDrugId: 'DRUG111',
    name: 'Bisoprolol 5mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 95 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Palpitations',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1212',
    referenceDrugId: 'DRUG112',
    name: 'Cetirizine 10mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Allergic rhinitis',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1213',
    referenceDrugId: 'DRUG113',
    name: 'Vitamin D3 1000 IU Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'capsule' },
    startDate: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Vitamin D deficiency',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1214',
    referenceDrugId: 'DRUG114',
    name: 'Folic Acid 5mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Anaemia',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1215',
    referenceDrugId: 'DRUG115',
    name: 'Naproxen 250mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily with food' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Arthralgia',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1216',
    referenceDrugId: 'DRUG116',
    name: 'Co-codamol 30/500 Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'qid', text: 'Up to four times daily' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Pain',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1217',
    referenceDrugId: 'DRUG117',
    name: 'Amoxicillin 500mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'tid', text: 'Three times daily' },
    quantity: { value: 21, unit: 'capsule' },
    startDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 43 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Sinusitis',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1218',
    referenceDrugId: 'DRUG118',
    name: 'Fluoxetine 20mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'capsule' },
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Depression',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1219',
    referenceDrugId: 'DRUG119',
    name: 'Gabapentin 300mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'tid', text: 'Three times daily' },
    quantity: { value: 90, unit: 'capsule' },
    startDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Neuropathic pain',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1220',
    referenceDrugId: 'DRUG120',
    name: 'Prednisolone 5mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Inflammation',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1221',
    referenceDrugId: 'DRUG121',
    name: 'Doxycycline 100mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 14, unit: 'capsule' },
    startDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Acne',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1222',
    referenceDrugId: 'DRUG122',
    name: 'Clopidogrel 75mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Secondary prevention',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1223',
    referenceDrugId: 'DRUG123',
    name: 'Aspirin 75mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 118 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Secondary prevention',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1224',
    referenceDrugId: 'DRUG124',
    name: 'Loratadine 10mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Allergic rhinitis',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1225',
    referenceDrugId: 'DRUG125',
    name: 'Montelukast 10mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily at night' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 95 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Asthma control',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1226',
    referenceDrugId: 'DRUG126',
    name: 'Loperamide 2mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'prn', text: 'As needed' },
    quantity: { value: 30, unit: 'capsule' },
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Diarrhoea',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1227',
    referenceDrugId: 'DRUG127',
    name: 'Hydrochlorothiazide 12.5mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 88 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypertension',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1228',
    referenceDrugId: 'DRUG128',
    name: 'Losartan 50mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 86 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypertension',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1229',
    referenceDrugId: 'DRUG129',
    name: 'Alendronic Acid 70mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'weekly', text: 'Once weekly' },
    quantity: { value: 4, unit: 'tablet' },
    startDate: new Date(Date.now() - 84 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Osteopenia',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1230',
    referenceDrugId: 'DRUG130',
    name: 'Calcium Carbonate 500mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 60, unit: 'tablet' },
    startDate: new Date(Date.now() - 82 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Supplement',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1231',
    referenceDrugId: 'DRUG131',
    name: 'Magnesium 200mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 78 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Supplement',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1232',
    referenceDrugId: 'DRUG132',
    name: 'Escitalopram 10mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'tablet' },
    startDate: new Date(Date.now() - 76 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Anxiety',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1233',
    referenceDrugId: 'DRUG133',
    name: 'Nitrofurantoin 100mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 14, unit: 'capsule' },
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 53 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'UTI',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1234',
    referenceDrugId: 'DRUG134',
    name: 'Codeine Phosphate 15mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'q6h', text: 'Every 6 hours as needed' },
    quantity: { value: 30, unit: 'tablet' },
    startDate: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Pain',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1235',
    referenceDrugId: 'DRUG135',
    name: 'Topical Hydrocortisone 1% Cream',
    dose: { value: 1, unit: 'tube' },
    doseText: 'Apply thin layer',
    route: 'Topical',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 1, unit: 'tube' },
    startDate: new Date(Date.now() - 52 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Dermatitis',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1236',
    referenceDrugId: 'DRUG136',
    name: 'Tramadol 50mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'q6h', text: 'Every 6 hours' },
    quantity: { value: 40, unit: 'capsule' },
    startDate: new Date(Date.now() - 48 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Post-op pain',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1237',
    referenceDrugId: 'DRUG137',
    name: 'Metoprolol 50mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 56, unit: 'tablet' },
    startDate: new Date(Date.now() - 72 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Hypertension',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1238',
    referenceDrugId: 'DRUG138',
    name: 'Clarithromycin 500mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'bid', text: 'Twice daily' },
    quantity: { value: 14, unit: 'tablet' },
    startDate: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Chest infection',
    prescriberId: '4',
    prescriberName: 'Dr. Aoife Kelly',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '4',
    updatedAt: new Date().toISOString(),
    updatedBy: '4',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1239',
    referenceDrugId: 'DRUG139',
    name: 'Cyclizine 50mg Tablets',
    dose: { value: 1, unit: 'tablet' },
    doseText: '1 tablet',
    route: 'Oral',
    frequency: { code: 'tid', text: 'Three times daily' },
    quantity: { value: 21, unit: 'tablet' },
    startDate: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'stopped',
    indication: 'Vertigo',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1240',
    referenceDrugId: 'DRUG140',
    name: 'Ferrous Fumarate 210mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'capsule' },
    startDate: new Date(Date.now() - 74 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Iron deficiency',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1241',
    referenceDrugId: 'DRUG141',
    name: 'Duloxetine 30mg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 28, unit: 'capsule' },
    startDate: new Date(Date.now() - 66 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Chronic pain',
    prescriberId: '2',
    prescriberName: 'Dr. Sarah O\'Brien',
    prescriberType: 'doctor',
    orderedFromList: false,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '2',
    updatedAt: new Date().toISOString(),
    updatedBy: '2',
  },
  {
    tenantId: 'demo-tenant',
    patientId: '12',
    medicationId: 'med-1242',
    referenceDrugId: 'DRUG142',
    name: 'Tamsulosin 400mcg Capsules',
    dose: { value: 1, unit: 'capsule' },
    doseText: '1 capsule',
    route: 'Oral',
    frequency: { code: 'od', text: 'Once daily' },
    quantity: { value: 30, unit: 'capsule' },
    startDate: new Date(Date.now() - 64 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: null,
    status: 'active',
    indication: 'Urinary symptoms',
    prescriberId: '3',
    prescriberName: 'Dr. Liam Murphy',
    prescriberType: 'doctor',
    orderedFromList: true,
    source: 'prescribed',
    isDoneInError: false,
    createdAt: new Date().toISOString(),
    createdBy: '3',
    updatedAt: new Date().toISOString(),
    updatedBy: '3',
  },
]

// In-memory terminology data (ICPC-2 + ICD-10 samples) for local development
interface MockTerminologyEntry {
  system: ConsultationTerminologySystem
  code: string
  version?: string
  display: string
  kind?: string
  chapterCode?: string
  parentCode?: string
  icd10Mappings?: string[]
}

const mockTerminology: MockTerminologyEntry[] = [
  {
    system: 'ICPC2',
    code: 'A84',
    version: 'ICPC-2e-v7.0',
    display: 'Poisoning by medical agent',
    kind: 'injury',
    chapterCode: 'A',
    parentCode: 'A.7',
    icd10Mappings: ['T36.0', 'T50.9'],
  },
  {
    system: 'ICPC2',
    code: 'A85',
    version: 'ICPC-2e-v7.0',
    display: 'Adverse effect medical agent',
    kind: 'injury',
    chapterCode: 'A',
    parentCode: 'A.7',
    icd10Mappings: ['T88.6', 'T88.7'],
  },
  {
    system: 'ICPC2',
    code: 'D70',
    version: 'ICPC-2e-v7.0',
    display: 'Gastrointestinal infection',
    kind: 'infection',
    chapterCode: 'D',
    parentCode: 'D.7',
    icd10Mappings: ['A09'],
  },
  {
    system: 'ICD10',
    code: 'A09',
    version: 'ICPC-2e-v7.0-map',
    display: 'Infectious gastroenteritis and colitis, unspecified',
    kind: 'diagnosis',
    chapterCode: 'I',
  },
  {
    system: 'ICD10',
    code: 'E11',
    version: 'ICPC-2e-v7.0-map',
    display: 'Type 2 diabetes mellitus',
    kind: 'diagnosis',
    chapterCode: 'IV',
  },
]

// Pre-populate mock appointments across multiple practitioners for calendar columns
const mockAppointments: Appointment[] = [
  {
    id: 'appt-1',
    patientId: '1',
    staffId: '2', // Dr. Sarah O'Brien (clinical)
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '09:30',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Follow-up anemia workup',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-2',
    patientId: '1',
    staffId: '3', // another clinical user
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '09:15',
    endTime: '09:45',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Chest pain review',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-3',
    patientId: '2',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '10:30',
    duration: 30,
    type: 'follow-up',
    status: 'scheduled',
    reason: 'Lab results discussion',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-4',
    patientId: '2',
    staffId: '4', // clinical user
    staffRole: 'nurse',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '10:15',
    endTime: '10:45',
    duration: 30,
    type: 'checkup',
    status: 'scheduled',
    reason: 'Blood pressure check',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-5',
    patientId: '1',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '11:00',
    endTime: '11:45',
    duration: 45,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Back pain evaluation',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-6b',
    patientId: '1',
    staffId: '5', // Dr. Emily Kavanagh
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '13:00',
    endTime: '13:30',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Diabetes review',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Multiple appointments at the same time slot - demonstrating overlapping bookings
  {
    id: 'appt-overlap-1',
    patientId: '3',
    staffId: '2', // Dr. Sarah O'Brien - same practitioner
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:30',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Quick follow-up',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-overlap-2',
    patientId: '4',
    staffId: '2', // Dr. Sarah O'Brien - same practitioner, same time
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:30',
    duration: 30,
    type: 'follow-up',
    status: 'scheduled',
    reason: 'Medication review',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-overlap-3',
    patientId: '5',
    staffId: '2', // Dr. Sarah O'Brien - same practitioner, same time
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:15',
    duration: 15,
    type: 'checkup',
    status: 'arrived',
    reason: 'Blood pressure check',
    arrivedAt: new Date().toISOString(),
    arrivedBy: '2',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-6',
    patientId: '1',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '16:00',
    endTime: '16:30',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Annual checkup',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-7',
    patientId: '2',
    staffId: '4',
    staffRole: 'nurse',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '15:00',
    endTime: '15:20',
    duration: 20,
    type: 'checkup',
    status: 'scheduled',
    reason: 'Wound dressing',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // STC test cases: completed appointments with and without STC indicator
  {
    id: 'appt-stc-1',
    patientId: '1',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '11:30',
    endTime: '12:00',
    duration: 30,
    type: 'consultation',
    status: 'completed',
    reason: 'Nebuliser for acute asthma',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    stcSuggested: true,
    stcSuggestedAt: new Date().toISOString(),
    stcSuggestedReasons: ['Nebuliser treatment (acute asthmatic attack)'],
    stcSuggestedConsultationId: 'consult-stc-1',
  },
  {
    id: 'appt-stc-2',
    patientId: '2',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '12:00',
    endTime: '12:30',
    duration: 30,
    type: 'consultation',
    status: 'completed',
    reason: 'Suturing laceration',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    stcSuggested: true,
    stcSuggestedAt: new Date().toISOString(),
    stcSuggestedReasons: ['Suturing cuts and lacerations'],
    stcSuggestedConsultationId: 'consult-stc-2',
  },
  {
    id: 'appt-stc-3',
    patientId: '1',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '12:30',
    endTime: '13:00',
    duration: 30,
    type: 'consultation',
    status: 'completed',
    reason: 'Routine BP check',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Appointments on another date to show multi-day view
  {
    id: 'appt-8',
    patientId: '1',
    staffId: '5',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '09:30',
    endTime: '10:00',
    duration: 30,
    type: 'consultation',
    status: 'scheduled',
    reason: 'Follow-up imaging',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'appt-9',
    patientId: '2',
    staffId: '4',
    staffRole: 'nurse',
    appointmentDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:30',
    endTime: '11:00',
    duration: 30,
    type: 'checkup',
    status: 'scheduled',
    reason: 'Vital signs review',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // DNA appointments for testing
  {
    id: 'appt-dna-1',
    patientId: '3',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    dnaReason: 'No contact made, patient did not respond to reminder calls',
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-2',
    patientId: '3',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '10:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    // No reason provided
    createdAt: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // DNA appointments for patient 9 (1 DNA)
  {
    id: 'appt-dna-3',
    patientId: '9',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '11:00',
    endTime: '11:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    dnaReason: 'Patient forgot appointment',
    createdAt: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // DNA appointments for patient 10 (3 DNAs)
  {
    id: 'appt-dna-4',
    patientId: '10',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '09:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    dnaReason: 'Transport issues',
    createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-5',
    patientId: '10',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:30',
    duration: 30,
    type: 'follow-up',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '3',
    createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-6',
    patientId: '10',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '10:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    dnaReason: 'Family emergency',
    createdAt: new Date(Date.now() - 43 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // DNA appointments for patient 11 (4 DNAs)
  {
    id: 'appt-dna-7',
    patientId: '11',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '09:30',
    endTime: '10:00',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-8',
    patientId: '11',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '11:00',
    endTime: '11:30',
    duration: 30,
    type: 'follow-up',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '3',
    dnaReason: 'Work conflict',
    createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-9',
    patientId: '11',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '14:30',
    endTime: '15:00',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    createdAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-10',
    patientId: '11',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:30',
    endTime: '11:00',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    dnaReason: 'Illness',
    createdAt: new Date(Date.now() - 53 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // DNA appointments for patient 12 (5 DNAs)
  {
    id: 'appt-dna-11',
    patientId: '12',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '09:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-12',
    patientId: '12',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '11:30',
    endTime: '12:00',
    duration: 30,
    type: 'follow-up',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '3',
    dnaReason: 'Double booked',
    createdAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-13',
    patientId: '12',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    createdAt: new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-14',
    patientId: '12',
    staffId: '2',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '10:30',
    duration: 30,
    type: 'consultation',
    status: 'dna',
    reason: 'Routine checkup',
    dnaAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '2',
    createdAt: new Date(Date.now() - 48 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'appt-dna-15',
    patientId: '12',
    staffId: '3',
    staffRole: 'doctor',
    appointmentDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '15:00',
    endTime: '15:30',
    duration: 30,
    type: 'follow-up',
    status: 'dna',
    reason: 'Follow-up appointment',
    dnaAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    dnaBy: '3',
    dnaReason: 'Forgot appointment',
    createdAt: new Date(Date.now() - 63 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockLabResults: LabResult[] = [
  {
    id: '1',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-001',
    accessionNumber: 'ACC-12345',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '09:30',
    receivedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '14:15',
    status: 'final',
    clinicalIndication: 'Routine checkup',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '1',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          {
            id: '1',
            testCode: 'CBC-WBC',
            testName: 'White Blood Cell Count',
            value: 7.2,
            unit: '×10⁹/L',
            referenceRange: '4.0-11.0',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '2',
            testCode: 'CBC-RBC',
            testName: 'Red Blood Cell Count',
            value: 4.8,
            unit: '×10¹²/L',
            referenceRange: 'Male: 4.5-5.5, Female: 4.0-5.0',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '3',
            testCode: 'CBC-HGB',
            testName: 'Haemoglobin',
            value: 13.5,
            unit: 'g/dL',
            referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '4',
            testCode: 'CBC-HCT',
            testName: 'Haematocrit',
            value: 0.42,
            unit: 'L/L',
            referenceRange: 'Male: 0.40-0.50, Female: 0.36-0.46',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '5',
            testCode: 'CBC-PLT',
            testName: 'Platelet Count',
            value: 185,
            unit: '×10⁹/L',
            referenceRange: '150-400',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
      {
        id: '2',
        panelCode: 'LFT',
        panelName: 'Liver Function Tests',
        tests: [
          {
            id: '6',
            testCode: 'ALT',
            testName: 'Alanine Aminotransferase',
            value: 45,
            unit: 'IU/L',
            referenceRange: '7-56',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '7',
            testCode: 'AST',
            testName: 'Aspartate Aminotransferase',
            value: 38,
            unit: 'IU/L',
            referenceRange: '10-40',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '8',
            testCode: 'ALP',
            testName: 'Alkaline Phosphatase',
            value: 125,
            unit: 'IU/L',
            referenceRange: '40-130',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '9',
            testCode: 'BIL-T',
            testName: 'Total Bilirubin',
            value: 1.2,
            unit: 'mg/dL',
            referenceRange: '0.2-1.2',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
    ],
    attachments: [
      {
        id: 'lab-attachment-1',
        fileName: 'FBC_LFT_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 187392, // ~183 KB
        fileUrl: 'https://example.com/lab-reports/FBC_LFT_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001234',
  },
  {
    id: '2',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-002',
    accessionNumber: 'ACC-12346',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '10:00',
    receivedDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '16:30',
    status: 'final',
    clinicalIndication: 'Chest pain evaluation',
    isAbnormal: true,
    isCritical: true,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '3',
        panelCode: 'UEC',
        panelName: 'Urea, Electrolytes & Creatinine',
        tests: [
          {
            id: '10',
            testCode: 'UREA',
            testName: 'Urea',
            value: 6.2,
            unit: 'mmol/L',
            referenceRange: '2.5-7.8',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '11',
            testCode: 'CREA',
            testName: 'Creatinine',
            value: 1.15,
            unit: 'mg/dL',
            referenceRange: '0.7-1.2',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '12',
            testCode: 'NA',
            testName: 'Sodium',
            value: 142,
            unit: 'mmol/L',
            referenceRange: '136-145',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '13',
            testCode: 'K',
            testName: 'Potassium',
            value: 4.2,
            unit: 'mmol/L',
            referenceRange: '3.5-5.0',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '14',
            testCode: 'CL',
            testName: 'Chloride',
            value: 105,
            unit: 'mmol/L',
            referenceRange: '98-107',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
      {
        id: '4',
        panelCode: 'TROP',
        panelName: 'Cardiac Troponin',
        tests: [
          {
            id: '15',
            testCode: 'TROP-I',
            testName: 'Troponin I',
            value: 0.08,
            unit: 'ng/mL',
            referenceRange: '<0.04',
            flag: 'high',
            status: 'final',
            notes: 'Elevated - may indicate myocardial injury',
          },
        ],
      },
    ],
    comments: 'Troponin I elevated. Recommend cardiology consultation.',
    attachments: [
      {
        id: 'lab-attachment-2',
        fileName: 'UEC_Troponin_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 215040, // ~210 KB
        fileUrl: 'https://example.com/lab-reports/UEC_Troponin_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001189',
  },
  {
    id: '3',
    patientId: '1',
    type: 'imaging-mri',
    orderNumber: 'ORD-2024-003',
    accessionNumber: 'ACC-12347',
    labName: 'Beaumont Hospital Radiology',
    labCode: 'BEAU-RAD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '14:00',
    receivedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '10:30',
    status: 'final',
    clinicalIndication: 'Headache and visual disturbances',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [], // Imaging results don't have test panels
    comments: 'MRI brain with contrast. No acute intracranial abnormality. Small chronic lacunar infarct in left basal ganglia. No mass effect or midline shift.',
    attachments: [
      {
        id: 'lab-attachment-4',
        fileName: 'MRI_Brain_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 312832, // ~305 KB
        fileUrl: 'https://example.com/lab-reports/MRI_Brain_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001256',
  },
  {
    id: '4',
    patientId: '1',
    type: 'imaging-ct',
    orderNumber: 'ORD-2024-004',
    accessionNumber: 'ACC-12348',
    labName: 'Beaumont Hospital Radiology',
    labCode: 'BEAU-RAD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '11:00',
    receivedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '15:45',
    status: 'final',
    clinicalIndication: 'Chest pain and shortness of breath',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [],
    comments: 'CT chest with contrast. No pulmonary embolism. Lungs clear. Heart size normal. No mediastinal lymphadenopathy.',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001198',
    attachments: [
      {
        id: 'lab-attachment-5',
        fileName: 'CT_Chest_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 278528, // ~272 KB
        fileUrl: 'https://example.com/lab-reports/CT_Chest_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: '6',
    patientId: '2',
    type: 'imaging-xray',
    orderNumber: 'ORD-2024-005',
    accessionNumber: 'ACC-12349',
    labName: 'Beaumont Hospital Radiology',
    labCode: 'BEAU-RAD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '09:15',
    receivedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '10:00',
    status: 'final',
    clinicalIndication: 'Ankle injury following fall',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [],
    comments: 'X-ray left ankle. Fracture of lateral malleolus with minimal displacement. No joint involvement.',
    attachments: [
      {
        id: 'lab-attachment-7',
        fileName: 'XRay_Ankle_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 156672, // ~153 KB
        fileUrl: 'https://example.com/lab-reports/XRay_Ankle_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001267',
  },
  {
    id: '6',
    patientId: '1',
    type: 'imaging-ultrasound',
    orderNumber: 'ORD-2024-006',
    accessionNumber: 'ACC-12350',
    labName: 'Beaumont Hospital Radiology',
    labCode: 'BEAU-RAD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '13:30',
    receivedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '16:00',
    status: 'final',
    clinicalIndication: 'Right upper quadrant pain',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [],
    comments: 'Ultrasound abdomen. Liver, gallbladder, pancreas, spleen, and kidneys appear normal. No gallstones. No free fluid.',
    attachments: [
      {
        id: 'lab-attachment-8',
        fileName: 'Ultrasound_Abdomen_Report_2024.pdf',
        fileType: 'application/pdf',
        fileSize: 189440, // ~185 KB
        fileUrl: 'https://example.com/lab-reports/Ultrasound_Abdomen_Report_2024.pdf',
        uploadedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001223',
  },
  // Lab result for patient 2
  {
    id: '8',
    patientId: '2',
    type: 'blood',
    orderNumber: 'ORD-2024-008',
    accessionNumber: 'ACC-12352',
    labName: 'St. James\'s Hospital Laboratory',
    labCode: 'SJH',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '08:45',
    receivedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '15:20',
    status: 'final',
    clinicalIndication: 'Annual health check',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [
      {
        id: '8',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          {
            id: '22',
            testCode: 'CBC-WBC',
            testName: 'White Blood Cell Count',
            value: 6.5,
            unit: '×10⁹/L',
            referenceRange: '4.0-11.0',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '23',
            testCode: 'CBC-HGB',
            testName: 'Haemoglobin',
            value: 13.8,
            unit: 'g/dL',
            referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
    ],
    attachments: [
      {
        id: 'lab-attachment-9',
        fileName: 'FBC_Report_2024_Patient2.pdf',
        fileType: 'application/pdf',
        fileSize: 198656, // ~194 KB
        fileUrl: 'https://example.com/lab-reports/FBC_Report_2024_Patient2.pdf',
        uploadedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-003456',
  },
  {
    id: '7',
    patientId: '1',
    type: 'pathology',
    orderNumber: 'ORD-2024-007',
    accessionNumber: 'ACC-12351',
    labName: 'Beaumont Hospital Pathology',
    labCode: 'BEAU-PATH',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '10:00',
    receivedDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '14:20',
    status: 'final',
    clinicalIndication: 'Skin lesion biopsy',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [],
    comments: 'Histopathology: Benign compound nevus with mild atypia. No evidence of malignancy. Complete excision.',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001201',
  },
  {
    id: '8',
    patientId: '1',
    type: 'microbiology',
    orderNumber: 'ORD-2024-008',
    accessionNumber: 'ACC-12352',
    labName: 'Beaumont Hospital Microbiology',
    labCode: 'BEAU-MICRO',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '08:00',
    receivedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '11:15',
    status: 'final',
    clinicalIndication: 'Urinary tract infection',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '5',
        panelCode: 'URINE-CULT',
        panelName: 'Urine Culture',
        tests: [
          {
            id: '16',
            testCode: 'CULT-ORG',
            testName: 'Organism',
            value: 'Escherichia coli',
            unit: '',
            referenceRange: 'No growth',
            flag: 'abnormal',
            status: 'final',
          },
          {
            id: '17',
            testCode: 'CULT-COUNT',
            testName: 'Colony Count',
            value: '>100,000',
            unit: 'CFU/mL',
            referenceRange: '<10,000',
            flag: 'high',
            status: 'final',
          },
          {
            id: '18',
            testCode: 'SENS-AMP',
            testName: 'Ampicillin Sensitivity',
            value: 'Resistant',
            unit: '',
            referenceRange: 'Sensitive',
            flag: 'abnormal',
            status: 'final',
          },
          {
            id: '19',
            testCode: 'SENS-CIP',
            testName: 'Ciprofloxacin Sensitivity',
            value: 'Sensitive',
            unit: '',
            referenceRange: 'Sensitive',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
    ],
    comments: 'E. coli isolated. Recommend ciprofloxacin based on sensitivity.',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001245',
  },
  {
    id: '9',
    patientId: '1',
    type: 'cardiology',
    orderNumber: 'ORD-2024-009',
    accessionNumber: 'ACC-12353',
    labName: 'Beaumont Hospital Cardiology',
    labCode: 'BEAU-CARD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '10:30',
    receivedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '11:00',
    status: 'final',
    clinicalIndication: 'Routine ECG for annual checkup',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [],
    comments: '12-lead ECG: Normal sinus rhythm. Rate 72 bpm. No acute changes. Normal axis. No ST elevation or depression.',
    createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001156',
  },
  {
    id: '10',
    patientId: '1',
    type: 'imaging-echocardiogram',
    orderNumber: 'ORD-2024-010',
    accessionNumber: 'ACC-12354',
    labName: 'Beaumont Hospital Cardiology',
    labCode: 'BEAU-CARD',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '14:00',
    receivedDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '15:30',
    status: 'final',
    clinicalIndication: 'Heart murmur evaluation',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [],
    comments: 'Echocardiogram: Normal left ventricular function (EF 60%). No valvular abnormalities. Mild tricuspid regurgitation, clinically insignificant.',
    createdAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001167',
  },
  {
    id: '11',
    patientId: '1',
    type: 'pulmonary',
    orderNumber: 'ORD-2024-011',
    accessionNumber: 'ACC-12355',
    labName: 'Beaumont Hospital Pulmonary Function Lab',
    labCode: 'BEAU-PULM',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '09:00',
    receivedDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '10:30',
    status: 'final',
    clinicalIndication: 'Pre-operative assessment',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [
      {
        id: '6',
        panelCode: 'SPIRO',
        panelName: 'Spirometry',
        tests: [
          {
            id: '20',
            testCode: 'FEV1',
            testName: 'FEV1',
            value: 3.2,
            unit: 'L',
            referenceRange: '>2.5',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '21',
            testCode: 'FVC',
            testName: 'FVC',
            value: 4.1,
            unit: 'L',
            referenceRange: '>3.0',
            flag: 'normal',
            status: 'final',
          },
          {
            id: '22',
            testCode: 'FEV1-FVC',
            testName: 'FEV1/FVC Ratio',
            value: 78,
            unit: '%',
            referenceRange: '>70',
            flag: 'normal',
            status: 'final',
          },
        ],
      },
    ],
    comments: 'Normal spirometry. No evidence of obstructive or restrictive lung disease.',
    createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001145',
  },
  {
    id: '12',
    patientId: '1',
    type: 'endoscopy',
    orderNumber: 'ORD-2024-012',
    accessionNumber: 'ACC-12356',
    labName: 'Beaumont Hospital Endoscopy Unit',
    labCode: 'BEAU-ENDO',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '13:00',
    receivedDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '14:30',
    status: 'final',
    clinicalIndication: 'Gastroesophageal reflux symptoms',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [],
    comments: 'Upper GI endoscopy: Mild esophagitis (Grade A). Small hiatal hernia. Stomach and duodenum normal. Biopsies taken from lower esophagus.',
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001134',
  },
  {
    id: '13',
    patientId: '1',
    type: 'biopsy',
    orderNumber: 'ORD-2024-013',
    accessionNumber: 'ACC-12357',
    labName: 'Beaumont Hospital Pathology',
    labCode: 'BEAU-PATH',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    collectionTime: '11:00',
    receivedDate: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '16:00',
    status: 'final',
    clinicalIndication: 'Esophageal biopsy from endoscopy',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [],
    comments: 'Esophageal biopsy: Reactive changes consistent with reflux esophagitis. No Barrett\'s esophagus. No dysplasia or malignancy.',
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001135',
  },
  // Additional blood results for time-series plotting
  {
    id: '14',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-014',
    accessionNumber: 'ACC-22358',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '09:10',
    status: 'final',
    clinicalIndication: 'Routine FBC monitoring',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [
      {
        id: '7',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          { id: '23', testCode: 'CBC-WBC', testName: 'White Blood Cell Count', value: 6.5, unit: '×10⁹/L', referenceRange: '4.0-11.0', flag: 'normal', status: 'final' },
          { id: '24', testCode: 'CBC-HGB', testName: 'Haemoglobin', value: 13.9, unit: 'g/dL', referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5', flag: 'normal', status: 'final' },
          { id: '25', testCode: 'CBC-PLT', testName: 'Platelet Count', value: 210, unit: '×10⁹/L', referenceRange: '150-400', flag: 'normal', status: 'final' },
        ],
      },
    ],
    createdAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001301',
  },
  {
    id: '15',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-015',
    accessionNumber: 'ACC-22359',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '10:05',
    status: 'final',
    clinicalIndication: 'Routine FBC monitoring',
    isAbnormal: false,
    isCritical: false,
    reviewStatus: 'reviewed',
    reviewedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: '2',
    reviewedByName: 'Dr. Sarah O\'Brien',
    panels: [
      {
        id: '8',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          { id: '26', testCode: 'CBC-WBC', testName: 'White Blood Cell Count', value: 7.8, unit: '×10⁹/L', referenceRange: '4.0-11.0', flag: 'normal', status: 'final' },
          { id: '27', testCode: 'CBC-HGB', testName: 'Haemoglobin', value: 13.2, unit: 'g/dL', referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5', flag: 'normal', status: 'final' },
          { id: '28', testCode: 'CBC-PLT', testName: 'Platelet Count', value: 198, unit: '×10⁹/L', referenceRange: '150-400', flag: 'normal', status: 'final' },
        ],
      },
    ],
    createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001302',
  },
  {
    id: '16',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-016',
    accessionNumber: 'ACC-22360',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '09:45',
    status: 'final',
    clinicalIndication: 'Follow-up anemia workup',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '9',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          { id: '29', testCode: 'CBC-WBC', testName: 'White Blood Cell Count', value: 8.4, unit: '×10⁹/L', referenceRange: '4.0-11.0', flag: 'normal', status: 'final' },
          { id: '30', testCode: 'CBC-HGB', testName: 'Haemoglobin', value: 11.1, unit: 'g/dL', referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5', flag: 'low', status: 'final' },
          { id: '31', testCode: 'CBC-PLT', testName: 'Platelet Count', value: 240, unit: '×10⁹/L', referenceRange: '150-400', flag: 'normal', status: 'final' },
        ],
      },
    ],
    comments: 'Mild anemia noted; monitor trend.',
    createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001303',
  },
  {
    id: '17',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-017',
    accessionNumber: 'ACC-22361',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '11:20',
    status: 'final',
    clinicalIndication: 'Follow-up anemia workup',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '10',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          { id: '32', testCode: 'CBC-WBC', testName: 'White Blood Cell Count', value: 7.9, unit: '×10⁹/L', referenceRange: '4.0-11.0', flag: 'normal', status: 'final' },
          { id: '33', testCode: 'CBC-HGB', testName: 'Haemoglobin', value: 10.4, unit: 'g/dL', referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5', flag: 'low', status: 'final' },
          { id: '34', testCode: 'CBC-PLT', testName: 'Platelet Count', value: 255, unit: '×10⁹/L', referenceRange: '150-400', flag: 'normal', status: 'final' },
        ],
      },
    ],
    comments: 'HGB trending down; consider further evaluation.',
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001304',
  },
  {
    id: '18',
    patientId: '1',
    type: 'blood',
    orderNumber: 'ORD-2024-018',
    accessionNumber: 'ACC-22362',
    labName: 'Beaumont Hospital Laboratory',
    labCode: 'BEAU',
    orderingPhysicianId: '2',
    orderingPhysicianName: 'Dr. Sarah O\'Brien',
    collectionDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reportedTime: '09:30',
    status: 'final',
    clinicalIndication: 'Follow-up anemia workup',
    isAbnormal: true,
    isCritical: false,
    reviewStatus: 'in-review',
    panels: [
      {
        id: '11',
        panelCode: 'FBC',
        panelName: 'Full Blood Count',
        tests: [
          { id: '35', testCode: 'CBC-WBC', testName: 'White Blood Cell Count', value: 7.1, unit: '×10⁹/L', referenceRange: '4.0-11.0', flag: 'normal', status: 'final' },
          { id: '36', testCode: 'CBC-HGB', testName: 'Haemoglobin', value: 9.8, unit: 'g/dL', referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5', flag: 'low', status: 'final' },
          { id: '37', testCode: 'CBC-PLT', testName: 'Platelet Count', value: 270, unit: '×10⁹/L', referenceRange: '150-400', flag: 'normal', status: 'final' },
        ],
      },
    ],
    comments: 'Anemia persists; consider iron studies or further diagnostics.',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    source: 'healthlink',
    healthlinkMessageId: 'HL-2024-001305',
  },
]

const mockVitalSigns: VitalSigns[] = [
  {
    id: '1',
    patientId: '1',
    consultationId: '1',
    recordedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    recordedBy: '2',
    recordedByName: 'Dr. Sarah O\'Brien',
    bloodPressure: { systolic: 120, diastolic: 80, unit: 'mmHg' },
    heartRate: { value: 72, unit: 'bpm' },
    pulseRhythm: 'regular',
    temperature: { value: 36.5, unit: 'celsius' },
    respiratoryRate: { value: 16, unit: 'breaths/min' },
    height: { value: 175, unit: 'cm' },
    weight: { value: 70, unit: 'kg' },
    bmi: 22.9,
    physicalExercise: 'moderate',
    source: 'structured',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    patientId: '1',
    consultationId: '2',
    recordedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    recordedBy: '2',
    recordedByName: 'Dr. Sarah O\'Brien',
    bloodPressure: { systolic: 118, diastolic: 78, unit: 'mmHg' },
    heartRate: { value: 68, unit: 'bpm' },
    pulseRhythm: 'regular',
    temperature: { value: 36.7, unit: 'celsius' },
    respiratoryRate: { value: 15, unit: 'breaths/min' },
    height: { value: 175, unit: 'cm' },
    weight: { value: 71, unit: 'kg' },
    bmi: 23.2,
    cholesterol: { value: 4.5, unit: 'mmol/L' },
    ldl: { value: 2.8, unit: 'mmol/L' },
    hdl: { value: 1.5, unit: 'mmol/L' },
    physicalExercise: 'moderate',
    source: 'structured',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    patientId: '1',
    recordedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    recordedBy: '2',
    recordedByName: 'Dr. Sarah O\'Brien',
    bloodPressure: { systolic: 125, diastolic: 82, unit: 'mmHg' },
    heartRate: { value: 75, unit: 'bpm' },
    pulseRhythm: 'regular',
    temperature: { value: 36.6, unit: 'celsius' },
    respiratoryRate: { value: 17, unit: 'breaths/min' },
    height: { value: 175, unit: 'cm' },
    weight: { value: 69, unit: 'kg' },
    bmi: 22.5,
    physicalExercise: 'light',
    source: 'structured',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockConsultations: Consultation[] = [
  {
    id: '1',
    patientId: '1',
    staffId: '2',
    staffName: 'Dr. Sarah O\'Brien',
    consultationDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '10:00',
    endTime: '10:30',
    duration: 30,
    status: 'completed',
    reason: 'Annual checkup',
    notes: 'Patient presents for annual checkup. Blood pressure normal, weight stable. No concerns reported.',
    diagnosis: ['Z00.00 - Encounter for general adult medical examination without abnormal findings'],
    plan: 'Continue current medications. Schedule follow-up in 6 months.',
    instructionsGiven: false,
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: '2',
    patientId: '1',
    staffId: '2',
    staffName: 'Dr. Sarah O\'Brien',
    consultationDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    startTime: '14:00',
    endTime: '14:45',
    duration: 45,
    status: 'completed',
    reason: 'Chest pain',
    notes: 'Patient reports intermittent chest pain over past week. EKG normal. Referred for cardiology consultation.',
    diagnosis: ['R06.02 - Shortness of breath', 'R50.9 - Fever, unspecified'],
    plan: 'Referral to cardiology. Continue monitoring symptoms.',
    artefacts: [
      {
        id: '1',
        consultationId: '2',
        type: 'referral-letter',
        title: 'Cardiology Referral',
        description: 'Referral to cardiology for chest pain evaluation',
        status: 'sent',
        createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: '2',
      },
    ],
    instructionsGiven: false,
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  // STC seed consultations (linked to appt-stc-1, appt-stc-2 for opening STC dialog from badge)
  {
    id: 'consult-stc-1',
    patientId: '1',
    staffId: '2',
    staffName: 'Dr. Sarah O\'Brien',
    appointmentId: 'appt-stc-1',
    consultationDate: new Date().toISOString().split('T')[0],
    startTime: '11:30',
    endTime: '12:00',
    duration: 30,
    status: 'completed',
    reason: 'Nebuliser for acute asthma',
    notes: 'Nebuliser given for acute asthmatic attack.',
    diagnosis: ['Acute asthma'],
    plan: 'Follow up as needed.',
    instructionsGiven: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: '2',
  },
  {
    id: 'consult-stc-2',
    patientId: '2',
    staffId: '2',
    staffName: 'Dr. Sarah O\'Brien',
    appointmentId: 'appt-stc-2',
    consultationDate: new Date().toISOString().split('T')[0],
    startTime: '12:00',
    endTime: '12:30',
    duration: 30,
    status: 'completed',
    reason: 'Suturing laceration',
    notes: 'Suturing of laceration on hand.',
    diagnosis: ['Laceration hand'],
    plan: 'Review in 5 days.',
    instructionsGiven: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: '2',
  },
]

const mockBillingAccounts: BillingAccount[] = [
  {
    id: 'billing-account-1',
    patientId: '1',
    status: 'active',
    currency: 'EUR',
    balance: 40,
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockBillableItems: BillableItem[] = [
  {
    id: 'billing-item-1',
    patientId: '1',
    accountId: 'billing-account-1',
    consultationId: '1',
    description: 'Consultation fee',
    amount: 60,
    currency: 'EUR',
    status: 'billed',
    provider: 'manual',
    createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'billing-item-2',
    patientId: '1',
    accountId: 'billing-account-1',
    consultationId: '2',
    description: 'ECG review',
    amount: 40,
    currency: 'EUR',
    status: 'pending',
    provider: 'manual',
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockBillingPayments: BillingPayment[] = [
  {
    id: 'billing-payment-1',
    patientId: '1',
    accountId: 'billing-account-1',
    amount: 60,
    currency: 'EUR',
    status: 'completed',
    provider: 'manual',
    paymentDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockBillingStatements: BillingStatement[] = [
  {
    id: 'billing-statement-1',
    patientId: '1',
    accountId: 'billing-account-1',
    statementDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    periodStart: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    periodEnd: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    totalAmount: 100,
    status: 'issued',
    itemIds: ['billing-item-1', 'billing-item-2'],
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

const mockBillingPaymentMethods: BillingPaymentMethod[] = []
const mockBillingPosItems: BillingPosItem[] = [
  { id: 'consult-standard', label: 'Standard Consultation', amount: 60, currency: 'EUR' },
  { id: 'consult-extended', label: 'Extended Consultation', amount: 90, currency: 'EUR' },
  { id: 'ecg-review', label: 'ECG Review', amount: 40, currency: 'EUR' },
  { id: 'bloods-panel', label: 'Bloods Panel', amount: 55, currency: 'EUR' },
  { id: 'vaccination-fee', label: 'Vaccination Administration', amount: 35, currency: 'EUR' },
]

// Default clinic hours: Monday-Friday, 9 AM - 5 PM, 30-minute slots
let mockClinicHours: ClinicHours = {
  id: '1',
  name: 'Default Clinic Hours',
  description: 'Standard clinic operating hours',
  days: [
    { dayOfWeek: 0, isOpen: false, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Sunday
    { dayOfWeek: 1, isOpen: true, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Monday
    { dayOfWeek: 2, isOpen: true, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Tuesday
    { dayOfWeek: 3, isOpen: true, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Wednesday
    { dayOfWeek: 4, isOpen: true, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Thursday
    { dayOfWeek: 5, isOpen: true, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Friday
    { dayOfWeek: 6, isOpen: false, startTime: '09:00', endTime: '17:00', slotDuration: 30 }, // Saturday
  ],
  timezone: 'Europe/Dublin',
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

// Default practice location with sample data
let mockPracticeLocation: PracticeLocation = {
  id: 'default',
  practiceName: 'Main Street Medical Practice',
  healthcareOrganisationIdentifier: 'HOI-123456',
  addresses: [
    {
      id: 'addr-1',
      type: 'primary',
      addressLine1: '123 Main Street',
      addressLine2: '',
      city: 'Dublin',
      county: 'Dublin',
      postcode: 'D01 AB12',
      country: 'Ireland',
      isActive: true,
    },
  ],
  contacts: [
    {
      id: 'contact-1',
      type: 'telephone',
      value: '+353 1 234 5678',
      label: 'Main',
      isPrimary: true,
    },
    {
      id: 'contact-2',
      type: 'email',
      value: 'info@mainstreetmedical.ie',
      label: 'General',
      isPrimary: false,
    },
  ],
  primaryCareTeam: {
    name: 'Dublin North Primary Care Team',
    code: 'PCT-DUB-N-001',
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

// Helper function to ensure all patients have linking fields with defaults
function ensureLinkingFields(patients: Partial<Patient>[]): Patient[] {
  return patients.map(p => ({
    ...p,
    isPrimary: p.isPrimary !== undefined ? p.isPrimary : true,
    primaryPatientId: p.primaryPatientId || undefined,
    linkedPatientIds: p.linkedPatientIds || undefined,
  } as Patient))
}

function buildDuplicatePairKey(patientIdA: string, patientIdB: string): string {
  return [patientIdA, patientIdB].sort().join('#')
}

const mockDuplicateIgnores: Array<{
  pairKey: string
  patientIdA: string
  patientIdB: string
  ignoredAt: string
}> = []

const mockPatients: Patient[] = ensureLinkingFields([
  {
    id: '1',
    patientNumber: 'PAT-001',
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: '1990-01-15',
    gender: 'male',
    email: 'john.doe@example.com',
    phone: '555-1234',
    ppsNumber: '1234567A',
    gmsNumber: 'GMS-001',
    currentMedications: ['Lisinopril 10mg daily', 'Metformin 500mg BID'],
    pastMedications: ['Amoxicillin 500mg TID x7d'],
    familyId: '1', // Linked to Smith Family
    coverage: {
      patientType: 'gms',
      gms: {
        number: 'GMS-001',
        reviewDate: '2025-12-31',
        doctorNumber: 'DOC-100',
        distanceCode: 'A',
        dispensingStatus: 'Regular',
      },
      privateInsurance: {
        company: 'VHI',
        membershipNumber: 'VHI-001',
        coverType: 'Family',
      },
    },
    identifiersExtended: {
      ppsNumber: '1234567A',
      ihiNumber: 'IHI-0001',
      internalNumber: 'PAT-001',
      externalIds: [
        { system: 'Hospital A', identifier: 'HOSP-A-001' },
        { system: 'Lab X', identifier: 'LABX-001' },
      ],
    },
    socioCultural: {
      occupation: 'Teacher',
      maritalStatus: 'married',
      religion: 'Catholic',
      ethnicity: 'whiteIrish',
    },
    communicationSupport: {
      preferredLanguage: 'English',
      interpreterRequired: false,
      transportNeeds: 'Occasional taxi support for mobility issues.',
      advocacyNeeds: '',
    },
    practiceRegistration: {
      registeredGpName: 'Dr. Anne Smith',
      registeredGpId: '2',
      usualGpName: 'Dr. Anne Smith',
      branchName: 'Main Street Surgery',
      registrationDate: '2015-01-01',
      primaryCareTeamEnrolmentStatus: 'Enrolled',
      primaryCareTeamEnrolmentDate: '2016-06-15',
    },
    nextOfKin: [
      {
        title: 'Mrs',
        firstName: 'Mary',
        familyName: 'Doe',
        gender: 'female',
        relationshipToPatient: 'Spouse',
        address: {
          street: '10 Oak Avenue',
          city: 'Dublin',
          state: 'Dublin',
          postalCode: 'D02 XY12',
          country: 'Ireland',
        },
        homePhone: '555-9999',
        mobilePhone: '085-111-2222',
        email: 'mary.doe@example.com',
      },
    ],
    carers: [
      {
        title: 'Ms',
        firstName: 'Sarah',
        familyName: 'O\'Neill',
        gender: 'female',
        relationshipToPatient: 'Daughter / Informal carer',
        isRegisteredPatient: false,
        homePhone: '021-555-0000',
        mobilePhone: '086-222-3333',
        email: 'sarah.oneill@example.com',
        consentRecordedAt: '2024-11-01',
        address: {
          street: '22 Elm Road',
          city: 'Cork',
          state: 'Cork',
          postalCode: 'T12 AB34',
          country: 'Ireland',
        },
      },
    ],
    // Smoking status missing - will trigger alert
    // Alcohol status missing - will trigger alert
    isActive: true,
    isArchived: false,
    // Patient record linking (defaults to unlinked primary)
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '2',
    patientNumber: 'PAT-002',
    firstName: 'Jane',
    lastName: 'Smith',
    dateOfBirth: '1985-05-20',
    gender: 'female',
    email: 'jane.smith@example.com',
    phone: '555-5678',
    currentMedications: ['Atorvastatin 20mg daily'],
    pastMedications: ['Ibuprofen PRN'],
    familyId: '1', // Linked to Smith Family
    smokingStatus: 'ex-smoker',
    smokingStatusLastRecorded: new Date(Date.now() - 400 * 24 * 60 * 60 * 1000).toISOString(), // ~13 months ago - will trigger alert
    smokingDetails: {
      status: 'ex-smoker',
      description: 'Patient quit 2 years ago; smoked 10 cigarettes daily for 15 years prior.',
      cessationDetails: 'Expressed willingness to quit. Discussed combination of medication and specialist support options.',
    },
    alcoholConsumption: {
      status: 'current-drinker',
      description: 'Patient drinks alcohol 3-4 times per week, typically consumes 2 drinks per occasion (e.g., 1 pint of beer and a spirit). Prefers wine and spirits.',
      frequency: '3-4 times per week',
      quantityPerOccasion: '2 drinks per occasion',
      type: 'Wine and spirits',
      bingeDrinking: true,
      bingeFrequency: 'once a month at social gatherings',
    },
    coverage: {
      patientType: 'private',
      privateInsurance: {
        company: 'Irish Life',
        membershipNumber: 'IL-200',
        coverType: 'Individual',
      },
    },
    identifiersExtended: {
      internalNumber: 'PAT-002',
      externalIds: [{ system: 'Hospital B', identifier: 'HOSPB-002' }],
    },
    socioCultural: {
      occupation: 'Accountant',
      maritalStatus: 'single',
      religion: 'None',
      ethnicity: 'otherWhite',
    },
    isActive: true,
    isArchived: false,
    // Patient record linking (defaults to unlinked primary)
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '3',
    patientNumber: 'PAT-003',
    firstName: 'Michael',
    lastName: 'O\'Brien',
    dateOfBirth: '1978-03-10',
    ppsNumber: '4567890C',
    gmsNumber: 'GMS-003',
    gender: 'male',
    email: 'michael.obrien@example.com',
    phone: '085-123-4567',
    address: {
      street: '15 Main Street',
      city: 'Dublin',
      state: 'Dublin',
      postalCode: 'D02 XY12',
      country: 'Ireland',
    },
    currentMedications: ['Amlodipine 5mg daily', 'Aspirin 75mg daily'],
    smokingStatus: 'current-daily',
    smokingStatusLastRecorded: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago - recent
    smokingDetails: {
      status: 'current-daily',
      description: 'Patient smokes daily, approximately 1 pack (20 cigarettes) per day for 10 years.',
      amountPerDay: 20,
      yearsSmoking: 10,
    },
    alcoholConsumption: {
      status: 'non-drinker',
      description: 'Patient reports no current alcohol consumption.',
    },
    // DNA history for testing
    dnaCount: 2,
    dnaHistory: [
      {
        appointmentId: 'appt-dna-1',
        appointmentDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'No contact made, patient did not respond to reminder calls',
      },
      {
        appointmentId: 'appt-dna-2',
        appointmentDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        // No reason provided
      },
    ],
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    patientNumber: 'PAT-004',
    firstName: 'Sarah',
    lastName: 'Murphy',
    dateOfBirth: '1992-07-22',
    gender: 'female',
    email: 'sarah.murphy@example.com',
    phone: '087-654-3210',
    address: {
      street: '42 Oak Avenue',
      city: 'Cork',
      state: 'Cork',
      postalCode: 'T12 AB34',
      country: 'Ireland',
    },
    currentMedications: ['Levothyroxine 75mcg daily'],
    smokingStatus: 'never',
    smokingStatusLastRecorded: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(), // 6 months ago - recent
    smokingDetails: {
      status: 'never',
      description: 'Patient reports never smoking.',
    },
    alcoholConsumption: {
      status: 'current-drinker',
      description: 'Patient drinks alcohol occasionally, typically 1-2 glasses of wine on weekends.',
      frequency: '1-2 times per week',
      quantityPerOccasion: '1-2 glasses of wine',
      type: 'Wine',
      bingeDrinking: false,
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    patientNumber: 'PAT-005',
    firstName: 'David',
    lastName: 'Walsh',
    dateOfBirth: '1965-11-05',
    gender: 'male',
    email: 'david.walsh@example.com',
    phone: '086-111-2222',
    address: {
      street: '8 Elm Road',
      city: 'Galway',
      state: 'Galway',
      postalCode: 'H91 CD56',
      country: 'Ireland',
    },
    currentMedications: ['Metoprolol 50mg BID', 'Simvastatin 40mg daily'],
    allergies: ['Penicillin'],
    smokingStatus: 'ex-smoker',
    smokingStatusLastRecorded: new Date(Date.now() - 450 * 24 * 60 * 60 * 1000).toISOString(), // ~15 months ago - will trigger alert
    smokingDetails: {
      status: 'ex-smoker',
      description: 'Patient quit 5 years ago; previously smoked 15-20 cigarettes daily for 25 years.',
      cessationDetails: 'Patient successfully quit using nicotine replacement therapy. No current interest in resuming.',
    },
    alcoholConsumption: {
      status: 'ex-drinker',
      description: 'Patient stopped drinking 6 months ago; previously drank beer heavily.',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 500 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 450 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '6',
    patientNumber: 'PAT-006',
    firstName: 'Emma',
    lastName: 'Kelly',
    dateOfBirth: '1988-09-14',
    gender: 'female',
    email: 'emma.kelly@example.com',
    phone: '083-999-8888',
    address: {
      street: '22 Park Street',
      city: 'Limerick',
      state: 'Limerick',
      postalCode: 'V94 EF78',
      country: 'Ireland',
    },
    currentMedications: ['Sertraline 50mg daily'],
    smokingStatus: 'current-occasional',
    smokingStatusLastRecorded: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago - recent
    smokingDetails: {
      status: 'current-occasional',
      description: 'Patient smokes occasionally, typically 2-3 cigarettes when drinking socially on weekends.',
      amountPerDay: 2,
      yearsSmoking: 3,
    },
    alcoholConsumption: {
      status: 'current-drinker',
      description: 'Patient drinks alcohol socially on weekends, typically 2-3 drinks per occasion.',
      frequency: 'Weekends only',
      quantityPerOccasion: '2-3 drinks',
      type: 'Mixed drinks',
      bingeDrinking: true,
      bingeFrequency: 'occasionally at parties',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '7',
    patientNumber: 'PAT-007',
    firstName: 'James',
    lastName: 'Ryan',
    dateOfBirth: '1995-04-18',
    gender: 'male',
    email: 'james.ryan@example.com',
    phone: '085-777-6666',
    smokingStatus: 'e-cigarette',
    smokingStatusLastRecorded: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), // 2 months ago - recent
    smokingDetails: {
      status: 'e-cigarette',
      description: 'Patient uses e-cigarettes daily (vaping). Switched from traditional cigarettes 1 year ago.',
      amountPerDay: 0, // Not applicable for e-cigarettes
      yearsSmoking: 4,
    },
    alcoholConsumption: {
      status: 'current-drinker',
      description: 'Patient drinks alcohol moderately, 1-2 drinks per week.',
      frequency: '1-2 times per week',
      quantityPerOccasion: '1-2 drinks',
      type: 'Beer',
      bingeDrinking: false,
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '13',
    patientNumber: 'PAT-013',
    firstName: 'Emma',
    lastName: 'Smith',
    dateOfBirth: '2023-11-15', // ~13 months old (child for testing vaccine schedule)
    gender: 'female',
    email: 'emma.smith@example.com',
    phone: '555-5678',
    address: {
      street: '456 Oak Street',
      city: 'Dublin',
      state: 'Dublin',
      postalCode: 'D02 CD34',
      country: 'Ireland',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date('2023-11-15').toISOString(),
    updatedAt: new Date('2023-11-15').toISOString(),
  },
  {
    id: '8',
    patientNumber: 'PAT-008',
    firstName: 'Lisa',
    lastName: 'O\'Connor',
    dateOfBirth: '1980-12-03',
    gender: 'female',
    email: 'lisa.oconnor@example.com',
    phone: '087-555-4444',
    address: {
      street: '5 Church Lane',
      city: 'Waterford',
      state: 'Waterford',
      postalCode: 'X91 GH12',
      country: 'Ireland',
    },
    currentMedications: ['Omeprazole 20mg daily'],
    allergies: ['Latex', 'Shellfish'],
    // Smoking status missing - will trigger alert
    alcoholConsumption: {
      status: 'non-drinker',
      description: 'Patient reports no current alcohol consumption.',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // Additional patients with various DNA counts for testing
  {
    id: '9',
    patientNumber: 'PAT-009',
    firstName: 'Robert',
    lastName: 'Brown',
    dateOfBirth: '1975-08-12',
    gender: 'male',
    email: 'robert.brown@example.com',
    phone: '086-222-3333',
    address: {
      street: '12 High Street',
      city: 'Dublin',
      state: 'Dublin',
      postalCode: 'D02 EF45',
      country: 'Ireland',
    },
    dnaCount: 1,
    dnaHistory: [
      {
        appointmentId: 'appt-dna-3',
        appointmentDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Patient forgot appointment',
      },
    ],
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 300 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '10',
    patientNumber: 'PAT-010',
    firstName: 'Mary',
    lastName: 'Wilson',
    dateOfBirth: '1983-02-28',
    gender: 'female',
    email: 'mary.wilson@example.com',
    phone: '087-333-4444',
    address: {
      street: '30 River Road',
      city: 'Cork',
      state: 'Cork',
      postalCode: 'T23 XY78',
      country: 'Ireland',
    },
    dnaCount: 3,
    dnaHistory: [
      {
        appointmentId: 'appt-dna-4',
        appointmentDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Transport issues',
      },
      {
        appointmentId: 'appt-dna-5',
        appointmentDate: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-6',
        appointmentDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Family emergency',
      },
    ],
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 400 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '11',
    patientNumber: 'PAT-011',
    firstName: 'Thomas',
    lastName: 'Taylor',
    dateOfBirth: '1991-06-15',
    gender: 'male',
    email: 'thomas.taylor@example.com',
    phone: '085-444-5555',
    address: {
      street: '7 Bridge Street',
      city: 'Limerick',
      state: 'Limerick',
      postalCode: 'V94 XY12',
      country: 'Ireland',
    },
    dnaCount: 4,
    dnaHistory: [
      {
        appointmentId: 'appt-dna-7',
        appointmentDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-8',
        appointmentDate: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Work conflict',
      },
      {
        appointmentId: 'appt-dna-9',
        appointmentDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-10',
        appointmentDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Illness',
      },
    ],
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 500 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '12',
    patientNumber: 'PAT-012',
    firstName: 'Jennifer',
    lastName: 'Anderson',
    dateOfBirth: '1987-10-08',
    gender: 'female',
    email: 'jennifer.anderson@example.com',
    phone: '086-555-6666',
    address: {
      street: '19 Market Square',
      city: 'Galway',
      state: 'Galway',
      postalCode: 'H91 AB34',
      country: 'Ireland',
    },
    identifiersExtended: {
      ppsNumber: '9876543B',
      ihiNumber: 'IHI-0012-9988',
      internalNumber: 'PAT-012',
      externalIds: [
        { system: 'Hospital Galway', identifier: 'HOSP-GAL-012' },
      ],
    },
    dnaCount: 5,
    dnaHistory: [
      {
        appointmentId: 'appt-dna-11',
        appointmentDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-12',
        appointmentDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Double booked',
      },
      {
        appointmentId: 'appt-dna-13',
        appointmentDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-14',
        appointmentDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        appointmentId: 'appt-dna-15',
        appointmentDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dnaAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        dnaReason: 'Forgot appointment',
      },
    ],
    isActive: true,
    isArchived: false,
    // Patient record linking (defaults to unlinked primary)
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 600 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // Test patients for duplicate detection
  {
    id: '14',
    patientNumber: 'PAT-014',
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: '1990-01-15', // Same name and DOB as patient 1 - HIGH confidence match
    gender: 'male',
    email: 'john.doe.new@example.com', // Different email
    phone: '555-9999', // Different phone
    ppsNumber: '1234567A', // Same PPS - will trigger 100 point match
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '15',
    patientNumber: 'PAT-015',
    firstName: 'Jane',
    lastName: 'Smith',
    dateOfBirth: '1985-05-20', // Same name and DOB as patient 2 - HIGH confidence match
    gender: 'female',
    email: 'jane.smith.alt@example.com', // Different email
    phone: '555-5678', // Same phone as patient 2 - will trigger 50 point match
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '16',
    patientNumber: 'PAT-016',
    firstName: 'Michael',
    lastName: 'O\'Brien',
    dateOfBirth: '1978-03-10', // Same name and DOB as patient 3 - HIGH confidence match
    gender: 'male',
    email: 'michael.obrien.work@example.com',
    phone: '085-123-4567', // Same phone as patient 3
    ppsNumber: '4567890C', // Same PPS as patient 3 - will trigger 100 point match
    address: {
      street: '15 Main Street',
      city: 'Dublin',
      state: 'Dublin',
      postalCode: 'D02 XY12',
      country: 'Ireland',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '17',
    patientNumber: 'PAT-017',
    firstName: 'Sarah',
    lastName: 'Murphy',
    dateOfBirth: '1992-07-22', // Same name and DOB as patient 4 - HIGH confidence match
    gender: 'female',
    email: 'sarah.murphy@example.com', // Same email
    phone: '087-654-3210', // Same phone as patient 4
    address: {
      street: '42 Oak Avenue',
      city: 'Cork',
      state: 'Cork',
      postalCode: 'T12 AB34',
      country: 'Ireland',
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '18',
    patientNumber: 'PAT-018',
    firstName: 'Niamh',
    lastName: 'Byrne',
    dateOfBirth: '1991-10-09',
    gender: 'female',
    email: 'niamh.byrne@example.com',
    phone: '085-445-7788',
    address: {
      street: '17 River Walk',
      city: 'Kilkenny',
      state: 'Kilkenny',
      postalCode: 'R95 XY22',
      country: 'Ireland',
    },
    coverage: {
      patientType: 'private',
      privateInsurance: {
        company: 'Laya Healthcare',
        membershipNumber: 'LAYA-018',
        coverType: 'Individual',
      },
    },
    smokingStatus: 'never',
    smokingStatusLastRecorded: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    smokingDetails: {
      status: 'never',
      description: 'Patient reports never smoking.',
    },
    alcoholConsumption: {
      status: 'current-drinker',
      description: 'Patient drinks occasionally, typically 1-2 drinks on weekends.',
      frequency: '1-2 times per week',
      quantityPerOccasion: '1-2 drinks',
      type: 'Wine',
      bingeDrinking: false,
    },
    isActive: true,
    isArchived: false,
    isPrimary: true,
    primaryPatientId: undefined,
    linkedPatientIds: undefined,
    createdAt: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
  },
])

const generatedMockPatients: Patient[] = ensureLinkingFields(
  Array.from({ length: 75 }, (_unused, index) => {
    const sequence = index + 18
    const year = 1980 + (index % 30)
    const month = String((index % 12) + 1).padStart(2, '0')
    const day = String((index % 28) + 1).padStart(2, '0')
    return {
      id: `gen-${sequence}`,
      patientNumber: `PAT-${String(sequence + 100).padStart(4, '0')}`,
      firstName: `Test${sequence}`,
      lastName: `Patient${sequence}`,
      dateOfBirth: `${year}-${month}-${day}`,
      gender: index % 2 === 0 ? 'male' : 'female',
      email: `test.patient${sequence}@example.com`,
      phone: `086-000-${String(sequence).padStart(4, '0')}`,
      isActive: true,
      isArchived: false,
      createdAt: new Date(Date.now() - index * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - index * 24 * 60 * 60 * 1000).toISOString(),
    }
  })
)

mockPatients.push(...generatedMockPatients)

// In-memory patient history for local development/accreditation UI
const mockPatientHistoryEntries: PatientHistoryEntry[] = [
  {
    id: 'hist-1',
    patientId: '1',
    changeType: 'create',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'doctor@clnicl.ie',
    reason: 'Initial registration of patient',
    changes: [
      {
        field: 'firstName',
        previousValue: null,
        newValue: 'John',
      },
      {
        field: 'lastName',
        previousValue: null,
        newValue: 'Doe',
      },
      {
        field: 'dateOfBirth',
        previousValue: null,
        newValue: '1990-01-15',
      },
    ],
    metadata: {
      source: 'mock',
    },
  },
  {
    id: 'hist-2',
    patientId: '1',
    changeType: 'update',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'nurse@clnicl.ie',
    reason: 'Updated contact details after phone call',
    changes: [
      {
        field: 'phone',
        previousValue: '555-0000',
        newValue: '555-1234',
      },
      {
        field: 'email',
        previousValue: null,
        newValue: 'john.doe@example.com',
      },
    ],
    metadata: {
      channel: 'phone',
    },
  },
  {
    id: 'hist-3',
    patientId: '2',
    changeType: 'create',
    timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'reception@clnicl.ie',
    reason: 'New patient registered at front desk',
    changes: [
      {
        field: 'firstName',
        previousValue: null,
        newValue: 'Jane',
      },
      {
        field: 'lastName',
        previousValue: null,
        newValue: 'Smith',
      },
    ],
    metadata: {
      source: 'front-desk',
    },
  },
]

// Mock audit history entries (EHR audit trail)
const mockAuditEntries: AuditEntry[] = [
  {
    id: 'audit-1',
    patientId: '1',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Initial patient registration',
    changes: [
      {
        field: 'firstName',
        previousValue: null,
        newValue: 'John',
      },
      {
        field: 'lastName',
        previousValue: null,
        newValue: 'Doe',
      },
      {
        field: 'dateOfBirth',
        previousValue: null,
        newValue: '1990-01-15',
      },
      {
        field: 'gender',
        previousValue: null,
        newValue: 'male',
      },
      {
        field: 'phone',
        previousValue: null,
        newValue: '555-0000',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-2',
    patientId: '1',
    timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'view',
    performedBy: 'nurse@clnicl.ie',
    performedByDisplayName: 'Nurse Murphy',
    reason: 'Patient record accessed',
    changes: [],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
    },
  },
  {
    id: 'audit-3',
    patientId: '1',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'nurse@clnicl.ie',
    performedByDisplayName: 'Nurse Murphy',
    reason: 'Updated contact information after phone call',
    changes: [
      {
        field: 'phone',
        previousValue: '555-0000',
        newValue: '555-1234',
      },
      {
        field: 'email',
        previousValue: null,
        newValue: 'john.doe@example.com',
      },
      {
        field: 'address.street',
        previousValue: null,
        newValue: '123 Main Street',
      },
      {
        field: 'address.city',
        previousValue: null,
        newValue: 'Dublin',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
    },
  },
  {
    id: 'audit-4',
    patientId: '1',
    timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Added medical history during consultation',
    changes: [
      {
        field: 'allergies',
        previousValue: null,
        newValue: ['Penicillin', 'Latex'],
      },
      {
        field: 'currentMedications',
        previousValue: null,
        newValue: ['Metformin 500mg twice daily'],
      },
      {
        field: 'chronicConditions',
        previousValue: null,
        newValue: ['Type 2 Diabetes'],
      },
      {
        field: 'bloodType',
        previousValue: null,
        newValue: 'O+',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-5',
    patientId: '1',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'view',
    performedBy: 'reception@clnicl.ie',
    performedByDisplayName: 'Reception Staff',
    reason: 'Patient record accessed',
    changes: [],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-6',
    patientId: '1',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Updated medications after lab results review',
    changes: [
      {
        field: 'currentMedications',
        previousValue: ['Metformin 500mg twice daily'],
        newValue: ['Metformin 500mg twice daily', 'Lisinopril 10mg daily'],
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-7',
    patientId: '2',
    timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'reception@clnicl.ie',
    performedByDisplayName: 'Reception Staff',
    reason: 'New patient registered at front desk',
    changes: [
      {
        field: 'firstName',
        previousValue: null,
        newValue: 'Jane',
      },
      {
        field: 'lastName',
        previousValue: null,
        newValue: 'Smith',
      },
      {
        field: 'dateOfBirth',
        previousValue: null,
        newValue: '1985-05-20',
      },
      {
        field: 'gender',
        previousValue: null,
        newValue: 'female',
      },
      {
        field: 'phone',
        previousValue: null,
        newValue: '555-5678',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-8',
    patientId: '2',
    timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'view',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Patient record accessed',
    changes: [],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
    },
  },
  {
    id: 'audit-9',
    patientId: '2',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'nurse@clnicl.ie',
    performedByDisplayName: 'Nurse Murphy',
    reason: 'Updated emergency contact information',
    changes: [
      {
        field: 'emergencyContact.name',
        previousValue: null,
        newValue: 'John Smith',
      },
      {
        field: 'emergencyContact.relationship',
        previousValue: null,
        newValue: 'Spouse',
      },
      {
        field: 'emergencyContact.phone',
        previousValue: null,
        newValue: '555-9999',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
    },
  },
  // Consultation audit entries
  {
    id: 'audit-10',
    patientId: '1',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Consultation created',
    entityType: 'consultation',
    entityId: 'consult-1',
    changes: [
      {
        field: 'reason',
        previousValue: null,
        newValue: 'Annual checkup',
      },
      {
        field: 'status',
        previousValue: null,
        newValue: 'scheduled',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      consultationDate: new Date().toISOString().split('T')[0],
    },
  },
  {
    id: 'audit-11',
    patientId: '1',
    timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Consultation completed',
    entityType: 'consultation',
    entityId: 'consult-1',
    changes: [
      {
        field: 'status',
        previousValue: 'scheduled',
        newValue: 'completed',
      },
      {
        field: 'notes',
        previousValue: null,
        newValue: 'Patient doing well. Continue current medications.',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      statusChanged: 'completed',
    },
  },
  // Lab result audit entries
  {
    id: 'audit-12',
    patientId: '1',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'system',
    performedByDisplayName: 'System',
    reason: 'Lab result synced from Healthlink',
    entityType: 'lab-result',
    entityId: 'lab-1',
    changes: [
      {
        field: 'labName',
        previousValue: null,
        newValue: 'Beaumont Hospital',
      },
      {
        field: 'type',
        previousValue: null,
        newValue: 'blood',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      source: 'healthlink',
      syncOperation: true,
    },
  },
  {
    id: 'audit-13',
    patientId: '1',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'update',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Lab result reviewed',
    entityType: 'lab-result',
    entityId: 'lab-1',
    changes: [
      {
        field: 'reviewStatus',
        previousValue: 'in-review',
        newValue: 'reviewed',
      },
      {
        field: 'reviewedAt',
        previousValue: null,
        newValue: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      reviewStatusChanged: 'reviewed',
    },
  },
  // Clinical history audit entries
  {
    id: 'audit-14',
    patientId: '1',
    timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Condition "Type 2 Diabetes" added',
    entityType: 'clinical-condition',
    entityId: 'cond-1',
    changes: [
      {
        field: 'condition',
        previousValue: null,
        newValue: 'Type 2 Diabetes',
      },
      {
        field: 'icd10Code',
        previousValue: null,
        newValue: 'E11.9',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      icd10Code: 'E11.9',
    },
  },
  {
    id: 'audit-15',
    patientId: '1',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'view',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Clinical history accessed',
    entityType: 'clinical-history',
    entityId: '1',
    changes: [],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
      accessedVia: 'GET /patients/{id}/clinical-history',
    },
  },
  // Allergy audit entries
  {
    id: 'audit-16',
    patientId: '1',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'create',
    performedBy: 'doctor@clnicl.ie',
    performedByDisplayName: 'Dr. Sarah O\'Brien',
    reason: 'Allergy created',
    entityType: 'allergy',
    entityId: 'allergy-1',
    changes: [
      {
        field: 'triggerDisplay',
        previousValue: null,
        newValue: 'Penicillin',
      },
      {
        field: 'reaction',
        previousValue: null,
        newValue: 'Rash',
      },
    ],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.100',
    },
  },
  {
    id: 'audit-17',
    patientId: '1',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    changeType: 'view',
    performedBy: 'nurse@clnicl.ie',
    performedByDisplayName: 'Nurse Murphy',
    reason: 'Allergies accessed',
    entityType: 'allergy',
    entityId: 'list',
    changes: [],
    metadata: {
      tenantId: 'tenant-1',
      subdomain: 'demo',
      ipAddress: '192.168.1.101',
      count: 2,
    },
  },
]

// In-memory clinical history for accreditation (separate from audit trail)
const mockClinicalHistory: PatientClinicalHistory[] = [
  {
    patientId: '1',
    conditions: [
      {
        id: 'cond-1',
        condition: 'Type 2 Diabetes',
        diagnosisDate: '2020-03-15',
        status: 'active',
        severity: 'moderate',
        onsetDate: '2020-03-15',
        icd10Code: 'E11.9',
        notes: 'Well controlled with Metformin. HbA1c target: <7%',
        recordedBy: 'doctor@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'cond-2',
        condition: 'Hypertension',
        diagnosisDate: '2018-06-20',
        status: 'active',
        severity: 'mild',
        onsetDate: '2018-06-20',
        icd10Code: 'I10',
        notes: 'Controlled with Lisinopril',
        recordedBy: 'doctor@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 4 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'cond-3',
        condition: 'Appendicitis',
        diagnosisDate: '2015-08-10',
        status: 'resolved',
        severity: 'moderate',
        onsetDate: '2015-08-10',
        resolvedDate: '2015-08-12',
        icd10Code: 'K35.9',
        notes: 'Appendectomy performed successfully',
        recordedBy: 'doctor@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 7 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    familyHistory: [
      {
        id: 'fh-1',
        relationship: 'Mother',
        condition: 'Type 2 Diabetes',
        ageAtDiagnosis: 52,
        notes: 'Diagnosed at age 52, currently well controlled',
        isPertinent: true,
        recordedAt: new Date(Date.now() - 365 * 2 * 24 * 60 * 60 * 1000).toISOString(),
        recordedBy: 'doctor@clnicl.ie',
      },
      {
        id: 'fh-2',
        relationship: 'Paternal Grandfather',
        condition: 'Coronary Artery Disease',
        ageAtDiagnosis: 65,
        ageAtDeath: 78,
        causeOfDeath: 'Myocardial Infarction',
        notes: 'History of heart disease in paternal line',
        isPertinent: true,
        recordedAt: new Date(Date.now() - 365 * 2 * 24 * 60 * 60 * 1000).toISOString(),
        recordedBy: 'doctor@clnicl.ie',
      },
      {
        id: 'fh-3',
        relationship: 'Maternal Aunt',
        condition: 'Breast Cancer',
        ageAtDiagnosis: 48,
        notes: 'Treated successfully, now in remission',
        isPertinent: false,
        recordedAt: new Date(Date.now() - 365 * 2 * 24 * 60 * 60 * 1000).toISOString(),
        recordedBy: 'doctor@clnicl.ie',
      },
    ],
    preferences: {
      preferredLanguage: 'en-IE',
      spokenLanguages: ['en-IE', 'ga-IE'],
      religion: 'Roman Catholic',
      culturalBackground: 'Irish',
      dietaryPreferences: ['No specific restrictions'],
      communicationPreferences: {
        preferredMethod: 'email',
        preferredContactTime: 'Afternoon',
        requiresInterpreter: false,
      },
      accessibilityNeeds: [],
      privacyPreferences: {
        canShareWithFamily: true,
        preferredFamilyContact: 'Spouse (Jane Doe)',
        restrictInformationSharing: false,
      },
      recordedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    },
    consents: [
      {
        id: 'consent-1',
        type: 'general-treatment',
        description: 'Consent for general medical treatment and procedures',
        status: 'granted',
        grantedDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        canPatientConsent: true,
        treatmentAuthority: {
          practitionerName: 'Dr. Sarah O\'Brien',
          scope: 'All routine treatments',
        },
        recordedBy: 'doctor@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'consent-2',
        type: 'data-sharing',
        description: 'Consent for sharing medical information with GMS and healthcare providers',
        status: 'granted',
        grantedDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        canPatientConsent: true,
        recordedBy: 'reception@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    lastReviewedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    reviewedBy: 'doctor@clnicl.ie',
  },
  {
    patientId: '2',
    conditions: [
      {
        id: 'cond-4',
        condition: 'Asthma',
        diagnosisDate: '2010-05-12',
        status: 'active',
        severity: 'mild',
        onsetDate: '2010-05-12',
        icd10Code: 'J45.9',
        notes: 'Well controlled with inhaler. No recent exacerbations',
        recordedBy: 'doctor@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    familyHistory: [
      {
        id: 'fh-4',
        relationship: 'Father',
        condition: 'Asthma',
        ageAtDiagnosis: 30,
        notes: 'Diagnosed in adulthood, well managed',
        isPertinent: true,
        recordedAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
        recordedBy: 'doctor@clnicl.ie',
      },
    ],
    preferences: {
      preferredLanguage: 'en-IE',
      spokenLanguages: ['en-IE'],
      religion: 'Church of Ireland',
      culturalBackground: 'Irish',
      dietaryPreferences: ['Vegetarian'],
      communicationPreferences: {
        preferredMethod: 'phone',
        preferredContactTime: 'Morning',
        requiresInterpreter: false,
      },
      accessibilityNeeds: [],
      privacyPreferences: {
        canShareWithFamily: true,
        preferredFamilyContact: 'Husband (John Doe)',
        restrictInformationSharing: false,
      },
      recordedAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    },
    consents: [
      {
        id: 'consent-3',
        type: 'general-treatment',
        description: 'Consent for general medical treatment',
        status: 'granted',
        grantedDate: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        canPatientConsent: true,
        treatmentAuthority: {
          practitionerName: 'Dr. Sarah O\'Brien',
          scope: 'All routine treatments',
        },
        recordedBy: 'reception@clnicl.ie',
        recordedAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ],
    createdAt: new Date(Date.now() - 365 * 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

// In-memory storage for tenants (mock data for testing)
const mockTenants: MockTenant[] = [
  {
    subdomain: 'best',
    tenantId: 'tenant-best-001',
    clinicName: 'Best Medical Clinic',
    contactEmail: 'admin@bestmedical.ie',
    contactPersonName: 'Dr. John Best',
    phoneNumber: '+353 1 234 5678',
    status: 'provisioned',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-best',
    fullDomain: 'best.clnicl.ie',
    provisionedAt: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    subdomain: 'dublin',
    tenantId: 'tenant-dublin-002',
    clinicName: 'Dublin Family Practice',
    contactEmail: 'info@dublinfamily.ie',
    contactPersonName: 'Sarah Murphy',
    phoneNumber: '+353 1 555 1234',
    status: 'provisioned',
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-dublin',
    fullDomain: 'dublin.clnicl.ie',
    provisionedAt: new Date(Date.now() - 19 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    subdomain: 'cork',
    tenantId: 'tenant-cork-003',
    clinicName: 'Cork Medical Centre',
    contactEmail: 'admin@corkmedical.ie',
    contactPersonName: 'Dr. Michael O\'Sullivan',
    phoneNumber: '+353 21 456 7890',
    status: 'provisioned',
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-cork',
    fullDomain: 'cork.clnicl.ie',
    provisionedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    subdomain: 'pending-test',
    tenantId: 'tenant-pending-004',
    clinicName: 'Pending Test Clinic',
    contactEmail: 'test@pending.ie',
    contactPersonName: 'Test User',
    status: 'pending',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-pending-test',
    fullDomain: 'pending-test.clnicl.ie',
  },
  {
    subdomain: 'provisioning-test',
    tenantId: 'tenant-provisioning-005',
    clinicName: 'Provisioning Test Clinic',
    contactEmail: 'test@provisioning.ie',
    contactPersonName: 'Test User',
    status: 'provisioning',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-provisioning-test',
    fullDomain: 'provisioning-test.clnicl.ie',
  },
  {
    subdomain: 'failed-test',
    tenantId: 'tenant-failed-006',
    clinicName: 'Failed Test Clinic',
    contactEmail: 'test@failed.ie',
    contactPersonName: 'Test User',
    status: 'failed',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    stackName: 'ClniclTenantStack-failed-test',
    fullDomain: 'failed-test.clnicl.ie',
    error: 'Infrastructure provisioning failed: Timeout during stack creation',
    failedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

// In-memory storage for patient files
const mockPatientFiles: PatientFile[] = []

// In-memory storage for batch documents (before matching to patients)
const mockBatchDocuments: BatchDocument[] = (() => {
  const batchId = crypto.randomUUID()
  const fileId1 = crypto.randomUUID()
  const fileId2 = crypto.randomUUID()
  const now = new Date().toISOString()
  const firstPatient = mockPatients[0]
  return [
    {
      batchId,
      fileId: fileId1,
      s3Key: `batch/${batchId}/${fileId1}/sample.pdf`,
      fileName: 'sample-letter.pdf',
      fileType: 'application/pdf',
      fileSize: 10240,
      uploadedAt: now,
      status: 'suggested',
      extractedText: `Patient: ${firstPatient?.firstName ?? 'John'} ${firstPatient?.lastName ?? 'Doe'}\nDOB: ${firstPatient?.dateOfBirth ?? '1990-01-01'}`,
      suggestedPatientId: firstPatient?.id,
      suggestedPatientName: firstPatient ? `${firstPatient.firstName} ${firstPatient.lastName}` : 'John Doe',
    },
    {
      batchId,
      fileId: fileId2,
      s3Key: `batch/${batchId}/${fileId2}/unknown.pdf`,
      fileName: 'unknown.pdf',
      fileType: 'application/pdf',
      fileSize: 2048,
      uploadedAt: now,
      status: 'unmatched',
      extractedText: 'Name: Unknown Person\nDOB: 01/01/1985',
    },
  ]
})()

// Mock maternity data
const mockPregnancies: Pregnancy[] = [
  {
    pregnancyId: 'preg-001',
    patientId: '1',
    lastMenstrualPeriod: '2024-01-15',
    estimatedDueDate: calculateDueDate('2024-01-15').toISOString().split('T')[0],
    pregnancyNumber: 1,
    status: 'active',
    babies: [],
    createdAt: '2024-01-20T10:00:00Z',
    updatedAt: '2024-01-20T10:00:00Z',
  },
]

const mockMaternityVisits: MaternityVisit[] = []

// Mock prescriptions storage
const mockPrescriptions: Prescription[] = []

// Mock providers - provider directory
const mockProviders: Provider[] = [
  // Internal providers (linked to mockUsers)
  {
    id: 'provider-1',
    type: 'internal',
    name: 'Sarah O\'Brien',
    firstName: 'Sarah',
    lastName: 'O\'Brien',
    fullName: 'Sarah O\'Brien',
    title: 'Dr',
    organization: 'Main Street Medical Practice',
    specialty: 'General Practice',
    isActive: true,
    createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-2',
    type: 'internal',
    name: 'Emily Kavanagh',
    firstName: 'Emily',
    lastName: 'Kavanagh',
    fullName: 'Emily Kavanagh',
    title: 'Dr',
    organization: 'Main Street Medical Practice',
    specialty: 'General Practice',
    isActive: true,
    createdAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
  },
  // External providers
  {
    id: 'provider-3',
    type: 'external',
    name: 'Michael O\'Connor',
    firstName: 'Michael',
    lastName: 'O\'Connor',
    fullName: 'Michael O\'Connor',
    title: 'Dr',
    organization: 'Beaumont Hospital',
    specialty: 'Cardiology',
    address: {
      street: 'Beaumont Road',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D09 XR72',
      country: 'Ireland',
    },
    phone: '+353 1 809 3000',
    email: 'cardiology@beaumont.ie',
    healthmailAddress: 'michael.oconnor@healthmail.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-4',
    type: 'external',
    name: 'Patricia Walsh',
    firstName: 'Patricia',
    lastName: 'Walsh',
    fullName: 'Patricia Walsh',
    title: 'Dr',
    organization: 'Mater Private Hospital',
    specialty: 'Orthopedics',
    address: {
      street: 'Eccles Street',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D07 K599',
      country: 'Ireland',
    },
    phone: '+353 1 885 8888',
    email: 'orthopedics@materprivate.ie',
    healthmailAddress: 'patricia.walsh@healthmail.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-5',
    type: 'external',
    name: 'James Murphy',
    firstName: 'James',
    lastName: 'Murphy',
    fullName: 'James Murphy',
    title: 'Dr',
    organization: 'Blackrock Clinic',
    specialty: 'Dermatology',
    address: {
      street: 'Rock Road',
      city: 'Blackrock',
      state: 'Leinster',
      postalCode: 'A94 XK29',
      country: 'Ireland',
    },
    phone: '+353 1 283 2222',
    email: 'dermatology@blackrockclinic.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-6',
    type: 'external',
    name: 'Catherine Ryan',
    firstName: 'Catherine',
    lastName: 'Ryan',
    fullName: 'Catherine Ryan',
    title: 'Dr',
    organization: 'St. Patrick\'s Hospital',
    specialty: 'Psychiatry',
    address: {
      street: 'James\'s Street',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D08 NHY1',
      country: 'Ireland',
    },
    phone: '+353 1 249 3200',
    email: 'psychiatry@stpatricks.ie',
    healthmailAddress: 'catherine.ryan@healthmail.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-7',
    type: 'external',
    name: 'David Byrne',
    firstName: 'David',
    lastName: 'Byrne',
    fullName: 'David Byrne',
    title: 'Dr',
    organization: 'Mater Misericordiae University Hospital',
    specialty: 'Ophthalmology',
    address: {
      street: 'Eccles Street',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D07 R2WY',
      country: 'Ireland',
    },
    phone: '+353 1 803 2000',
    email: 'ophthalmology@mater.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-8',
    type: 'external',
    name: 'Lisa O\'Sullivan',
    firstName: 'Lisa',
    lastName: 'O\'Sullivan',
    fullName: 'Lisa O\'Sullivan',
    title: 'Dr',
    organization: 'Beaumont Hospital',
    specialty: 'ENT',
    address: {
      street: 'Beaumont Road',
      city: 'Dublin',
      state: 'Leinster',
      postalCode: 'D09 XR72',
      country: 'Ireland',
    },
    phone: '+353 1 809 3000',
    email: 'ent@beaumont.ie',
    isActive: true,
    createdAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'provider-9',
    type: 'external',
    name: 'Robert Kelly',
    firstName: 'Robert',
    lastName: 'Kelly',
    fullName: 'Robert Kelly',
    title: 'Dr',
    organization: 'Private Practice',
    specialty: 'Orthopedics',
    address: {
      street: '123 Medical Centre',
      city: 'Cork',
      state: 'Munster',
      postalCode: 'T12 XY34',
      country: 'Ireland',
    },
    phone: '+353 21 123 4567',
    email: 'r.kelly@ortho.ie',
    isActive: false, // Inactive provider for testing
    createdAt: new Date(Date.now() - 300 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

// Mock referrals - comprehensive sample data
const mockReferrals: Referral[] = [
  {
    id: 'referral-1',
    patientId: '1',
    consultationId: '2',
    referringProviderId: '2',
    referringProviderName: 'Dr. Sarah O\'Brien',
    referredToProviderId: 'provider-3',
    referredToProviderName: 'Michael O\'Connor',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'Michael O\'Connor',
      firstName: 'Michael',
      lastName: 'O\'Connor',
      fullName: 'Michael O\'Connor',
      title: 'Dr',
      organization: 'Beaumont Hospital',
      specialty: 'Cardiology',
      address: {
        street: 'Beaumont Road',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D09 XR72',
        country: 'Ireland',
      },
      phone: '+353 1 809 3000',
      email: 'cardiology@beaumont.ie',
      healthmailAddress: 'michael.oconnor@healthmail.ie',
    },
    referralDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'urgent',
    status: 'sent',
    reason: 'Chest pain evaluation',
    clinicalDetails: {
      presentingComplaint: 'Intermittent chest pain over past week, worse on exertion',
      historyOfPresentingComplaint: 'Patient reports chest pain started 7 days ago. Pain is substernal, radiates to left arm. Associated with shortness of breath on exertion.',
      pastMedicalHistory: 'Hypertension, Type 2 Diabetes',
      currentMedications: ['Metformin 500mg BD', 'Lisinopril 10mg OD'],
      allergies: ['Penicillin'],
      examinationFindings: 'BP 140/90, HR 88 regular, no murmurs. EKG shows normal sinus rhythm.',
      investigations: 'EKG normal, awaiting bloods',
      diagnosis: ['R06.02 - Shortness of breath', 'Chest pain - ?cardiac'],
      treatmentToDate: 'Started on aspirin 75mg OD. Advised to return if symptoms worsen.',
      specificQuestions: 'Please assess for coronary artery disease. Consider stress test if indicated.',
    },
    insuranceInformation: {
      insuranceProvider: 'VHI',
      policyNumber: 'VHI-123456',
      groupNumber: 'GRP-001',
    },
    templateId: 'template-1',
    templateName: 'Referral Letter Template',
    communicationMethod: 'healthmail',
    communicationStatus: {
      method: 'healthmail',
      sentAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      healthmailMessageId: 'HM-2024-001234',
    },
    followUpRequired: true,
    followUpDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    providerCustomNote: 'Please prioritise this patient given recent symptom progression.',
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: 'referral-2',
    patientId: '1',
    referringProviderId: '2',
    referringProviderName: 'Dr. Sarah O\'Brien',
    referredToProviderId: 'provider-5',
    referredToProviderName: 'James Murphy',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'James Murphy',
      firstName: 'James',
      lastName: 'Murphy',
      fullName: 'James Murphy',
      title: 'Dr',
      organization: 'Blackrock Clinic',
      specialty: 'Dermatology',
      address: {
        street: 'Rock Road',
        city: 'Blackrock',
        state: 'Leinster',
        postalCode: 'A94 XK29',
        country: 'Ireland',
      },
      phone: '+353 1 283 2222',
      email: 'dermatology@blackrockclinic.ie',
    },
    referralDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'routine',
    status: 'acknowledged',
    reason: 'Persistent skin rash',
    clinicalDetails: {
      presentingComplaint: 'Rash on arms and legs for 3 months',
      historyOfPresentingComplaint: 'Patient reports itchy rash that started 3 months ago. Has tried over-the-counter creams with no improvement.',
      pastMedicalHistory: 'None',
      currentMedications: [],
      allergies: ['Penicillin'],
      examinationFindings: 'Erythematous papular rash on extensor surfaces of arms and legs. No signs of infection.',
      diagnosis: ['Rash - ?eczema ?psoriasis'],
      treatmentToDate: 'Tried hydrocortisone cream with minimal improvement.',
      specificQuestions: 'Please assess and provide diagnosis. Recommend treatment plan.',
    },
    insuranceInformation: {
      insuranceProvider: 'VHI',
      policyNumber: 'VHI-123456',
    },
    communicationMethod: 'email',
    communicationStatus: {
      method: 'email',
      sentAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      acknowledgedAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    },
    followUpRequired: false,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: 'referral-3',
    patientId: '2',
    referringProviderId: '2',
    referringProviderName: 'Dr. Sarah O\'Brien',
    referredToProviderId: 'provider-4',
    referredToProviderName: 'Patricia Walsh',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'Patricia Walsh',
      firstName: 'Patricia',
      lastName: 'Walsh',
      fullName: 'Patricia Walsh',
      title: 'Dr',
      organization: 'Mater Private Hospital',
      specialty: 'Orthopedics',
      address: {
        street: 'Eccles Street',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D07 K599',
        country: 'Ireland',
      },
      phone: '+353 1 885 8888',
      email: 'orthopedics@materprivate.ie',
      healthmailAddress: 'patricia.walsh@healthmail.ie',
    },
    referralDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'routine',
    status: 'draft',
    reason: 'Knee pain assessment',
    clinicalDetails: {
      presentingComplaint: 'Right knee pain for 6 months',
      historyOfPresentingComplaint: 'Patient reports progressive right knee pain, worse on walking and stairs.',
      pastMedicalHistory: 'Obesity, previous knee injury 10 years ago',
      currentMedications: ['Paracetamol 1g QDS'],
      allergies: [],
      examinationFindings: 'Reduced range of motion, crepitus on movement. X-ray shows mild osteoarthritis.',
      diagnosis: ['M25.561 - Pain in right knee', 'M19.90 - Unspecified osteoarthritis'],
      treatmentToDate: 'Conservative management with paracetamol. Physiotherapy recommended.',
      specificQuestions: 'Please assess for surgical intervention options. Consider joint injection if appropriate.',
    },
    insuranceInformation: {
      insuranceProvider: 'Laya Healthcare',
      policyNumber: 'LAY-789012',
    },
    communicationMethod: 'pending',
    followUpRequired: true,
    followUpDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: 'referral-4',
    patientId: '3',
    referringProviderId: '5',
    referringProviderName: 'Dr. Emily Kavanagh',
    referredToProviderId: 'provider-6',
    referredToProviderName: 'Catherine Ryan',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'Catherine Ryan',
      firstName: 'Catherine',
      lastName: 'Ryan',
      fullName: 'Catherine Ryan',
      title: 'Dr',
      organization: 'St. Patrick\'s Hospital',
      specialty: 'Psychiatry',
      address: {
        street: 'James\'s Street',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D08 NHY1',
        country: 'Ireland',
      },
      phone: '+353 1 249 3200',
      email: 'psychiatry@stpatricks.ie',
      healthmailAddress: 'catherine.ryan@healthmail.ie',
    },
    referralDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'urgent',
    status: 'sent',
    reason: 'Depression assessment',
    clinicalDetails: {
      presentingComplaint: 'Low mood, anxiety, sleep disturbance',
      historyOfPresentingComplaint: 'Patient reports worsening mood over past 3 months. Difficulty sleeping, loss of interest in activities.',
      pastMedicalHistory: 'Anxiety disorder',
      currentMedications: ['Sertraline 50mg OD'],
      allergies: [],
      examinationFindings: 'Patient appears tearful, low mood evident. PHQ-9 score: 18 (moderate-severe depression).',
      diagnosis: ['F32.1 - Moderate depressive episode'],
      treatmentToDate: 'Started on sertraline 4 weeks ago. Some improvement but still struggling.',
      specificQuestions: 'Please assess for medication adjustment or additional interventions. Consider CBT referral.',
    },
    insuranceInformation: {
      insuranceProvider: 'GMS',
      policyNumber: 'GMS-456789',
    },
    communicationMethod: 'healthmail',
    communicationStatus: {
      method: 'healthmail',
      sentAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      healthmailMessageId: 'HM-2024-001567',
    },
    followUpRequired: true,
    followUpDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '5',
  },
  {
    id: 'referral-5',
    patientId: '1',
    referringProviderId: '2',
    referringProviderName: 'Dr. Sarah O\'Brien',
    referredToProviderId: 'provider-7',
    referredToProviderName: 'David Byrne',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'David Byrne',
      firstName: 'David',
      lastName: 'Byrne',
      fullName: 'David Byrne',
      title: 'Dr',
      organization: 'Mater Misericordiae University Hospital',
      specialty: 'Ophthalmology',
      address: {
        street: 'Eccles Street',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D07 R2WY',
        country: 'Ireland',
      },
      phone: '+353 1 803 2000',
      email: 'ophthalmology@mater.ie',
    },
    referralDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'routine',
    status: 'completed',
    reason: 'Cataract assessment',
    clinicalDetails: {
      presentingComplaint: 'Progressive vision loss',
      historyOfPresentingComplaint: 'Patient reports gradual vision loss over past year. Difficulty reading and driving.',
      pastMedicalHistory: 'Diabetes, Hypertension',
      currentMedications: ['Metformin', 'Lisinopril'],
      allergies: [],
      examinationFindings: 'Visual acuity reduced. Slit lamp shows significant cataract in both eyes.',
      diagnosis: ['H25.9 - Unspecified senile cataract'],
      treatmentToDate: 'Conservative management. Patient ready for surgical intervention.',
      specificQuestions: 'Please assess for cataract surgery. Patient prefers to proceed when convenient.',
    },
    insuranceInformation: {
      insuranceProvider: 'VHI',
      policyNumber: 'VHI-123456',
      authorizationNumber: 'AUTH-789',
    },
    communicationMethod: 'email',
    communicationStatus: {
      method: 'email',
      sentAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      acknowledgedAt: new Date(Date.now() - 58 * 24 * 60 * 60 * 1000).toISOString(),
    },
    followUpRequired: false,
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: 'referral-6',
    patientId: '2',
    referringProviderId: '2',
    referringProviderName: 'Dr. Sarah O\'Brien',
    referredToProviderName: 'John Smith',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'John Smith',
      firstName: 'John',
      lastName: 'Smith',
      fullName: 'John Smith',
      title: 'Dr',
      organization: 'Private Practice',
      specialty: 'Cardiology',
      address: {
        street: '456 Medical Centre',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D02 XY34',
        country: 'Ireland',
      },
      phone: '+353 1 234 5678',
      email: 'j.smith@cardio.ie',
    },
    referralDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'emergency',
    status: 'sent',
    reason: 'Acute chest pain',
    clinicalDetails: {
      presentingComplaint: 'Acute onset chest pain',
      historyOfPresentingComplaint: 'Patient presented with sudden onset severe chest pain. EKG shows ST elevation.',
      pastMedicalHistory: 'Hypertension',
      currentMedications: ['Amlodipine 5mg OD'],
      allergies: [],
      examinationFindings: 'BP 160/100, HR 110 irregular. EKG shows ST elevation in leads II, III, aVF.',
      investigations: 'Troponin elevated, awaiting further results',
      diagnosis: ['I21.9 - Acute myocardial infarction, unspecified'],
      treatmentToDate: 'Given aspirin 300mg, clopidogrel 600mg. Transferred urgently.',
      specificQuestions: 'URGENT: Please assess immediately for acute coronary syndrome.',
    },
    insuranceInformation: {
      insuranceProvider: 'VHI',
      policyNumber: 'VHI-123456',
    },
    communicationMethod: 'healthmail',
    communicationStatus: {
      method: 'healthmail',
      sentAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      healthmailMessageId: 'HM-2024-002345',
    },
    followUpRequired: true,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '2',
  },
  {
    id: 'referral-7',
    patientId: '1',
    referringProviderId: '5',
    referringProviderName: 'Dr. Emily Kavanagh',
    referredToProviderId: 'provider-8',
    referredToProviderName: 'Lisa O\'Sullivan',
    referredToProviderType: 'external',
    providerDetails: {
      name: 'Lisa O\'Sullivan',
      firstName: 'Lisa',
      lastName: 'O\'Sullivan',
      fullName: 'Lisa O\'Sullivan',
      title: 'Dr',
      organization: 'Beaumont Hospital',
      specialty: 'ENT',
      address: {
        street: 'Beaumont Road',
        city: 'Dublin',
        state: 'Leinster',
        postalCode: 'D09 XR72',
        country: 'Ireland',
      },
      phone: '+353 1 809 3000',
      email: 'ent@beaumont.ie',
    },
    referralDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    priority: 'routine',
    status: 'cancelled',
    reason: 'Hearing loss assessment',
    clinicalDetails: {
      presentingComplaint: 'Progressive hearing loss',
      historyOfPresentingComplaint: 'Patient reports gradual hearing loss over past year.',
      pastMedicalHistory: 'None',
      currentMedications: [],
      allergies: [],
      examinationFindings: 'Ear examination normal. Audiometry shows sensorineural hearing loss.',
      diagnosis: ['H90.3 - Sensorineural hearing loss, bilateral'],
      treatmentToDate: 'Patient decided to pursue alternative treatment options.',
    },
    communicationMethod: 'manual',
    followUpRequired: false,
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: '5',
  },
]

// OLD Mock formulary storage - REMOVED (using mockFormularyMedications array with localStorage instead)

// Mock appointment types - initialize with default types
let mockAppointmentTypes: AppointmentTypeConfig[] = DEFAULT_APPOINTMENT_TYPES.map((type, index) => ({
  ...type,
  id: `default-${index + 1}`,
  createdAt: new Date(Date.now() - (DEFAULT_APPOINTMENT_TYPES.length - index) * 24 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date(Date.now() - (DEFAULT_APPOINTMENT_TYPES.length - index) * 24 * 60 * 60 * 1000).toISOString(),
}))

// In-memory storage for templates
const letterTemplateOttBase64 = 'UEsDBBQAAAgAALZujFvkEZIpMAAAADAAAAAIAAAAbWltZXR5cGVhcHBsaWNhdGlvbi92bmQub2FzaXMub3BlbmRvY3VtZW50LnRleHQtdGVtcGxhdGVQSwMEFAAACAAAtm6MWxDlTeFDBQAAQwUAABgAAABUaHVtYm5haWxzL3RodW1ibmFpbC5wbmeJUE5HDQoaCgAAAA1JSERSAAAAtQAAAQAIAgAAAHpBoIwAAAUKSURBVHic7drPSlVrHMdhs11KDRSUCIoKwnKiA8FIwrA40CDoBgKlibOuoSvoNsKJjhqHjlQUIgKJlKKM/teoyP7p+bE3iHjO+caxwTmD5xks1l6++11L389absHG1tZWG/yDxn99Afyv6YNEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog+SPfYxPT1d256enrGxscePH585c6Zerq2tnT59et++fdvDvn792tHRseu9Dx48OHnyZHd3999+dXV1ta+vr7W/tbX15cuXt2/fnjp1aueEKysrP378GBwcnJ2dbR28cOHC06dPBwYGds326NGj/v7+vX2PtO2tj1q2I0eOfPv2bWlp6cOHD41GY2Fh4dq1a7UYr1+/PnTo0NGjR+/evTs5OXn79u2LFy++ePGi1vjgwYMHDhx48+ZNb2/v+vr6xsZGhTU0NPTz58+rV69uT37//v25ubka3N7e/u7du7Nnz9aAmZmZ48ePVxNVz61bt+7cuTMyMvLs2bNXr17VW+p4TdvZ2Tk/P18XVueqSzpx4kR18/HjR338jr30UU+IWrPqo/afPHlSL2ulq4OXL1/W7V7FDA8PVx/Xr1+ve31xcbEiqJWu8XXf15OjbvT379/XtlauCrty5UpVVcVUAdVWV1dXLXxtl5eXq6d6IN27d+/YsWM1+ebmZnVT56phNf758+f1EKprqKdLxVTpVB+XL1+u/TpY+zXzp0+fquadjzT+lT3+fqnbt7ajo6OtH33dwbWi4+Pj379/r/u4bt+pqalaxZs3b+7fv7++eu7cuRr28OHDWrB6V610HW9rplY7taL1yGnNXC8rixs3bkxMTNTxCrGWudZ4s6n6qG1NW2NqZ/ufZw8fPlxnqQRb7daRGlmX8fnzZ3H8jj320frcsOvTQ2dTa7/iaGsuUltzyVsjz58//8uZ/2jaflkd/HXM9ll2ajTtOnjp0qVfnpHA3y8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6INEHiT5I9EGiDxJ9kOiDRB8k+iDRB4k+SPRBog8SfZDog0QfJPog0QeJPkj0QaIPEn2Q6INEHyT6IPkTVjYz9zEULT0AAAAASUVORK5CYIJQSwMEFAAICAgAtm6MWwAAAAAAAAAAAAAAAAsAAABjb250ZW50LnhtbK1W247aMBB971dEqdS34AVaaUmBVaWqUqXdfShU6qtxHLDqS2o7BFRV6j/0D/slHTskmF3CRqISAjI+xzM+c4mndzvBoy3Vhik5i4eDmziikqiMyfUs/rr8lNzGd/NXU5XnjNA0U6QUVNqEKGnhNwK2NGm9OotLLVOFDTOpxIKa1JJUFVQ2rDREp95XbTF2z3vTPThkW7qzfckOe8LFq/6ePThkZxpXfckOC6KG9Fz1Je8MT3IFqosCW/Ykih1n8vss3lhbpAhVVTWoxgOl12g4mUyQX20DJi2uKDX3qIwgyqlzZtBwMEQNVlCL+8bnsGFIshQrqntLgy1+llWzXfeuiO26Qxqywbp3bXjwaXrHWf/0jrOQK7DddOTkFj3Aov96uD/WghZ9fTnsiVREs6L3MWt0yFdKtaE6Qt2gPtzRzc1bVD8H6OoivNLMUh3AyUU4wZy0iitxTjTADREgErp1ZdoWvhPCdBBGqF5uwSbr3Prbw/2CbKjARzB7GZwwaSyWR2W0S0LnSd8hTQulbStM3n9gQrZGbWwbK3h3u7vVBrrWWXYWCuGMEbQ+NF6yZbR6fTIPL9fDBHlQOH0vEobjJwWUM8qbvmrBBwHorqCaubNj7konEQZkhnJSRRqw6+o9MIO31yieN6+quswNag05vLKSHBOaZJRwM5/WI6c1R/Wzi2MW35eEZThaYGmGcQQDpsEJxvez+A0ulHkfgGpDHJ3s6bDJmko4EbSAqZgxMbrsdwlnN9EjraIvSmDZ6foJrod7fdjviCiYJTCjtlgzn9AXQvsAMH4moMb+0smvcB0I/b+SsTeWimtiWjCxKM8lqF241jvqKtyDHZcWcmoZSfw+QamvVLZvH1yDzqf+4mPojxJud+1Gz42RN2XMFBzvE1VauD3QhMPchTkCLeeXawE+c14aqyEAJV24V222bA593S7w9+pNPtYXNZ+BbtWKmuKVTw4VAS+DDOssbgJA8+WGmQg+GNACXFsa/f39J/rpriow4wZ5yfkjkH8dXBXHrNdpQycZRR3X8Pk/UEsHCKl0GBsOAwAAxwsAAFBLAwQUAAAIAAC2boxbAAAAAAAAAAAAAAAAGgAAAENvbmZpZ3VyYXRpb25zMi90b29scGFuZWwvUEsDBBQAAAgAALZujFsAAAAAAAAAAAAAAAAaAAAAQ29uZmlndXJhdGlvbnMyL3BvcHVwbWVudS9QSwMEFAAACAAAtm6MWwAAAAAAAAAAAAAAAB8AAABDb25maWd1cmF0aW9uczIvaW1hZ2VzL0JpdG1hcHMvUEsDBBQACAgIALZujFsAAAAAAAAAAAAAAAAnAAAAQ29uZmlndXJhdGlvbnMyL2FjY2VsZXJhdG9yL2N1cnJlbnQueG1sAwBQSwcIAAAAAAIAAAAAAAAAUEsDBBQAAAgAALZujFsAAAAAAAAAAAAAAAAYAAAAQ29uZmlndXJhdGlvbnMyL2Zsb2F0ZXIvUEsDBBQAAAgAALZujFsAAAAAAAAAAAAAAAAYAAAAQ29uZmlndXJhdGlvbnMyL21lbnViYXIvUEsDBBQAAAgAALZujFsAAAAAAAAAAAAAAAAaAAAAQ29uZmlndXJhdGlvbnMyL3N0YXR1c2Jhci9QSwMEFAAACAAAtm6MWwAAAAAAAAAAAAAAABgAAABDb25maWd1cmF0aW9uczIvdG9vbGJhci9QSwMEFAAACAAAtm6MWwAAAAAAAAAAAAAAABwAAABDb25maWd1cmF0aW9uczIvcHJvZ3Jlc3NiYXIvUEsDBBQACAgIALZujFsAAAAAAAAAAAAAAAAMAAAAbWFuaWZlc3QucmRmzZPNboMwEITvPIVlzthALwUFcijKuWqfwDWGWAUv8poS3r6Ok1ZRpKrqn9TjrkYz3460m+1hHMiLsqjBVDRjKSXKSGi16Ss6uy65pds62ti2Kx+aHfFqg6WfKrp3bio5X5aFLTcMbM+zoih4mvM8T7wiwdU4cUgMxrSOCAkejUJp9eR8GjnO4glmV1F066CQefcgPYvdOqmgsgphtlK9h7YgkYFAjQlMyoR0gxy6TkvFM5bzUTnBoe3ix2C904OiPGDwK47P2N6IDKblXuC9sO5cg998lWh67mN6ddPF8d8jlGCcMu5P6rs7ef/n/i7P/xnir7R2RGxAzqNn+pDntPIfVUevUEsHCLT3aNIFAQAAgwMAAFBLAwQUAAgICAC2boxbAAAAAAAAAAAAAAAACgAAAHN0eWxlcy54bWzdWltv2zYUft+vMFRsb7QsO2lir0kxFBtWoO2AtXsuaIm2uFKiQFJW3F+/Q1KUaFly1CTeg/MQQDyH5/KdC29+8/YhY5MdEZLy/C6IprNgQvKYJzTf3gX/fPkD3QZv7396wzcbGpNVwuMyI7lCUu0ZkROYnMuVJd4FpchXHEsqVznOiFypeMULkrtJK597ZVTZESNs7HTD7M9W5EGNnax5D+bi9XjNhtmfnQhcjZ2seQFTf/qGj538IBnacBTzrMCKdqx4YDT/dhekShWrMKyqalotplxsw2i5XIaG2hgcN3xFKZjhSuKQMKKVyTCaRqHjzYjCY+3TvL5JeZmtiRgNDVb4KKpytx2dEbvtADRxisXo3DDMh+FdJOPDu0j8uRlW6UBMbsOPQDT/Pn5oc0FkY3Vp3gOoYkGL0W5abn8+57wxVU+wBWrMnc9mV6H99rirk+yVoIoIjz0+yR5jFjeI86wPNOCLQuBAZKfT1HEL7fSg5OtQkIIL1RiyGd+gAJ15U16pythweWmqY92KJOllBXMWIZQaJDraUVK9Oug/p/FfhobJ73YnJ0SLJmB1p/W6+zy4d618w6GNb3BMUEJiJu/f2BJshif2WwN1F3woY5rgyWecyyiYQME5voyy/V3wCy64/NVjsgPB5ECm5kVbkhNBISVkRaUMwtN6v1AI0+QTqSZ/8wzng6o7fCPUi1pey1FQFUPN7rCgBvBHTPsN2FiPQW78Mc+fodoD+qWCsZeKZM+x6TPNPpd9AWoIz9UeDiVuPW63JM7KhGxwyeqNipNcm7QVuEhpHDje+hsVAqpJKAqptOGrCkYRL5QpnZwj/R1M9Dq+kilOeIVAsSQKPdwFs+kiznqJ+w5RwSKDYE0mSBY4hh0BSrmg38EnzDTr/PYk807bFx+zQhMbK/WItUdmjRcDPyqqUmT3WBvMpJcIBRbYQOcDZ0maH+FSca0DsoMmhFtWzIoUOwXGjLUgGPYvUkEuKEfRi4i2LeMJTGcCqfVBftA8Ibpz672o74wz0tkIvRNSgBdSJ9Cw2Q27tvvIm1ISgCHXUTXKY8447G6UKKHJQqaYQUm/g6XRvFBmjOF8W+ItDJHcDMS8zJWAdHj/e+M+UbBSom9E5MZ0K9DzUstEsF7h3EmuJ9bCHe176ii1Fkd49+lYnt4qMfIwILGhprQrsyG9/xS0cB4U2pjqa/APTiYSQJbui5TkWFcgYjhJACtjiylHRjPamD8y34oyj1VpBepyBi/Bb8D+8YR0iYQSCmWZayWwnF5HbbUcpmwBYLal8oS88sI2sBaeM/O0SpdEPR38pRPTqGvy63B9O0f6+tlFXM/oppwgGaY50ucCl3fzI6ailGmH5Rm1Ybd6XvNixE8bewJdc6FLQecZ9GxIGoYLqZP4uYqR4FVHOYx0ivIbIQVSfEtUqo94uugeU+wrrHcLUEIJFkkw2Btc8BiWEsyD+mmr6VjenwQnXhkPioOB5vYC9ZuS61L1Gb7AwNf57OuaJ/s+sx7rYhkW0GIAskIvsFdzs8C242uulD72wNobzWuSwdgsvLlZeDGr8F4+1k68XlFvQjsd4qotmB8u8t65P1qxWkibK4/kSB/u0HwLhvdeZCY++Tlxf3JIT4dztLsfYF15ih8n0pNpkSNzpjeW0Xjz32GzU37BSIC9Av9gdbUlNFRd5gbQ3k+ZjaNsur+hmA2Au76aDVfcwNprx/QM2IPA3hsOGWOWzXaao/ZMPhGlcYukp6Wh13pGh/k9bLkfXjDI1Mg7GeQXitgT893o4KWyh4wjPP6ylKDDyMiOsJrdWqkHIBzNKlNmSN/jYejujfu6XuupXf99EpfUbD0AUHPsIe6UhNdAhnhuc32b1Se2w1LLNoMb2EfwiiRovbedAzYAgae82QA7/bqobl67ejNwU3MguwuQT6krkZGNaqe0OPc6DdQhPEciPb8opKPpLHrdi7RPOUDaEc6O9OLCkJ7fDADdEDo4m/Gzw3x1YTBfz68GcG4pHaAt4exIX18Y0jc3twNIt5QO0pZwdqRfXxTS8+ls0b8c+pQDpB3h7EjfXBjS89v+5dCndJC2hLMjfXthSF/3t2mP0MH5+n9p0ssLg/lmOYRzS+kAbQlnRzqaXRTUi+nsqn899CkHUDvCc6A+JPn451wRCcfRfEO3pTC3ypOGgOpz8oZzpb/7QhHVHtvnwh1mJdEnYTvoJkoPAvM24c+xx2f9eKHluZ9DaH/HW0jyZMhA2m+gE68RaS3oUzN4+LePrOZedblsn2L60KmFtCjo2NY0msfC/ARKb/68p2YjrX1h1nfdIJPGyBHctcUWoo33EN2Dy4GPRRYFPUydiyRDqWiifzE0h2PbLKqT0BBSQreprvfl9GbQw1oFAKgQFxQ8wXWouVACUxV078oG7sn6dymHg6I2aOjpq/Naa/MPZfih8UXfmbY/J6gZJCmcOIvFDE6wt60S9/CG1gQ8N/yaJ5pFPTx4o9+3+lhw8m8plQ22TQE7LqBWXRCuf24fj8yT3KuZ+Qv8V+S+cDqnUoL1E435CH1PvcFjQW3iHWdaTciwbGQ02upBLenkK4tvs5ehXsJ3xIf9P/u8/w9QSwcI9l8jbZsHAAA2KgAAUEsDBBQACAgIALZujFsAAAAAAAAAAAAAAAAMAAAAc2V0dGluZ3MueG1stVrRcto6EH2/X5HhnQBJpg1MQseQ0tKQwABp5vZN2AvoIms9khzg77uygckF0xJsPSWxpd3V7p6zu1buvqxCcfEGSnOU96XaZbV0AdLHgMvZfell3Cnflr40/7nD6ZT70AjQj0OQpqzBGFqiL2i71I309X0pVrKBTHPdkCwE3TB+AyOQ222N96sbibL0yUpwubgvzY2JGpXKcrm8XF5foppVavV6vZK83S71UU757FRV6er3qhBxp8huSI1JlF1VqzeV9O/tagMr88cdtevdjs2x3jnzqtTcem7rsObdxqT0R5kbCK03LzaP7WHuS6Sy8cZhufNzKWvf//f8pPWeAjbGqLR9Y9YRvREoZ6Vm9a5yKOJ0sT2YGhdyX3lg5pmCr6q3n2/yCf8OfDbPNrt2Xa/fnid9NMflEALKSmjPmZyB3tMwQRTAZKlpVAzn6ejKlsKlhicM4Jj0KRP6ZPHlkEVlLgNYQXDorOwES/YQmNT6NJd3gz1TtVHcOtvm8tX5oTyae/XP9dr5Yo8h5bpaPdtYzScCisdKIrZwZCdSh0chYgGYzxEtNAbDo/j7dJ7wX4jhmETtJ9sclcnFRz22xti0UcSh3Md0UdJbiIvCQH3olw7zDaps22vVM63v6hEI8A0EHUUPzjA94+F7cjn2esNX2QuoNp5eTdMHsWKGavNHymo7VoqsfGCGTZgG+3OEsfIPsi+lusrfJQ5ooRmC7RNgnzCLSINEfhvJsygcVKZE/IDNoMX8xUxhLI8eIqcST0o0ScAyAJMPj4n8b4pFc+678pGtAtZPruSPGTGsK+EjSmYBP3ByVEHeDB0o1BGRiiv5SV1z6f8OWz0fsuEHeWDAIlAdheEITLxf3ovwhRekpN2fTon1XETziZl5i6jR9sOe4DNp56/ifb7puCltDGUNkcIjrPe1kBWfblpcMrU+JQKEIC+BaZfqDAUU1YApNrO0oLuyx7WL5HzRQIpCUD06zShivk0XFxhI/GRVuUIYqMyk/WAQ9gpsG8OQHZSU01HVFjzyNM1lijohUJ70qVBA8KpoqeqIdQIGFzB4wGc0bRaZWMGDYsv+5D/dl5aAHCjLdlpGU0xhOrtI9pAFQ2ABSnGAtKIcNgRiJYs6zxilKVuf47CDx/kjDxNabiLIjwxGA9TcckjxNEWoXrxEATNZo/u2Ec/hsB+xNny6tuShX7mZPzEZM9FSwBYu8gyl5gGoMazMK/FiX1Jak/Nc8KJcUfh9+AUKv66IllngihkJFBahyQRQeALs+H3rs8jNMTwhcJmchbq0NpM+iOIPQ92DBeemTI0x6TnbIFwMFqSL4g5KMvGX4Of4rKZ7bALiYfOV2EFYujNJ9abDlTYWo2ln0ZVEaxNQbjKhHxvbGfVopBT/chCBdqltU/pztb5d/Uhx9jRnchBL38TsT2Sc31gbBXs5QKFIv8Ic+U45p5mhrECjiK1BOXS2BE3HtpC5oMoEhUNcPgK4GBnS8mWHkjGEkaDfiwciHUEPgWTzNxhjihInXNwXwSYJnoCifHzaz415eyRPBhR5ubCdjIV/mwk/Fq6ye8Te4JvACdsRmu0+nE/sw3HPlYqvYWTWjoZ2L4rEmjJC2a65ePEdy7xebDCFj6tkdkntu9aFmjyK87ZJdlhHvvOAkG87JTefPmzzkrDlpoPxzMgw5YBpdgMnjWIRVRB7BVz4HYXF+8/0erkv2wK1k9GSTmJc5nFGWFx9IaW8GlAhhjkKGmIKurdI7h0qB/f6lWP/I9H8DVBLBwhNy/BjLAUAAGUhAABQSwMEFAAACAAAtm6MWwd1OlQcBAAAHAQAAAgAAABtZXRhLnhtbDw/eG1sIHZlcnNpb249IjEuMCIgZW5jb2Rpbmc9IlVURi04Ij8+CjxvZmZpY2U6ZG9jdW1lbnQtbWV0YSB4bWxuczpvZmZpY2U9InVybjpvYXNpczpuYW1lczp0YzpvcGVuZG9jdW1lbnQ6eG1sbnM6b2ZmaWNlOjEuMCIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHhtbG5zOmRjPSJodHRwOi8vcHVybC5vcmcvZGMvZWxlbWVudHMvMS4xLyIgeG1sbnM6bWV0YT0idXJuOm9hc2lzOm5hbWVzOnRjOm9wZW5kb2N1bWVudDp4bWxuczptZXRhOjEuMCIgeG1sbnM6b29vPSJodHRwOi8vb3Blbm9mZmljZS5vcmcvMjAwNC9vZmZpY2UiIHhtbG5zOmdyZGRsPSJodHRwOi8vd3d3LnczLm9yZy8yMDAzL2cvZGF0YS12aWV3IyIgeG1sbnM6dGV4dG9vbz0iaHR0cDovL29wZW5vZmZpY2Uub3JnLzIwMTMvb2ZmaWNlIiBvZmZpY2U6dmVyc2lvbj0iMS4yIj48b2ZmaWNlOm1ldGE+PG1ldGE6Y3JlYXRpb24tZGF0ZT4yMDI1LTEyLTEyVDEzOjUzOjA2PC9tZXRhOmNyZWF0aW9uLWRhdGU+PG1ldGE6ZWRpdGluZy1kdXJhdGlvbj5QVDM3UzwvbWV0YTplZGl0aW5nLWR1cmF0aW9uPjxtZXRhOmVkaXRpbmctY3ljbGVzPjI8L21ldGE6ZWRpdGluZy1jeWNsZXM+PG1ldGE6Z2VuZXJhdG9yPk9wZW5PZmZpY2UvNC4xLjE2JFVuaXggT3Blbk9mZmljZS5vcmdfcHJvamVjdC80MTE2bTMkQnVpbGQtOTgxNjwvbWV0YTpnZW5lcmF0b3I+PG1ldGE6aW5pdGlhbC1jcmVhdG9yPkphc29uIEJhcnJ5PC9tZXRhOmluaXRpYWwtY3JlYXRvcj48bWV0YTpkb2N1bWVudC1zdGF0aXN0aWMgbWV0YTp0YWJsZS1jb3VudD0iMCIgbWV0YTppbWFnZS1jb3VudD0iMCIgbWV0YTpvYmplY3QtY291bnQ9IjAiIG1ldGE6cGFnZS1jb3VudD0iMSIgbWV0YTpwYXJhZ3JhcGgtY291bnQ9IjEiIG1ldGE6d29yZC1jb3VudD0iNiIgbWV0YTpjaGFyYWN0ZXItY291bnQ9IjQwIi8+PGRjOmRhdGU+MjAyNS0xMi0xMlQxMzo1Mzo0MzwvZGM6ZGF0ZT48ZGM6Y3JlYXRvcj5KYXNvbiBCYXJyeTwvZGM6Y3JlYXRvcj48L29mZmljZTptZXRhPjwvb2ZmaWNlOmRvY3VtZW50LW1ldGE+UEsDBBQACAgIALZujFsAAAAAAAAAAAAAAAAVAAAATUVUQS1JTkYvbWFuaWZlc3QueG1srZTBbgIhEIbvPsWGa7PQemqIqweTPoF9AMrOriQwEBiM+/ZlTVZtq2m13mYm//zfMAQWq72z1Q5iMh4b9sKfWQWofWuwb9j75q1+ZavlbOEUmg4SySmoSh+mY9qwHFF6lUySqBwkSVr6ANh6nR0gya96eSAds7MB5mw5q068zlioS38cTmoHrVE1DQEapkKwRisq3WKHLT+MwM/JnGBPNYELVhFcg57KXba2Doq2DRNM3DSMcaoHEbC/bLfZZveBytgkaAr5qL6NMp5HlHVehmiPNJ56FNzme9lv7bEzfY6HFae5UFqDhZL6KHSO8T7W92tLGUcXng3X58C/zfQPeGy7p6u7nGq8qB56SYkGC+mOvf1iC0Tl4T7e2AGpyXQhfnwGy09QSwcIt8m8AiUBAABHBAAAUEsBAhQAFAAACAAAtm6MW+QRkikwAAAAMAAAAAgAAAAAAAAAAAAAAAAAAAAAAG1pbWV0eXBlUEsBAhQAFAAACAAAtm6MWxDlTeFDBQAAQwUAABgAAAAAAAAAAAAAAAAAVgAAAFRodW1ibmFpbHMvdGh1bWJuYWlsLnBuZ1BLAQIUABQACAgIALZujFupdBgbDgMAAMcLAAALAAAAAAAAAAAAAAAAAM8FAABjb250ZW50LnhtbFBLAQIUABQAAAgAALZujFsAAAAAAAAAAAAAAAAaAAAAAAAAAAAAAAAAABYJAABDb25maWd1cmF0aW9uczIvdG9vbHBhbmVsL1BLAQIUABQAAAgAALZujFsAAAAAAAAAAAAAAAAaAAAAAAAAAAAAAAAAAE4JAABDb25maWd1cmF0aW9uczIvcG9wdXBtZW51L1BLAQIUABQAAAgAALZujFsAAAAAAAAAAAAAAAAfAAAAAAAAAAAAAAAAAIYJAABDb25maWd1cmF0aW9uczIvaW1hZ2VzL0JpdG1hcHMvUEsBAhQAFAAICAgAtm6MWwAAAAACAAAAAAAAACcAAAAAAAAAAAAAAAAAwwkAAENvbmZpZ3VyYXRpb25zMi9hY2NlbGVyYXRvci9jdXJyZW50LnhtbFBLAQIUABQAAAgAALZujFsAAAAAAAAAAAAAAAAYAAAAAAAAAAAAAAAAABoKAABDb25maWd1cmF0aW9uczIvZmxvYXRlci9QSwECFAAUAAAIAAC2boxbAAAAAAAAAAAAAAAAGAAAAAAAAAAAAAAAAABQCgAAQ29uZmlndXJhdGlvbnMyL21lbnViYXIvUEsBAhQAFAAACAAAtm6MWwAAAAAAAAAAAAAAABoAAAAAAAAAAAAAAAAAhgoAAENvbmZpZ3VyYXRpb25zMi9zdGF0dXNiYXIvUEsBAhQAFAAACAAAtm6MWwAAAAAAAAAAAAAAABgAAAAAAAAAAAAAAAAAvgoAAENvbmZpZ3VyYXRpb25zMi90b29sYmFyL1BLAQIUABQAAAgAALZujFsAAAAAAAAAAAAAAAAcAAAAAAAAAAAAAAAAAPQKAABDb25maWd1cmF0aW9uczIvcHJvZ3Jlc3NiYXIvUEsBAhQAFAAICAgAtm6MW7T3aNIFAQAAgwMAAAwAAAAAAAAAAAAAAAAALgsAAG1hbmlmZXN0LnJkZlBLAQIUABQACAgIALZujFv2XyNtmwcAADYqAAAKAAAAAAAAAAAAAAAAAG0MAABzdHlsZXMueG1sUEsBAhQAFAAICAgAtm6MW03L8GMsBQAAZSEAAAwAAAAAAAAAAAAAAAAAQBQAAHNldHRpbmdzLnhtbFBLAQIUABQAAAgAALZujFsHdTpUHAQAABwEAAAIAAAAAAAAAAAAAAAAAKYZAABtZXRhLnhtbFBLAQIUABQACAgIALZujFu3ybwCJQEAAEcEAAAVAAAAAAAAAAAAAAAAAOgdAABNRVRBLUlORi9tYW5pZmVzdC54bWxQSwUGAAAAABEAEQBwBAAAUB8AAAAA'


const mockTemplateFiles: Record<string, string> = {}
const TEMPLATE_S3_PREFIX = 'templates/'

const mockTemplates: WordTemplate[] = [
  {
    id: 'template-1',
    name: 'Referral Letter Template',
    description: 'Standard referral letter template for specialist referrals',
    category: 'referral',
    format: 'ott',
    mimeType: 'application/vnd.oasis.opendocument.text-template',
    s3Key: 'templates/template-1/letter-template.ott',
    fileName: 'letter-template.ott',
    fileSize: 1024,
    variables: [
      { name: 'patient.firstName', label: 'Patient First Name', type: 'string', category: 'patient', required: true },
      { name: 'patient.lastName', label: 'Patient Last Name', type: 'string', category: 'patient', required: true },
      { name: 'patient.dateOfBirthFormatted', label: 'Date of Birth', type: 'date', category: 'patient' },
      { name: 'patient.address.fullAddress', label: 'Full Address', type: 'string', category: 'patient' },
      { name: 'system.currentDateFormatted', label: 'Current Date', type: 'date', category: 'system' },
    ],
    isActive: true,
    isArchived: false,
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'template-2',
    name: 'Medical Certificate Template',
    description: 'Template for generating medical certificates',
    category: 'certificate',
    format: 'ott',
    mimeType: 'application/vnd.oasis.opendocument.text-template',
    s3Key: 'templates/template-2/medical-certificate-template.ott',
    fileName: 'medical-certificate-template.ott',
    fileSize: 1024,
    variables: [
      { name: 'patient.fullName', label: 'Patient Full Name', type: 'string', category: 'patient', required: true },
      { name: 'patient.dateOfBirthFormatted', label: 'Date of Birth', type: 'date', category: 'patient' },
      { name: 'system.currentDateFormatted', label: 'Certificate Date', type: 'date', category: 'system' },
    ],
    isActive: true,
    isArchived: false,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'template-3',
    name: 'Patient Information Form',
    description: 'Form template for collecting patient information',
    category: 'form',
    format: 'ott',
    mimeType: 'application/vnd.oasis.opendocument.text-template',
    s3Key: 'templates/template-3/patient-information-form.ott',
    fileName: 'patient-information-form.ott',
    fileSize: 1024,
    variables: [
      { name: 'patient.firstName', label: 'First Name', type: 'string', category: 'patient' },
      { name: 'patient.lastName', label: 'Last Name', type: 'string', category: 'patient' },
      { name: 'patient.email', label: 'Email', type: 'string', category: 'patient' },
      { name: 'patient.phone', label: 'Phone', type: 'string', category: 'patient' },
      { name: 'patient.allergiesList', label: 'Allergies', type: 'string', category: 'patient' },
    ],
    isActive: true,
    isArchived: false,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
]

mockTemplates.forEach((template) => {
  mockTemplateFiles[template.s3Key] = letterTemplateOttBase64
})

const mockFamilies: Family[] = [
  {
    id: '1',
    name: 'Smith Family',
    description: 'Primary family unit',
    primaryContactId: '1',
    memberIds: ['1', '2'],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

function generatePatientNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `PAT-${timestamp}-${random}`
}

function transformFormDataToPatient(formData: PatientFormData, existingPatient?: Patient): Patient {
  const now = new Date().toISOString()
  const patientId = existingPatient?.id || crypto.randomUUID()

  const patient: Patient = {
    id: patientId,
    patientNumber: existingPatient?.patientNumber || generatePatientNumber(),
    salutation: formData.salutation || existingPatient?.salutation,
    firstName: formData.firstName,
    middleName: formData.middleName,
    lastName: formData.lastName,
    dateOfBirth: formData.dateOfBirth,
    gender: formData.gender as Patient['gender'],
    maritalStatus: formData.maritalStatus as Patient['maritalStatus'],
    email: formData.email,
    phone: formData.phone,
    alternatePhone: formData.alternatePhone,
    isActive: existingPatient?.isActive !== undefined ? existingPatient.isActive : true,
    isArchived: existingPatient?.isArchived || false,
    // Patient record linking - preserve existing values or default to unlinked primary
    isPrimary: existingPatient?.isPrimary !== undefined ? existingPatient.isPrimary : true,
    primaryPatientId: existingPatient?.primaryPatientId || undefined,
    linkedPatientIds: existingPatient?.linkedPatientIds || undefined,
    createdAt: existingPatient?.createdAt || now,
    updatedAt: now,
  }

  if (formData.street || formData.city || formData.state) {
    patient.address = {
      street: formData.street || '',
      city: formData.city || '',
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      country: formData.country || 'USA',
    }
  }

  if (formData.emergencyContactName || formData.emergencyContactPhone) {
    patient.emergencyContact = {
      name: formData.emergencyContactName || '',
      relationship: formData.emergencyContactRelationship || '',
      phone: formData.emergencyContactPhone || '',
      email: formData.emergencyContactEmail,
    }
  }

  if (formData.bloodType) patient.bloodType = formData.bloodType as Patient['bloodType']
  if (formData.allergies) {
    patient.allergies = formData.allergies.split(',').map((a) => a.trim()).filter(Boolean)
  }
  if (formData.currentMedications) {
    patient.currentMedications = formData.currentMedications
      .split(',')
      .map((m) => m.trim())
      .filter(Boolean)
  }
  if (formData.pastMedications) {
    patient.pastMedications = formData.pastMedications
      .split(',')
      .map((m) => m.trim())
      .filter(Boolean)
  }
  if (formData.chronicConditions) {
    patient.chronicConditions = formData.chronicConditions
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
  }
  if (formData.medicalHistory) patient.medicalHistory = formData.medicalHistory

  // Health identifiers
  if (formData.ppsNumber) {
    patient.ppsNumber = formData.ppsNumber.trim()
  } else if (existingPatient?.ppsNumber) {
    patient.ppsNumber = existingPatient.ppsNumber
  }

  if (formData.gmsNumber) {
    patient.gmsNumber = formData.gmsNumber.trim()
  } else if (existingPatient?.gmsNumber) {
    patient.gmsNumber = existingPatient.gmsNumber
  }

  // Lifestyle factors - Smoking
  if (formData.smokingStatus !== undefined) {
    // formData.smokingStatus is SmokingStatus | undefined, but we are inside
    // a guard so we can safely assert it here.
    const status = formData.smokingStatus as SmokingStatus

    patient.smokingStatus = status
    // Record timestamp when smoking status is updated
    patient.smokingStatusLastRecorded = now

    if (
      formData.smokingDescription ||
      formData.smokingCessationDetails ||
      formData.smokingAmountPerDay !== undefined ||
      formData.smokingYearsSmoking !== undefined
    ) {
      patient.smokingDetails = {
        status,
        ...(formData.smokingDescription && { description: formData.smokingDescription }),
        ...(formData.smokingCessationDetails && { cessationDetails: formData.smokingCessationDetails }),
        ...(formData.smokingAmountPerDay !== undefined && { amountPerDay: formData.smokingAmountPerDay }),
        ...(formData.smokingYearsSmoking !== undefined && { yearsSmoking: formData.smokingYearsSmoking }),
      }
    } else {
      // If status is set but no details, still create smokingDetails with just status
      patient.smokingDetails = {
        status,
      }
    }
  } else if (existingPatient?.smokingStatus) {
    // Preserve existing smoking status and timestamp if not being updated
    patient.smokingStatus = existingPatient.smokingStatus
    patient.smokingStatusLastRecorded = existingPatient.smokingStatusLastRecorded
    patient.smokingDetails = existingPatient.smokingDetails
  }

  // Lifestyle factors - Alcohol
  if (formData.alcoholStatus !== undefined) {
    const alcoholStatus = formData.alcoholStatus as AlcoholStatus
    // Always set alcohol consumption if status is provided (even if just status)
    patient.alcoholConsumption = {
      status: alcoholStatus,
      ...(formData.alcoholDescription && { description: formData.alcoholDescription }),
      ...(formData.alcoholFrequency && { frequency: formData.alcoholFrequency }),
      ...(formData.alcoholQuantityPerOccasion && { quantityPerOccasion: formData.alcoholQuantityPerOccasion }),
      ...(formData.alcoholType && { type: formData.alcoholType }),
      ...(formData.alcoholBingeDrinking !== undefined && { bingeDrinking: formData.alcoholBingeDrinking }),
      ...(formData.alcoholBingeFrequency && { bingeFrequency: formData.alcoholBingeFrequency }),
    }
  } else if (existingPatient?.alcoholConsumption) {
    // Preserve existing alcohol consumption if not being updated
    patient.alcoholConsumption = existingPatient.alcoholConsumption
  }

  if (formData.insuranceProvider || formData.insurancePolicyNumber) {
    patient.insurance = {
      provider: formData.insuranceProvider || '',
      policyNumber: formData.insurancePolicyNumber || '',
      groupNumber: formData.insuranceGroupNumber,
    }
  }

  // Family and Relationships
  if (formData.familyId) patient.familyId = formData.familyId
  if (formData.guardianId) patient.guardianId = formData.guardianId
  if (formData.isDependent !== undefined) patient.isDependent = formData.isDependent

  if (formData.notes) patient.notes = formData.notes

  // Practice registration – ensure registered doctor and related fields are persisted
  const practiceRegistration = existingPatient?.practiceRegistration
    ? { ...existingPatient.practiceRegistration }
    : {}

  if (formData.registeredGpName) {
    practiceRegistration.registeredGpName = formData.registeredGpName
  }
  if (formData.registeredGpId) {
    practiceRegistration.registeredGpId = formData.registeredGpId
  }
  if (formData.usualGpName) {
    practiceRegistration.usualGpName = formData.usualGpName
  }
  if (formData.branchName) {
    practiceRegistration.branchName = formData.branchName
  }
  if (formData.registrationDate) {
    practiceRegistration.registrationDate = formData.registrationDate
  }
  if (formData.deregistrationDate) {
    practiceRegistration.deregistrationDate = formData.deregistrationDate
  }
  if (formData.deregistrationReason) {
    practiceRegistration.deregistrationReason = formData.deregistrationReason
  }
  if (formData.primaryCareTeamEnrolmentStatus) {
    practiceRegistration.primaryCareTeamEnrolmentStatus = formData.primaryCareTeamEnrolmentStatus
  }
  if (formData.primaryCareTeamEnrolmentDate) {
    practiceRegistration.primaryCareTeamEnrolmentDate = formData.primaryCareTeamEnrolmentDate
  }
  if (formData.primaryCareTeamDeEnrolmentDate) {
    practiceRegistration.primaryCareTeamDeEnrolmentDate = formData.primaryCareTeamDeEnrolmentDate
  }

  if (
    practiceRegistration.registeredGpName ||
    practiceRegistration.registeredGpId ||
    practiceRegistration.usualGpName ||
    practiceRegistration.branchName ||
    practiceRegistration.registrationDate ||
    practiceRegistration.deregistrationDate ||
    practiceRegistration.deregistrationReason ||
    practiceRegistration.primaryCareTeamEnrolmentStatus ||
    practiceRegistration.primaryCareTeamEnrolmentDate ||
    practiceRegistration.primaryCareTeamDeEnrolmentDate
  ) {
    patient.practiceRegistration = practiceRegistration
  }

  return patient
}

// Helper function to convert SQL-like wildcard pattern to regex
function patternToRegex(pattern: string): RegExp | null {
  if (!pattern) return null
  
  // If pattern is just '*', match everything
  if (pattern.trim() === '*') {
    return /.*/
  }
  
  // Escape special regex characters except * and ?
  let escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&')
  
  // Convert SQL-like wildcards to regex
  escaped = escaped.replace(/\*/g, '.*')  // * matches any sequence
  escaped = escaped.replace(/\?/g, '.')    // ? matches single character
  
  try {
    return new RegExp(`^${escaped}$`, 'i') // Case-insensitive, anchored
  } catch (e) {
    // If regex is invalid, fall back to simple string matching
    return null
  }
}

// Helper function to check if a pattern contains wildcards
function hasWildcards(pattern: string): boolean {
  return pattern.includes('*') || pattern.includes('?')
}

// Helper function to match field against pattern (supports wildcards or substring)
function matchesPattern(fieldValue: string | undefined, pattern: string): boolean {
  if (!pattern || !fieldValue) return false
  
  const field = String(fieldValue).toLowerCase()
  const searchPattern = pattern.toLowerCase()
  
  // If pattern contains wildcards, use regex matching
  if (hasWildcards(searchPattern)) {
    const regex = patternToRegex(searchPattern)
    if (regex) {
      return regex.test(field)
    }
  }
  
  // Otherwise, use substring matching for backward compatibility
  return field.includes(searchPattern)
}

function matchesSearch(patient: Patient, searchTerm: string): boolean {
  if (!searchTerm) return true
  
  // If search term is just '*', match all patients
  if (searchTerm.trim() === '*') {
    return true
  }

  const firstName = patient.firstName || ''
  const middleName = patient.middleName || ''
  const lastName = patient.lastName || ''
  const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim()
  const firstLast = `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim()
  const lastFirst = `${lastName} ${firstName}`.replace(/\s+/g, ' ').trim()
  
  const searchableFields = [
    patient.firstName,
    patient.middleName,
    patient.lastName,
    fullName,
    firstLast,
    lastFirst,
    patient.email,
    patient.phone,
    patient.patientNumber,
    patient.ppsNumber,
    patient.gmsNumber,
    patient.id,
  ].filter(Boolean)

  return searchableFields.some((field) => matchesPattern(field, searchTerm))
}

// Helper function to match structured filters
function matchesStructuredFilters(patient: Patient, filters: Record<string, string>): boolean {
  // If no filters provided, match all
  if (!filters || Object.keys(filters).length === 0) return true
  
  // Check each filter - all must match (AND logic)
  if (filters.firstName && !matchesPattern(patient.firstName, filters.firstName)) {
    return false
  }
  
  if (filters.lastName && !matchesPattern(patient.lastName, filters.lastName)) {
    return false
  }
  
  if (filters.phone && !matchesPattern(patient.phone, filters.phone)) {
    return false
  }
  
  if (filters.email && !matchesPattern(patient.email, filters.email)) {
    return false
  }
  
  if (filters.patientNumber && !matchesPattern(patient.patientNumber, filters.patientNumber)) {
    return false
  }
  
  if (filters.ppsNumber && !matchesPattern(patient.ppsNumber, filters.ppsNumber)) {
    return false
  }
  
  if (filters.gmsNumber && !matchesPattern(patient.gmsNumber, filters.gmsNumber)) {
    return false
  }
  
  if (filters.dateOfBirth && patient.dateOfBirth !== filters.dateOfBirth) {
    return false
  }
  
  return true
}

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Helper to format dates
function formatDate(dateString: string | undefined, format = 'DD/MM/YYYY'): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const monthName = monthNames[date.getMonth()]

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'MMMM DD, YYYY':
      return `${monthName} ${day}, ${year}`
    case 'DD MMMM YYYY':
      return `${day} ${monthName} ${year}`
    default:
      return `${day}/${month}/${year}`
  }
}

// Helper to build template context from patient and optional referral/consultation
function buildTemplateContext(patient: Patient, referral?: Referral, consultation?: any) {
  const normalizedAllergies = Array.isArray(patient.allergies)
    ? patient.allergies
    : patient.allergies
      ? String(patient.allergies).split(',').map((item) => item.trim()).filter(Boolean)
      : []

  const patientAddress = patient.address
    ? {
        ...patient.address,
        fullAddress: `${patient.address.street || ''}, ${patient.address.city || ''}, ${patient.address.state || ''} ${patient.address.postalCode || ''}`.trim(),
      }
    : null

  const patientInsurance = patient.insurance
    ? {
        ...patient.insurance,
        provider: patient.insurance.provider || '',
        policyNumber: patient.insurance.policyNumber || '',
        groupNumber: patient.insurance.groupNumber || '',
        effectiveDateFormatted: patient.insurance.effectiveDate ? formatDate(patient.insurance.effectiveDate) : '',
        expirationDateFormatted: patient.insurance.expirationDate ? formatDate(patient.insurance.expirationDate) : '',
      }
    : null

  const patientCoverage = patient.coverage
    ? {
        ...patient.coverage,
        gms: patient.coverage.gms
          ? {
              ...patient.coverage.gms,
              reviewDateFormatted: patient.coverage.gms.reviewDate ? formatDate(patient.coverage.gms.reviewDate) : '',
            }
          : null,
        doctorVisitCard: patient.coverage.doctorVisitCard
          ? {
              ...patient.coverage.doctorVisitCard,
              reviewDateFormatted: patient.coverage.doctorVisitCard.reviewDate
                ? formatDate(patient.coverage.doctorVisitCard.reviewDate)
                : '',
            }
          : null,
        dps: patient.coverage.dps
          ? {
              ...patient.coverage.dps,
              expiryDateFormatted: patient.coverage.dps.expiryDate ? formatDate(patient.coverage.dps.expiryDate) : '',
            }
          : null,
      }
    : null

  const context: any = {
    patient: {
      ...patient,
      id: patient.id,
      patientNumber: patient.patientNumber || '',
      firstName: patient.firstName || '',
      middleName: patient.middleName || '',
      lastName: patient.lastName || '',
      fullName: `${patient.firstName || ''} ${patient.middleName || ''} ${patient.lastName || ''}`.trim(),
      dateOfBirth: patient.dateOfBirth || '',
      dateOfBirthFormatted: formatDate(patient.dateOfBirth),
      age: patient.dateOfBirth
        ? Math.floor((new Date().getTime() - new Date(patient.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
        : null,
      gender: patient.gender || '',
      maritalStatus: patient.maritalStatus || '',
      email: patient.email || '',
      phone: patient.phone || '',
      alternatePhone: patient.alternatePhone || '',
      ppsNumber: patient.ppsNumber || '',
      gmsNumber: patient.gmsNumber || '',
      smokingStatus: patient.smokingStatus || patient.smokingDetails?.status || '',
      smokingDescription: patient.smokingDetails?.description || '',
      alcoholStatus: patient.alcoholConsumption?.status || '',
      alcoholDescription: patient.alcoholConsumption?.description || '',
      address: patientAddress,
      emergencyContact: patient.emergencyContact ? {
        ...patient.emergencyContact,
        name: patient.emergencyContact.name || '',
        relationship: patient.emergencyContact.relationship || '',
        phone: patient.emergencyContact.phone || '',
        email: patient.emergencyContact.email || '',
      } : null,
      bloodType: patient.bloodType || '',
      allergies: normalizedAllergies,
      allergiesList: normalizedAllergies.join(', '),
      currentMedications: patient.currentMedications || [],
      currentMedicationsList: (patient.currentMedications || []).join(', '),
      pastMedications: patient.pastMedications || [],
      chronicConditions: patient.chronicConditions || [],
      chronicConditionsList: (patient.chronicConditions || []).join(', '),
      medicalHistory: patient.medicalHistory || '',
      insurance: patientInsurance,
      coverage: patientCoverage,
    },
    system: {
      currentDate: new Date().toISOString().split('T')[0],
      currentDateFormatted: formatDate(new Date().toISOString()),
      currentDateTime: new Date().toISOString(),
    },
  }

  if (consultation) {
    const diagnosisList = Array.isArray(consultation.diagnosis) ? consultation.diagnosis : []
    const codes = Array.isArray(consultation.codes) ? consultation.codes : []
    const formatConsultationCode = (c: { system?: string; code?: string; display?: string; version?: string }) => {
      if (!c?.system) return ''
      const head = `${c.system}:${c.code || ''}`
      const body = c.display ? ` – ${c.display}` : ''
      const ver = c.version ? ` [${c.version}]` : ''
      return `${head}${body}${ver}`
    }
    const icd10Codes = codes.filter((c: { system?: string }) => c.system === 'ICD10')
    const icpc2Codes = codes.filter((c: { system?: string }) => c.system === 'ICPC2')
    const localCodes = codes.filter(
      (c: { system?: string }) => c.system === 'LOCAL' || c.system === 'TEMP',
    )
    context.consultation = {
      id: consultation.id,
      date: consultation.consultationDate || '',
      notes: consultation.notes || '',
      diagnosis: diagnosisList,
      diagnosisList: diagnosisList.join('; '),
      codes,
      codesFormatted: codes.map(formatConsultationCode).filter(Boolean).join('\n'),
      icd10CodesFormatted: icd10Codes.map(formatConsultationCode).filter(Boolean).join('\n'),
      icpc2CodesFormatted: icpc2Codes.map(formatConsultationCode).filter(Boolean).join('\n'),
      localCodesFormatted: localCodes.map(formatConsultationCode).filter(Boolean).join('\n'),
      icd10PrimaryCode: icd10Codes[0]?.code || '',
      icd10PrimaryDescription: icd10Codes[0]?.display || '',
    }
  }

  if (referral) {
    context.referral = {
      id: referral.id,
      patientId: referral.patientId || '',
      consultationId: referral.consultationId || '',
      referringProviderId: referral.referringProviderId || '',
      referringProviderName: referral.referringProviderName || '',
      referredToProviderId: referral.referredToProviderId || '',
      referredToProviderName: referral.referredToProviderName || '',
      referredToProviderType: referral.referredToProviderType || '',
      referralDate: referral.referralDate || '',
      referralDateFormatted: formatDate(referral.referralDate),
      reason: referral.reason || '',
      priority: referral.priority || '',
      status: referral.status || '',
      notes: referral.notes || '',
      providerCustomNote: referral.providerCustomNote || '',
      templateId: referral.templateId || '',
      templateName: referral.templateName || '',
      documentContent: referral.documentContent || '',
      communicationMethod: referral.communicationMethod || '',
      followUpRequired: referral.followUpRequired || false,
      followUpDate: referral.followUpDate || '',
      followUpDateFormatted: referral.followUpDate ? formatDate(referral.followUpDate) : '',
      createdAt: referral.createdAt || '',
      updatedAt: referral.updatedAt || '',
      createdBy: referral.createdBy || '',
      updatedBy: referral.updatedBy || '',
      providerDetails: referral.providerDetails ? {
        name: referral.providerDetails.name || '',
        organization: referral.providerDetails.organization || '',
        specialty: referral.providerDetails.specialty || '',
        address: referral.providerDetails.address ? {
          street: referral.providerDetails.address.street || '',
          city: referral.providerDetails.address.city || '',
          state: referral.providerDetails.address.state || '',
          postalCode: referral.providerDetails.address.postalCode || '',
          country: referral.providerDetails.address.country || '',
          fullAddress: [
            referral.providerDetails.address.street,
            referral.providerDetails.address.city,
            referral.providerDetails.address.state,
            referral.providerDetails.address.postalCode,
            referral.providerDetails.address.country,
          ].filter(Boolean).join(', '),
        } : null,
        phone: referral.providerDetails.phone || '',
        email: referral.providerDetails.email || '',
        healthmailAddress: referral.providerDetails.healthmailAddress || '',
      } : null,
      clinicalDetails: referral.clinicalDetails ? {
        presentingComplaint: referral.clinicalDetails.presentingComplaint || '',
        historyOfPresentingComplaint: referral.clinicalDetails.historyOfPresentingComplaint || '',
        pastMedicalHistory: referral.clinicalDetails.pastMedicalHistory || '',
        currentMedications: Array.isArray(referral.clinicalDetails.currentMedications)
          ? referral.clinicalDetails.currentMedications
          : (referral.clinicalDetails.currentMedications ? [referral.clinicalDetails.currentMedications] : []),
        currentMedicationsList: Array.isArray(referral.clinicalDetails.currentMedications)
          ? referral.clinicalDetails.currentMedications.join(', ')
          : (referral.clinicalDetails.currentMedications || ''),
        allergies: Array.isArray(referral.clinicalDetails.allergies)
          ? referral.clinicalDetails.allergies
          : (referral.clinicalDetails.allergies ? [referral.clinicalDetails.allergies] : []),
        allergiesList: Array.isArray(referral.clinicalDetails.allergies)
          ? referral.clinicalDetails.allergies.join(', ')
          : (referral.clinicalDetails.allergies || ''),
        examinationFindings: referral.clinicalDetails.examinationFindings || '',
        investigations: referral.clinicalDetails.investigations || '',
        diagnosis: Array.isArray(referral.clinicalDetails.diagnosis)
          ? referral.clinicalDetails.diagnosis
          : (referral.clinicalDetails.diagnosis ? [referral.clinicalDetails.diagnosis] : []),
        diagnosisList: Array.isArray(referral.clinicalDetails.diagnosis)
          ? referral.clinicalDetails.diagnosis.join(', ')
          : (referral.clinicalDetails.diagnosis || ''),
        treatmentToDate: referral.clinicalDetails.treatmentToDate || '',
        specificQuestions: referral.clinicalDetails.specificQuestions || '',
      } : null,
      insuranceInformation: referral.insuranceInformation ? {
        insuranceProvider: referral.insuranceInformation.insuranceProvider || '',
        policyNumber: referral.insuranceInformation.policyNumber || '',
        groupNumber: referral.insuranceInformation.groupNumber || '',
        authorizationNumber: referral.insuranceInformation.authorizationNumber || '',
        expiryDate: referral.insuranceInformation.expiryDate || '',
        expiryDateFormatted: referral.insuranceInformation.expiryDate ? formatDate(referral.insuranceInformation.expiryDate) : '',
      } : null,
      communicationStatus: referral.communicationStatus ? {
        method: referral.communicationStatus.method || '',
        sentAt: referral.communicationStatus.sentAt || '',
        sentAtFormatted: referral.communicationStatus.sentAt ? formatDate(referral.communicationStatus.sentAt) : '',
        acknowledgedAt: referral.communicationStatus.acknowledgedAt || '',
        acknowledgedAtFormatted: referral.communicationStatus.acknowledgedAt ? formatDate(referral.communicationStatus.acknowledgedAt) : '',
        healthmailMessageId: referral.communicationStatus.healthmailMessageId || '',
        errorMessage: referral.communicationStatus.errorMessage || '',
      } : null,
    }
  }

  return context
}

// Helper to get nested value from object by path
function getValueByPath(obj: any, path: string): any {
  const keys = path.split('.')
  let current: any = obj
  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined
    }
    if (typeof current !== 'object') {
      return undefined
    }
    current = current[key]
    if (current === undefined) {
      return undefined
    }
  }
  return current
}

// Helper to escape XML special characters
function escapeXml(text: any): string {
  if (text === null || text === undefined) return ''
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// Process OpenOffice template and populate variables
async function processOpenOfficeTemplate(templateBase64: string, context: any): Promise<string> {
  // Decode base64 to binary
  const binaryString = atob(templateBase64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Load ZIP
  const zip = await JSZip.loadAsync(bytes)

  // Get content.xml
  const contentFile = zip.file('content.xml')
  if (!contentFile) {
    throw new Error('content.xml not found in template')
  }

  // Read content.xml as text
  let contentXml = await contentFile.async('string')
  console.log('[Mock API] Original content.xml (first 500 chars):', contentXml.substring(0, 500))
  
  // Use string-based replacement - simpler and more reliable
  // Replace variables in the XML string directly, but only within text content (not in tags/attributes)
  // We'll use a regex that matches variables in text nodes (between > and <)
  const variableRegex = /\{([^}]+)\}|(\b(patient|system|consultation|referral)\.[a-zA-Z0-9.]+)\b/g
  
  // Replace variables in the XML string
  // Match text between > and < to avoid replacing in tags/attributes
  contentXml = contentXml.replace(/>([^<]*\{[^}]*\}[^<]*)</g, (match: string, textContent: string) => {
    const beforeText = match.substring(0, match.indexOf(textContent))
    const afterText = match.substring(match.indexOf(textContent) + textContent.length)
    
    const updatedText = textContent.replace(variableRegex, (varMatch: string, varPathWithBraces: string, varPathWithoutBraces: string) => {
      const varPath = varPathWithBraces || varPathWithoutBraces
      if (!varPath) return varMatch
      
      // Skip control structures
      if (varPath.startsWith('#') || varPath.startsWith('/') || varPath.startsWith(':')) {
        return varMatch
      }

      // Get value from context
      const trimmedPath = varPath.trim()
      const value = getValueByPath(context, trimmedPath)

      console.log('[Mock API] Variable replacement:', { match: varMatch, varPath: trimmedPath, value, valueType: typeof value, hasValue: value !== null && value !== undefined })

      // For providerCustomNote: when empty, remove placeholder entirely (no "Not Available")
      const isProviderNote = trimmedPath === 'providerCustomNote' || trimmedPath === 'referral.providerCustomNote'
      const isEmpty = value === null || value === undefined || value === ''
      if (isProviderNote && isEmpty) {
        return ''
      }

      // If no value found for other variables, return original match
      if (value === null || value === undefined) {
        console.log('[Mock API] No value found for variable:', trimmedPath, 'Context keys:', Object.keys(context), 'patient keys:', context.patient ? Object.keys(context.patient) : 'no patient')
        return varMatch
      }

      // Escape XML and return
      const escaped = escapeXml(value)
      console.log('[Mock API] Replacing', varMatch, 'with', escaped)
      return escaped
    })
    
    if (textContent !== updatedText) {
      console.log('[Mock API] Text changed from:', textContent, 'to:', updatedText)
    }
    
    return beforeText + updatedText + afterText
  })
  
  // Validate the XML is still valid by parsing it
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(contentXml, 'text/xml')
  
  // Check for parsing errors
  const parseError = xmlDoc.documentElement && xmlDoc.documentElement.tagName === 'parsererror'
  if (parseError) {
    console.error('[Mock API] XML parsing error after replacement:', xmlDoc.documentElement.textContent)
    throw new Error('Failed to parse XML after replacement: ' + xmlDoc.documentElement.textContent)
  }
  console.log('[Mock API] XML validated successfully after replacement')

  // Use the updated XML string
  let updatedContentXml = contentXml
  
  // Ensure XML declaration is present
  if (!updatedContentXml.startsWith('<?xml')) {
    updatedContentXml = '<?xml version="1.0" encoding="UTF-8"?>\n' + updatedContentXml
  }

  console.log('[Mock API] Updated content.xml (first 500 chars):', updatedContentXml.substring(0, 500))

  // Update content.xml in the original ZIP
  zip.file('content.xml', updatedContentXml)

  // Ensure mimetype is stored uncompressed (required by OpenOffice)
  const mimetypeFile = zip.file('mimetype')
  if (mimetypeFile) {
    const mimetypeContent = await mimetypeFile.async('string')
    zip.remove('mimetype')
    // Re-add mimetype with STORE compression (uncompressed)
    zip.file('mimetype', mimetypeContent, { compression: 'STORE' })
  }

  // Generate the new ODT file as a blob
  // Use streamFiles: false to ensure all files are included
  const blob = await zip.generateAsync({ 
    type: 'base64',
    streamFiles: false,
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  })

  return blob
}

export const mockApi = {
  get: async <T>(url: string, config?: any): Promise<{ data: T }> => {
    await delay(300) // Simulate network delay
    
    // Log all GET requests for debugging (especially for new features)
    if (url.includes('care-plans') || url.includes('protocols') || url.includes('research-studies') || url.includes('guidance') || url.includes('reminders')) {
      console.log('[Mock API] GET request:', url, 'config:', config)
    }

    if (url.startsWith('/leave')) {
      const data = await handleLeaveMock('GET', url, undefined, config)
      return { data: data as T }
    }

    if (url === '/dashboard/summary' || url.startsWith('/dashboard/summary')) {
      const role = (config?.params?.role as DashboardRoleGroup | undefined) || undefined
      const summary = await mockApi.getDashboardSummary(role)
      return { data: summary as T }
    }

    if (url === '/dashboard/ppt/queue-snapshot' || url.startsWith('/dashboard/ppt/queue-snapshot')) {
      const payload = await mockApi.getPptDashboardQueueSnapshot()
      return { data: payload as T }
    }

    if (url === '/dashboard/ppt/pipeline' || url.startsWith('/dashboard/ppt/pipeline')) {
      const payload = await mockApi.getPptDashboardPipeline()
      return { data: payload as T }
    }

    if (url === '/dashboard/ppt/batch-readiness' || url.startsWith('/dashboard/ppt/batch-readiness')) {
      const payload = await mockApi.getPptDashboardBatchReadiness()
      return { data: payload as T }
    }

    if (url === '/dashboard/ppt/exceptions' || url.startsWith('/dashboard/ppt/exceptions')) {
      const limit = Number(config?.params?.limit || 25)
      const payload = await mockApi.getPptDashboardExceptions(limit)
      return { data: payload as T }
    }

    if (url === '/dashboard/stats' || url.startsWith('/dashboard/stats')) {
      const stats = await mockApi.getDashboardStats()
      return { data: stats as T }
    }

    if (url === '/pcrs/refresh-last-run' || url.endsWith('/pcrs/refresh-last-run')) {
      // Mock: simulate last auto-refresh (16th of current/previous month)
      const now = new Date()
      const day = now.getDate()
      const lastRun = day >= 16
        ? new Date(now.getFullYear(), now.getMonth(), 16)
        : new Date(now.getFullYear(), now.getMonth() - 1, 16)
      return {
        data: {
          lastRunAt: lastRun.toISOString(),
          updatedCount: 3,
          skippedCount: 1,
        } as T,
      }
    }

    if (url.startsWith('/billing')) {
      const [pathOnly, queryString] = url.split('?')
      const params = config?.params || {}
      if (queryString) {
        const query = new URLSearchParams(queryString)
        query.forEach((value, key) => {
          params[key] = value
        })
      }
      const segments = pathOnly.split('/').filter(Boolean)

      if (segments[1] === 'pos-items') {
        return { data: mockBillingPosItems as T }
      }

      if (segments[1] === 'accounts') {
        if (segments[2] && segments[3] === 'summary') {
          const account = mockBillingAccounts.find((entry) => entry.id === segments[2])
          if (!account) throw new Error('Billing account not found')
          const charges = mockBillableItems
            .filter((item) => item.accountId === account.id)
            .reduce((sum, item) => sum + item.amount, 0)
          const payments = mockBillingPayments
            .filter((payment) => payment.accountId === account.id)
            .reduce((sum, payment) => sum + payment.amount, 0)
          return { data: { account, totals: { charges, payments } } as T }
        }
        if (segments[2]) {
          const account = mockBillingAccounts.find((entry) => entry.id === segments[2])
          if (!account) throw new Error('Billing account not found')
          return { data: account as T }
        }
        if (params.patientId) {
          const accounts = mockBillingAccounts.filter((entry) => entry.patientId === params.patientId)
          return { data: accounts as T }
        }
        return { data: mockBillingAccounts as T }
      }

      if (segments[1] === 'items') {
        if (segments[2]) {
          const item = mockBillableItems.find((entry) => entry.id === segments[2])
          if (!item) throw new Error('Billable item not found')
          return { data: item as T }
        }
        let items = [...mockBillableItems]
        if (params.patientId) items = items.filter((entry) => entry.patientId === params.patientId)
        if (params.accountId) items = items.filter((entry) => entry.accountId === params.accountId)
        if (params.consultationId) items = items.filter((entry) => entry.consultationId === params.consultationId)
        if (params.status) items = items.filter((entry) => entry.status === params.status)
        return { data: items as T }
      }

      if (segments[1] === 'consultations' && segments[2] && segments[3] === 'items') {
        const items = mockBillableItems.filter((entry) => entry.consultationId === segments[2])
        return { data: items as T }
      }

      if (segments[1] === 'payments') {
        if (segments[2]) {
          const payment = mockBillingPayments.find((entry) => entry.id === segments[2])
          if (!payment) throw new Error('Payment not found')
          return { data: payment as T }
        }
        let payments = [...mockBillingPayments]
        if (params.patientId) payments = payments.filter((entry) => entry.patientId === params.patientId)
        if (params.accountId) payments = payments.filter((entry) => entry.accountId === params.accountId)
        return { data: payments as T }
      }

      if (segments[1] === 'statements') {
        if (segments[2]) {
          const statement = mockBillingStatements.find((entry) => entry.id === segments[2])
          if (!statement) throw new Error('Statement not found')
          return { data: statement as T }
        }
        let statements = [...mockBillingStatements]
        if (params.patientId) statements = statements.filter((entry) => entry.patientId === params.patientId)
        if (params.accountId) statements = statements.filter((entry) => entry.accountId === params.accountId)
        return { data: statements as T }
      }

      if (segments[1] === 'payment-methods') {
        if (segments[2]) {
          const method = mockBillingPaymentMethods.find((entry) => entry.id === segments[2])
          if (!method) throw new Error('Payment method not found')
          return { data: method as T }
        }
        let methods = [...mockBillingPaymentMethods]
        if (params.patientId) methods = methods.filter((entry) => entry.patientId === params.patientId)
        if (params.accountId) methods = methods.filter((entry) => entry.accountId === params.accountId)
        return { data: methods as T }
      }
    }

    // Reports endpoints
    if (url === '/reports/formats' || url.startsWith('/reports/formats')) {
      return {
        data: {
          formats: [
            { id: 'fhir', name: 'FHIR R4', description: 'HL7 FHIR R4 Bundle format' },
            { id: 'csv', name: 'CSV', description: 'Comma-separated values' },
            { id: 'text', name: 'Text', description: 'Human-readable text format' },
            { id: 'json', name: 'JSON', description: 'JavaScript Object Notation' },
            { id: 'xml', name: 'XML', description: 'Extensible Markup Language' },
          ],
        } as T,
      }
    }

    if (url.startsWith('/reports/query')) {
      // Ad-hoc query endpoint - return mock data based on table parameter
      const params = config?.params || {}
      const table = params.table || 'patients'
      const format = params.format || 'json'
      
      // Generate mock response based on format
      let mockData: any = []
      if (table === 'patients') {
        mockData = mockPatients.slice(0, 10)
      } else if (table === 'consultations') {
        mockData = mockConsultations.slice(0, 10)
      } else if (table === 'appointments') {
        mockData = mockAppointments.slice(0, 10)
      }

      // Convert to requested format (simplified - just return JSON for now)
      return { data: { data: mockData, format } as T }
    }

    // Terminology search: GET /terminologies?system=ICPC2&q=...
    if (url === '/terminologies' || url.startsWith('/terminologies?')) {
      const params = config?.params || {}
      const system = (params.system || 'ICPC2') as ConsultationTerminologySystem
      const q = String(params.q || '').toLowerCase()
      const limit = params.limit ? parseInt(String(params.limit), 10) : 25
      const reqVersion = params.version ? String(params.version) : undefined

      const matches = mockTerminology
        .filter((entry) => entry.system === system)
        .filter((entry) => !reqVersion || entry.version === reqVersion)
        .filter((entry) => {
          if (!q) return true
          const haystack = `${entry.code} ${entry.display}`.toLowerCase()
          return haystack.includes(q)
        })
        .slice(0, limit)

      return {
        data: matches as T,
      }
    }

    if (url === '/gms/check-status') {
      // This is handled in post method
      throw new Error('Use POST for check-status')
    }

    if (url.startsWith('/gms/status/')) {
      const patientId = url.split('/gms/status/')[1]
      const status = await mockApi.getGMSStatus(patientId)
      return { data: status as T }
    }

    if (url === '/gms/check-dps') {
      // This is handled in post method
      throw new Error('Use POST for check-dps')
    }

    if (url.startsWith('/gms/drugs')) {
      const searchTerm = config?.params?.search || ''
      const drugs = await mockApi.searchDrug(searchTerm)
      return { data: drugs as T }
    }

    if (url === '/drugs' || url.startsWith('/drugs?')) {
      // Parse filters from URL query string or config params
      let filters: any = config?.params || {}
      
      // If URL has query string, parse it
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          if (key === 'isWithdrawn' || key === 'isInterchangeable') {
            filters[key] = value === 'true'
          } else {
            filters[key] = value
          }
        })
      }
      
      console.log('getDrugs called with filters:', filters, 'from URL:', url)
      const drugs = await mockApi.getDrugs(filters)
      return { data: drugs as T }
    }

    if (url.startsWith('/drugs/search')) {
      const searchTerm = config?.params?.q || ''
      const drugs = await mockApi.searchDrugs(searchTerm)
      return { data: drugs as T }
    }

    if (url.startsWith('/drugs/by-drug-id/')) {
      const drugIdPK = url.split('/drugs/by-drug-id/')[1]
      const drug = await mockApi.getDrugByDrugIdPK(drugIdPK)
      return { data: drug as T }
    }

    if (url.startsWith('/drugs/') && !url.includes('/sync') && !url.includes('/by-drug-id')) {
      const id = url.split('/drugs/')[1]
      const drug = await mockApi.getDrugById(id)
      return { data: drug as T }
    }

    if (url === '/drugs/sync-status') {
      const status = await mockApi.getDrugSyncStatus()
      return { data: status as T }
    }

    // Reference medications: GET /medications
    if (url === '/medications' || url.startsWith('/medications?')) {
      const params = config?.params || {}
      const search = (params.search || '').toString().toLowerCase()
      const atc = params.atc as string | undefined
      const route = params.route as string | undefined
      const isInterchangeable = params.isInterchangeable
      const gmsReimbursable = params.gmsReimbursable

      let items = [...mockMedicationReferences]

      if (search) {
        items = items.filter((m) => {
          const haystack = `${m.productName} ${m.drugIdPK} ${(m.atcCodes || []).join(' ')} ${(m.activeSubstances || []).join(' ')}`.toLowerCase()
          return haystack.includes(search)
        })
      }

      if (atc) {
        items = items.filter((m: MedicationReference) => (m.atcCodes || []).includes(atc))
      }

      if (route) {
        items = items.filter((m) => (m.routesOfAdministration || []).some((r) => r === route))
      }

      if (typeof isInterchangeable !== 'undefined') {
        const flag = isInterchangeable === 'true' || isInterchangeable === true
        items = items.filter((m) => !!m.isInterchangeable === flag)
      }

      if (typeof gmsReimbursable !== 'undefined') {
        const flag = gmsReimbursable === 'true' || gmsReimbursable === true
        items = items.filter((m) => !!m.gmsReimbursable === flag)
      }

      const response = {
        items,
        totalCount: items.length,
      }

      return { data: response as T }
    }

    // Prescription endpoints - GET
    if (url.startsWith('/prescriptions/patient/')) {
      const patientId = url.split('/prescriptions/patient/')[1].split('?')[0]
      const params = config?.params || {}
      
      let filtered = mockPrescriptions.filter((p) => p.patientId === patientId)
      
      if (params.status) {
        filtered = filtered.filter((p) => p.status === params.status)
      }
      if (params.prescriptionType) {
        filtered = filtered.filter((p) => p.prescriptionType === params.prescriptionType)
      }
      if (params.scriptType) {
        filtered = filtered.filter((p) => p.scriptType === params.scriptType)
      }
      
      return {
        data: {
          items: filtered,
          totalCount: filtered.length,
          hasMore: false,
        } as T,
      }
    }

    if (url.startsWith('/prescriptions/') && !url.includes('/patient/') && !url.includes('/check-')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('/')[0]
      const prescription = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!prescription) {
        throw new Error('Prescription not found')
      }
      return { data: prescription as T }
    }

    // OLD Formulary endpoints - REMOVED (using new implementation below with localStorage)
    // The new implementation uses mockFormularyMedications array with localStorage persistence

    // Patient medications: GET /patients/:id/medications
    if (url.startsWith('/patients/') && url.endsWith('/medications')) {
      const patientId = url.split('/patients/')[1].split('/medications')[0]
      const params = config?.params || {}
      const status = params.status as string | undefined

      let items = mockPatientMedications.filter((m) => m.patientId === patientId)
      if (status) {
        items = items.filter((m) => m.status === status)
      }

      const result = {
        items,
        totalCount: items.length,
        hasMore: false,
      }

      return { data: result as T }
    }

    // Patient assessments: GET /patients/:id/assessments
    if (url.startsWith('/patients/') && url.includes('/assessments') && !url.match(/\/assessments\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const assessments = await mockApi.getAssessments(patientId)
        return { data: assessments as T }
      }
    }

    // Care Plans GET endpoints - check early to avoid conflicts
    if (url.startsWith('/patients/') && url.includes('/care-plans')) {
      const match = url.match(/^\/patients\/([^\/]+)\/care-plans\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Care plans route matched:', url, 'patientId:', patientId)
        const plans = await mockApi.getCarePlans(patientId)
        console.log('[Mock API] Returning care plans:', plans)
        return { data: plans as T }
      }
    }

    // Patient Protocols GET endpoints - check early to avoid conflicts
    if (url.startsWith('/patients/') && url.includes('/protocols')) {
      const match = url.match(/^\/patients\/([^\/]+)\/protocols\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Patient protocols route matched:', url, 'patientId:', patientId)
        const enrollments = await mockApi.getPatientProtocols(patientId)
        console.log('[Mock API] Returning patient protocols:', enrollments)
        return { data: enrollments as T }
      }
    }

    // Patient Research Studies GET endpoints - check early to avoid conflicts
    if (url.startsWith('/patients/') && url.includes('/research-studies')) {
      const match = url.match(/^\/patients\/([^\/]+)\/research-studies\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Patient research studies route matched:', url, 'patientId:', patientId)
        const enrollments = await mockApi.getPatientResearchStudies(patientId)
        console.log('[Mock API] Returning patient research studies:', enrollments)
        return { data: enrollments as T }
      }
    }

    // Assessment: GET /assessments/:id
    if (url.startsWith('/assessments/')) {
      const id = url.split('/assessments/')[1]
      const assessment = await mockApi.getAssessment(id)
      return { data: assessment as T }
    }

    // Patient immunisations: GET /patients/:id/immunisations
    if (url.startsWith('/patients/') && url.endsWith('/immunisations')) {
      const patientId = url.split('/patients/')[1].split('/immunisations')[0]
      const params = config?.params || {}
      const dateFrom = params.dateFrom as string | undefined
      const dateTo = params.dateTo as string | undefined

      let items = mockPatientImmunisations.filter((imm) => imm.patientId === patientId)
      
      // Filter by date range if provided
      if (dateFrom) {
        items = items.filter((imm) => imm.date >= dateFrom)
      }
      if (dateTo) {
        items = items.filter((imm) => imm.date <= dateTo)
      }

      // Sort by date descending (newest first)
      items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

      const result = {
        items,
        totalCount: items.length,
        hasMore: false,
      }

      return { data: result as T }
    }

    // Vaccine Reference endpoints
    // GET /vaccine-reference
    if (url === '/vaccine-reference') {
      return { data: { items: mockVaccineReferences, totalCount: mockVaccineReferences.length, hasMore: false } as T }
    }

    // GET /vaccine-reference/{vaccineType}
    if (url.startsWith('/vaccine-reference/')) {
      const vaccineType = decodeURIComponent(url.split('/vaccine-reference/')[1]?.split('?')[0] || '')
      const reference = mockVaccineReferences.find((v) => v.vaccineType === vaccineType)
      if (!reference) {
        throw new Error('Vaccine reference not found')
      }
      return { data: reference as T }
    }

    // Practice Vaccine Inventory endpoints
    // GET /practice-vaccines
    if (url === '/practice-vaccines') {
      return { data: { items: mockPracticeVaccines, totalCount: mockPracticeVaccines.length, hasMore: false } as T }
    }

    // GET /practice-vaccines/expired
    if (url === '/practice-vaccines/expired') {
      const today = new Date().toISOString().split('T')[0]
      const expired = mockPracticeVaccines.filter((v) => v.expiryDate < today && v.isActive)
      return { data: { items: expired, totalCount: expired.length, hasMore: false } as T }
    }

    // GET /practice-vaccines/type/{vaccineType}
    if (url.startsWith('/practice-vaccines/type/')) {
      const vaccineType = decodeURIComponent(url.split('/practice-vaccines/type/')[1]?.split('?')[0] || '')
      const vaccines = mockPracticeVaccines.filter((v) => v.vaccineType === vaccineType && v.isActive)
      return { data: { items: vaccines, totalCount: vaccines.length, hasMore: false } as T }
    }

    // GET /practice-vaccines/{vaccineId}
    if (url.startsWith('/practice-vaccines/') && !url.includes('/type/') && !url.includes('/expired')) {
      const vaccineId = decodeURIComponent(url.split('/practice-vaccines/')[1]?.split('?')[0] || '')
      const vaccine = mockPracticeVaccines.find((v) => v.vaccineId === vaccineId)
      if (!vaccine) {
        throw new Error('Practice vaccine not found')
      }
      return { data: vaccine as T }
    }

    // Practice Formulary endpoints
    // GET /formulary
    if (url === '/formulary') {
      // Reload from localStorage to ensure we have the latest data
      mockFormularyMedications = loadFormulary()
      return { data: mockFormularyMedications as T }
    }

    // GET /formulary/check/{medicationId}
    if (url.startsWith('/formulary/check/')) {
      const medicationId = decodeURIComponent(url.split('/formulary/check/')[1]?.split('?')[0] || '')
      const inFormulary = mockFormularyMedications.some((f) => f.medicationId === medicationId)
      return { data: { medicationId, inFormulary } as T }
    }

    // GET /formulary/alternatives/{referenceDrugId}
    if (url.startsWith('/formulary/alternatives/')) {
      const referenceDrugId = decodeURIComponent(url.split('/formulary/alternatives/')[1]?.split('?')[0] || '')
      return { data: { referenceDrugId, alternatives: [] } as T }
    }

    // /drugs/sync should only be handled via POST, not GET

    // Audit endpoints
    if (url === '/audit' || url.startsWith('/audit?')) {
      const params = config?.params || {}
      const performedBy = params.performedBy
      const limit = parseInt(params.limit || '100', 10)
      const changeType = params.changeType
      const startTime = params.startTime || params.dateFrom
      const endTime = params.endTime || params.dateTo

      if (!performedBy) {
        return {
          data: {
            entries: [],
            count: 0,
            queryParams: { performedBy, startTime, endTime, changeType },
          } as T,
        }
      }

      let entries = mockAuditEntries
        .filter((entry) => entry.performedBy === performedBy)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      // Apply filters
      if (changeType) {
        entries = entries.filter((e) => e.changeType === changeType)
      }

      if (startTime) {
        entries = entries.filter((e) => e.timestamp >= startTime)
      }

      if (endTime) {
        entries = entries.filter((e) => e.timestamp <= endTime)
      }

      // Apply limit
      entries = entries.slice(0, Math.min(limit, 1000))

      const response: AuditHistoryResponse = {
        entries,
        count: entries.length,
        queryParams: { performedBy, startTime, endTime, changeType },
      }

      return { data: response as T }
    }

    if (url.startsWith('/stc-records/typeahead') || url.startsWith('/ppt-stc-records/typeahead')) {
      const isPptTypeahead = url.startsWith('/ppt-stc-records/typeahead')
      const queryString = url.includes('?') ? url.split('?')[1] || '' : ''
      const query = new URLSearchParams(queryString)
      const field = query.get('field') || 'patientName'
      const q = (query.get('q') || '').trim().toLowerCase()
      const limit = Math.max(1, Math.min(Number(query.get('limit') || 8), 25))
      const seen = new Set<string>()
      const items: Array<{
        id: string
        patientName?: string
        gmsNumber?: string | null
        ppsn?: string | null
        dateOfBirth?: string
        patientKey?: string
        formType?: 'STC' | 'MICS'
        updatedAt?: string
        formData?: Record<string, unknown>
      }> = []

      const addItem = (entry: {
        id: string
        patientName?: string
        gmsNumber?: string | null
        ppsn?: string | null
        patientKey?: string
        dateOfBirth?: string
        formType?: 'STC' | 'MICS'
        updatedAt?: string
        formData?: Record<string, unknown>
      }) => {
        const patientName = String(entry.patientName || '').trim()
        const gmsNumber = String(entry.gmsNumber || '').trim()
        const source = field === 'gmsNumber' ? gmsNumber : patientName
        if (!source || !source.toLowerCase().includes(q)) return
        const dedupe = gmsNumber
          ? `GMS#${gmsNumber.toUpperCase()}`
          : entry.ppsn
            ? `PPS#${String(entry.ppsn).toUpperCase()}`
            : patientName && entry.dateOfBirth
              ? `${patientName.toLowerCase()}#${entry.dateOfBirth}`
              : (entry.patientKey || patientName.toLowerCase())
        if (!dedupe || seen.has(dedupe)) return
        seen.add(dedupe)
        items.push({
          id: entry.id,
          patientName: entry.patientName,
          gmsNumber: entry.gmsNumber,
          ppsn: entry.ppsn,
          dateOfBirth: entry.dateOfBirth,
          patientKey: entry.patientKey,
          formType: entry.formType,
          updatedAt: entry.updatedAt,
          formData: entry.formData,
        })
      }

      if (isPptTypeahead) {
        const sortedPptPatients = [...mockPptPatients].sort((a, b) =>
          String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || ''))
        )
        sortedPptPatients.forEach((patient) => {
          if (items.length >= limit) return
          addItem({
            id: patient.id,
            patientName: patient.displayName,
            gmsNumber: patient.gmsOrDvcNumber,
            ppsn: patient.ppsn,
            patientKey: patient.patientKey,
            dateOfBirth: patient.dateOfBirth,
            updatedAt: patient.updatedAt || patient.createdAt,
            formData: {
              patientName: patient.displayName,
              gmsNumber: patient.gmsOrDvcNumber,
              ppsn: patient.ppsn,
              dateOfBirth: patient.dateOfBirth,
            },
          })
        })
      } else {
        const sortedPatients = [...mockPatients].sort((a, b) =>
          String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || ''))
        )
        sortedPatients.forEach((patient) => {
          if (items.length >= limit) return
          const patientName = `${String(patient.firstName || '').trim()} ${String(patient.lastName || '').trim()}`.trim()
          const gmsNumber = String(patient.gmsNumber || '').trim()
          const ppsn = String(patient.ppsNumber || '').trim()
          addItem({
            id: patient.id,
            patientName,
            gmsNumber,
            ppsn,
            patientKey: gmsNumber
              ? `GMS#${gmsNumber.toUpperCase()}`
              : (ppsn
                ? `PPS#${ppsn.toUpperCase()}`
                : (patientName ? `NAME#${patientName.toLowerCase().replace(/\s+/g, ' ')}` : undefined)),
            dateOfBirth: String(patient.dateOfBirth || '').trim(),
            updatedAt: patient.updatedAt || patient.createdAt,
            formData: {
              patientName,
              gmsNumber,
              ppsn,
              dateOfBirth: String(patient.dateOfBirth || '').trim(),
            },
          })
        })
      }

      return { data: { items } as T }
    }

    if (url.startsWith('/ppt-patients/import-jobs/')) {
      const jobId = url.split('/ppt-patients/import-jobs/')[1]?.split('?')[0]
      const job = mockPptImportJobs.find((entry) => entry.id === jobId)
      if (!job) throw new Error('Import job not found')
      return { data: job as T }
    }

    if (url === '/ppt-patients' || url.startsWith('/ppt-patients?')) {
      const queryString = url.includes('?') ? url.split('?')[1] || '' : ''
      const urlParams = new URLSearchParams(queryString)
      const configParams = config?.params || {}
      const page = Math.max(1, parseInt(String(configParams.page ?? urlParams.get('page') ?? 1), 10))
      const pageSize = Math.max(1, Math.min(100, parseInt(String(configParams.pageSize ?? urlParams.get('pageSize') ?? 25), 10)))
      const search = String(configParams.search ?? urlParams.get('search') ?? '').trim().toLowerCase()

      let items = [...mockPptPatients]
      if (search) {
        items = items.filter((patient) =>
          String(patient.displayName || '').toLowerCase().includes(search) ||
          String(patient.gmsOrDvcNumber || '').toLowerCase().includes(search) ||
          String(patient.ppsn || '').toLowerCase().includes(search)
        )
      }
      items.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
      const totalCount = items.length
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
      const paged = items.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
      return { data: { items: paged, totalCount, totalPages, page, pageSize } as T }
    }

    // GET /stc-records/:id and /ppt-stc-records/:id (+ signature/form-png)
    if ((url.startsWith('/stc-records/') || url.startsWith('/ppt-stc-records/')) && !url.startsWith('/stc-records?') && !url.startsWith('/ppt-stc-records?')) {
      const pathAfter = url.replace(/^\/(?:ppt-)?stc-records\//, '').split('?')[0]
      const [id, sub] = pathAfter.split('/')
      const record = mockStcRecords.find((r) => r.id === id)
      if (!record) throw new Error('STC record not found')
      if (sub === 'signature') {
        if (!record.signatureS3Key) throw new Error('Signature not found')
        const placeholderPng = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='
        return { data: { url: placeholderPng, s3Key: record.signatureS3Key } as T }
      }
      if (sub === 'signature-inline') {
        if (!record.signatureS3Key) throw new Error('Signature not found')
        const placeholderPng = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='
        return { data: { dataUrl: placeholderPng, s3Key: record.signatureS3Key } as T }
      }
      if (sub === 'form-png') {
        if (!record.formPngS3Key) throw new Error('Form PNG not found')
        const placeholderPng = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='
        return { data: { url: placeholderPng, s3Key: record.formPngS3Key } as T }
      }
      if (!sub) {
        const patient = mockPatients.find((p) => p.id === record.patientId)
        return {
          data: {
            ...record,
            formData: record.formData ?? undefined,
            gmsNumber: patient?.gmsNumber ?? undefined,
            ppsNumber: patient?.ppsNumber ?? undefined,
          } as T,
        }
      }
    }

    if (url === '/stc-batches' || url.startsWith('/stc-batches?')) {
      const queryString = url.includes('?') ? url.split('?')[1] || '' : ''
      const params = new URLSearchParams(queryString)
      const limit = Math.max(1, Math.min(Number(params.get('limit') || 10), 50))
      const items = [...mockStcBatchRuns]
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, limit)
      return { data: { items } as T }
    }

    if (url.startsWith('/stc-batches/')) {
      const id = url.split('/stc-batches/')[1]?.split('?')[0]
      const run = mockStcBatchRuns.find((r) => r.id === id)
      if (!run) throw new Error('Batch run not found')
      return { data: run as T }
    }

    // Task endpoints
    if (url === '/stc-records' || url.startsWith('/stc-records?') || url === '/ppt-stc-records' || url.startsWith('/ppt-stc-records?')) {
      // Params may be in config (when using api.get(url, { params })) or in the URL query string (when using api.get('/stc-records?page=1&pageSize=10'))
      const configParams = config?.params || {}
      const queryString = url.includes('?') ? url.split('?')[1] || '' : ''
      const urlParams = new URLSearchParams(queryString)
      const params: Record<string, string | number | undefined> = { ...configParams }
      urlParams.forEach((value, key) => {
        params[key] = value
      })
      let items = [...mockStcRecords]
      if (params.status) items = items.filter((r) => r.status === params.status)
      if (params.startDate) items = items.filter((r) => r.createdAt && r.createdAt >= String(params.startDate))
      if (params.endDate) items = items.filter((r) => r.createdAt && r.createdAt <= String(params.endDate))
      if (params.patientId) items = items.filter((r) => r.patientId === params.patientId)
      if (params.patientKey) items = items.filter((r) => (r.patientKey || '').toLowerCase() === String(params.patientKey).trim().toLowerCase())
      const sortBy = (params.sortBy as string) || 'createdAt'
      const sortDirection = (params.sortDirection as string) === 'asc' ? 1 : -1
      items.sort((a, b) => {
        let cmp = 0
        if (sortBy === 'patientName') {
          const na = (a.patientName || a.patientId || '').toLowerCase()
          const nb = (b.patientName || b.patientId || '').toLowerCase()
          cmp = na.localeCompare(nb)
        } else if (sortBy === 'createdAt') {
          cmp = (a.createdAt || '').localeCompare(b.createdAt || '')
        } else if (sortBy === 'signedAt') {
          const sa = a.signedAt || ''
          const sb = b.signedAt || ''
          cmp = sa.localeCompare(sb)
        } else if (sortBy === 'processedAt') {
          const pa = a.processedAt || ''
          const pb = b.processedAt || ''
          cmp = pa.localeCompare(pb)
        } else {
          cmp = (a.createdAt || '').localeCompare(b.createdAt || '')
        }
        return cmp * sortDirection
      })
      const page = params.page != null ? parseInt(String(params.page), 10) : 1
      const pageSize = params.pageSize != null ? parseInt(String(params.pageSize), 10) : 25
      const safePage = page > 0 ? page : 1
      const safePageSize = pageSize > 0 ? pageSize : 25
      const totalCount = items.length
      const totalPages = Math.max(1, Math.ceil(totalCount / safePageSize))
      const startIndex = (safePage - 1) * safePageSize
      const pagedItems = items.slice(startIndex, startIndex + safePageSize)
      const itemsWithGmsPps = pagedItems.map((r) => {
        const patient = mockPatients.find((p) => p.id === r.patientId)
        return {
          ...r,
          gmsNumber: patient?.gmsNumber ?? undefined,
          ppsNumber: patient?.ppsNumber ?? undefined,
        }
      })
      return {
        data: {
          items: itemsWithGmsPps,
          totalCount,
          totalPages,
          page: safePage,
          pageSize: safePageSize,
        } as T,
      }
    }

    if (url === '/tasks' || url.startsWith('/tasks?')) {
      const params = config?.params || {}
      let filteredTasks = [...mockTasks]
      
      // Filter by assignedTo if provided
      if (params.assignedTo) {
        filteredTasks = filteredTasks.filter((task) => task.assignedTo === params.assignedTo)
      }
      
      // Filter by status if provided
      if (params.status) {
        filteredTasks = filteredTasks.filter((task) => task.status === params.status)
      }
      
      // Filter by patientId if provided
      if (params.patientId) {
        filteredTasks = filteredTasks.filter((task) => task.patientId === params.patientId)
      }
      
      // Filter by unassigned if provided
      if (params.unassigned === 'true') {
        filteredTasks = filteredTasks.filter((task) => !task.assignedTo)
      }
      
      return { data: filteredTasks as T }
    }

    if (url.startsWith('/tasks/')) {
      // Extract ID from URL, handling query parameters and additional path segments
      const pathAfterTasks = url.split('/tasks/')[1]
      const id = pathAfterTasks.split('?')[0].split('/')[0] // Get ID before query params or additional path segments
      const task = mockTasks.find((t) => t.id === id)
      if (!task) {
        throw new Error('Task not found')
      }
      return { data: task as T }
    }

    // Communication logs endpoints
    if (url === '/communication-logs' || url.startsWith('/communication-logs?')) {
      const params = config?.params || {}
      let filteredLogs = [...mockCommunicationLogs]

      if (params.patientId) {
        filteredLogs = filteredLogs.filter((log) => log.patientId === params.patientId)
      }

      if (params.taskId) {
        filteredLogs = filteredLogs.filter((log) => log.taskId === params.taskId)
      }

      return { data: filteredLogs as T }
    }

    if (url.startsWith('/communication-logs/')) {
      const id = url.split('/communication-logs/')[1]?.split('?')[0]
      const log = mockCommunicationLogs.find((entry) => entry.id === id)
      if (!log) {
        throw new Error('Communication log not found')
      }
      return { data: log as T }
    }

    // Automation rules endpoints
    if (url === '/task-automation/rules' || url.startsWith('/task-automation/rules?')) {
      const rules = await mockApi.getAllAutomationRules()
      return { data: rules as T }
    }

    if (url.startsWith('/task-automation/rules/') && !url.includes('/toggle') && !url.includes('/test')) {
      const id = url.split('/task-automation/rules/')[1]
      const rule = await mockApi.getAutomationRule(id)
      return { data: rule as T }
    }

    // Chat endpoints
    if (url === '/chat/messages/unread-count') {
      const count = await mockApi.getUnreadMessageCount()
      return { data: { count } as T }
    }

    if (url === '/chat/messages/team') {
      const messages = await mockApi.getTeamMessages()
      return { data: messages as T }
    }

    if (url.startsWith('/chat/messages/direct/')) {
      const userId = url.split('/chat/messages/direct/')[1]
      const messages = await mockApi.getDirectMessages(userId)
      return { data: messages as T }
    }

    if (url === '/chat/messages' || url.startsWith('/chat/messages?')) {
      const messages = await mockApi.getAllMessages()
      return { data: messages as T }
    }

    if (url === '/users' || url.startsWith('/users?')) {
      const users = await mockApi.getUsers()
      return { data: users as T }
    }

    // GET /users/{id}/signature
    if (url.includes('/users/') && url.endsWith('/signature')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const url_result = await mockApi.getSignatureUrl(id)
        return { data: { url: url_result } as T }
      }
    }

    if (url.startsWith('/users/') && !url.includes('/activate') && !url.includes('/deactivate') && !url.includes('/reset-password') && !url.includes('/record-login') && !url.includes('/signature')) {
      const id = url.split('/users/')[1]
      const user = await mockApi.getUserById(id)
      return { data: user as T }
    }

    if (url === '/role-permissions' || url.startsWith('/role-permissions?')) {
      const rolePermissions = await mockApi.getRolePermissions()
      return { data: rolePermissions as T }
    }

    if (url.startsWith('/role-permissions/')) {
      // Handle confidentiality-policy endpoints
      if (url === '/role-permissions/confidentiality-policy') {
        const policy = await mockApi.getConfidentialityPolicy()
        return { data: policy as T }
      }
      
      // Handle scope-templates endpoint
      if (url === '/role-permissions/scope-templates') {
        const templates = await mockApi.getScopeTemplates()
        return { data: templates as T }
      }
      
      // Handle role-specific permissions
      const role = url.split('/role-permissions/')[1] as UserRole
      const rolePermission = await mockApi.getRolePermission(role)
      return { data: rolePermission as T }
    }

    if (url === '/permission-templates' || url.startsWith('/permission-templates?')) {
      const templates = await mockApi.getPermissionTemplates()
      return { data: templates as T }
    }

    // Emergency override endpoints
    if (url.startsWith('/emergency-override/')) {
      if (url === '/emergency-override/active') {
        const params = config?.params || {}
        const activeOverrides = await mockApi.getActiveOverrides(params.patientId, params.fields)
        return { data: activeOverrides as T }
      }
      
      if (url === '/emergency-override/pending') {
        const pending = await mockApi.getPendingOverrides()
        return { data: pending as T }
      }
      
      // GET /emergency-override/{id}
      const overrideId = url.split('/emergency-override/')[1]
      if (overrideId) {
        const override = await mockApi.getOverrideById(overrideId)
        return { data: override as T }
      }
    }

    if (url === '/tenant/settings') {
      const settings = await mockApi.getTenantSettings()
      return { data: settings as T }
    }

    if (url === '/certificates' || url.startsWith('/certificates?')) {
      const certificates = await mockApi.getCertificates()
      return { data: certificates as T }
    }

    if (url === '/certificates/pcrs-credentials') {
      return { data: { hasCredentials: mockPcrsCredentialsConfigured } as T }
    }

    if (url.startsWith('/certificates/') && !url.includes('/activate') && !url.includes('/revoke') && !url.includes('/validate')) {
      const id = url.split('/certificates/')[1]
      const certificate = await mockApi.getCertificateById(id)
      return { data: certificate as T }
    }

    // Template endpoints
    // Referrals endpoints - GET
    if (url === '/referrals/overview' || url.startsWith('/referrals/overview')) {
      const params = config?.params || {}
      const referringProviderId = params.referringProviderId
      
      let referrals = [...mockReferrals]
      if (referringProviderId) {
        referrals = referrals.filter((r) => r.referringProviderId === referringProviderId)
      }
      
      const overview: ReferralOverview = {
        total: referrals.length,
        byStatus: {
          draft: referrals.filter((r) => r.status === 'draft').length,
          sent: referrals.filter((r) => r.status === 'sent').length,
          acknowledged: referrals.filter((r) => r.status === 'acknowledged').length,
          completed: referrals.filter((r) => r.status === 'completed').length,
          cancelled: referrals.filter((r) => r.status === 'cancelled').length,
        },
        byPriority: {
          routine: referrals.filter((r) => r.priority === 'routine').length,
          urgent: referrals.filter((r) => r.priority === 'urgent').length,
          emergency: referrals.filter((r) => r.priority === 'emergency').length,
        },
        bySpecialty: {},
        byProvider: {},
        pendingFollowUps: referrals.filter(
          (r) => r.followUpRequired && r.status !== 'completed' && r.status !== 'cancelled'
        ).length,
        recentReferrals: referrals
          .sort((a, b) => {
            const dateA = new Date(a.createdAt || 0)
            const dateB = new Date(b.createdAt || 0)
            return dateB.getTime() - dateA.getTime()
          })
          .slice(0, 10)
          .map((r) => ({
            id: r.id,
            patientId: r.patientId,
            referredToProviderName: r.referredToProviderName,
            status: r.status,
            priority: r.priority,
            referralDate: r.referralDate,
            createdAt: r.createdAt,
          })),
      }
      
      // Count by specialty
      referrals.forEach((r) => {
        const specialty = r.providerDetails?.specialty || 'Unknown'
        overview.bySpecialty[specialty] = (overview.bySpecialty[specialty] || 0) + 1
      })
      
      // Count by provider
      referrals.forEach((r) => {
        const providerName = r.referredToProviderName || 'Unknown'
        overview.byProvider[providerName] = (overview.byProvider[providerName] || 0) + 1
      })
      
      return { data: overview as T }
    }

    if (url === '/referrals' || url.startsWith('/referrals?')) {
      const params = config?.params || {}
      
      let referrals = [...mockReferrals]
      
      // Apply filters
      if (params.patientId) {
        referrals = referrals.filter((r) => r.patientId === params.patientId)
      }
      if (params.referringProviderId) {
        referrals = referrals.filter((r) => r.referringProviderId === params.referringProviderId)
      }
      if (params.status) {
        const statusArray = Array.isArray(params.status) ? params.status : params.status.split(',')
        referrals = referrals.filter((r) => statusArray.includes(r.status))
      }
      if (params.startDate) {
        referrals = referrals.filter((r) => r.referralDate >= params.startDate)
      }
      if (params.endDate) {
        referrals = referrals.filter((r) => r.referralDate <= params.endDate)
      }
      if (params.priority) {
        referrals = referrals.filter((r) => r.priority === params.priority)
      }
      
      // Sort by referral date (most recent first)
      referrals.sort((a, b) => {
        const dateA = new Date(a.referralDate || a.createdAt || 0)
        const dateB = new Date(b.referralDate || b.createdAt || 0)
        return dateB.getTime() - dateA.getTime()
      })
      
      return { data: referrals as T }
    }

    if (url.startsWith('/referrals/') && !url.includes('/send') && !url.includes('/overview')) {
      const id = url.split('/referrals/')[1].split('?')[0]
      const referral = mockReferrals.find((r) => r.id === id)
      if (!referral) {
        throw new Error('Referral not found')
      }
      return { data: referral as T }
    }

    // Providers endpoints - GET
    if (url === '/providers' || url.startsWith('/providers?')) {
      const params = config?.params || {}
      
      let providers = [...mockProviders]
      
      // Apply filters
      if (params.type) {
        providers = providers.filter((p) => p.type === params.type)
      }
      if (params.specialty) {
        providers = providers.filter((p) => p.specialty === params.specialty)
      }
      if (params.isActive !== undefined) {
        const isActive = params.isActive === 'true' || params.isActive === true
        providers = providers.filter((p) => p.isActive === isActive)
      }
      
      // Sort by name
      providers.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase()
        const nameB = (b.name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
      
      return { data: providers as T }
    }

    if (url.startsWith('/providers/')) {
      const id = url.split('/providers/')[1].split('?')[0]
      const provider = mockProviders.find((p) => p.id === id)
      if (!provider) {
        throw new Error('Provider not found')
      }
      return { data: provider as T }
    }

    if (url === '/templates' || url.startsWith('/templates?')) {
      const category = config?.params?.category
      let templates = [...mockTemplates].filter((t) => t.isActive && !t.isArchived)
      
      if (category && category !== 'all') {
        templates = templates.filter((t) => t.category === category)
      }
      
      return { data: templates as T }
    }


    if (url.startsWith('/templates/') && !url.includes('/generate') && !url.includes('/preview')) {
      const id = url.split('/templates/')[1]
      const template = mockTemplates.find((t) => t.id === id)
      if (!template) {
        throw new Error('Template not found')
      }
      return { data: template as T }
    }
    
    if (url === '/patients' || url.startsWith('/patients?')) {
      // Extract search parameters
      const searchTerm = config?.params?.search
      const structuredFilters = {
        firstName: config?.params?.firstName,
        lastName: config?.params?.lastName,
        phone: config?.params?.phone,
        email: config?.params?.email,
        patientNumber: config?.params?.patientNumber,
        ppsNumber: config?.params?.ppsNumber,
        gmsNumber: config?.params?.gmsNumber,
        dateOfBirth: config?.params?.dateOfBirth,
      }
      
      // Remove undefined values
      Object.keys(structuredFilters).forEach(key => {
        if (!structuredFilters[key as keyof typeof structuredFilters]) {
          delete structuredFilters[key as keyof typeof structuredFilters]
        }
      })
      
      const fetchAll = config?.params?.fetchAll === 'true' || config?.params?.fetchAll === true
      const includeInactive = config?.params?.includeInactive === 'true' || config?.params?.includeInactive === true
      const limit = config?.params?.limit ? parseInt(config.params.limit, 10) : undefined
      const page = config?.params?.page ? parseInt(config.params.page, 10) : undefined
      const pageSize = config?.params?.pageSize ? parseInt(config.params.pageSize, 10) : undefined
      const sortBy = config?.params?.sortBy || 'name'
      const sortDirection = config?.params?.sortDirection === 'desc' ? 'desc' : 'asc'
      const gender = config?.params?.gender
      const status = config?.params?.status
      const dateOfBirthFrom = config?.params?.dateOfBirthFrom
      const dateOfBirthTo = config?.params?.dateOfBirthTo
      const hasAllergies = config?.params?.hasAllergies === 'true' || config?.params?.hasAllergies === true
        ? true
        : config?.params?.hasAllergies === 'false'
          ? false
          : undefined
      const hasInsurance = config?.params?.hasInsurance === 'true' || config?.params?.hasInsurance === true
        ? true
        : config?.params?.hasInsurance === 'false'
          ? false
          : undefined
      
      // If no search criteria provided and fetchAll is not explicitly requested, return empty array
      if (!searchTerm && Object.keys(structuredFilters).length === 0 && !fetchAll) {
        // Return paginated response structure if limit is specified
        if (limit !== undefined) {
          return { 
            data: {
              patients: [],
              hasMore: false,
              lastEvaluatedKey: null,
              count: 0,
            } as T
          }
        }
        return { data: [] as T }
      }

      // Handle backward compatibility - default isPrimary to true if not set
      // Primary records: include if active, or if includeInactive is true
      // Secondary records: include in search but don't count as active
      let patients = [...mockPatients].map(p => ({
        ...p,
        isPrimary: p.isPrimary !== undefined ? p.isPrimary : true,
      }))

      const filteredByStatus = patients.filter((p) => {
        const isPrimary = p.isPrimary !== undefined ? p.isPrimary : true
        if (isPrimary) {
          if (status === 'archived') {
            return p.isArchived
          }
          if (status === 'inactive') {
            return !p.isActive && !p.isArchived
          }
          if (status === 'active') {
            return p.isActive && !p.isArchived
          }
          // Always exclude archived patients
          if (p.isArchived) return false
          // Include active patients, or inactive if includeInactive is true
          return includeInactive || p.isActive
        } else {
          // Secondary records: include in search but don't count as active
          return true
        }
      })

      // Apply search filters (AND logic: general search term AND structured filters)
      let filteredPatients = filteredByStatus
      
      // Apply general search term if provided
      if (searchTerm) {
        filteredPatients = filteredPatients.filter((p) => matchesSearch(p, searchTerm))
      }
      
      // Apply structured filters if provided
      if (Object.keys(structuredFilters).length > 0) {
        filteredPatients = filteredPatients.filter((p) => matchesStructuredFilters(p, structuredFilters as Record<string, string>))
      }

      if (gender) {
        filteredPatients = filteredPatients.filter((p) => p.gender === gender)
      }

      if (dateOfBirthFrom) {
        filteredPatients = filteredPatients.filter(
          (p) => p.dateOfBirth && new Date(p.dateOfBirth) >= new Date(dateOfBirthFrom)
        )
      }

      if (dateOfBirthTo) {
        filteredPatients = filteredPatients.filter(
          (p) => p.dateOfBirth && new Date(p.dateOfBirth) <= new Date(dateOfBirthTo)
        )
      }

      if (hasAllergies === true) {
        filteredPatients = filteredPatients.filter((p) => p.allergies && p.allergies.length > 0)
      }

      if (hasInsurance === true) {
        filteredPatients = filteredPatients.filter((p) => p.insurance && p.insurance.provider)
      }

      if (hasAllergies === false) {
        filteredPatients = filteredPatients.filter((p) => !p.allergies || p.allergies.length === 0)
      }

      if (hasInsurance === false) {
        filteredPatients = filteredPatients.filter((p) => !p.insurance || !p.insurance.provider)
      }

      const sortMultiplier = sortDirection === 'desc' ? -1 : 1
      filteredPatients.sort((a, b) => {
        if (sortBy === 'patientNumber') {
          const valueA = (a.patientNumber || a.id || '').toString().toLowerCase()
          const valueB = (b.patientNumber || b.id || '').toString().toLowerCase()
          return valueA.localeCompare(valueB) * sortMultiplier
        }
        if (sortBy === 'dateOfBirth') {
          const valueA = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0
          const valueB = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0
          return (valueA - valueB) * sortMultiplier
        }
        const nameA = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase()
        const nameB = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase()
        return nameA.localeCompare(nameB) * sortMultiplier
      })

      if (page || pageSize) {
        const safePageSize = pageSize && pageSize > 0 ? pageSize : 50
        const safePage = page && page > 0 ? page : 1
        const totalCount = filteredPatients.length
        const totalPages = safePageSize ? Math.ceil(totalCount / safePageSize) : 1
        const startIndex = (safePage - 1) * safePageSize
        const paginatedPatients = filteredPatients.slice(startIndex, startIndex + safePageSize)
        const hasMore = safePage < totalPages

        return {
          data: {
            patients: paginatedPatients,
            totalCount,
            totalPages,
            page: safePage,
            pageSize: safePageSize,
            hasMore,
            lastEvaluatedKey: null,
            count: paginatedPatients.length,
          } as T,
        }
      }

      // Apply pagination if limit is specified
      if (limit !== undefined) {
        const paginatedPatients = filteredPatients.slice(0, limit)
        return {
          data: {
            patients: paginatedPatients,
            hasMore: filteredPatients.length > limit,
            lastEvaluatedKey: null, // Mock doesn't use keys
            count: paginatedPatients.length,
          } as T
        }
      }

      return { data: filteredPatients as T }
    }

    // Patient audit history (change log) - mock-only for accreditation UI
    if (url.startsWith('/patients/') && url.endsWith('/history')) {
      const id = url.split('/patients/')[1].split('/history')[0]
      const entries = mockPatientHistoryEntries
        .filter((entry) => entry.patientId === id)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      return { data: entries as T }
    }

    // Patient audit history (EHR audit trail)
    if (url.startsWith('/patients/') && url.endsWith('/audit')) {
      const id = url.split('/patients/')[1].split('/audit')[0]
      const params = config?.params || {}
      const limit = parseInt(params.limit || '100', 10)
      const changeType = params.changeType
      const startTime = params.startTime
      const endTime = params.endTime

      let entries = mockAuditEntries
        .filter((entry) => entry.patientId === id)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      // Apply filters
      if (changeType) {
        entries = entries.filter((e) => e.changeType === changeType)
      }

      if (startTime) {
        entries = entries.filter((e) => e.timestamp >= startTime)
      }

      if (endTime) {
        entries = entries.filter((e) => e.timestamp <= endTime)
      }

      // Apply limit
      entries = entries.slice(0, Math.min(limit, 1000))

      const response: AuditHistoryResponse = {
        patientId: id,
        entries,
        count: entries.length,
      }

      return { data: response as T }
    }

    // Patient clinical history (conditions, family history, preferences, consent)
    const clinicalHistoryPath = stripMockRequestPath(url)
    if (clinicalHistoryPath.startsWith('/patients/') && clinicalHistoryPath.includes('/clinical-history')) {
      const parts = clinicalHistoryPath.split('/patients/')[1].split('/clinical-history')
      const patientId = parts[0].split('/')[0].split('?')[0]

      const emptyClinicalHistory = (pid: string): PatientClinicalHistory => {
        const now = new Date().toISOString()
        return {
          patientId: pid,
          conditions: [],
          familyHistory: [],
          preferences: {
            recordedAt: now,
            updatedAt: now,
          },
          consents: [],
          createdAt: now,
          updatedAt: now,
        }
      }

      // GET /patients/:id/clinical-history
      if (parts[1] === '' || parts[1] === '/') {
        let history = mockClinicalHistory.find((h) => h.patientId === patientId)
        if (!history) {
          // No row yet — return empty history so UI can add conditions (matches add-first flows)
          return { data: emptyClinicalHistory(patientId) as T }
        }
        return { data: history as T }
      }
      
      // GET /patients/:id/clinical-history/conditions/:conditionId
      // POST /patients/:id/clinical-history/conditions
      if (parts[1].startsWith('/conditions')) {
        let history = mockClinicalHistory.find(h => h.patientId === patientId)
        if (!history) {
          // Create new history if it doesn't exist
          history = {
            patientId,
            conditions: [],
            familyHistory: [],
            preferences: {
              recordedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            consents: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }
          mockClinicalHistory.push(history)
        }
        
        const conditionMatch = parts[1].match(/^\/conditions\/(.+)$/)
        if (conditionMatch) {
          // GET/PUT/DELETE specific condition
          const conditionId = conditionMatch[1]
          const condition = history.conditions.find(c => c.id === conditionId)
          if (!condition) {
            throw new Error('Condition not found')
          }
          return { data: condition as T }
        }
        
        // POST new condition - handled in POST handler
        // For GET, return all conditions
        return { data: history.conditions as T }
      }
      
      // Similar handlers for family-history, preferences, consents
      // For now, return full history and let the service extract what it needs
      let history = mockClinicalHistory.find((h) => h.patientId === patientId)
      if (!history) {
        return { data: emptyClinicalHistory(patientId) as T }
      }
      return { data: history as T }
    }

    // Patient summary endpoint: GET /patients/:id/summary
    if (url.startsWith('/patients/') && url.endsWith('/summary')) {
      const patientId = url.split('/patients/')[1].split('/summary')[0]
      const patient = mockPatients.find((p) => p.id === patientId)
      if (!patient) {
        throw new Error('Patient not found')
      }

      const consultations = mockConsultations.filter((c) => c.patientId === patientId)
      const labResults = mockLabResults.filter((r) => r.patientId === patientId)
      const appointments = mockAppointments.filter((a) => a.patientId === patientId)
      const vitalSigns = mockVitalSigns.filter((vs) => vs.patientId === patientId)
      const patientFiles = mockPatientFiles.filter((f) => f.patientId === patientId)
      const tasks = mockTasks.filter((t) => t.patientId === patientId)
      const communicationLogs = mockCommunicationLogs.filter((log) => log.patientId === patientId)
      const referrals = mockReferrals.filter((r) => r.patientId === patientId)
      const assessments = mockAssessments.filter((a) => a.patientId === patientId)
      const carePlans = mockCarePlans.filter((plan) => plan.patientId === patientId)
      const protocolEnrollments = mockProtocolEnrollments.filter((enrollment) => enrollment.patientId === patientId)
      const researchEnrollments = mockResearchEnrollments.filter((enrollment) => enrollment.patientId === patientId)
      const prescriptions = mockPrescriptions.filter((p) => p.patientId === patientId)
      const patientMedications = mockPatientMedications.filter((m) => m.patientId === patientId)
      const pregnancies = mockPregnancies.filter((p) => p.patientId === patientId)
      const maternityVisits = mockMaternityVisits.filter((visit) =>
        pregnancies.some((pregnancy) => pregnancy.pregnancyId === visit.pregnancyId)
      )
      const clinicalHistory = mockClinicalHistory.find((history) => history.patientId === patientId) || null
      const immunisations = mockPatientImmunisations.filter((imm) => imm.patientId === patientId)
      const gmsStatus = await mockApi.getGMSStatus(patientId)

      const allergies: any[] = patient.allergies
        ? patient.allergies.map((allergyName, idx) => ({
            id: `allergy-${patientId}-${idx}`,
            patientId,
            triggerDisplay: allergyName,
            triggerCode: 'UNKNOWN',
            status: 'active',
            recordedDate: patient.createdAt,
          }))
        : []

      return {
        data: {
          patient,
          consultations,
          labResults,
          appointments,
          allergies,
          referrals,
          assessments,
          carePlans,
          protocolEnrollments,
          researchEnrollments,
          prescriptions: {
            items: prescriptions,
            totalCount: prescriptions.length,
          },
          patientMedications: {
            items: patientMedications,
            totalCount: patientMedications.length,
            hasMore: false,
          },
          pregnancies,
          maternityVisits,
          clinicalHistory,
          immunisations: {
            items: immunisations,
            totalCount: immunisations.length,
            hasMore: false,
          },
          patientFiles,
          tasks,
          communicationLogs,
          vitalSigns,
          gmsStatus,
        } as T,
      }
    }

    // FHIR Export endpoint: GET /patients/:id/export/fhir?maskPII=true|false
    if (url.startsWith('/patients/') && url.includes('/export/fhir')) {
      const parts = url.split('/patients/')[1].split('/export/fhir')
      const patientId = parts[0]
      const maskPII = config?.params?.maskPII === 'true' || config?.params?.maskPII === true
      
      const patient = mockPatients.find((p) => p.id === patientId)
      if (!patient) {
        throw new Error('Patient not found')
      }

      // Get related data
      const consultations = mockConsultations.filter((c) => c.patientId === patientId)
      const labResults = mockLabResults.filter((r) => r.patientId === patientId)
      const vitalSigns = mockVitalSigns.filter((vs) => vs.patientId === patientId)
      // Note: Allergies are stored in the allergyService's in-memory store, not in mockApi
      // For mock purposes, we'll use patient.allergies if available, or empty array
      const allergies: any[] = patient.allergies ? patient.allergies.map((allergyName, idx) => ({
        id: `allergy-${patientId}-${idx}`,
        patientId,
        triggerDisplay: allergyName,
        triggerCode: 'UNKNOWN',
        status: 'active',
        recordedDate: patient.createdAt,
      })) : []
      const medications = mockPatientMedications.filter((m) => m.patientId === patientId)
      const immunisations = mockPatientImmunisations.filter((imm) => imm.patientId === patientId)

      // Apply PII masking if requested (simplified - just mask names and contact info)
      let exportPatient = { ...patient }
      if (maskPII) {
        const maskName = (name: string) => name && name.length > 0 ? name[0] + '***' : name
        const maskEmail = (email: string) => {
          if (!email) return email
          const [local, domain] = email.split('@')
          return local && local.length > 0 ? `${local[0]}***@***.${domain?.split('.').pop() || 'com'}` : email
        }
        const maskPhone = (phone: string) => {
          if (!phone) return phone
          const digits = phone.replace(/\D/g, '')
          const last4 = digits.slice(-4)
          return `***-***-${last4}`
        }
        
        exportPatient = {
          ...exportPatient,
          firstName: maskName(exportPatient.firstName),
          middleName: exportPatient.middleName ? maskName(exportPatient.middleName) : undefined,
          lastName: maskName(exportPatient.lastName),
          email: maskEmail(exportPatient.email || ''),
          phone: maskPhone(exportPatient.phone),
          alternatePhone: exportPatient.alternatePhone ? maskPhone(exportPatient.alternatePhone) : undefined,
          ppsNumber: exportPatient.ppsNumber ? `***${exportPatient.ppsNumber.slice(-4)}` : undefined,
          gmsNumber: exportPatient.gmsNumber ? `***${exportPatient.gmsNumber.slice(-4)}` : undefined,
          dateOfBirth: exportPatient.dateOfBirth ? `${exportPatient.dateOfBirth.split('-')[0]}-XX-XX` : exportPatient.dateOfBirth,
        }
        
        if (exportPatient.address) {
          exportPatient.address = {
            ...exportPatient.address,
            street: exportPatient.address.street ? `*** ${exportPatient.address.street.split(' ').slice(1).join(' ')}` : exportPatient.address.street,
          }
        }
      }

      // Create a simplified FHIR Bundle
      const fhirBundle = {
        resourceType: 'Bundle',
        id: `bundle-${patientId}-${Date.now()}`,
        type: 'document',
        timestamp: new Date().toISOString(),
        entry: [
          {
            fullUrl: `urn:uuid:patient-${patientId}`,
            resource: {
              resourceType: 'Patient',
              id: patientId,
              identifier: [
                ...(exportPatient.patientNumber ? [{
                  use: 'usual',
                  system: 'http://clinicly.com/patient-number',
                  value: exportPatient.patientNumber,
                }] : []),
                ...(exportPatient.ppsNumber ? [{
                  use: 'official',
                  system: 'http://hl7.org/fhir/sid/ie-pps',
                  value: exportPatient.ppsNumber,
                }] : []),
              ],
              name: [{
                use: 'official',
                family: exportPatient.lastName,
                given: [exportPatient.firstName, ...(exportPatient.middleName ? [exportPatient.middleName] : [])],
              }],
              gender: exportPatient.gender,
              birthDate: exportPatient.dateOfBirth,
              telecom: [
                ...(exportPatient.phone ? [{
                  system: 'phone',
                  value: exportPatient.phone,
                  use: 'mobile',
                }] : []),
                ...(exportPatient.email ? [{
                  system: 'email',
                  value: exportPatient.email,
                }] : []),
              ],
              address: exportPatient.address ? [{
                use: 'home',
                line: [exportPatient.address.street],
                city: exportPatient.address.city,
                state: exportPatient.address.state,
                postalCode: exportPatient.address.postalCode,
                country: exportPatient.address.country || 'IE',
              }] : [],
              active: exportPatient.isActive && !exportPatient.isArchived,
            },
          },
          // Add consultations as Encounters
          ...consultations.map((c) => ({
            fullUrl: `urn:uuid:encounter-${c.id}`,
            resource: {
              resourceType: 'Encounter',
              id: c.id,
              status: c.status === 'completed' ? 'finished' : 'in-progress',
              class: {
                system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
                code: 'AMB',
                display: 'ambulatory',
              },
              subject: {
                reference: `Patient/${patientId}`,
              },
              period: {
                start: c.consultationDate || c.createdAt,
                end: c.status === 'completed' ? c.updatedAt : undefined,
              },
            },
          })),
          // Add lab results as DiagnosticReports
          ...labResults.slice(0, 5).map((lr) => ({
            fullUrl: `urn:uuid:diagnostic-report-${lr.id}`,
            resource: {
              resourceType: 'DiagnosticReport',
              id: lr.id,
              status: lr.status === 'final' ? 'final' : 'preliminary',
              category: [{
                coding: [{
                  system: 'http://terminology.hl7.org/CodeSystem/v2-0074',
                  code: 'LAB',
                  display: lr.type,
                }],
              }],
              subject: {
                reference: `Patient/${patientId}`,
              },
              effectiveDateTime: lr.collectionDate || lr.reportedDate,
              issued: lr.reportedDate,
            },
          })),
          // Add vital signs as Observations
          ...vitalSigns.slice(0, 10).map((vs, idx) => ({
            fullUrl: `urn:uuid:observation-vs-${vs.id}-${idx}`,
            resource: {
              resourceType: 'Observation',
              id: `vs-${vs.id}-${idx}`,
              status: 'final',
              category: [{
                coding: [{
                  system: 'http://terminology.hl7.org/CodeSystem/observation-category',
                  code: 'vital-signs',
                  display: 'Vital Signs',
                }],
              }],
              subject: {
                reference: `Patient/${patientId}`,
              },
              effectiveDateTime: vs.recordedAt || vs.createdAt,
              ...(vs.bloodPressure ? {
                code: {
                  coding: [{
                    system: 'http://loinc.org',
                    code: '85354-9',
                    display: 'Blood pressure panel',
                  }],
                },
                component: [
                  {
                    code: { coding: [{ system: 'http://loinc.org', code: '8480-6', display: 'Systolic BP' }] },
                    valueQuantity: { value: vs.bloodPressure.systolic, unit: 'mmHg' },
                  },
                  {
                    code: { coding: [{ system: 'http://loinc.org', code: '8462-4', display: 'Diastolic BP' }] },
                    valueQuantity: { value: vs.bloodPressure.diastolic, unit: 'mmHg' },
                  },
                ],
              } : {}),
            },
          })),
          // Add allergies as AllergyIntolerance
          ...allergies.filter((a) => a.status === 'active').map((a) => ({
            fullUrl: `urn:uuid:allergy-${a.id}`,
            resource: {
              resourceType: 'AllergyIntolerance',
              id: a.id,
              clinicalStatus: {
                coding: [{
                  system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                  code: 'active',
                  display: 'Active',
                }],
              },
              code: {
                coding: [{
                  system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                  code: a.triggerCode || 'UNKNOWN',
                  display: a.triggerDisplay || 'Unknown',
                }],
              },
              patient: {
                reference: `Patient/${patientId}`,
              },
            },
          })),
          // Add medications as MedicationStatement
          ...medications.filter((m) => m.status === 'active').slice(0, 10).map((m) => ({
            fullUrl: `urn:uuid:medication-${m.medicationId}`,
            resource: {
              resourceType: 'MedicationStatement',
              id: m.medicationId,
              status: 'active',
              medicationCodeableConcept: {
                coding: [{
                  system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                  code: m.referenceDrugId || 'UNKNOWN',
                  display: m.name,
                }],
              },
              subject: {
                reference: `Patient/${patientId}`,
              },
            },
          })),
          // Add immunisations as Immunization
          ...immunisations.map((imm) => ({
            fullUrl: `urn:uuid:immunization-${imm.immunisationId}`,
            resource: {
              resourceType: 'Immunization',
              id: imm.immunisationId,
              status: 'completed',
              vaccineCode: {
                coding: [{
                  system: 'http://snomed.info/sct',
                  code: 'UNKNOWN', // Could be enhanced with SNOMED codes for specific vaccines
                  display: imm.type,
                }],
                text: imm.type,
              },
              patient: {
                reference: `Patient/${patientId}`,
              },
              occurrenceDateTime: imm.date,
              recorded: imm.createdAt || imm.date,
              lotNumber: imm.batchNumber,
              expirationDate: imm.expiryDate,
              site: imm.siteGiven
                ? {
                    coding: [{
                      system: 'http://terminology.hl7.org/CodeSystem/v3-ActSite',
                      text: imm.siteGiven,
                    }],
                    text: imm.siteGiven,
                  }
                : undefined,
              route: imm.methodOfAdmin
                ? {
                    coding: [{
                      system: 'http://terminology.hl7.org/CodeSystem/v3-RouteOfAdministration',
                      code: imm.methodOfAdmin === 'IM' ? 'IM' : imm.methodOfAdmin === 'SC' ? 'SC' : imm.methodOfAdmin === 'ID' ? 'ID' : imm.methodOfAdmin === 'Oral' ? 'PO' : imm.methodOfAdmin === 'Nasal' ? 'NS' : imm.methodOfAdmin === 'Intramuscular' ? 'IM' : imm.methodOfAdmin === 'Subcutaneous' ? 'SC' : imm.methodOfAdmin === 'Intradermal' ? 'ID' : imm.methodOfAdmin === 'Intravenous' ? 'IV' : imm.methodOfAdmin,
                      display: imm.methodOfAdmin,
                    }],
                  }
                : undefined,
              doseQuantity: imm.dose
                ? {
                    value: imm.dose.value,
                    unit: imm.dose.unit,
                    system: 'http://unitsofmeasure.org',
                    code: imm.dose.unit,
                  }
                : undefined,
              performer: imm.administeredByName
                ? [
                    {
                      actor: {
                        display: imm.administeredByName,
                      },
                    },
                  ]
                : undefined,
              note: imm.notes
                ? [
                    {
                      text: imm.notes,
                    },
                  ]
                : undefined,
            },
          })),
        ],
      }

      // Record FHIR export in audit trail (mock)
      const timestamp = new Date().toISOString()
      const auditId = `audit-fhir-export-${patientId}-${Date.now()}`
      
      // Add to patient history entries (for /patients/{id}/history endpoint)
      const historyEntry: PatientHistoryEntry = {
        id: `hist-fhir-export-${patientId}-${Date.now()}`,
        patientId: patientId,
        changeType: 'fhir_export',
        timestamp: timestamp,
        performedBy: 'mock-user@clinicly.com',
        performedByDisplayName: 'Mock User',
        reason: `FHIR export ${maskPII ? 'with masked PII' : 'with full PII'}`,
        changes: [],
        metadata: {
          accessedVia: 'GET /patients/{id}/export/fhir',
          maskPII: maskPII,
          resourceCount: fhirBundle.entry?.length || 0,
          source: 'mock',
        },
      }
      mockPatientHistoryEntries.push(historyEntry)

      // Add to audit entries (for /patients/{id}/audit endpoint)
      const auditEntry: AuditEntry = {
        id: auditId,
        patientId: patientId,
        timestamp: timestamp,
        changeType: 'fhir_export',
        performedBy: 'mock-user@clinicly.com',
        performedByDisplayName: 'Mock User',
        reason: `FHIR export ${maskPII ? 'with masked PII' : 'with full PII'}`,
        entityType: 'patient',
        entityId: patientId,
        changes: [],
        metadata: {
          tenantId: 'tenant-1',
          subdomain: 'demo',
          ipAddress: '127.0.0.1',
          userAgent: 'Mozilla/5.0 (compatible; Mock)',
          accessedVia: 'GET /patients/{id}/export/fhir',
          maskPII: maskPII,
          resourceCount: fhirBundle.entry?.length || 0,
        },
      }
      mockAuditEntries.push(auditEntry)

      return { data: fhirBundle as T }
    }

    // GET /patients/{id}/duplicates - Get potential duplicate suggestions
    if (url.startsWith('/patients/') && url.includes('/duplicates')) {
      const id = url.split('/patients/')[1].split('/duplicates')[0]
      const patient = mockPatients.find((p) => p.id === id)
      if (!patient) {
        throw new Error('Patient not found')
      }

      // Build exclude list (already linked records)
      const excludeIds = [id]
      if (patient.primaryPatientId) {
        excludeIds.push(patient.primaryPatientId)
      }
      if (patient.linkedPatientIds && patient.linkedPatientIds.length > 0) {
        excludeIds.push(...patient.linkedPatientIds)
      }

      const ignoredIds = mockDuplicateIgnores
        .filter((entry) => entry.patientIdA === id || entry.patientIdB === id)
        .map((entry) => (entry.patientIdA === id ? entry.patientIdB : entry.patientIdA))
      if (ignoredIds.length > 0) {
        excludeIds.push(...ignoredIds)
      }

      const normalizeName = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()
      const getNameSimilarity = (a: Patient, b: Patient) => {
        const fullA = normalizeName(`${a.firstName} ${a.lastName}`)
        const fullB = normalizeName(`${b.firstName} ${b.lastName}`)
        if (fullA === fullB) return 1
        if (a.lastName?.toLowerCase() === b.lastName?.toLowerCase()) {
          if (a.firstName?.[0]?.toLowerCase() === b.firstName?.[0]?.toLowerCase()) return 0.7
          return 0.6
        }
        return 0
      }

      // Simple duplicate detection for mock - require gender + DOB, then fuzzy name
      // IMPORTANT: 'p' is the OTHER patient (potential duplicate), 'patient' is the CURRENT patient
      const duplicates = mockPatients
        .filter((p) => {
          // Exclude current patient and already linked records
          if (p.id === id || excludeIds.includes(p.id)) return false
          const sameGender = p.gender && patient.gender && p.gender === patient.gender
          const sameDOB = p.dateOfBirth === patient.dateOfBirth
          if (!sameGender || !sameDOB) return false
          const nameSimilarity = getNameSimilarity(p, patient)
          return nameSimilarity > 0
        })
        .map((p) => {
          // 'p' is the duplicate patient, 'patient' is the current patient being checked
          // Calculate match score comparing duplicate (p) with current patient
          const nameSimilarity = getNameSimilarity(p, patient)
          const score = Math.round(nameSimilarity * 100)
          const reasons: string[] = []
          reasons.push(`Name similarity: ${Math.round(nameSimilarity * 100)}%`)
          // Return the duplicate patient (p), not the current patient
          return {
            patient: p, // This is the duplicate patient, not the current one
            matchScore: score,
            matchReasons: reasons,
            confidence: score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low',
          }
        })
        .filter((d) => {
          // Additional safeguard: ensure we never return the current patient
          if (d.patient.id === id) {
            console.warn('Duplicate detection error: Current patient included in results', { currentId: id, duplicateId: d.patient.id })
            return false
          }
          return d.matchScore >= 30
        })
        .sort((a, b) => b.matchScore - a.matchScore)

      return { data: duplicates as T }
    }

    if (url.startsWith('/patients/') && !url.includes('/restore') && !url.includes('/export') && !url.includes('/duplicates') && !url.includes('/link') && !url.includes('/unlink') && !url.includes('/set-primary') && !url.includes('/reminders')) {
      const id = url.split('/patients/')[1]
      const patient = mockPatients.find((p) => p.id === id)
      if (!patient) {
        throw new Error('Patient not found')
      }

      // Handle backward compatibility
      const isPrimary = patient.isPrimary !== undefined ? patient.isPrimary : true

      // If this is a primary record, fetch linked secondary records
      let patientWithLinks = { ...patient }
      if (isPrimary && patient.linkedPatientIds && patient.linkedPatientIds.length > 0) {
        const linkedRecords = patient.linkedPatientIds
          .map((linkedId) => {
            const linked = mockPatients.find((p) => p.id === linkedId)
            if (!linked) return null
            return {
              id: linked.id,
              firstName: linked.firstName,
              lastName: linked.lastName,
              dateOfBirth: linked.dateOfBirth,
              patientNumber: linked.patientNumber,
            }
          })
          .filter((r) => r !== null) as any[]
        patientWithLinks.linkedRecords = linkedRecords
      }

      // If this is a secondary record, fetch primary record summary
      if (!isPrimary && patient.primaryPatientId) {
        const primary = mockPatients.find((p) => p.id === patient.primaryPatientId)
        if (primary) {
          patientWithLinks.primaryRecord = {
            id: primary.id,
            firstName: primary.firstName,
            lastName: primary.lastName,
            dateOfBirth: primary.dateOfBirth,
            patientNumber: primary.patientNumber,
          }
        }
      }

      return { data: patientWithLinks as T }
    }

    // Family endpoints
    if (url === '/families') {
      return { data: mockFamilies as T }
    }

    if (url.startsWith('/families/') && !url.includes('/members') && !url.includes('/search-patients')) {
      const id = url.split('/families/')[1]
      const family = mockFamilies.find((f) => f.id === id)
      if (!family) {
        throw new Error('Family not found')
      }
      return { data: family as T }
    }

    if (url.startsWith('/families/') && url.includes('/members') && !url.includes('/search-patients')) {
      const familyId = url.split('/families/')[1].split('/members')[0]
      const family = mockFamilies.find((f) => f.id === familyId)
      if (!family) {
        throw new Error('Family not found')
      }
      // Return family members with patient details
      const members: FamilyMember[] = family.memberIds
        .map((patientId) => {
          const patient = mockPatients.find((p) => p.id === patientId)
          if (!patient) return null
          return {
            patientId: patient.id,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth,
            gender: patient.gender as string,
            isActive: patient.isActive,
            role: patientId === family.primaryContactId ? 'primary' : 'member',
          }
        })
        .filter((m) => m !== null) as FamilyMember[]
      return { data: members as T }
    }

    // Patient Files endpoints
    if (url === '/patient-files' || url.startsWith('/patient-files?')) {
      let filters: any = config?.params || {}
      
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          filters[key] = value
        })
      }

      let files = [...mockPatientFiles]

      if (filters.patientId) {
        files = files.filter(f => f.patientId === filters.patientId)
      }

      if (filters.category) {
        files = files.filter(f => f.category === filters.category)
      }

      if (filters.source) {
        const sources = Array.isArray(filters.source) ? filters.source : [filters.source]
        files = files.filter(f => sources.includes(f.source))
      }

      return { data: files as T }
    }

    if (url.startsWith('/patient-files/') && !url.includes('/download') && !url.includes('/upload')) {
      const id = url.split('/patient-files/')[1]
      const file = mockPatientFiles.find(f => f.id === id)
      if (!file) {
        throw new Error('Patient file not found')
      }
      return { data: file as T }
    }

    if (url.startsWith('/patient-files/') && url.includes('/download')) {
      const id = url.split('/patient-files/')[1].split('/download')[0]
      const file = mockPatientFiles.find(f => f.id === id)
      if (!file) {
        throw new Error('Patient file not found')
      }
      // Return a mock blob with appropriate type for testing viewer
      // For images, create a small data URL; for PDFs/text, return appropriate content
      let content = 'Mock file content'
      if (file.fileType.startsWith('image/')) {
        // Return a 1x1 transparent PNG as a data URL
        content = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
      } else if (file.fileType === 'application/pdf') {
        content = '%PDF-1.4\nMock PDF content'
      } else if (file.fileType.startsWith('text/') || file.fileType === 'application/json') {
        content = 'Mock text file content\nThis is a sample text file for testing.'
      }
      const blob = new Blob([content], { type: file.fileType || 'application/octet-stream' })
      return { data: blob as T }
    }

    // Batch documents endpoints
    if (url === '/batch-documents' || url.startsWith('/batch-documents?')) {
      const params = url.includes('?') ? new URLSearchParams(url.split('?')[1]) : null
      const batchId = params?.get('batchId') ?? config?.params?.batchId

      if (batchId) {
        const files = mockBatchDocuments.filter((d) => d.batchId === batchId)
        return { data: files as T }
      }

      const byBatch: Record<string, { batchId: string; fileCount: number; uploadedAt?: string }> = {}
      for (const d of mockBatchDocuments) {
        if (!byBatch[d.batchId]) {
          byBatch[d.batchId] = { batchId: d.batchId, fileCount: 0, uploadedAt: d.uploadedAt }
        }
        byBatch[d.batchId].fileCount += 1
        if (d.uploadedAt && (!byBatch[d.batchId].uploadedAt || d.uploadedAt > byBatch[d.batchId].uploadedAt!)) {
          byBatch[d.batchId].uploadedAt = d.uploadedAt
        }
      }
      const batches: BatchSummary[] = Object.values(byBatch).sort(
        (a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || '')
      )
      return { data: batches as T }
    }

    if (url.startsWith('/batch-documents/') && !url.includes('/upload')) {
      const pathPart = url.replace('/batch-documents/', '')
      const fileId = pathPart.split('/')[0]

      if (pathPart === fileId) {
        const doc = mockBatchDocuments.find((d) => d.fileId === fileId)
        if (!doc) throw new Error('Batch document not found')
        return { data: doc as T }
      }

      if (pathPart.endsWith('/preview')) {
        const doc = mockBatchDocuments.find((d) => d.fileId === fileId)
        if (!doc) throw new Error('Batch document not found')
        return { data: { url: `https://example.com/preview/${fileId}` } as T }
      }
    }

    // Tenant registration endpoints (for root domain)
    if (url === '/register' || url.startsWith('/register')) {
      // This is handled in POST
      throw new Error('Use POST for registration')
    }

    if (url === '/check-subdomain' || url.startsWith('/check-subdomain')) {
      const subdomain = config?.params?.subdomain || (url.includes('?') ? new URLSearchParams(url.split('?')[1]).get('subdomain') : null)
      
      if (!subdomain) {
        return { data: { available: false, message: 'Subdomain parameter is required' } as T }
      }

      const normalizedSubdomain = subdomain.toLowerCase().trim()
      
      // Check if subdomain exists in mock tenants
      const existingTenant = mockTenants.find(t => t.subdomain === normalizedSubdomain)
      const available = !existingTenant

      return { data: { 
        subdomain: normalizedSubdomain,
        available,
        message: available ? 'Subdomain is available' : 'Subdomain is not available'
      } as T }
    }

    if (url.startsWith('/families/') && url.includes('/search-patients')) {
      const familyId = url.split('/families/')[1].split('/search-patients')[0]
      const family = mockFamilies.find((f) => f.id === familyId)
      const searchTerm = (config?.params?.q || '').toLowerCase()
      // Return patients that are not already members
      const existingMemberIds = family?.memberIds || []
      const availablePatients = mockPatients.filter(
        (p) =>
          !existingMemberIds.includes(p.id) &&
          !p.isArchived &&
          (searchTerm === '' ||
            p.firstName.toLowerCase().includes(searchTerm) ||
            p.lastName.toLowerCase().includes(searchTerm) ||
            p.patientNumber?.toLowerCase().includes(searchTerm) ||
            `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm))
      )
      return { data: availablePatients as T }
    }

    // Appointment endpoints - check for available-slots FIRST before general appointments
    if (url.startsWith('/appointments/available-slots')) {
      console.log('[Mock API] ===== AVAILABLE SLOTS REQUEST =====')
      console.log('[Mock API] URL:', url)
      console.log('[Mock API] Config:', config)
      
      // Extract params from config or URL query string
      let staffId = config?.params?.staffId || ''
      let date = config?.params?.date || ''
      
      // Also check URL query string if params not in config
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        if (!staffId) staffId = params.get('staffId') || ''
        if (!date) date = params.get('date') || ''
      }
      
      console.log('[Mock API] Available slots request:', { staffId, date, url, configParams: config?.params })
      
      // Validate inputs
      if (!staffId || !date) {
        console.log('[Mock API] Missing staffId or date, returning empty slots')
        return { data: [] as T }
      }
      
      // Normalize date format to YYYY-MM-DD
      let normalizedDate = date
      try {
        // If date is already in YYYY-MM-DD format, use it directly
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          normalizedDate = date
        } else {
          // Try to parse and format the date
          const dateObj = new Date(date + 'T00:00:00') // Add time to avoid timezone issues
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear()
            const month = String(dateObj.getMonth() + 1).padStart(2, '0')
            const day = String(dateObj.getDate()).padStart(2, '0')
            normalizedDate = `${year}-${month}-${day}`
          }
        }
      } catch (e) {
        console.error('[Mock API] Date parsing error:', e, 'Using original date:', date)
        // If date parsing fails, use as-is
      }
      
      console.log('[Mock API] Normalized date:', normalizedDate, 'from original:', date)
      console.log('[Mock API] Existing appointments:', mockAppointments.length)
      console.log('[Mock API] Clinic hours config:', JSON.stringify(mockClinicHours, null, 2))
      
      // Get day of week (0 = Sunday, 1 = Monday, etc.)
      // Parse the date string directly to avoid timezone issues
      const [year, month, day] = normalizedDate.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day) // month is 0-indexed in Date constructor
      const dayOfWeek = dateObj.getDay() // 0 = Sunday, 1 = Monday, etc.
      
      console.log('[Mock API] Date object:', dateObj.toISOString(), 'Local day of week:', dayOfWeek, '(0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat)')
      console.log('[Mock API] Requested date:', normalizedDate, 'Parsed as:', { year, month, day, dayOfWeek })

      // Find clinic hours for this SPECIFIC day only
      const dayHours = mockClinicHours.days.find((d) => d.dayOfWeek === dayOfWeek)
      
      console.log('[Mock API] Found dayHours for dayOfWeek', dayOfWeek, ':', dayHours ? JSON.stringify(dayHours) : 'null')
      if (dayHours && dayHours.closedPeriods) {
        console.log('[Mock API] Closed periods for this day:', dayHours.closedPeriods.length, 'periods')
      }

      if (!dayHours || !dayHours.isOpen) {
        console.log('[Mock API] Clinic is closed on this day (dayOfWeek:', dayOfWeek, ', dayHours:', dayHours, ')')
        return { data: [] as T }
      }

      // Generate all 15-minute slots for the day based on clinic hours
      // Slots are always 15 minutes, regardless of clinic hours slotDuration setting
      const allSlots: string[] = []
      const [startHour, startMinute] = dayHours.startTime.split(':').map(Number)
      const [endHour, endMinute] = dayHours.endTime.split(':').map(Number)
      const SLOT_INTERVAL = 15 // Always use 15-minute intervals
      
      console.log('[Mock API] Generating 15-minute slots:', { startHour, startMinute, endHour, endMinute })

      // Generate all 15-minute slots from start to end time
      // Convert times to minutes for easier comparison
      const startMinutes = startHour * 60 + startMinute
      const endMinutes = endHour * 60 + endMinute
      
      console.log('[Mock API] Time range in minutes:', { startMinutes, endMinutes, startTime: dayHours.startTime, endTime: dayHours.endTime })
      
      let currentMinutes = startMinutes
      let slotCount = 0
      const MAX_SLOTS = 1000 // Safety limit
      
      while (currentMinutes < endMinutes && slotCount < MAX_SLOTS) {
        const hour = Math.floor(currentMinutes / 60)
        const minute = currentMinutes % 60
        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
        allSlots.push(time)
        slotCount++
        
        // Move to next 15-minute slot
        currentMinutes += SLOT_INTERVAL
      }
      
      console.log('[Mock API] Generated', allSlots.length, 'total 15-minute slots (first 10):', allSlots.slice(0, 10), 'last 5:', allSlots.slice(-5))

      // Get existing appointments for this staff member on this SPECIFIC date only
      // IMPORTANT: Only consider appointments that match the exact requested date
      // This prevents appointments from other days from blocking slots
      const existingAppointments = mockAppointments.filter((a) => {
        // Normalize appointment date for comparison - use same method as normalizedDate
        let appointmentDate = a.appointmentDate
        try {
          if (/^\d{4}-\d{2}-\d{2}$/.test(a.appointmentDate)) {
            appointmentDate = a.appointmentDate
          } else {
            // Parse date string directly to avoid timezone issues
            const parts = a.appointmentDate.split('-')
            if (parts.length === 3) {
              const [year, month, day] = parts.map(Number)
              appointmentDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
            } else {
              const dateObj = new Date(a.appointmentDate + 'T00:00:00')
              if (!isNaN(dateObj.getTime())) {
                const year = dateObj.getFullYear()
                const month = String(dateObj.getMonth() + 1).padStart(2, '0')
                const day = String(dateObj.getDate()).padStart(2, '0')
                appointmentDate = `${year}-${month}-${day}`
              }
            }
          }
        } catch (e) {
          // Use as-is if parsing fails
        }
        
        const matches = (
          a.staffId === staffId &&
          appointmentDate === normalizedDate &&
          a.status !== 'cancelled' &&
          a.status !== 'dna' &&
          a.status !== 'arrived' &&
          a.status !== 'completed'
        )
        
        if (matches) {
          console.log('[Mock API] Including appointment:', a.id, 'on', appointmentDate, 'for staff', staffId)
        }
        
        return matches
      })
      
      console.log('[Mock API] Found', existingAppointments.length, 'existing appointments for staff', staffId, 'on', normalizedDate)

      // Create a set of blocked slots based on appointment durations
      const blockedSlots = new Set<string>()
      
      existingAppointments.forEach((appointment) => {
        const [apptStartHour, apptStartMinute] = appointment.startTime.split(':').map(Number)
        const duration = appointment.duration || 30 // Default to 30 minutes if not specified
        const numberOfSlots = Math.ceil(duration / SLOT_INTERVAL) // How many 15-minute slots this appointment occupies
        
        console.log('[Mock API] Appointment:', appointment.startTime, 'duration:', duration, 'minutes, blocks', numberOfSlots, 'slots')
        
        // Block out the slots this appointment occupies
        let blockHour = apptStartHour
        let blockMinute = apptStartMinute
        
        for (let i = 0; i < numberOfSlots; i++) {
          const blockedTime = `${String(blockHour).padStart(2, '0')}:${String(blockMinute).padStart(2, '0')}`
          
          // Only block if this slot is within the clinic hours
          if (allSlots.includes(blockedTime)) {
            blockedSlots.add(blockedTime)
            console.log('[Mock API] Blocking slot:', blockedTime)
          }
          
          // Move to next 15-minute slot
          blockMinute += SLOT_INTERVAL
          if (blockMinute >= 60) {
            blockHour += Math.floor(blockMinute / 60)
            blockMinute = blockMinute % 60
          }
        }
      })
      
      console.log('[Mock API] Total blocked slots:', blockedSlots.size, 'Blocked times:', Array.from(blockedSlots).slice(0, 10))

      // Create a set of closed period slots
      // IMPORTANT: Only use closed periods from the current day (dayHours)
      // This ensures closed periods from other days don't affect availability
      const closedPeriodSlots = new Set<string>()
      if (dayHours && dayHours.closedPeriods && dayHours.closedPeriods.length > 0) {
        console.log('[Mock API] Processing', dayHours.closedPeriods.length, 'closed periods for dayOfWeek', dayOfWeek, 'only')
        dayHours.closedPeriods.forEach((period, index) => {
          const [periodStartH, periodStartM] = period.startTime.split(':').map(Number)
          const [periodEndH, periodEndM] = period.endTime.split(':').map(Number)
          const periodStartMinutes = periodStartH * 60 + periodStartM
          const periodEndMinutes = periodEndH * 60 + periodEndM

          console.log('[Mock API] Closed period', index + 1, ':', period.startTime, '-', period.endTime, 'for dayOfWeek', dayOfWeek)

          // Find all slots that fall within this closed period
          allSlots.forEach((slotTime) => {
            const [slotH, slotM] = slotTime.split(':').map(Number)
            const slotMinutes = slotH * 60 + slotM
            if (slotMinutes >= periodStartMinutes && slotMinutes < periodEndMinutes) {
              closedPeriodSlots.add(slotTime)
            }
          })
        })
        console.log('[Mock API] Total closed period slots for dayOfWeek', dayOfWeek, ':', closedPeriodSlots.size, 'Closed times:', Array.from(closedPeriodSlots).slice(0, 10))
      } else {
        console.log('[Mock API] No closed periods for dayOfWeek', dayOfWeek)
      }

      // Filter out blocked slots and closed period slots
      const availableSlots = allSlots.filter(
        (time) => !blockedSlots.has(time) && !closedPeriodSlots.has(time)
      )
      
      console.log('[Mock API] Final result:', {
        totalSlots: allSlots.length,
        blockedSlots: blockedSlots.size,
        availableSlots: availableSlots.length,
        date: normalizedDate,
        dayOfWeek: dayOfWeek,
        staffId: staffId,
        firstAvailable: availableSlots.slice(0, 5),
        lastAvailable: availableSlots.slice(-5)
      })
      
      if (availableSlots.length === 0 && allSlots.length > 0) {
        console.warn('[Mock API] WARNING: All slots are blocked! This might indicate a problem with the blocking logic.')
      }
      if (allSlots.length === 0) {
        console.error('[Mock API] ERROR: No slots generated! Check clinic hours configuration.')
      }
      
      return { data: availableSlots as T }
    }

    // Appointment endpoints (but NOT /appointments/available-slots which is handled above)
    if (url === '/appointments' || (url.startsWith('/appointments/') && !url.includes('/available-slots') && !url.includes('/cancel') && !url.includes('/dna') && !url.includes('/arrived'))) {
      // Start with params from config (axios params) - this is the primary source
      let filters: any = config?.params ? { ...config.params } : {}
      
      // If config.params wasn't provided, try parsing from URL query string
      // (This handles cases where params might be in the URL directly)
      if (!config?.params && url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        
        // Get all unique keys from the query string
        const keys = Array.from(new Set(params.keys()))
        
        keys.forEach((key) => {
          if (key === 'status' || key === 'type') {
            // Handle arrays: axios may serialize as status[]=value1&status[]=value2 or status=value1,value2
            // Use getAll to get all values for this key
            const allValues = params.getAll(key)
            const values: string[] = []
            
            // Flatten: each value might be comma-separated
            allValues.forEach(val => {
              if (val.includes(',')) {
                values.push(...val.split(',').filter(Boolean))
              } else {
                values.push(val)
              }
            })
            
            // Remove duplicates and set as array
            filters[key] = Array.from(new Set(values.filter(Boolean)))
          } else if (key.endsWith('[]')) {
            // Handle axios array notation: status[]=value1&status[]=value2
            const baseKey = key.slice(0, -2)
            const allValues = params.getAll(key)
            filters[baseKey] = allValues.filter(Boolean)
          } else {
            // Single value - use get() to get the last value (URLSearchParams behavior)
            const value = params.get(key)
            if (value !== null) {
              filters[key] = value
            }
          }
        })
      }
      
      // Ensure status and type are arrays if they exist (normalize to arrays)
      if (filters.status && !Array.isArray(filters.status)) {
        filters.status = [filters.status]
      }
      if (filters.type && !Array.isArray(filters.type)) {
        filters.type = [filters.type]
      }
      
      // Debug logging
      console.log('[Mock API] Appointment filters:', {
        originalParams: config?.params,
        parsedFilters: filters,
        url,
      })
      
      let appointments = [...mockAppointments]
      
      // Apply filters (only apply if filter value is truthy and not empty)
      if (filters.patientId) {
        appointments = appointments.filter((a) => a.patientId === filters.patientId)
      }
      if (filters.staffId) {
        appointments = appointments.filter((a) => a.staffId === filters.staffId)
      }
      if (filters.status && filters.status.length > 0) {
        const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status]
        appointments = appointments.filter((a) => statusArray.includes(a.status))
      }
      if (filters.type && filters.type.length > 0) {
        const typeArray = Array.isArray(filters.type) ? filters.type : [filters.type]
        appointments = appointments.filter((a) => typeArray.includes(a.type))
      }
      if (filters.startDate) {
        appointments = appointments.filter((a) => a.appointmentDate >= filters.startDate)
      }
      if (filters.endDate) {
        appointments = appointments.filter((a) => a.appointmentDate <= filters.endDate)
      }
      
      // Filter by minimum DNA count - only applies when status includes 'dna'
      if (filters.minDnaCount !== undefined && filters.minDnaCount > 0) {
        const statusArray = filters.status ? (Array.isArray(filters.status) ? filters.status : [filters.status]) : []
        if (statusArray.includes('dna')) {
          // Get patient DNA counts
          const patientDnaCounts = new Map<string, number>()
          mockPatients.forEach((patient) => {
            if (patient.dnaCount !== undefined) {
              patientDnaCounts.set(patient.id, patient.dnaCount)
            }
          })
          
          // Filter appointments to only those where patient has >= minDnaCount
          appointments = appointments.filter((a) => {
            const patientDnaCount = patientDnaCounts.get(a.patientId) || 0
            return patientDnaCount >= filters.minDnaCount!
          })
        }
      }
      
      return { data: appointments as T }
    }

    // Consultation endpoints - check for specific routes first
    if (url.startsWith('/consultations/') && url.includes('/artefacts')) {
      const parts = url.split('/')
      const consultationId = parts[2]
      const artefactId = parts[4]
      
      if (url.includes('/artefacts/') && artefactId) {
        // GET /consultations/:id/artefacts/:artefactId
        if (config?.method === 'GET' || !config) {
          const consultation = mockConsultations.find((c) => c.id === consultationId)
          if (!consultation) {
            throw new Error('Consultation not found')
          }
          const artefact = consultation.artefacts?.find((a) => a.id === artefactId)
          if (!artefact) {
            throw new Error('Artefact not found')
          }
          return { data: artefact as T }
        }
        // PUT /consultations/:id/artefacts/:artefactId
        if (config?.method === 'PUT' || url.includes('PUT')) {
          const consultation = mockConsultations.find((c) => c.id === consultationId)
          if (!consultation) {
            throw new Error('Consultation not found')
          }
          if (!consultation.artefacts) {
            consultation.artefacts = []
          }
          const index = consultation.artefacts.findIndex((a) => a.id === artefactId)
          if (index === -1) {
            throw new Error('Artefact not found')
          }
          consultation.artefacts[index] = {
            ...consultation.artefacts[index],
            ...config?.data,
            updatedAt: new Date().toISOString(),
          }
          consultation.updatedAt = new Date().toISOString()
          return { data: consultation.artefacts[index] as T }
        }
        // DELETE /consultations/:id/artefacts/:artefactId
        if (config?.method === 'DELETE' || url.includes('DELETE')) {
          const consultation = mockConsultations.find((c) => c.id === consultationId)
          if (!consultation || !consultation.artefacts) {
            throw new Error('Consultation or artefact not found')
          }
          consultation.artefacts = consultation.artefacts.filter((a) => a.id !== artefactId)
          consultation.updatedAt = new Date().toISOString()
          return { data: {} as T }
        }
      } else {
        // POST /consultations/:id/artefacts
        const consultation = mockConsultations.find((c) => c.id === consultationId)
        if (!consultation) {
          throw new Error('Consultation not found')
        }
        if (!consultation.artefacts) {
          consultation.artefacts = []
        }
        const newArtefact: ConsultationArtefact = {
          id: String(mockConsultations.length + consultation.artefacts.length + 1),
          consultationId,
          ...config?.data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        consultation.artefacts.push(newArtefact)
        consultation.updatedAt = new Date().toISOString()
        return { data: newArtefact as T }
      }
    }

    if (url.startsWith('/consultations/') && url.includes('/complete')) {
      const id = url.split('/consultations/')[1].split('/complete')[0]
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      consultation.status = 'completed'
      if (config?.data?.reason !== undefined) consultation.reason = config.data.reason
      if (config?.data?.notes !== undefined) consultation.notes = config.data.notes
      if (config?.data?.diagnosis !== undefined) consultation.diagnosis = config.data.diagnosis
      if (config?.data?.plan !== undefined) consultation.plan = config.data.plan
      if (!consultation.endTime) {
        const now = new Date()
        consultation.endTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        if (consultation.startTime) {
          const [startHour, startMinute] = consultation.startTime.split(':').map(Number)
          const startDate = new Date()
          startDate.setHours(startHour, startMinute, 0, 0)
          consultation.duration = Math.round((now.getTime() - startDate.getTime()) / (1000 * 60))
        }
      }
      consultation.updatedAt = new Date().toISOString()
      return { data: consultation as T }
    }

    if (url.startsWith('/consultations/') && url.includes('/cancel')) {
      const id = url.split('/consultations/')[1].split('/cancel')[0]
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      consultation.status = 'cancelled'
      consultation.updatedAt = new Date().toISOString()
      return { data: consultation as T }
    }

    if (url.startsWith('/consultations/') && !url.includes('/artefacts') && !url.includes('/complete') && !url.includes('/cancel')) {
      const id = url.split('/consultations/')[1]
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      return { data: consultation as T }
    }

    if (url === '/consultations' || url.startsWith('/consultations')) {
      let filters: any = config?.params || {}
      
      // Parse query string if present
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          if (key === 'status') {
            filters[key] = value.split(',').filter(Boolean)
          } else {
            filters[key] = value
          }
        })
      }
      
      let consultations = [...mockConsultations]
      
      // Apply filters
      if (filters.patientId) {
        consultations = consultations.filter((c) => c.patientId === filters.patientId)
      }
      if (filters.staffId) {
        consultations = consultations.filter((c) => c.staffId === filters.staffId)
      }
      if (filters.status) {
        const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status]
        consultations = consultations.filter((c) => statusArray.includes(c.status))
      }
      if (filters.startDate) {
        consultations = consultations.filter((c) => c.consultationDate >= filters.startDate)
      }
      if (filters.endDate) {
        consultations = consultations.filter((c) => c.consultationDate <= filters.endDate)
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        consultations = consultations.filter(
          (c) =>
            c.reason?.toLowerCase().includes(searchLower) ||
            c.notes?.toLowerCase().includes(searchLower) ||
            c.diagnosis?.some((d) => d.toLowerCase().includes(searchLower))
        )
      }
      
      // Sort by date (most recent first)
      consultations.sort((a, b) => {
        const dateA = new Date(a.consultationDate + 'T' + (a.startTime || '00:00'))
        const dateB = new Date(b.consultationDate + 'T' + (b.startTime || '00:00'))
        return dateB.getTime() - dateA.getTime()
      })
      
      return { data: consultations as T }
    }

    // Lab Results endpoints - check for specific routes first
    if (url.startsWith('/lab-results/') && url.includes('/sync-healthlink')) {
      // POST /lab-results/sync-healthlink
      const { patientId } = config?.data || {}
      // Simulate syncing from Healthlink - generate some new results
      const newResults: LabResult[] = []
      const now = new Date()
      
      // Generate a random lab result
      const result: LabResult = {
        id: crypto.randomUUID(),
        patientId: patientId || '1',
        type: 'blood',
        orderNumber: `ORD-${now.getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
        accessionNumber: `ACC-${String(Math.floor(Math.random() * 100000)).padStart(5, '0')}`,
        labName: 'Beaumont Hospital Laboratory',
        labCode: 'BEAU',
        orderingPhysicianId: '2',
        orderingPhysicianName: 'Dr. Sarah O\'Brien',
        collectionDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        collectionTime: '09:00',
        receivedDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reportedDate: now.toISOString().split('T')[0],
        reportedTime: now.toTimeString().slice(0, 5),
        status: 'final',
        reviewStatus: 'in-review',
        clinicalIndication: 'Routine monitoring',
        isAbnormal: false,
        isCritical: false,
        panels: [
          {
            id: crypto.randomUUID(),
            panelCode: 'FBC',
            panelName: 'Full Blood Count',
            tests: [
              {
                id: crypto.randomUUID(),
                testCode: 'CBC-WBC',
                testName: 'White Blood Cell Count',
                value: 6.8,
                unit: '×10⁹/L',
                referenceRange: '4.0-11.0',
                flag: 'normal',
                status: 'final',
              },
              {
                id: crypto.randomUUID(),
                testCode: 'CBC-HGB',
                testName: 'Haemoglobin',
                value: 14.2,
                unit: 'g/dL',
                referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5',
                flag: 'normal',
                status: 'final',
              },
            ],
          },
        ],
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        source: 'healthlink',
        healthlinkMessageId: `HL-${now.getFullYear()}-${String(Math.floor(Math.random() * 1000000)).padStart(6, '0')}`,
      }
      mockLabResults.push(result)
      newResults.push(result)
      
      return { data: { count: newResults.length, results: newResults } as T }
    }

    if (url === '/lab-results/summaries' || url.startsWith('/lab-results/summaries')) {
      let filters: any = config?.params || {}
      
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          if (key === 'status') {
            filters[key] = value.split(',').filter(Boolean)
          } else {
            filters[key] = value
          }
        })
      }
      
      let results = [...mockLabResults]
      
      // Apply filters
      if (filters.patientId) {
        results = results.filter((r) => r.patientId === filters.patientId)
      }
      if (filters.orderingPhysicianId) {
        results = results.filter((r) => r.orderingPhysicianId === filters.orderingPhysicianId)
      }
      if (filters.labName) {
        results = results.filter((r) => r.labName.toLowerCase().includes(filters.labName.toLowerCase()))
      }
      if (filters.status) {
        const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status]
        results = results.filter((r) => statusArray.includes(r.status))
      }
      if (filters.startDate) {
        results = results.filter((r) => r.reportedDate >= filters.startDate)
      }
      if (filters.endDate) {
        results = results.filter((r) => r.reportedDate <= filters.endDate)
      }
      if (filters.isAbnormal !== undefined) {
        results = results.filter((r) => r.isAbnormal === (filters.isAbnormal === 'true' || filters.isAbnormal === true))
      }
      if (filters.isCritical !== undefined) {
        results = results.filter((r) => r.isCritical === (filters.isCritical === 'true' || filters.isCritical === true))
      }
      if (filters.reviewStatus) {
        const reviewStatusArray = Array.isArray(filters.reviewStatus) ? filters.reviewStatus : [filters.reviewStatus]
        results = results.filter((r) => reviewStatusArray.includes(r.reviewStatus))
      }
      
      // Sort by reported date (most recent first)
      results.sort((a, b) => {
        const dateA = new Date(a.reportedDate + 'T' + (a.reportedTime || '00:00'))
        const dateB = new Date(b.reportedDate + 'T' + (b.reportedTime || '00:00'))
        return dateB.getTime() - dateA.getTime()
      })
      
      // Convert to summaries
      const summaries: LabResultSummary[] = results.map((result) => {
        const patient = mockPatients.find((p) => p.id === result.patientId)
        return {
          id: result.id,
          patientId: result.patientId,
          patientName: patient ? `${patient.firstName} ${patient.lastName}` : undefined,
          type: result.type,
          orderNumber: result.orderNumber,
          labName: result.labName,
          collectionDate: result.collectionDate,
          reportedDate: result.reportedDate,
          status: result.status,
          panelCount: result.panels.length,
          testCount: result.panels.reduce((sum, panel) => sum + panel.tests.length, 0),
          isAbnormal: result.isAbnormal,
          isCritical: result.isCritical,
          reviewStatus: result.reviewStatus,
        }
      })
      
      return { data: summaries as T }
    }

    if (url.startsWith('/lab-results/') && !url.includes('/sync-healthlink') && !url.includes('/summaries')) {
      const id = url.split('/lab-results/')[1]
      const result = mockLabResults.find((r) => r.id === id)
      if (!result) {
        throw new Error('Lab result not found')
      }
      return { data: result as T }
    }

    if (url === '/lab-results' || url.startsWith('/lab-results')) {
      let filters: any = config?.params || {}
      
      // Parse query string if present
      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          if (key === 'status') {
            filters[key] = value.split(',').filter(Boolean)
          } else {
            filters[key] = value
          }
        })
      }
      
      let results = [...mockLabResults]
      
      // Apply filters
      if (filters.patientId) {
        results = results.filter((r) => r.patientId === filters.patientId)
      }
      if (filters.orderingPhysicianId) {
        results = results.filter((r) => r.orderingPhysicianId === filters.orderingPhysicianId)
      }
      if (filters.labName) {
        results = results.filter((r) => r.labName.toLowerCase().includes(filters.labName.toLowerCase()))
      }
      if (filters.status) {
        const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status]
        results = results.filter((r) => statusArray.includes(r.status))
      }
      if (filters.startDate) {
        results = results.filter((r) => r.reportedDate >= filters.startDate)
      }
      if (filters.endDate) {
        results = results.filter((r) => r.reportedDate <= filters.endDate)
      }
      if (filters.isAbnormal !== undefined) {
        results = results.filter((r) => r.isAbnormal === (filters.isAbnormal === 'true' || filters.isAbnormal === true))
      }
      if (filters.isCritical !== undefined) {
        results = results.filter((r) => r.isCritical === (filters.isCritical === 'true' || filters.isCritical === true))
      }
      if (filters.reviewStatus) {
        const reviewStatusArray = Array.isArray(filters.reviewStatus) ? filters.reviewStatus : [filters.reviewStatus]
        results = results.filter((r) => reviewStatusArray.includes(r.reviewStatus))
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        results = results.filter(
          (r) =>
            r.orderNumber?.toLowerCase().includes(searchLower) ||
            r.accessionNumber?.toLowerCase().includes(searchLower) ||
            r.labName.toLowerCase().includes(searchLower) ||
            r.clinicalIndication?.toLowerCase().includes(searchLower)
        )
      }
      
      // Sort by reported date (most recent first)
      results.sort((a, b) => {
        const dateA = new Date(a.reportedDate + 'T' + (a.reportedTime || '00:00'))
        const dateB = new Date(b.reportedDate + 'T' + (b.reportedTime || '00:00'))
        return dateB.getTime() - dateA.getTime()
      })
      
      return { data: results as T }
    }

    // Vital Signs endpoints
    if (url.startsWith('/vital-signs/patient/') && url.endsWith('/summary')) {
      const patientId = url.split('/vital-signs/patient/')[1].split('/summary')[0]
      const patientVitals = mockVitalSigns.filter((v) => v.patientId === patientId)
      const latest = patientVitals.length > 0 ? patientVitals[0] : null
      return {
        data: {
          latest,
          totalCount: patientVitals.length,
          latestValues: latest
            ? {
                bloodPressure: latest.bloodPressure,
                heartRate: latest.heartRate,
                temperature: latest.temperature,
                respiratoryRate: latest.respiratoryRate,
                weight: latest.weight,
                height: latest.height,
                bmi: latest.bmi,
              }
            : null,
        } as T,
      }
    }

    if (url.startsWith('/vital-signs/') && !url.includes('/patient/')) {
      const id = url.split('/vital-signs/')[1]
      const vitalSigns = mockVitalSigns.find((v) => v.id === id)
      if (!vitalSigns) {
        throw new Error('Vital signs record not found')
      }
      return { data: vitalSigns as T }
    }

    if (url === '/vital-signs' || url.startsWith('/vital-signs')) {
      let filters: any = config?.params || {}

      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        filters = {}
        params.forEach((value, key) => {
          filters[key] = value
        })
      }

      let vitalSigns = [...mockVitalSigns]

      if (filters.patientId) {
        vitalSigns = vitalSigns.filter((v) => v.patientId === filters.patientId)
      }
      if (filters.consultationId) {
        vitalSigns = vitalSigns.filter((v) => v.consultationId === filters.consultationId)
      }
      if (filters.startDate) {
        vitalSigns = vitalSigns.filter((v) => v.recordedAt >= filters.startDate)
      }
      if (filters.endDate) {
        vitalSigns = vitalSigns.filter((v) => v.recordedAt <= filters.endDate)
      }

      vitalSigns.sort((a, b) => {
        const dateA = new Date(a.recordedAt || a.createdAt).getTime()
        const dateB = new Date(b.recordedAt || b.createdAt).getTime()
        return dateB - dateA
      })

      return { data: vitalSigns as T }
    }

    // Settings endpoints
    if (url === '/settings/clinic-hours') {
      console.log('[Mock API] GET /settings/clinic-hours - returning clinic hours:', mockClinicHours)
      return { data: mockClinicHours as T }
    }

    if (url.startsWith('/settings/clinic-hours/available-slots')) {
      const date = config?.params?.date || ''
      if (!date) {
        return { data: [] as T }
      }

      // Get day of week (0 = Sunday, 1 = Monday, etc.)
      const dateObj = new Date(date + 'T00:00:00')
      const dayOfWeek = dateObj.getDay()

      // Find clinic hours for this day
      const dayHours = mockClinicHours.days.find((d) => d.dayOfWeek === dayOfWeek)

      if (!dayHours || !dayHours.isOpen) {
        return { data: [] as T }
      }

      // Generate slots based on clinic hours
      const slots: string[] = []
      const [startHour, startMinute] = dayHours.startTime.split(':').map(Number)
      const [endHour, endMinute] = dayHours.endTime.split(':').map(Number)
      const slotDuration = dayHours.slotDuration || 30

      let currentHour = startHour
      let currentMinute = startMinute

      while (
        currentHour < endHour ||
        (currentHour === endHour && currentMinute < endMinute)
      ) {
        const time = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`
        slots.push(time)

        // Move to next slot
        currentMinute += slotDuration
        if (currentMinute >= 60) {
          currentHour += Math.floor(currentMinute / 60)
          currentMinute = currentMinute % 60
        }
      }

      return { data: slots as T }
    }

    if (url === '/settings/practice-location') {
      console.log('[Mock API] GET /settings/practice-location - returning practice location:', mockPracticeLocation)
      return { data: mockPracticeLocation as T }
    }

    // Appointment types endpoints
    if (url === '/settings/appointment-types') {
      console.log('[Mock API] GET /settings/appointment-types - returning appointment types:', mockAppointmentTypes)
      return { data: [...mockAppointmentTypes] as T }
    }

    if (url.startsWith('/settings/appointment-types/') && !url.includes('/toggle-active')) {
      const id = url.split('/settings/appointment-types/')[1]
      const type = mockAppointmentTypes.find((t) => t.id === id)
      if (!type) {
        throw new Error('Appointment type not found')
      }
      return { data: type as T }
    }

    if (url.startsWith('/appointments/') && !url.includes('/cancel') && !url.includes('/dna') && !url.includes('/available-slots')) {
      const id = url.split('/appointments/')[1]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      return { data: appointment as T }
    }

    // Maternity routes
    if (url.startsWith('/pregnancies')) {
      if (url.includes('?patientId=')) {
        const patientId = new URL(url, 'http://localhost').searchParams.get('patientId')
        return { data: await mockApi.getPregnancies(patientId || '') as T }
      }
      if (url.match(/\/pregnancies\/([^/]+)$/)) {
        const match = url.match(/\/pregnancies\/([^/]+)$/)
        return { data: await mockApi.getPregnancy(match?.[1] || '') as T }
      }
      if (url.match(/\/pregnancies\/([^/]+)\/visits$/)) {
        const match = url.match(/\/pregnancies\/([^/]+)\/visits$/)
        return { data: await mockApi.getVisits(match?.[1] || '') as T }
      }
    }

    if (url.startsWith('/maternity-visits/')) {
      const visitId = url.split('/maternity-visits/')[1]
      return { data: await mockApi.getVisit(visitId) as T }
    }

    // Care Plans GET endpoints - check before other /patients/ routes
    if (url.startsWith('/patients/') && url.includes('/care-plans')) {
      // Match /patients/:id/care-plans but not /patients/:id/care-plans/:carePlanId
      const match = url.match(/^\/patients\/([^\/]+)\/care-plans\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Care plans route matched:', url, 'patientId:', patientId)
        const plans = await mockApi.getCarePlans(patientId)
        console.log('[Mock API] Returning care plans:', plans)
        return { data: plans as T }
      }
    }

    // GET /care-plans/templates - List care plan templates
    if (url === '/care-plans/templates' || url.startsWith('/care-plans/templates?')) {
      const params = config?.params || {}
      const category = params.category
      let templates = mockCarePlans.filter((cp) => cp.isTemplate === true)
      if (category) {
        templates = templates.filter((cp) => cp.templateCategory === category)
      }
      return { data: templates as T }
    }

    // GET /care-plans/templates/{id} - Get single template
    if (url.startsWith('/care-plans/templates/')) {
      const templateId = url.split('/care-plans/templates/')[1]?.split('/')[0]
      if (templateId) {
        const template = mockCarePlans.find((cp) => cp.id === templateId && cp.isTemplate === true)
        if (!template) {
          throw new Error('Template not found')
        }
        return { data: template as T }
      }
    }

    // GET /care-plans/{id}/history - Get care plan history
    if (url.startsWith('/care-plans/') && url.includes('/history')) {
      const carePlanId = url.split('/care-plans/')[1]?.split('/history')[0]
      if (carePlanId) {
        // Get care plan to find patientId
        const plan = await mockApi.getCarePlan(carePlanId)
        if (!plan || !plan.patientId) {
          return { data: [] as T }
        }
        // Filter audit entries for this care plan
        const history = mockAuditEntries
          .filter((entry) => entry.patientId === plan.patientId && entry.entityType === 'carePlan' && entry.entityId === carePlanId)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        return { data: history as T }
      }
    }

    if (url.startsWith('/care-plans/')) {
      const id = url.split('/care-plans/')[1]?.split('/')[0]
      if (id) {
        const plan = await mockApi.getCarePlan(id)
        return { data: plan as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/care-plans/suggestions')) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const suggestions = await mockApi.getCarePlanSuggestions(patientId)
        return { data: suggestions as T }
      }
    }

    // Protocols GET endpoints
    if (url === '/protocols' || url.startsWith('/protocols?')) {
      const protocols = await mockApi.getProtocols()
      return { data: protocols as T }
    }

    if (url.startsWith('/protocols/') && !url.includes('/compliance') && !url.includes('/patients/')) {
      const id = url.split('/protocols/')[1]?.split('/')[0]
      if (id) {
        const protocol = await mockApi.getProtocol(id)
        return { data: protocol as T }
      }
    }

    // Patient Protocols GET endpoints - check before other /patients/ routes
    if (url.startsWith('/patients/') && url.includes('/protocols')) {
      // Match /patients/:id/protocols but not /patients/:id/protocols/:protocolId or /patients/:id/protocols/:protocolId/exclude
      const match = url.match(/^\/patients\/([^\/]+)\/protocols\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Patient protocols route matched:', url, 'patientId:', patientId)
        const enrollments = await mockApi.getPatientProtocols(patientId)
        console.log('[Mock API] Returning patient protocols:', enrollments)
        return { data: enrollments as T }
      }
    }

    if (url.startsWith('/protocol-eligibility/check')) {
      const urlParams = url.includes('?') ? new URL(url, 'http://dummy').searchParams : new URLSearchParams()
      const patientId = urlParams.get('patientId') || config?.params?.patientId
      const protocolId = urlParams.get('protocolId') || config?.params?.protocolId
      if (patientId && protocolId) {
        const result = await mockApi.checkProtocolEligibility(patientId, protocolId)
        return { data: result as T }
      }
    }

    if (url.startsWith('/protocol-eligibility/screen')) {
      const urlParams = url.includes('?') ? new URL(url, 'http://dummy').searchParams : new URLSearchParams()
      const protocolId = urlParams.get('protocolId') || config?.params?.protocolId
      if (protocolId) {
        const result = await mockApi.screenEligiblePatients(protocolId)
        return { data: result as T }
      }
    }

    if (url.startsWith('/protocols/') && url.includes('/compliance')) {
      const id = url.split('/protocols/')[1]?.split('/')[0]
      if (id) {
        if (url.includes('/compliance/report')) {
          const report = await mockApi.getProtocolComplianceReport(id)
          return { data: report as T }
        }
        const metrics = await mockApi.getProtocolCompliance(id)
        return { data: metrics as T }
      }
    }

    // Drug Recalls GET endpoints
    if (url === '/drug-recalls' || url.startsWith('/drug-recalls?')) {
      const recalls = await mockApi.getDrugRecalls()
      return { data: recalls as T }
    }

    if (url.startsWith('/drug-recalls/') && !url.includes('/affected-patients') && !url.includes('/notifications')) {
      const id = url.split('/drug-recalls/')[1]?.split('/')[0]
      if (id && !url.includes('/affected-patients')) {
        const recall = await mockApi.getDrugRecall(id)
        return { data: recall as T }
      }
    }

    if (url.startsWith('/drug-recalls/') && url.includes('/affected-patients')) {
      const id = url.split('/drug-recalls/')[1]?.split('/')[0]
      const patients = await mockApi.getAffectedPatients(id)
      return { data: patients as T }
    }

    if (url.startsWith('/patient-medication-search')) {
      const params = config?.params || {}
      const patients = await mockApi.searchPatientsByMedication(params.drugId)
      return { data: patients as T }
    }

    // Research Studies GET endpoints
    if (url === '/research-studies' || url.startsWith('/research-studies?')) {
      const studies = await mockApi.getResearchStudies()
      return { data: studies as T }
    }

    if (url.startsWith('/research-studies/') && !url.includes('/patients/') && !url.includes('/protocol')) {
      const id = url.split('/research-studies/')[1]?.split('/')[0]
      if (id) {
        const study = await mockApi.getResearchStudy(id)
        return { data: study as T }
      }
    }

    // Patient Research Studies GET endpoints - check before other /patients/ routes
    if (url.startsWith('/patients/') && url.includes('/research-studies')) {
      // Match /patients/:id/research-studies but not /patients/:id/research-studies/:studyId
      const match = url.match(/^\/patients\/([^\/]+)\/research-studies\/?$/)
      if (match) {
        const patientId = match[1]
        console.log('[Mock API] Patient research studies route matched:', url, 'patientId:', patientId)
        const enrollments = await mockApi.getPatientResearchStudies(patientId)
        console.log('[Mock API] Returning patient research studies:', enrollments)
        return { data: enrollments as T }
      }
    }

    if (url.startsWith('/research-studies/') && url.includes('/protocol')) {
      const id = url.split('/research-studies/')[1]?.split('/')[0]
      if (id) {
        const version = config?.params?.version
        const protocol = await mockApi.getStudyProtocol(id, version)
        return { data: protocol as T }
      }
    }

    // Patient Guidance GET endpoints
    if (url.startsWith('/patients/') && url.includes('/guidance') && !url.match(/\/guidance\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const guidance = await mockApi.getPatientGuidance(patientId)
        return { data: guidance as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/guidance/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const guidanceId = parts[4]
      if (patientId && guidanceId) {
        const guidance = await mockApi.getPatientGuidanceById(patientId, guidanceId)
        return { data: guidance as T }
      }
    }

    if (url === '/reminder-rules') {
      const rules = await mockApi.getReminderRules()
      return { data: rules as T }
    }

    if (url === '/patient-reminders' || url.startsWith('/patient-reminders?')) {
      const reminders = await mockApi.getAllPatientReminders()
      return { data: reminders as T }
    }

    // Patient Reminders GET endpoints
    if (url.startsWith('/patients/') && url.includes('/reminders') && !url.match(/\/reminders\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const reminders = await mockApi.getPatientReminders(patientId)
        console.log('[Mock API] Reminders fetched:', { patientId, count: reminders.length })
        return { data: reminders as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/reminders/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const reminderId = parts[4]
      if (patientId && reminderId) {
        const reminder = await mockApi.getPatientReminderById(patientId, reminderId)
        return { data: reminder as T }
      }
    }

    console.log('[Mock API] ✗ No matching GET route found for:', url)
    throw new Error('Not found')
  },

  post: async <T>(url: string, data?: any, config?: any): Promise<{ data: T }> => {
    await delay(300)
    console.log('[Mock API] POST handler called with:', { url, dataType: typeof data, hasData: !!data, config })

    if (url.startsWith('/leave')) {
      const leaveData = await handleLeaveMock('POST', url, data)
      return { data: leaveData as T }
    }

    if (url === '/ai-assist/medication-order-insights') {
      return {
        data: {
          explanations: [],
          rankedAlternatives: [],
          educationalNotes:
            'Mock API: adjunct AI is not simulated. Use real API or disable mock mode to use client-side AI.',
        } as T,
      }
    }

    if (url === '/batch-documents/upload' || url.startsWith('/batch-documents/upload')) {
      const formData = data as FormData | undefined
      if (!formData || !(formData instanceof FormData)) {
        throw new Error('FormData with file(s) is required')
      }
      const batchId = crypto.randomUUID()
      const files: BatchDocument[] = []
      const fileList = formData.getAll('file') as File[]
      const now = new Date().toISOString()
      for (const file of fileList) {
        if (!(file instanceof File)) continue
        const fileId = crypto.randomUUID()
        const doc: BatchDocument = {
          batchId,
          fileId,
          s3Key: `batch/${batchId}/${fileId}/${file.name}`,
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          uploadedAt: now,
          status: 'pending_ocr',
        }
        mockBatchDocuments.push(doc)
        files.push(doc)
      }
      if (files.length === 0) throw new Error('At least one file is required')
      return { data: { batchId, files } as T }
    }

    if (url.startsWith('/batch-documents/') && !url.includes('/upload')) {
      const pathPart = url.replace('/batch-documents/', '')
      const fileId = pathPart.split('/')[0]

      if (pathPart.endsWith('/extract')) {
        const doc = mockBatchDocuments.find((d) => d.fileId === fileId)
        if (!doc) throw new Error('Batch document not found')
        const firstPatient = mockPatients[0]
        const updated: BatchDocument = {
          ...doc,
          extractedText: `Patient: ${firstPatient?.firstName ?? 'John'} ${firstPatient?.lastName ?? 'Doe'}\nDOB: ${firstPatient?.dateOfBirth ?? '1990-01-01'}`,
          status: firstPatient ? 'suggested' : 'unmatched',
          suggestedPatientId: firstPatient?.id,
          suggestedPatientName: firstPatient ? `${firstPatient.firstName} ${firstPatient.lastName}` : undefined,
        }
        const idx = mockBatchDocuments.findIndex((d) => d.fileId === fileId)
        if (idx !== -1) mockBatchDocuments[idx] = updated
        return { data: updated as T }
      }

      if (pathPart.endsWith('/accept-match')) {
        const doc = mockBatchDocuments.find((d) => d.fileId === fileId)
        if (!doc) throw new Error('Batch document not found')
        const patientId = (data as { patientId?: string })?.patientId
        if (!patientId) throw new Error('patientId is required')
        if (doc.suggestedPatientId && doc.suggestedPatientId !== patientId) {
          throw new Error('patientId does not match suggested patient')
        }
        const newPatientFile: PatientFile = {
          id: doc.fileId,
          patientId,
          fileName: doc.fileName,
          fileType: doc.fileType,
          fileSize: doc.fileSize,
          s3Key: `${patientId}/${doc.fileId}/${doc.fileName}`,
          source: 'upload',
          uploadedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        mockPatientFiles.push(newPatientFile)
        const idx = mockBatchDocuments.findIndex((d) => d.fileId === fileId)
        if (idx !== -1) mockBatchDocuments.splice(idx, 1)
        return { data: newPatientFile as T }
      }

      if (pathPart.endsWith('/link')) {
        const doc = mockBatchDocuments.find((d) => d.fileId === fileId)
        if (!doc) throw new Error('Batch document not found')
        const patientId = (data as { patientId?: string })?.patientId
        if (!patientId) throw new Error('patientId is required')
        const newPatientFile: PatientFile = {
          id: doc.fileId,
          patientId,
          fileName: doc.fileName,
          fileType: doc.fileType,
          fileSize: doc.fileSize,
          s3Key: `${patientId}/${doc.fileId}/${doc.fileName}`,
          source: 'upload',
          uploadedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        mockPatientFiles.push(newPatientFile)
        const idx = mockBatchDocuments.findIndex((d) => d.fileId === fileId)
        if (idx !== -1) mockBatchDocuments.splice(idx, 1)
        return { data: newPatientFile as T }
      }
    }

    if (url.startsWith('/billing')) {
      const segments = url.split('?')[0].split('/').filter(Boolean)

      if (segments[1] === 'accounts') {
        const now = new Date().toISOString()
        const account: BillingAccount = {
          id: crypto.randomUUID(),
          patientId: data.patientId,
          status: data.status || 'active',
          currency: data.currency || 'EUR',
          balance: 0,
          metadata: data.metadata,
          createdAt: now,
          updatedAt: now,
        }
        mockBillingAccounts.push(account)
        return { data: account as T }
      }

      if (segments[1] === 'items') {
        const now = new Date().toISOString()
        let accountId = data.accountId
        if (!accountId && data.patientId) {
          const account = mockBillingAccounts.find((entry) => entry.patientId === data.patientId)
          if (account) {
            accountId = account.id
          } else {
            const newAccount: BillingAccount = {
              id: crypto.randomUUID(),
              patientId: data.patientId,
              status: 'active',
              currency: data.currency || 'EUR',
              balance: 0,
              createdAt: now,
              updatedAt: now,
            }
            mockBillingAccounts.push(newAccount)
            accountId = newAccount.id
          }
        }

        const item: BillableItem = {
          id: crypto.randomUUID(),
          patientId: data.patientId,
          accountId,
          consultationId: data.consultationId,
          description: data.description || '',
          amount: data.amount || 0,
          currency: data.currency || 'EUR',
          status: data.status || 'pending',
          provider: data.provider || 'manual',
          providerItemId: data.providerItemId,
          metadata: data.metadata,
          createdAt: now,
          updatedAt: now,
        }
        mockBillableItems.push(item)
        const account = mockBillingAccounts.find((entry) => entry.id === accountId)
        if (account) {
          account.balance += item.amount
          account.updatedAt = now
        }
        return { data: item as T }
      }

      if (segments[1] === 'consultations' && segments[2] && segments[3] === 'items') {
        if (segments[4]) {
          const item = mockBillableItems.find((entry) => entry.id === segments[4])
          if (!item) throw new Error('Billable item not found')
          item.consultationId = segments[2]
          item.updatedAt = new Date().toISOString()
          return { data: item as T }
        }
        return await mockApi.post(`/billing/items`, { ...data, consultationId: segments[2] })
      }

      if (segments[1] === 'payments') {
        const now = new Date().toISOString()
        let accountId = data.accountId
        if (!accountId && data.patientId) {
          const account = mockBillingAccounts.find((entry) => entry.patientId === data.patientId)
          if (account) accountId = account.id
        }
        const payment: BillingPayment = {
          id: crypto.randomUUID(),
          patientId: data.patientId,
          accountId,
          amount: data.amount || 0,
          currency: data.currency || 'EUR',
          status: data.status || 'completed',
          provider: data.provider || 'manual',
          providerPaymentId: data.providerPaymentId,
          paymentDate: data.paymentDate || now,
          metadata: data.metadata,
          createdAt: now,
          updatedAt: now,
        }
        mockBillingPayments.push(payment)
        const account = mockBillingAccounts.find((entry) => entry.id === accountId)
        if (account && payment.status === 'completed') {
          account.balance -= payment.amount
          account.updatedAt = now
        }
        return { data: payment as T }
      }

      if (segments[1] === 'statements') {
        const now = new Date().toISOString()
        const statement: BillingStatement = {
          id: crypto.randomUUID(),
          patientId: data.patientId,
          accountId: data.accountId,
          statementDate: data.statementDate,
          periodStart: data.periodStart,
          periodEnd: data.periodEnd,
          totalAmount: data.totalAmount || 0,
          status: data.status || 'issued',
          itemIds: data.itemIds || [],
          metadata: data.metadata,
          createdAt: now,
          updatedAt: now,
        }
        mockBillingStatements.push(statement)
        return { data: statement as T }
      }

      if (segments[1] === 'payment-methods') {
        const now = new Date().toISOString()
        const method: BillingPaymentMethod = {
          id: crypto.randomUUID(),
          patientId: data.patientId,
          accountId: data.accountId,
          provider: data.provider || 'manual',
          providerToken: data.providerToken,
          providerMethodId: data.providerMethodId,
          last4: data.last4,
          brand: data.brand,
          expiresAt: data.expiresAt,
          metadata: data.metadata,
          createdAt: now,
          updatedAt: now,
        }
        mockBillingPaymentMethods.push(method)
        return { data: method as T }
      }
    }

    // Reports endpoints
    if (url === '/reports/patient' || url.startsWith('/reports/patient')) {
      const request = data as any
      const { patientId, format = 'json', includeLinked = false, maskPII = false } = request

      // Find patient
      const patient = mockPatients.find((p) => p.id === patientId)
      if (!patient) {
        throw new Error('Patient not found')
      }

      // Generate mock report data
      const reportData = {
        patient: maskPII ? { ...patient, firstName: '***', lastName: '***', email: '***', phone: '***' } : patient,
        consultations: mockConsultations.filter((c) => c.patientId === patientId),
        appointments: mockAppointments.filter((a) => a.patientId === patientId),
        prescriptions: [],
        labResults: [],
        vitalSigns: [],
        allergies: [],
        immunisations: [],
        medications: [],
        clinicalHistory: null,
        audit: [],
        metadata: {
          generatedAt: new Date().toISOString(),
          patientId,
          includeLinked,
        },
      }

      // Convert to requested format (simplified - return JSON string for blob)
      const reportContent = format === 'json'
        ? JSON.stringify(reportData, null, 2)
        : format === 'csv'
        ? 'Patient ID,First Name,Last Name\n' + `${patientId},${patient.firstName},${patient.lastName}\n`
        : format === 'xml'
        ? `<?xml version="1.0"?><report><patient><id>${patientId}</id></patient></report>`
        : JSON.stringify(reportData, null, 2)

      // Return as blob-like object
      return { data: new Blob([reportContent], { type: 'application/json' }) as T }
    }

    if (url === '/reports/aggregate' || url.startsWith('/reports/aggregate')) {
      const request = data as any
      const { format = 'json', filters = {}, limit = 1000 } = request

      // Generate mock aggregate report
      let patients = [...mockPatients]

      // Apply filters
      if (filters.demographics) {
        if (filters.demographics.gender) {
          patients = patients.filter((p) => p.gender === filters.demographics.gender)
        }
        if (filters.demographics.ageRange) {
          const { min, max } = filters.demographics.ageRange
          patients = patients.filter((p) => {
            const age = new Date().getFullYear() - new Date(p.dateOfBirth).getFullYear()
            return (!min || age >= min) && (!max || age <= max)
          })
        }
      }

      const reportData = {
        patients: patients.slice(0, limit),
        summary: {
          totalPatients: patients.length,
          totalRecords: patients.length,
        },
        metadata: {
          generatedAt: new Date().toISOString(),
          filters,
          limit,
        },
      }

      const reportContent = format === 'json'
        ? JSON.stringify(reportData, null, 2)
        : format === 'csv'
        ? 'Patient ID,First Name,Last Name\n' + patients.map((p) => `${p.id},${p.firstName},${p.lastName}`).join('\n')
        : format === 'xml'
        ? `<?xml version="1.0"?><report><patients>${patients.map((p) => `<patient><id>${p.id}</id></patient>`).join('')}</patients></report>`
        : JSON.stringify(reportData, null, 2)

      return { data: new Blob([reportContent], { type: 'application/json' }) as T }
    }

    if (url === '/reports/measurement-analysis' || url.startsWith('/reports/measurement-analysis')) {
      const request = data as any
      const { format = 'csv', filters = {}, fields = [] } = request
      const selectedFields = new Set(fields || [])
      const dateRange = filters.dateRange || {}
      const diagnosisFilters = Array.isArray(filters.diagnoses)
        ? filters.diagnoses.map((item: string) => item.toLowerCase())
        : []

      const filteredConsultations = mockConsultations.filter((consultation) => {
        if (dateRange.startDate && consultation.consultationDate < dateRange.startDate) {
          return false
        }
        if (dateRange.endDate && consultation.consultationDate > dateRange.endDate) {
          return false
        }

        if (diagnosisFilters.length > 0) {
          const diagnosisEntries = consultation.diagnosis || []
          return diagnosisEntries.some((entry) =>
            String(entry).toLowerCase().includes(diagnosisFilters[0])
          )
        }

        return true
      })

      const records: Record<string, any>[] = []
      filteredConsultations.forEach((consultation) => {
        const patient = mockPatients.find((p) => p.id === consultation.patientId)
        if (!patient) return
        const diagnosisEntries = Array.isArray(consultation.diagnosis) ? consultation.diagnosis : []
        diagnosisEntries.forEach((diagnosisEntry) => {
          const record: Record<string, any> = {
            patientId: `anon-${patient.id}`,
            age: new Date().getFullYear() - new Date(patient.dateOfBirth).getFullYear(),
            sex: patient.gender,
            gmsStatus: patient.gmsNumber ? 'gms' : 'unknown',
            county: patient.address?.state || '',
            postcode: patient.address?.postalCode || '',
            consultationDate: consultation.consultationDate,
            diagnosisCode: String(diagnosisEntry).split('-')[0]?.trim() || '',
            diagnosisText: String(diagnosisEntry).split('-')[1]?.trim() || '',
          }

          if (selectedFields.has('symptoms')) {
            record.symptoms = consultation.notes || ''
          }
          if (selectedFields.has('reasonForEncounter')) {
            record.reasonForEncounter = consultation.reason || ''
          }
          if (selectedFields.has('medicationsPrescribed')) {
            record.medicationsPrescribed = (mockPrescriptions || [])
              .filter((prescription) => prescription.patientId === patient.id)
              .flatMap((prescription) =>
                Array.isArray(prescription.medications)
                  ? prescription.medications
                      .map((medication) => medication?.name || '')
                      .filter(Boolean)
                  : [],
              )
          }
          if (selectedFields.has('procedures')) {
            record.procedures = []
          }
          if (selectedFields.has('treatments')) {
            record.treatments = consultation.plan || ''
          }
          if (selectedFields.has('investigations')) {
            record.investigations = []
          }
          if (selectedFields.has('referrals')) {
            record.referrals = []
          }
          if (selectedFields.has('testsOrdered')) {
            record.testsOrdered = []
          }
          if (selectedFields.has('outcomeMeasures')) {
            record.outcomeMeasures = []
          }
          if (selectedFields.has('staffId')) {
            record.staffId = consultation.staffId || ''
          }
          if (selectedFields.has('staffName')) {
            record.staffName = consultation.staffName || ''
          }
          if (selectedFields.has('staffRole')) {
            record.staffRole = ''
          }

          records.push(record)
        })
      })

      const reportContent = format === 'json'
        ? JSON.stringify(records, null, 2)
        : format === 'xml'
        ? `<?xml version="1.0"?><report>${records
            .map((record) => `<record>${Object.entries(record)
              .map(([key, value]) => `<${key}>${String(value)}</${key}>`)
              .join('')}</record>`)
            .join('')}</report>`
        : (() => {
            if (records.length === 0) return ''
            const headers = Object.keys(records[0])
            const rows = [headers.join(',')]
            records.forEach((record) => {
              const row = headers.map((key) => JSON.stringify(record[key] ?? ''))
              rows.push(row.join(','))
            })
            return rows.join('\n')
          })()

      return { data: new Blob([reportContent], { type: 'application/json' }) as T }
    }

    // POST /reports/data-portability - Data portability export endpoint (Admin only)
    if (url === '/reports/data-portability' || url.startsWith('/reports/data-portability')) {
      const request = data as any
      const { patientId, format = 'csv', includeFiles = true } = request

      // Generate mock report data for data portability
      let reportData: any
      let reportContent: string

      if (patientId) {
        // Individual patient export
        const patient = mockPatients.find((p) => p.id === patientId)
        if (!patient) {
          throw new Error('Patient not found')
        }

        // Get all related data for the patient
        const consultations = mockConsultations.filter((c) => c.patientId === patientId)
        const appointments = mockAppointments.filter((a) => a.patientId === patientId)
        const prescriptions = (mockPrescriptions || []).filter((p) => p.patientId === patientId)
        const labResults = mockLabResults.filter((r) => r.patientId === patientId)
        const vitalSigns = mockVitalSigns.filter((v) => v.patientId === patientId)
        const allergies: any[] = [] // Mock allergies not available
        const immunisations: any[] = [] // Mock immunisations not available
        const medications = (mockPatientMedications || []).filter((m) => m.patientId === patientId)
        const patientFiles = includeFiles ? mockPatientFiles.filter((f) => f.patientId === patientId) : []
        const audit: AuditEntry[] = [] // Mock audit trail

        reportData = {
          patient,
          consultations,
          appointments,
          prescriptions,
          labResults,
          vitalSigns,
          allergies,
          immunisations,
          medications,
          patientFiles,
          audit,
          metadata: {
            generatedAt: new Date().toISOString(),
            patientId,
            format,
            includeFiles,
          },
        }
      } else {
        // Practice population export
        reportData = {
          patients: mockPatients,
          consultations: mockConsultations,
          appointments: mockAppointments,
          prescriptions: mockPrescriptions || [],
          labResults: mockLabResults,
          vitalSigns: mockVitalSigns,
          allergies: [], // Mock allergies not available
          immunisations: [], // Mock immunisations not available
          medications: mockPatientMedications || [],
          patientFiles: includeFiles ? mockPatientFiles : [],
          audit: [],
          metadata: {
            generatedAt: new Date().toISOString(),
            format,
            includeFiles,
            totalPatients: mockPatients.length,
          },
        }
      }

      // Convert to requested format
      if (format === 'csv') {
        // Generate CSV format
        if (patientId) {
          // Single patient CSV
          const lines: string[] = []
          lines.push('Section,Field,Value')
          lines.push(`Patient,ID,${reportData.patient.id}`)
          lines.push(`Patient,First Name,${reportData.patient.firstName}`)
          lines.push(`Patient,Last Name,${reportData.patient.lastName}`)
          lines.push(`Patient,Date of Birth,${reportData.patient.dateOfBirth}`)
          lines.push(`Consultations,Count,${reportData.consultations.length}`)
          lines.push(`Appointments,Count,${reportData.appointments.length}`)
          lines.push(`Prescriptions,Count,${reportData.prescriptions.length}`)
          lines.push(`Lab Results,Count,${reportData.labResults.length}`)
          lines.push(`Patient Files,Count,${reportData.patientFiles.length}`)
          lines.push(`Audit Entries,Count,${reportData.audit.length}`)
          reportContent = lines.join('\n')
        } else {
          // Practice-wide CSV
          const lines: string[] = []
          lines.push('Patient ID,First Name,Last Name,Date of Birth,Consultations,Appointments,Prescriptions')
          reportData.patients.forEach((patient: any) => {
            const consultations = reportData.consultations.filter((c: any) => c.patientId === patient.id).length
            const appointments = reportData.appointments.filter((a: any) => a.patientId === patient.id).length
            const prescriptions = reportData.prescriptions.filter((p: any) => p.patientId === patient.id).length
            lines.push(`${patient.id},${patient.firstName},${patient.lastName},${patient.dateOfBirth},${consultations},${appointments},${prescriptions}`)
          })
          reportContent = lines.join('\n')
        }
      } else if (format === 'text') {
        // Generate text format
        const lines: string[] = []
        lines.push('DATA PORTABILITY EXPORT')
        lines.push('='.repeat(50))
        lines.push(`Generated: ${new Date().toISOString()}`)
        lines.push(`Format: ${format}`)
        lines.push('')

        if (patientId) {
          lines.push('PATIENT INFORMATION')
          lines.push('-'.repeat(50))
          lines.push(`ID: ${reportData.patient.id}`)
          lines.push(`Name: ${reportData.patient.firstName} ${reportData.patient.lastName}`)
          lines.push(`Date of Birth: ${reportData.patient.dateOfBirth}`)
          lines.push('')
          lines.push(`Consultations: ${reportData.consultations.length}`)
          lines.push(`Appointments: ${reportData.appointments.length}`)
          lines.push(`Prescriptions: ${reportData.prescriptions.length}`)
          lines.push(`Lab Results: ${reportData.labResults.length}`)
          lines.push(`Patient Files: ${reportData.patientFiles.length}`)
          lines.push(`Audit Entries: ${reportData.audit.length}`)
        } else {
          lines.push('PRACTICE POPULATION EXPORT')
          lines.push('-'.repeat(50))
          lines.push(`Total Patients: ${reportData.patients.length}`)
          lines.push(`Total Consultations: ${reportData.consultations.length}`)
          lines.push(`Total Appointments: ${reportData.appointments.length}`)
          lines.push(`Total Prescriptions: ${reportData.prescriptions.length}`)
        }

        reportContent = lines.join('\n')
      } else {
        // Default to JSON
        reportContent = JSON.stringify(reportData, null, 2)
      }

      // Determine content type
      const contentType = format === 'csv' ? 'text/csv' : format === 'text' ? 'text/plain' : 'application/json'

      // Return as blob with appropriate content type
      return {
        data: new Blob([reportContent], { type: contentType }) as T,
      }
    }

    // Autocomplete endpoint for consultation notes - check early
    if (url === '/notes/autocomplete' || url.startsWith('/notes/autocomplete')) {
      const request = data as any
      const typedPrefix = (request?.typedPrefix || '').toLowerCase().trim()
      const sectionType = request?.sectionType || 'notes'
      
      console.log('[Mock API] Autocomplete request:', {
        url,
        typedPrefix,
        sectionType,
        prefixLength: typedPrefix.length,
        requestData: request,
      })
      
      // Mock suggestions - return sample terminology, phrase, and template suggestions
      const suggestions: any[] = []
      
      // Terminology suggestions (mock ICPC codes)
      if (typedPrefix.length >= 2) {
        const mockTerminology = [
          { code: 'D01', display: 'Abdominal pain', system: 'ICPC2' },
          { code: 'R05', display: 'Cough', system: 'ICPC2' },
          { code: 'A01', display: 'Pain general', system: 'ICPC2' },
          { code: 'A04', display: 'Weakness/tiredness general', system: 'ICPC2' },
          { code: 'K85', display: 'Hypertension', system: 'ICPC2' },
          { code: 'T90', display: 'Diabetes non-insulin dependent', system: 'ICPC2' },
          { code: 'D02', display: 'Abdominal pain epigastric', system: 'ICPC2' },
          { code: 'R02', display: 'Shortness of breath', system: 'ICPC2' },
        ]
        
        const matchingTerms = mockTerminology.filter((item) => {
          const displayMatch = item.display.toLowerCase().startsWith(typedPrefix) || item.display.toLowerCase().includes(typedPrefix)
          const codeMatch = item.code.toLowerCase().startsWith(typedPrefix) || item.code.toLowerCase().includes(typedPrefix)
          return displayMatch || codeMatch
        })
        
        console.log('[Mock API] Matching terminology:', matchingTerms.length, 'for prefix:', typedPrefix)
        
        matchingTerms.slice(0, 5).forEach((item) => {
          suggestions.push({
            type: 'terminology',
            preview_text: item.display,
            full_text: item.display,
            confidence: 0.9,
            code_refs: [
              {
                system: item.system,
                code: item.code,
                display: item.display,
              },
            ],
          })
        })
      }
      
      // Phrase suggestions
      if (typedPrefix.length >= 2) {
        const mockPhrases = [
          { trigger: 'normal cardiovascular', text: 'Normal cardiovascular examination. Heart sounds S1 and S2 present, no murmurs, rubs, or gallops. No peripheral oedema.' },
          { trigger: 'normal respiratory', text: 'Normal respiratory examination. Chest expansion symmetrical, breath sounds vesicular throughout, no wheeze or crackles.' },
          { trigger: 'safety net', text: 'Discussed safety-netting: advised to return or seek urgent care if symptoms worsen, new red-flag symptoms develop, or no improvement over the next few days.' },
          { trigger: 'normal', text: 'Normal examination findings.' },
        ]
        
        const matchingPhrases = mockPhrases.filter((item) => {
          return item.trigger.toLowerCase().startsWith(typedPrefix) || item.trigger.toLowerCase().includes(typedPrefix)
        })
        
        console.log('[Mock API] Matching phrases:', matchingPhrases.length, 'for prefix:', typedPrefix)
        
        matchingPhrases.forEach((item) => {
          suggestions.push({
            type: 'phrase',
            preview_text: item.trigger,
            full_text: item.text,
            confidence: 0.6,
          })
        })
      }
      
      // Custom phrases suggestions (user's saved phrases)
      // Allow single character matches for short triggers like "no"
      if (typedPrefix.length >= 1) {
        // Reload from localStorage to ensure we have the latest data
        const latestPhrases = loadCustomPhrases()
        if (latestPhrases.length !== mockCustomPhrases.length) {
          console.log('[Mock API] Reloading custom phrases from localStorage (count changed:', mockCustomPhrases.length, '->', latestPhrases.length, ')')
          mockCustomPhrases = latestPhrases
        }
        
        const currentUserId = mockApi.getCurrentUserId() || '1'
        const typedLower = typedPrefix.toLowerCase()
        
        console.log('[Mock API] Searching custom phrases for user:', currentUserId, 'prefix:', typedLower)
        console.log('[Mock API] Total phrases in memory:', mockCustomPhrases.length)
        console.log('[Mock API] Phrases for current user:', mockCustomPhrases.filter(p => p.userId === currentUserId).length)
        console.log('[Mock API] Available custom phrases:', mockCustomPhrases.filter(p => p.userId === currentUserId).map(p => ({ trigger: p.trigger, text: p.text.slice(0, 30) })))
        
        const matchingCustomPhrases = mockCustomPhrases
          .filter((p) => p.userId === currentUserId)
          .map((p) => {
            const triggerLower = (p.trigger || '').toLowerCase()
            const textLower = (p.text || '').toLowerCase()
            
            // Calculate match score: exact match = highest, starts with = high, contains = lower
            let matchScore = 0
            let matches = false
            
            if (triggerLower === typedLower) {
              matchScore = 100 // Exact trigger match
              matches = true
            } else if (triggerLower.startsWith(typedLower)) {
              matchScore = 50 // Starts with
              matches = true
            } else if (triggerLower.includes(typedLower)) {
              matchScore = 25 // Contains
              matches = true
            } else if (textLower.includes(typedLower)) {
              matchScore = 10 // Text contains
              matches = true
            }
            
            return { phrase: p, matchScore, matches }
          })
          .filter((item) => item.matches)
          .sort((a, b) => {
            // Sort by match score (highest first)
            if (a.matchScore !== b.matchScore) return b.matchScore - a.matchScore
            // Then by usage count
            const aUsage = a.phrase.usageCount || 0
            const bUsage = b.phrase.usageCount || 0
            if (aUsage !== bUsage) return bUsage - aUsage
            // Then alphabetically
            return (a.phrase.trigger || a.phrase.text || '').localeCompare(b.phrase.trigger || b.phrase.text || '')
          })
          .map((item) => item.phrase)
        
        console.log('[Mock API] Matching custom phrases:', matchingCustomPhrases.length, 'for prefix:', typedLower)
        matchingCustomPhrases.forEach(p => console.log('[Mock API] - Matched:', p.trigger, '->', p.text.slice(0, 50)))
        
        matchingCustomPhrases.slice(0, 5).forEach((p) => {
          suggestions.push({
            type: 'phrase',
            preview_text: p.trigger || p.text,
            full_text: p.text,
            confidence: 0.8,
            custom_phrase_id: p.id,
            metadata: {
              sectionType: p.sectionType,
              usageCount: p.usageCount || 0,
            },
          })
        })
      }
      
      // Template suggestions
      if (typedPrefix.length >= 2) {
        const matchingTemplates = mockTemplates
          .filter((t) => t.isActive && !t.isArchived)
          .filter((t) => {
            const nameMatch = t.name.toLowerCase().startsWith(typedPrefix) || t.name.toLowerCase().includes(typedPrefix)
            const descMatch = t.description?.toLowerCase().includes(typedPrefix)
            return nameMatch || descMatch
          })
        
        console.log('[Mock API] Matching templates:', matchingTemplates.length, 'for prefix:', typedPrefix)
        
        matchingTemplates.slice(0, 3).forEach((t) => {
          suggestions.push({
            type: 'template',
            preview_text: t.name,
            full_text: t.name,
            confidence: 0.7,
            template_id: t.id,
            metadata: {
              category: t.category,
              description: t.description,
            },
          })
        })
      }
      
      // Sort suggestions by confidence (highest first), then by type priority
      suggestions.sort((a, b) => {
        const aConf = a.confidence || 0
        const bConf = b.confidence || 0
        if (aConf !== bConf) return bConf - aConf
        
        // Type priority: terminology > custom phrase > built-in phrase > template
        const typePriority: Record<string, number> = {
          terminology: 4,
          phrase: a.custom_phrase_id ? 3 : 2, // Custom phrases rank higher
          template: 1,
        }
        const aPriority = typePriority[a.type] || 0
        const bPriority = typePriority[b.type] || 0
        if (aPriority !== bPriority) return bPriority - aPriority
        
        return 0
      })
      
      console.log('[Mock API] Returning', suggestions.length, 'suggestions (sorted by confidence)')
      console.log('[Mock API] Suggestion types:', suggestions.map(s => ({ type: s.type, confidence: s.confidence, preview: s.preview_text.slice(0, 30) })))
      
      return { data: { suggestions } as T }
    }

    // POST /patients/{id}/duplicates/ignore - Ignore duplicate suggestion
    if (url.startsWith('/patients/') && url.includes('/duplicates/ignore')) {
      const id = url.split('/patients/')[1].split('/duplicates/ignore')[0]
      const { ignoredPatientId } = data || {}

      if (!id || !ignoredPatientId) {
        throw new Error('Patient ID and ignored patient ID are required')
      }

      if (id === ignoredPatientId) {
        throw new Error('Cannot ignore the same patient record')
      }

      const pairKey = buildDuplicatePairKey(id, ignoredPatientId)
      const existing = mockDuplicateIgnores.find((entry) => entry.pairKey === pairKey)
      if (!existing) {
        const [patientIdA, patientIdB] = [id, ignoredPatientId].sort()
        mockDuplicateIgnores.push({
          pairKey,
          patientIdA,
          patientIdB,
          ignoredAt: new Date().toISOString(),
        })
      }

      return { data: { message: 'Duplicate suggestion ignored' } as T }
    }

    // POST /patients/{id}/link - Link patients together
    if (url.startsWith('/patients/') && url.includes('/link')) {
      console.log('[Mock API] Link endpoint called:', { url, data, dataType: typeof data, config })
      const id = url.split('/patients/')[1].split('/link')[0]
      const { secondaryPatientId, setAsPrimary } = data || {}
      
      console.log('[Mock API] Parsed link request:', { id, secondaryPatientId, setAsPrimary })
      
      if (!id || !secondaryPatientId) {
        console.error('[Mock API] Link validation failed:', { id, secondaryPatientId, data })
        throw new Error(`Patient ID and secondary patient ID are required. Got: id=${id}, secondaryPatientId=${secondaryPatientId}`)
      }

      if (id === secondaryPatientId) {
        throw new Error('Cannot link a patient to itself')
      }

      const patient1 = mockPatients.find((p) => p.id === id)
      const patient2 = mockPatients.find((p) => p.id === secondaryPatientId)
      
      if (!patient1 || !patient2) {
        throw new Error('One or both patients not found')
      }

      // Handle backward compatibility
      const patient1IsPrimary = patient1.isPrimary !== undefined ? patient1.isPrimary : true
      const patient2IsPrimary = patient2.isPrimary !== undefined ? patient2.isPrimary : true

      // Determine which becomes primary (matching backend logic)
      let primaryId, secondaryId
      if (setAsPrimary) {
        // Make patient1 the primary
        primaryId = id
        secondaryId = secondaryPatientId
      } else if (patient1IsPrimary && !patient2IsPrimary) {
        // patient1 is already primary, patient2 is secondary
        primaryId = id
        secondaryId = secondaryPatientId
      } else if (!patient1IsPrimary && patient2IsPrimary) {
        // patient2 is already primary, patient1 is secondary
        primaryId = secondaryPatientId
        secondaryId = id
      } else if (patient1IsPrimary && patient2IsPrimary) {
        // Both are primary - make patient1 the primary
        primaryId = id
        secondaryId = secondaryPatientId
      } else {
        // Neither is primary - make patient1 the primary
        primaryId = id
        secondaryId = secondaryPatientId
      }

      const primary = mockPatients.find((p) => p.id === primaryId)!
      const secondary = mockPatients.find((p) => p.id === secondaryId)!

      // Check if already linked
      const existingLinkedIds = primary.linkedPatientIds || []
      if (existingLinkedIds.includes(secondaryId)) {
        throw new Error('Patients are already linked')
      }

      // Check if secondary is already linked to a different primary
      if (secondary.primaryPatientId && secondary.primaryPatientId !== primaryId) {
        throw new Error('Secondary patient is already linked to a different primary')
      }

      // Update primary - add secondary to linkedPatientIds
      primary.linkedPatientIds = [...existingLinkedIds, secondaryId]
      primary.isPrimary = true
      primary.updatedAt = new Date().toISOString()

      // Update secondary - set primaryPatientId and isPrimary
      secondary.primaryPatientId = primaryId
      secondary.isPrimary = false
      secondary.updatedAt = new Date().toISOString()

      console.log('[Mock API] Patients linked successfully:', { primaryId, secondaryId })
      return { data: { message: 'Patients linked successfully' } as T }
    }

    // POST /patients/{id}/unlink - Unlink a patient record
    if (url.startsWith('/patients/') && url.includes('/unlink')) {
      const id = url.split('/patients/')[1].split('/unlink')[0]
      const { linkedPatientId } = data || {}
      
      if (!linkedPatientId) {
        throw new Error('Linked patient ID is required')
      }

      const primary = mockPatients.find((p) => p.id === id)
      if (!primary) {
        throw new Error('Patient not found')
      }

      const isPrimary = primary.isPrimary ?? true
      if (!isPrimary) {
        throw new Error('Only primary records can unlink secondary records')
      }

      const linkedIds = primary.linkedPatientIds || []
      if (!linkedIds.includes(linkedPatientId)) {
        throw new Error('Patients are not linked')
      }

      // Update primary
      primary.linkedPatientIds = linkedIds.filter((lid) => lid !== linkedPatientId)
      if (primary.linkedPatientIds.length === 0) {
        primary.linkedPatientIds = undefined
      }

      // Update secondary
      const secondary = mockPatients.find((p) => p.id === linkedPatientId)!
      secondary.primaryPatientId = undefined
      secondary.isPrimary = true
      secondary.updatedAt = new Date().toISOString()

      return { data: { message: 'Patients unlinked successfully' } as T }
    }

    // Custom phrases endpoints
    if (url === '/custom-phrases' || url.startsWith('/custom-phrases')) {
      const currentUserId = mockApi.getCurrentUserId() || '1'
      
      // POST /custom-phrases - check this FIRST before GET
      // POST requests have data, GET requests don't (for this endpoint)
      if (data && (!config || config.method !== 'GET')) {
        const phraseData = data as any
      if (!phraseData.text || !phraseData.text.trim()) {
        throw new Error('Phrase text is required')
      }
      
      const now = new Date().toISOString()
      const triggerText = (phraseData.trigger?.trim() || phraseData.text.trim().slice(0, 50)).toLowerCase()
      const newPhrase = {
        id: crypto.randomUUID(),
        userId: currentUserId,
        text: phraseData.text.trim(),
        trigger: triggerText,
        sectionType: phraseData.sectionType || 'notes',
        category: phraseData.category || 'custom',
        usageCount: 0,
        createdAt: now,
        updatedAt: now,
      }
      
      console.log('[Mock API] Creating custom phrase:', {
        userId: currentUserId,
        trigger: newPhrase.trigger,
        textPreview: newPhrase.text.slice(0, 50),
        textLength: newPhrase.text.length,
        sectionType: newPhrase.sectionType,
      })
      
      mockCustomPhrases.push(newPhrase)
      saveCustomPhrases(mockCustomPhrases)
      
      // Verify it was saved
      const saved = loadCustomPhrases()
      console.log('[Mock API] Verified save - phrases in localStorage:', saved.length)
      console.log('[Mock API] Saved phrase in localStorage:', saved.find(p => p.id === newPhrase.id) ? 'YES' : 'NO')
      
      const userPhraseCount = mockCustomPhrases.filter(p => p.userId === currentUserId).length
      console.log('[Mock API] Total custom phrases for user:', userPhraseCount)
      console.log('[Mock API] All user phrases:', mockCustomPhrases.filter(p => p.userId === currentUserId).map(p => p.trigger))
      
      return { data: newPhrase as T }
      }
      
      // GET /custom-phrases
      if (config?.method === 'GET' || !config) {
        const sectionType = config?.params?.sectionType
        let phrases = mockCustomPhrases.filter((p) => p.userId === currentUserId)
        
        if (sectionType) {
          phrases = phrases.filter((p) => p.sectionType === sectionType || !p.sectionType)
        }
        
        phrases.sort((a, b) => {
          const aUsage = a.usageCount || 0
          const bUsage = b.usageCount || 0
          if (aUsage !== bUsage) return bUsage - aUsage
          return (a.trigger || a.text || '').localeCompare(b.trigger || b.text || '')
        })
        
        return { data: phrases as T }
      }
    }
    
    // GET /custom-phrases/{id}
    if (url.startsWith('/custom-phrases/') && (config?.method === 'GET' || !config)) {
      const id = url.split('/custom-phrases/')[1]?.split('?')[0]
      const currentUserId = mockApi.getCurrentUserId() || '1'
      const phrase = mockCustomPhrases.find((p) => p.id === id && p.userId === currentUserId)
      
      if (!phrase) {
        throw new Error('Phrase not found')
      }
      
      return { data: phrase as T }
    }

    // Template endpoints - check generate first (before other template routes)
    if (url === '/templates/upload/init') {
      const { fileName, fileSize, fileType } = data || {}
      if (!fileName || !fileSize || !fileType) {
        throw new Error('fileName, fileSize, and fileType are required')
      }

      const templateId = crypto.randomUUID()
      const s3Key = `${TEMPLATE_S3_PREFIX}${templateId}/${fileName}`
      mockTemplateFiles[s3Key] = letterTemplateOttBase64

      return {
        data: {
          templateId,
          s3Key,
          uploadUrl: 'mock://s3-upload',
          fields: {},
        } as T,
      }
    }
    if (url === '/templates/generate' || url.startsWith('/templates/generate')) {
      console.log('[Mock API] ✓ Matched /templates/generate route')
      console.log('[Mock API] Template generate request:', { url, data })
      const request = data as DocumentGenerationRequest
      if (!request) {
        console.error('[Mock API] No request data provided')
        throw new Error('Request data is required')
      }
      if (!request.templateId || !request.patientId) {
        console.error('[Mock API] Missing required fields:', { templateId: request.templateId, patientId: request.patientId })
        throw new Error('templateId and patientId are required')
      }
      const template = mockTemplates.find((t) => t.id === request.templateId)
      const patient = mockPatients.find((p) => p.id === request.patientId)
      if (!template) {
        console.error('[Mock API] Template not found:', request.templateId, 'Available templates:', mockTemplates.map(t => t.id))
        throw new Error(`Template not found: ${request.templateId}`)
      }
      if (!patient) {
        console.error('[Mock API] Patient not found:', request.patientId, 'Available patients:', mockPatients.map(p => p.id))
        throw new Error(`Patient not found: ${request.patientId}`)
      }
      const templateContent = mockTemplateFiles[template.s3Key]
      console.log('[Mock API] Generating document for template:', template.name, 'patient:', patient.firstName, patient.lastName)
      console.log('[Mock API] Template content available:', templateContent ? 'Yes (length: ' + templateContent.length + ')' : 'No')
      
      // Validate template content
      if (!templateContent || templateContent.trim().length === 0) {
        console.error('[Mock API] Template content is empty or missing!')
        throw new Error('Template content is missing. Please update the template with a valid file.')
      }
      
      const referralId = request.referralId
      const consultationId = request.consultationId
      const referral = referralId ? mockReferrals.find((r) => r.id === referralId) : undefined
      const consultation = consultationId ? mockConsultations.find((c) => c.id === consultationId) : undefined
      if (referralId && !referral) {
        console.warn('[Mock API] Referral not found for template context:', referralId)
      }
      if (consultationId && !consultation) {
        console.warn('[Mock API] Consultation not found for template context:', consultationId)
      }

      // Build template context
      const context = buildTemplateContext(patient, referral, consultation)
      const customVariables = request.customVariables || {}
      if (customVariables && typeof customVariables === 'object') {
        if (customVariables.patient) {
          context.patient = { ...context.patient, ...customVariables.patient }
        }
        if (customVariables.referral) {
          context.referral = { ...context.referral, ...customVariables.referral }
        }
        if (customVariables.consultation) {
          context.consultation = { ...context.consultation, ...customVariables.consultation }
        }
        Object.keys(customVariables).forEach((key) => {
          if (key !== 'patient' && key !== 'referral' && key !== 'consultation') {
            context[key] = customVariables[key]
          }
        })
      }
      console.log('[Mock API] Template context:', JSON.stringify(context, null, 2))
      
      // Process OpenOffice template and populate variables
      let populatedContent: string
      try {
        console.log('[Mock API] Processing template with context...')
        console.log('[Mock API] Using template content (first 100 chars):', templateContent.substring(0, 100))
        populatedContent = await processOpenOfficeTemplate(templateContent, context)
        console.log('[Mock API] Template processing completed successfully')
      } catch (error) {
        console.error('[Mock API] Error processing template:', error)
        throw error // Don't fallback - let the error bubble up so user knows what went wrong
      }
      
      // Generate a populated OpenOffice document response (.odt)
      const fileName = `${template.name.replace(/[^a-z0-9]/gi, '_')}_${patient.id}_${Date.now()}.odt`
      const mimeType = 'application/vnd.oasis.opendocument.text'
      const response: DocumentGenerationResponse = {
        documentId: crypto.randomUUID(),
        fileName,
        mimeType,
        content: populatedContent,
        generatedAt: new Date().toISOString(),
      }
      return { data: response as T }
    }

    if (url === '/templates/preview') {
      const { templateId, sampleData } = data || {}
      if (!templateId) {
        throw new Error('templateId is required')
      }
      const template = mockTemplates.find((t) => t.id === templateId)
      if (!template) {
        throw new Error('Template not found')
      }
      
      console.log('[Mock API] Generating preview for template:', template.name)
      
      // Create sample patient data for preview
      const samplePatient: Patient = sampleData?.patientId 
        ? (mockPatients.find(p => p.id === sampleData.patientId) || mockPatients[0])
        : mockPatients[0] || {
            id: 'preview-1',
            patientNumber: 'PAT-PREVIEW',
            firstName: 'John',
            lastName: 'Doe',
            dateOfBirth: '1990-01-15',
            gender: 'male',
            email: 'john.doe@example.com',
            phone: '555-1234',
            isActive: true,
            isArchived: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }
      
      // Build template context with sample patient
      const context = buildTemplateContext(samplePatient)
      console.log('[Mock API] Preview context:', JSON.stringify(context, null, 2))
      
      const templateContent = mockTemplateFiles[template.s3Key] || letterTemplateOttBase64

      // Process template with sample data
      let populatedContent: string
      try {
        console.log('[Mock API] Processing template for preview...')
        populatedContent = await processOpenOfficeTemplate(templateContent, context)
        console.log('[Mock API] Preview processing completed successfully')
      } catch (error) {
        console.error('[Mock API] Error processing preview:', error)
        throw error
      }
      
      // Return base64 content as blob data
      // The API service expects a Blob, so we'll return the base64 string
      // and the service will convert it
      return { data: populatedContent as T }
    }

    if (url === '/templates') {
      const templateData: TemplateFormData = data
      const now = new Date().toISOString()

      if (!templateData.s3Key || !templateData.fileName || !templateData.fileSize) {
        throw new Error('s3Key, fileName, and fileSize are required')
      }
      if (!templateData.s3Key.startsWith(TEMPLATE_S3_PREFIX)) {
        throw new Error(`s3Key must start with ${TEMPLATE_S3_PREFIX}`)
      }

      const templateContent = mockTemplateFiles[templateData.s3Key] || letterTemplateOttBase64
      
      // Auto-extract variables from template content if not provided
      let variables = templateData.variables || []
      if (variables.length === 0 && templateContent) {
        try {
          const extracted = await extractVariablesFromContent(templateContent, true)
          variables = await enrichVariables(extracted)
          console.log('[Mock API] Auto-extracted', extracted.length, 'variables from template')
        } catch (error) {
          console.error('[Mock API] Error auto-extracting variables:', error)
        }
      }
      
      const newTemplate: WordTemplate = {
        id: templateData.templateId || crypto.randomUUID(),
        name: templateData.name,
        description: templateData.description,
        category: templateData.category,
        format: templateData.format || 'ott',
        mimeType: templateData.mimeType || 'application/vnd.oasis.opendocument.text-template',
        s3Key: templateData.s3Key,
        fileName: templateData.fileName,
        fileSize: templateData.fileSize,
        variables,
        isActive: true,
        isArchived: false,
        createdAt: now,
        updatedAt: now,
      }
      mockTemplateFiles[newTemplate.s3Key] = templateContent
      mockTemplates.push(newTemplate)
      return { data: newTemplate as T }
    }

    // Lab Results endpoints - check for review routes first
    if (url.startsWith('/lab-results/') && url.includes('/review') && !url.includes('/review-multiple')) {
      // POST /lab-results/:id/review
      const id = url.split('/lab-results/')[1].split('/review')[0]
      const result = mockLabResults.find((r) => r.id === id)
      if (!result) {
        throw new Error('Lab result not found')
      }
      const currentUser = mockUsers[0] // In real app, get from auth context
      result.reviewStatus = 'reviewed'
      result.reviewedAt = new Date().toISOString()
      result.reviewedBy = currentUser.id
      result.reviewedByName = `${currentUser.firstName} ${currentUser.lastName}`
      result.updatedAt = new Date().toISOString()
      return { data: result as T }
    }

    if (url === '/lab-results/review-multiple' || url.startsWith('/lab-results/review-multiple')) {
      // POST /lab-results/review-multiple
      const { ids } = data || {}
      if (!ids || !Array.isArray(ids)) {
        throw new Error('Invalid request: ids array required')
      }
      const currentUser = mockUsers[0] // In real app, get from auth context
      const reviewedResults: LabResult[] = []
      ids.forEach((id: string) => {
        const result = mockLabResults.find((r) => r.id === id)
        if (result) {
          result.reviewStatus = 'reviewed'
          result.reviewedAt = new Date().toISOString()
          result.reviewedBy = currentUser.id
          result.reviewedByName = `${currentUser.firstName} ${currentUser.lastName}`
          result.updatedAt = new Date().toISOString()
          reviewedResults.push(result)
        }
      })
      return { data: reviewedResults as T }
    }

    // Lab Results endpoints - check for sync first
    if (url === '/lab-results/sync-healthlink' || url.startsWith('/lab-results/sync-healthlink')) {
      const { patientId } = data || {}
      // Simulate syncing from Healthlink - generate some new results
      const newResults: LabResult[] = []
      const now = new Date()
      
      // Generate a random lab result
      const result: LabResult = {
        id: crypto.randomUUID(),
        patientId: patientId || '1',
        type: 'blood',
        orderNumber: `ORD-${now.getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
        accessionNumber: `ACC-${String(Math.floor(Math.random() * 100000)).padStart(5, '0')}`,
        labName: 'Beaumont Hospital Laboratory',
        labCode: 'BEAU',
        orderingPhysicianId: '2',
        orderingPhysicianName: 'Dr. Sarah O\'Brien',
        collectionDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        collectionTime: '09:00',
        receivedDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reportedDate: now.toISOString().split('T')[0],
        reportedTime: now.toTimeString().slice(0, 5),
        status: 'final',
        reviewStatus: 'in-review',
        clinicalIndication: 'Routine monitoring',
        isAbnormal: false,
        isCritical: false,
        panels: [
          {
            id: crypto.randomUUID(),
            panelCode: 'FBC',
            panelName: 'Full Blood Count',
            tests: [
              {
                id: crypto.randomUUID(),
                testCode: 'CBC-WBC',
                testName: 'White Blood Cell Count',
                value: 6.8,
                unit: '×10⁹/L',
                referenceRange: '4.0-11.0',
                flag: 'normal',
                status: 'final',
              },
              {
                id: crypto.randomUUID(),
                testCode: 'CBC-HGB',
                testName: 'Haemoglobin',
                value: 14.2,
                unit: 'g/dL',
                referenceRange: 'Male: 13.5-17.5, Female: 12.0-15.5',
                flag: 'normal',
                status: 'final',
              },
            ],
          },
        ],
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        source: 'healthlink',
        healthlinkMessageId: `HL-${now.getFullYear()}-${String(Math.floor(Math.random() * 1000000)).padStart(6, '0')}`,
      }
      mockLabResults.push(result)
      newResults.push(result)
      
      return { data: { count: newResults.length, results: newResults } as T }
    }

    if (url === '/gms/check-eligibility') {
      const { schemeId, patientId } = data || {}
      const result = await mockApi.checkEligibility(schemeId, patientId)
      return { data: result as T }
    }

    if (url === '/gms/check-status') {
      const { patientId } = data || {}
      const status = await mockApi.checkGMSStatus(patientId)
      return { data: status as T }
    }

    if (url === '/gms/check-dps') {
      const { patientId, drugCodes } = data || {}
      const result = await mockApi.checkDPSDrugs(patientId, drugCodes)
      return { data: result as T }
    }

    if (url === '/drugs/sync') {
      // Update sync status to in progress
      const result = await mockApi.syncDrugsFromHPRA()
      return { data: result as T }
    }

    if (url === '/patients') {
      const newPatient = transformFormDataToPatient(data as PatientFormData)
      mockPatients.push(newPatient)
      return { data: newPatient as T }
    }

    // Clinical history endpoints - POST
    const clinicalHistoryPostPath = stripMockRequestPath(url)
    if (clinicalHistoryPostPath.startsWith('/patients/') && clinicalHistoryPostPath.includes('/clinical-history')) {
      const parts = clinicalHistoryPostPath.split('/patients/')[1].split('/clinical-history')
      const patientId = parts[0].split('/')[0].split('?')[0]
      console.log('[Mock API] Clinical history POST:', { url, patientId, parts, data })
      
      // Find or create clinical history
      let history = mockClinicalHistory.find(h => h.patientId === patientId)
      if (!history) {
        history = {
          patientId,
          conditions: [],
          familyHistory: [],
          preferences: {
            recordedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          consents: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        mockClinicalHistory.push(history)
      }
      
      // POST /patients/:id/clinical-history/conditions
      // Check if the path ends with /conditions (not /conditions/:id)
      const conditionsPath = parts[1] || ''
      if (conditionsPath === '/conditions' || (conditionsPath.startsWith('/conditions') && !conditionsPath.match(/\/conditions\/[^/]+/))) {
        console.log('[Mock API] Adding condition:', { patientId, data, parts, conditionsPath })
        if (!data) {
          throw new Error('Condition data is required')
        }
        const condition: PatientCondition = {
          id: crypto.randomUUID(),
          ...(data as ConditionFormData),
          condition: (data as ConditionFormData).condition || '',
          status: (data as ConditionFormData).status || 'active',
          recordedAt: new Date().toISOString(),
          recordedBy: 'current-user@clnicl.ie', // In real app, get from auth context
        }
        history.conditions.push(condition)
        history.updatedAt = new Date().toISOString()
        console.log('[Mock API] Condition added successfully:', condition)
        return { data: condition as T }
      }
      
      // POST /patients/:id/clinical-history/family-history
      if (parts[1]?.startsWith('/family-history') && !parts[1].includes('/family-history/')) {
        const fhEntry: FamilyHistory = {
          id: crypto.randomUUID(),
          ...(data as FamilyHistoryFormData),
          recordedAt: new Date().toISOString(),
          recordedBy: 'current-user@clnicl.ie',
        }
        history.familyHistory.push(fhEntry)
        history.updatedAt = new Date().toISOString()
        return { data: fhEntry as T }
      }
      
      // POST /patients/:id/clinical-history/consents
      if (parts[1]?.startsWith('/consents') && !parts[1].includes('/consents/')) {
        const consent: PatientConsent = {
          id: crypto.randomUUID(),
          ...(data as ConsentFormData),
          recordedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          recordedBy: 'current-user@clnicl.ie',
        }
        history.consents.push(consent)
        history.updatedAt = new Date().toISOString()
        return { data: consent as T }
      }
      
      // POST /patients/:id/clinical-history/consents/:id/withdraw
      const withdrawMatch = parts[1]?.match(/^\/consents\/(.+)\/withdraw$/)
      if (withdrawMatch) {
        const consentId = withdrawMatch[1]
        const consent = history.consents.find(c => c.id === consentId)
        if (!consent) {
          throw new Error('Consent not found')
        }
        consent.status = 'withdrawn'
        consent.withdrawnDate = new Date().toISOString().split('T')[0]
        consent.updatedAt = new Date().toISOString()
        if ((data as any).reason) {
          consent.notes = (consent.notes ? consent.notes + '\n' : '') + `Withdrawn: ${(data as any).reason}`
        }
        history.updatedAt = new Date().toISOString()
        return { data: consent as T }
      }
      
      // POST /patients/:id/clinical-history (create new)
      // This is handled by PUT for create-or-update behavior
    }

    // Handle FormData for certificate uploads
    let processedData = data
    if (data instanceof FormData) {
      processedData = {
        type: data.get('type') as string,
        name: data.get('name') as string,
        description: data.get('description') as string || undefined,
        password: data.get('password') as string || undefined,
        notes: data.get('notes') as string || undefined,
        certificateData: 'mock-certificate-data', // In real implementation, would read from file
      }
    }

    // Task endpoints
    if (url === '/tasks') {
      const task = await mockApi.createTask(processedData)
      return { data: task as T }
    }

    // PCRS broker endpoints
    if (url === '/pcrs/patient-lookup') {
      const { patientNum, patientCodeLetter, ppsn } = processedData as {
        patientNum?: string
        patientCodeLetter?: string
        ppsn?: string
      }
      const normalizedPpsn = String(ppsn || '').trim().toUpperCase()
      if (normalizedPpsn) {
        if (normalizedPpsn === 'INVALIDEMPTY') {
          return { data: { found: false, lookupStatus: 'invalid' } as T }
        }
        const patientByPpsn = mockPatients.find((p) => String(p.ppsNumber || '').trim().toUpperCase() === normalizedPpsn)
        if (patientByPpsn) {
          return {
            data: {
              found: true,
              lookupStatus: 'found',
              namFore: patientByPpsn.firstName,
              namSur: patientByPpsn.lastName,
              cardNum: null,
              dateOfBirth: patientByPpsn.dateOfBirth,
              ppsn: null,
              gender: patientByPpsn.gender ? String(patientByPpsn.gender).charAt(0).toUpperCase() : undefined,
            },
          } as { data: T }
        }
        return { data: { found: false, lookupStatus: 'not_found' } as T }
      }

      const normalizedPatientNum = String(patientNum || '').trim()
      if (!normalizedPatientNum) {
        return { data: { found: false, lookupStatus: 'not_found' } as T }
      }
      const letter = patientCodeLetter || 'A'
      const cardPattern = `${normalizedPatientNum}${letter}`.toUpperCase()
      // Dev-only: fixed PCRS name for name-mismatch testing. Use GMS 0999999B, type a different name (e.g. "John Smith"), then click Check.
      const NAME_MISMATCH_TEST_NUM = '0999999'
      if (normalizedPatientNum.toUpperCase() === NAME_MISMATCH_TEST_NUM) {
        return {
          data: {
            found: true,
            lookupStatus: 'found',
            namFore: 'Mary',
            namSur: 'Jones',
            cardNum: cardPattern,
            dateOfBirth: '1985-06-15',
            ppsn: '1234567T',
            gender: 'F',
          },
        } as { data: T }
      }
      const patient = mockPatients.find((p) => {
        const gms = (p.gmsNumber || '').replace(/-/g, '').toUpperCase()
        return gms === cardPattern || gms.startsWith(normalizedPatientNum) || p.gmsNumber === normalizedPatientNum
      })
      if (patient) {
        return {
          data: {
            found: true,
            lookupStatus: 'found',
            namFore: patient.firstName,
            namSur: patient.lastName,
            cardNum: patient.gmsNumber || cardPattern,
            dateOfBirth: patient.dateOfBirth,
            ppsn: patient.ppsNumber,
            gender: patient.gender,
          },
        } as { data: T }
      }
      // Fallback for STC seed data in dev: allow lookup against seeded STC records
      // (these card numbers are used in report/typeahead fixtures like 0032064B).
      const stcSeedMatch = mockStcRecords.find((r) => {
        const gms = String(r.gmsNumber || '').replace(/-/g, '').toUpperCase()
        return gms === cardPattern || gms.startsWith(normalizedPatientNum)
      })
      if (stcSeedMatch) {
        const fullName = String(stcSeedMatch.patientName || '').trim()
        const parts = fullName.split(/\s+/).filter(Boolean)
        const first = parts.shift() || ''
        const last = parts.join(' ')
        return {
          data: {
            found: true,
            lookupStatus: 'found',
            namFore: first || undefined,
            namSur: last || undefined,
            cardNum: String(stcSeedMatch.gmsNumber || cardPattern),
            dateOfBirth: undefined,
            ppsn: undefined,
            gender: undefined,
          },
        } as { data: T }
      }
      return { data: { found: false, lookupStatus: 'not_found' } as T }
    }

    if (url === '/pcrs/submit-claim') {
      // Mock: same contract as real API (stcRecordId, docNum, docNam).
      const payload = processedData as { stcRecordId?: string; docNum?: string; docNam?: string }
      const record = mockStcRecords.find((r) => r.id === payload.stcRecordId)
      const generatedClaimNumber = String(112000000 + Math.floor(Math.random() * 900000))
      const generatedFormNumber = String(741000 + Math.floor(Math.random() * 9000))
      const generatedClaimSummaryUrl = `https://secure.sspcrs.ie/stcss/sec/claim/${1790000 + Math.floor(Math.random() * 9000)}?mode=saved`
      if (record) {
        record.claimNumber = generatedClaimNumber
        record.formNumber = generatedFormNumber
        record.lastSubmissionClaimSummaryUrl = generatedClaimSummaryUrl
      }
      return {
        data: {
          success: true,
          message: `Claim ${generatedClaimNumber} Saved (Form No: ${generatedFormNumber}). To print this claim please click on the 'Printing Claim' link`,
          claimNumber: generatedClaimNumber,
          formNumber: generatedFormNumber,
          claimSummaryUrl: generatedClaimSummaryUrl,
        } as T,
      }
    }

    if (url === '/pcrs/claim-inquiry') {
      const payload = processedData as { claimNum?: string }
      const claimNum = String(payload.claimNum || '').trim()
      if (!claimNum) {
        throw new Error('claimNum is required')
      }
      const record = mockStcRecords.find((r) => String(r.claimNumber || '').trim() === claimNum)
      const normalizedStatus = record?.status === 'paid'
        ? 'paid'
        : record?.status === 'rejected'
          ? 'rejected'
          : 'to_pay'
      const statusRaw = normalizedStatus === 'paid'
        ? 'Paid'
        : normalizedStatus === 'rejected'
          ? 'Rejected'
          : 'To Pay'
      const processedPeriod = normalizedStatus === 'paid' ? new Date().toISOString().slice(0, 7).replace('-', '') : ''
      return {
        data: {
          success: true,
          claimNum,
          statusRaw,
          processedPeriod: processedPeriod || undefined,
          normalizedStatus,
        } as T,
      }
    }

    if (url === '/pcrs/refresh-claim-statuses') {
      const payload = processedData as { stcRecordIds?: string[] }
      const recordIds = Array.isArray(payload.stcRecordIds) ? payload.stcRecordIds : []
      const { isEligibleForPcrsRefresh } = await import('../utils/pcrsRefreshEligibility')
      const results = recordIds.map((id) => {
        const record = mockStcRecords.find((r) => r.id === id)
        if (!record) return { stcRecordId: id, updated: false, reason: 'record_not_found' as const }
        if (record.status !== 'to_pay') {
          return {
            stcRecordId: id,
            claimNum: record.claimNumber || undefined,
            updated: false,
            reason: 'not_to_pay' as const,
          }
        }
        const claimNum = String(record.claimNumber || '').trim()
        if (!claimNum) return { stcRecordId: id, updated: false, reason: 'missing_claim_number' as const }
        if (!isEligibleForPcrsRefresh(record as import('../types/stc').StcRecord)) {
          return {
            stcRecordId: id,
            claimNum: record.claimNumber || undefined,
            updated: false,
            reason: 'not_eligible_payment_cycle' as const,
          }
        }
        const markPaid = Number(claimNum.slice(-1) || '0') % 2 === 0
        const nextStatus = markPaid ? 'paid' : 'to_pay'
        const statusRaw = markPaid ? 'Paid' : 'To Pay'
        const processedPeriod = markPaid ? new Date().toISOString().slice(0, 7).replace('-', '') : ''
        const changed = nextStatus !== record.status
        record.status = nextStatus
        if (markPaid && !record.processedAt) record.processedAt = new Date().toISOString()
        record.lastInquiryAt = new Date().toISOString()
        record.lastInquiryStatusRaw = statusRaw
        record.lastInquiryStatusNormalized = nextStatus
        record.lastInquiryProcessedPeriod = processedPeriod || undefined
        return {
          stcRecordId: id,
          claimNum,
          updated: changed,
          status: nextStatus,
          statusRaw,
          processedPeriod: processedPeriod || undefined,
          normalizedStatus: nextStatus,
        }
      })
      return {
        data: {
          success: true,
          total: recordIds.length,
          updated: results.filter((r) => r.updated).length,
          skipped: results.filter((r) => !r.updated).length,
          results,
        } as T,
      }
    }

    if (url === '/stc-batches/start') {
      const payload = processedData as {
        source?: 'pms' | 'ppt'
        recordIds?: string[]
        submitDocNum?: string
        submitDocNam?: string
      }
      const source = payload.source === 'ppt' ? 'ppt' : 'pms'
      const recordIds = Array.isArray(payload.recordIds) ? payload.recordIds : []
      const queued = mockStcRecords.filter((r) => recordIds.includes(r.id) && r.status === 'queued' && !r.lockBatchRunId)
      const now = new Date().toISOString()
      const run: MockStcBatchRun = {
        id: crypto.randomUUID(),
        source,
        status: queued.length > 0 ? 'running' : 'failed',
        createdAt: now,
        startedAt: now,
        completedAt: queued.length > 0 ? undefined : now,
        submitDocNum: String(payload.submitDocNum || ''),
        submitDocNam: String(payload.submitDocNam || ''),
        totalItems: queued.length,
        processedItems: 0,
        submittedCount: 0,
        failedCount: 0,
        skippedCount: recordIds.length - queued.length,
      }
      mockStcBatchRuns.unshift(run)
      queued.forEach((record) => {
        record.lockState = 'in-batch'
        record.lockBatchRunId = run.id
      })

      if (run.status === 'running') {
        setTimeout(() => {
          let submittedCount = 0
          let failedCount = 0
          queued.forEach((record, idx) => {
            const shouldFail = idx % 7 === 0
            record.status = shouldFail ? 'failed' : 'to_pay'
            record.lockState = undefined
            record.lockBatchRunId = undefined
            if (!shouldFail) {
              record.claimNumber = record.claimNumber || String(112500000 + idx + 1)
              record.formNumber = record.formNumber || String(742000 + idx + 1)
              record.lastSubmissionClaimSummaryUrl =
                record.lastSubmissionClaimSummaryUrl ||
                `https://secure.sspcrs.ie/stcss/sec/claim/${1795000 + idx + 1}?mode=saved`
            }
            if (shouldFail) failedCount += 1
            else submittedCount += 1
          })
          run.processedItems = queued.length
          run.submittedCount = submittedCount
          run.failedCount = failedCount
          run.status = failedCount > 0 ? 'completed_with_errors' : 'completed'
          run.completedAt = new Date().toISOString()
        }, 1500)
      }

      return {
        data: {
          batchRunId: run.id,
          status: run.status,
          totalItems: run.totalItems,
          lockedCount: run.totalItems,
          skippedCount: run.skippedCount,
        } as T,
      }
    }

    if (url === '/ppt-patients/import-csv') {
      const payload = processedData as { csvContent?: string }
      const csvContent = String(payload.csvContent || '')
      const lines = csvContent.replace(/\r/g, '').split('\n').map((line) => line.trim()).filter(Boolean)
      if (lines.length < 2) {
        throw new Error('CSV must include a header row and at least one data row')
      }
      const headers = parseMockCsvLine(lines[0]).map((h) => h.replace(/^\uFEFF/, '').trim())
      const expected = ['Name', 'DateOfBirth', 'GMSOrDVCNumber', 'PPSN']
      if (headers.length !== expected.length || !expected.every((header, idx) => headers[idx] === header)) {
        throw new Error(`Invalid CSV headers. Expected exactly: ${expected.join(', ')}`)
      }

      const processRows = () => {
        const existingCards = new Set(mockPptPatients.map((patient) => String(patient.gmsOrDvcNumber || '')))
        const existingKeys = new Set(mockPptPatients.map((patient) => String(patient.patientKey || '')))

        let inserted = 0
        let duplicatesSkipped = 0
        let invalidRows = 0
        const errors: Array<{ row: number; message: string }> = []
        const now = new Date().toISOString()
        const importBatchId = crypto.randomUUID()

        lines.slice(1).forEach((line, idx) => {
          const cols = parseMockCsvLine(line)
          const normalizedCols = cols.length > 4
            ? [cols.slice(0, cols.length - 3).join(', '), ...cols.slice(cols.length - 3)]
            : cols
          const parsedName = normalizeMockPptPatientName(normalizedCols[0] || '')
          const dob = normalizeMockPptDob(normalizedCols[1] || '')
          const card = normalizeMockPptCard(normalizedCols[2] || '')
          const ppsn = normalizeMockPpsn(normalizedCols[3] || '')
          const patientKey = normalizeMockPptKey(parsedName.displayName, dob)

          if (!parsedName.displayName || !dob) {
            invalidRows += 1
            errors.push({ row: idx + 2, message: 'Name and DateOfBirth are required and must be valid' })
            return
          }
          if ((card && existingCards.has(card)) || (!card && patientKey && existingKeys.has(patientKey))) {
            duplicatesSkipped += 1
            return
          }

          const item: MockPptPatient = {
            id: `ppt-import-${crypto.randomUUID()}`,
            tenantId: 'mock-tenant',
            firstName: parsedName.firstName || undefined,
            middleName: parsedName.middleName || undefined,
            lastName: parsedName.lastName || undefined,
            displayName: parsedName.displayName,
            dateOfBirth: dob,
            gmsOrDvcNumber: card || undefined,
            ppsn: ppsn || undefined,
            patientKey: patientKey || undefined,
            importSource: 'csv',
            importBatchId,
            createdAt: now,
            updatedAt: now,
          }
          mockPptPatients.push(item)
          if (item.gmsOrDvcNumber) existingCards.add(item.gmsOrDvcNumber)
          if (item.patientKey) existingKeys.add(item.patientKey)
          inserted += 1
        })

        return {
          mode: 'sync' as const,
          processed: lines.length - 1,
          inserted,
          duplicatesSkipped,
          invalidRows,
          errors,
          importBatchId,
        }
      }

      const rowCount = lines.length - 1
      if (rowCount >= 200) {
        const now = new Date().toISOString()
        const job: MockPptImportJob = {
          id: crypto.randomUUID(),
          importBatchId: crypto.randomUUID(),
          mode: 'async',
          status: 'pending',
          totalRows: rowCount,
          processed: 0,
          inserted: 0,
          duplicatesSkipped: 0,
          invalidRows: 0,
          errorsSample: [],
          createdAt: now,
          updatedAt: now,
        }
        mockPptImportJobs.unshift(job)
        setTimeout(() => {
          const result = processRows()
          job.status = result.invalidRows > 0 ? 'completed_with_errors' : 'completed'
          job.startedAt = new Date().toISOString()
          job.completedAt = new Date().toISOString()
          job.updatedAt = new Date().toISOString()
          job.importBatchId = result.importBatchId
          job.processed = result.processed
          job.inserted = result.inserted
          job.duplicatesSkipped = result.duplicatesSkipped
          job.invalidRows = result.invalidRows
          job.errorsSample = result.errors.slice(0, 25)
        }, 1200)
        return {
          data: {
            mode: 'async',
            jobId: job.id,
            status: 'pending',
            importBatchId: job.importBatchId,
            totalRows: rowCount,
          } as T,
        }
      }

      return { data: processRows() as T }
    }

    if (url === '/stc-records' || url === '/ppt-stc-records') {
      const isPptStcPath = url === '/ppt-stc-records'
      const data = processedData as {
        id?: string
        consultationId: string
        patientId: string
        appointmentId?: string
        formType?: string
        status?: string
        claimNumber?: string
        formNumber?: string
        lastSubmissionClaimSummaryUrl?: string
        signatureS3Key?: string
        signatureDataUrl?: string
        formPngS3Key?: string
        formData?: Record<string, unknown>
        signedAt?: string
        processedAt?: string
      }
      const now = new Date().toISOString()
      const id = data.id || crypto.randomUUID()
      const existingIdx = mockStcRecords.findIndex((r) => r.id === id)
      const existingRecord = existingIdx >= 0 ? mockStcRecords[existingIdx] : null
      const patient = mockPatients.find((p) => p.id === data.patientId)
      const patientName = patient ? [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim() : (data.formData?.patientName as string) || data.patientId
      const gms = ((data.formData?.gmsNumber as string) || patient?.gmsNumber || '').toString().trim().toUpperCase()
      const pps = ((data.formData?.ppsNumber as string) || patient?.ppsNumber || '').toString().trim().toUpperCase()
      const normalizedName = (patientName || '').toLowerCase().trim().replace(/\s+/g, ' ')
      const patientKey = gms ? `GMS#${gms}` : pps ? `PPS#${pps}` : normalizedName ? `NAME#${normalizedName}` : 'UNKNOWN'
      const rawFeeCode = normalizeMockFeeCode((data.formData?.specialService as string) || (data.formData?.micsService as string) || '')
      const feeCode = rawFeeCode && rawFeeCode !== '0' ? rawFeeCode : null
      const estimatedFee = feeCode ? (MOCK_STC_FEE_BY_CODE[feeCode] ?? null) : null
      const generatedSignatureKey = data.signatureDataUrl
        ? `${isPptStcPath ? 'ppt-stc/signatures' : 'stc/signatures'}/${id}.png`
        : null
      const record: MockStcRecord = {
        id,
        patientId: data.patientId,
        patientKey,
        patientName,
        gmsNumber: gms || undefined,
        ppsNumber: pps || undefined,
        feeCode,
        estimatedFee,
        consultationId: data.consultationId,
        formType: (data.formType as MockStcRecord['formType']) || 'STC',
        status: (data.status as MockStcRecord['status']) || 'draft',
        claimNumber: data.claimNumber ?? existingRecord?.claimNumber ?? null,
        formNumber: data.formNumber ?? existingRecord?.formNumber ?? null,
        lastSubmissionClaimSummaryUrl:
          data.lastSubmissionClaimSummaryUrl ??
          existingRecord?.lastSubmissionClaimSummaryUrl ??
          null,
        signatureS3Key: data.signatureS3Key ?? generatedSignatureKey,
        formPngS3Key: data.formPngS3Key ?? null,
        formData: data.formData ?? undefined,
        createdAt: now,
        signedAt: data.signedAt ?? null,
        processedAt: data.processedAt ?? null,
      }
      if (existingIdx >= 0) {
        if (mockStcRecords[existingIdx].lockBatchRunId) {
          throw new Error('Claim is locked while batch processing is in progress')
        }
        mockStcRecords[existingIdx] = { ...mockStcRecords[existingIdx], ...record }
      } else {
        mockStcRecords.push(record)
      }
      if (isPptStcPath) upsertMockPptPatientFromStc(existingIdx >= 0 ? mockStcRecords[existingIdx] : record)
      if (data.appointmentId) {
        const appt = mockAppointments.find((a) => a.id === data.appointmentId)
        if (appt) {
          appt.stcSuggested = false
          appt.stcRaised = true
          appt.stcRaisedAt = now
        }
      }
      return { data: (existingIdx >= 0 ? mockStcRecords[existingIdx] : record) as T }
    }

    // Communication log endpoints
    if (url === '/communication-logs') {
      const now = new Date().toISOString()
      const newLog: CommunicationLog = {
        id: crypto.randomUUID(),
        patientId: processedData.patientId,
        taskId: processedData.taskId || undefined,
        communicationType: processedData.communicationType || 'verbal',
        direction: processedData.direction || 'internal',
        participants: processedData.participants || [],
        dateTime: processedData.dateTime || now,
        duration: processedData.duration || undefined,
        summary: processedData.summary || 'Communication logged',
        notes: processedData.notes || undefined,
        recordedBy: 'mock-user',
        recordedByName: 'Mock User',
        createdAt: now,
        updatedAt: now,
      }
      mockCommunicationLogs.push(newLog)
      return { data: newLog as T }
    }

    // Automation rules endpoints
    if (url === '/task-automation/rules') {
      const rule = await mockApi.createAutomationRule(processedData as AutomationRuleFormData)
      return { data: rule as T }
    }

    if (url === '/task-automation/rules/test') {
      const { rule, sampleData } = processedData as { rule: AutomationRuleFormData; sampleData: any }
      const result = await mockApi.testAutomationRule(rule, sampleData)
      return { data: result as T }
    }

    // Chat endpoints
    if (url === '/chat/messages') {
      const message = await mockApi.sendMessage(processedData as ChatFormData)
      return { data: message as T }
    }

    if (url === '/chat/messages/read') {
      await mockApi.markMessagesAsRead(processedData.messageIds || [])
      return { data: undefined as T }
    }

    if (url === '/users') {
      const user = await mockApi.createUser(processedData as UserFormData)
      return { data: user as T }
    }

    if (url.startsWith('/role-permissions/') && url.includes('/apply-template')) {
      const parts = url.split('/')
      const role = parts[2] as UserRole
      const data = processedData as { templateName: string }
      if (!data.templateName) {
        throw new Error('Template name is required')
      }
      const updated = await mockApi.applyTemplateToRole(role, data.templateName)
      return { data: updated as T }
    }

    // Family endpoints
    if (url === '/families') {
      const now = new Date().toISOString()
      const newFamily: Family = {
        id: crypto.randomUUID(),
        name: data.name || 'New Family',
        description: data.description,
        primaryContactId: data.primaryContactId,
        address: data.address,
        phone: data.phone,
        email: data.email,
        memberIds: data.memberIds || [],
        isActive: data.isActive !== undefined ? data.isActive : true,
        notes: data.notes,
        createdAt: now,
        updatedAt: now,
      }
      mockFamilies.push(newFamily)
      // Update patient familyId
      if (data.memberIds) {
        data.memberIds.forEach((patientId: string) => {
          const patient = mockPatients.find((p) => p.id === patientId)
          if (patient) {
            patient.familyId = newFamily.id
          }
        })
      }
      return { data: newFamily as T }
    }

    if (url.startsWith('/families/') && url.includes('/members') && !url.includes('/search-patients')) {
      const familyId = url.split('/families/')[1].split('/members')[0]
      const { patientId } = data || {}
      const family = mockFamilies.find((f) => f.id === familyId)
      if (!family) {
        throw new Error('Family not found')
      }
      if (!family.memberIds.includes(patientId)) {
        family.memberIds.push(patientId)
        family.updatedAt = new Date().toISOString()
        // Update patient familyId
        const patient = mockPatients.find((p) => p.id === patientId)
        if (patient) {
          patient.familyId = familyId
        }
      }
      return { data: {} as T }
    }

    // Appointment endpoints
    if (url === '/appointments') {
      const now = new Date().toISOString()
      const staff = mockUsers.find((u) => u.id === data.staffId)
      if (!staff) {
        throw new Error('Staff member not found')
      }
      // Verify staff is clinical
      if (staff.roleCategory !== 'Clinical') {
        throw new Error('Appointments can only be scheduled with clinical staff')
      }
      
      const newAppointment: Appointment = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        staffId: data.staffId,
        staffRole: staff.role,
        appointmentDate: data.appointmentDate,
        startTime: data.startTime,
        endTime: data.endTime,
        duration: data.duration,
        type: data.type,
        status: data.status || 'scheduled',
        reason: data.reason,
        reasonCode: data.reasonCode,
        pregnancyId: data.pregnancyId,
        notes: data.notes,
        createdAt: now,
        updatedAt: now,
      }
      mockAppointments.push(newAppointment)
      return { data: newAppointment as T }
    }

    if (url.includes('/users/') && url.includes('/activate')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.activateUser(id)
        return { data: updated as T }
      }
    }

    if (url.includes('/users/') && url.includes('/deactivate')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.deactivateUser(id)
        return { data: updated as T }
      }
    }

    if (url.includes('/users/') && url.includes('/2fa/enable')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.enableUser2fa(id)
        return { data: updated as T }
      }
    }

    if (url.includes('/users/') && url.includes('/2fa/disable')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.disableUser2fa(id)
        return { data: updated as T }
      }
    }

    if (url.includes('/users/') && url.includes('/reset-password')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        await mockApi.resetUserPassword(id, processedData.password)
        return { data: { success: true } as T }
      }
    }

    if (url.includes('/users/') && url.endsWith('/record-login')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        await mockApi.recordLastLogin(id)
        return { data: undefined as T }
      }
    }

    // POST /users/{id}/signature/init - Initialize signature upload and get presigned POST URL
    if (url.includes('/users/') && url.endsWith('/signature/init')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const { fileName, fileType } = data as { fileName: string; fileSize: number; fileType: string }
        const s3Key = `signatures/${id}/${crypto.randomUUID()}-${fileName}`
        // Return mock presigned POST data
        return {
          data: {
            s3Key,
            uploadUrl: 'https://mock-s3-upload.example.com/upload', // Mock S3 URL
            fields: {
              'Content-Type': fileType || 'image/png',
              'x-amz-meta-user-id': id,
              'x-amz-meta-uploaded-at': new Date().toISOString(),
            },
          } as T,
        }
      }
    }

    // POST /users/{id}/signature/complete - Complete signature upload and store S3 key
    if (url.includes('/users/') && url.endsWith('/signature/complete')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        const { s3Key } = data as { s3Key: string }
        // Store S3 key in mock user
        const userIndex = mockUsers.findIndex((u) => u.id === id)
        if (userIndex !== -1) {
          mockUsers[userIndex] = { ...mockUsers[userIndex], signatureS3Key: s3Key }
        }
        return { data: { message: 'Signature uploaded successfully', s3Key } as T }
      }
    }

    // Note: DELETE /users/{id}/signature is handled in the delete() function

    // Referrals endpoints - POST
    if (url === '/referrals') {
      const now = new Date().toISOString()
      const currentUser = mockUsers[0] // In real app, get from auth context
      
      const newReferral: Referral = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        consultationId: data.consultationId || undefined,
        referringProviderId: data.referringProviderId || currentUser.id,
        referringProviderName: data.referringProviderName || `${currentUser.firstName} ${currentUser.lastName}`,
        referredToProviderId: data.referredToProviderId || undefined,
        referredToProviderName: data.referredToProviderName || data.providerDetails?.name || '',
        referredToProviderType: data.referredToProviderType || 'external',
        providerDetails: data.providerDetails || {},
        referralDate: data.referralDate || new Date().toISOString().split('T')[0],
        priority: data.priority || 'routine',
        status: data.status || 'draft',
        reason: data.reason || '',
        clinicalDetails: data.clinicalDetails || {},
        insuranceInformation: data.insuranceInformation || undefined,
        templateId: data.templateId || undefined,
        templateName: data.templateName || undefined,
        communicationMethod: data.communicationMethod || 'pending',
        followUpRequired: data.followUpRequired || false,
        followUpDate: data.followUpDate || undefined,
        notes: data.notes || undefined,
        providerCustomNote: data.providerCustomNote || undefined,
        createdAt: now,
        updatedAt: now,
        createdBy: currentUser.id,
      }
      
      mockReferrals.push(newReferral)

      // Audit entry for referral create (when providerCustomNote present)
      if (data.providerCustomNote?.trim()) {
        const auditId = crypto.randomUUID()
        const timestamp = new Date().toISOString()
        mockAuditEntries.push({
          id: auditId,
          patientId: data.patientId,
          timestamp,
          changeType: 'create',
          performedBy: mockApi.getCurrentUserId?.() || 'mock-user@clinicly.com',
          performedByDisplayName: 'Mock User',
          reason: 'Referral created with provider custom note',
          entityType: 'referral',
          entityId: newReferral.id,
          changes: [{ field: 'providerCustomNote', previousValue: '', newValue: data.providerCustomNote.trim() }],
          metadata: { tenantId: 'tenant-1', subdomain: 'demo', source: 'mock' },
        })
      }
      return { data: newReferral as T }
    }

    if (url.startsWith('/referrals/') && url.includes('/send')) {
      const id = url.split('/referrals/')[1].split('/send')[0]
      const referral = mockReferrals.find((r) => r.id === id)
      if (!referral) {
        throw new Error('Referral not found')
      }
      
      const now = new Date().toISOString()
      referral.status = 'sent'
      referral.communicationStatus = {
        method: referral.communicationMethod || data?.communicationMethod || 'pending',
        sentAt: now,
        ...(data?.healthmailMessageId && { healthmailMessageId: data.healthmailMessageId }),
        ...(data?.errorMessage && { errorMessage: data.errorMessage }),
      }
      referral.updatedAt = now
      
      return { data: referral as T }
    }

    // Providers endpoints - POST
    if (url === '/providers') {
      const now = new Date().toISOString()
      const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ') || undefined
      
      const newProvider: Provider = {
        id: crypto.randomUUID(),
        type: data.type || 'external',
        name: data.name || fullName || '',
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        fullName,
        title: data.title || undefined,
        organization: data.organization || undefined,
        specialty: data.specialty || undefined,
        address: data.address || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
        healthmailAddress: data.healthmailAddress || undefined,
        isActive: data.isActive !== undefined ? data.isActive : true,
        notes: data.notes || undefined,
        createdAt: now,
        updatedAt: now,
      }
      
      mockProviders.push(newProvider)
      return { data: newProvider as T }
    }

    // Consultation endpoints
    if (url === '/consultations') {
      const now = new Date().toISOString()
      const staff = mockUsers.find((u) => u.id === data.staffId)
      if (!staff) {
        throw new Error('Staff member not found')
      }
      // Verify staff is clinical
      if (staff.roleCategory !== 'Clinical') {
        throw new Error('Consultations can only be created by clinical staff')
      }
      
      const newConsultation: Consultation = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        staffId: data.staffId,
        staffName: `${staff.firstName} ${staff.lastName}`,
        appointmentId: data.appointmentId || undefined,
        reasonCode: data.reasonCode,
        pregnancyId: data.pregnancyId,
        consultationDate: data.consultationDate,
        startTime: data.startTime || new Date().toTimeString().slice(0, 5),
        endTime: data.endTime,
        duration: data.duration,
        status: data.status || 'in-progress',
        reason: data.reason,
        notes: data.notes,
        diagnosis: data.diagnosis,
        plan: data.plan,
        followUpDate: data.followUpDate,
        followUpNotes: data.followUpNotes,
        // Structured terminology codes (ICPC-2 / ICD-10)
        codes: data.codes || [],
        artefacts: [],
        instructionsGiven: data.instructionsGiven || false,
        createdAt: now,
        updatedAt: now,
        createdBy: data.staffId,
      }
      mockConsultations.push(newConsultation)
      
      // If consultation is linked to an appointment, update appointment status to 'in-progress'
      if (data.appointmentId) {
        console.log('[Mock API] Updating appointment status for appointmentId:', data.appointmentId)
        const appointment = mockAppointments.find((a) => a.id === data.appointmentId)
        if (appointment) {
          console.log('[Mock API] Found appointment, updating status from', appointment.status, 'to in-progress')
          appointment.status = 'in-progress'
          appointment.updatedAt = now
          console.log('[Mock API] Appointment updated:', { id: appointment.id, status: appointment.status })
        } else {
          console.warn('[Mock API] Appointment not found for ID:', data.appointmentId)
          console.log('[Mock API] Available appointment IDs:', mockAppointments.map(a => a.id))
        }
      } else {
        console.log('[Mock API] No appointmentId provided in consultation data')
      }
      
      return { data: newConsultation as T }
    }

    if (url === '/certificates') {
      const certificate = await mockApi.createCertificate(processedData)
      // Don't return sensitive data
      const { certificateData, privateKey, password, ...response } = certificate as any
      return { data: response as T }
    }

    if (url.includes('/activate')) {
      const id = url.split('/certificates/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.updateCertificate(id, { isActive: true, status: 'active' })
        const { certificateData, privateKey, password, ...response } = updated
        return { data: response as T }
      }
    }

    if (url.includes('/revoke')) {
      const id = url.split('/certificates/')[1]?.split('/')[0]
      if (id) {
        const updated = await mockApi.updateCertificate(id, { isActive: false, status: 'revoked' })
        const { certificateData, privateKey, password, ...response } = updated
        return { data: response as T }
      }
    }

    // Consultation endpoints
    if (url.startsWith('/consultations/') && url.includes('/artefacts') && !url.includes('/artefacts/')) {
      // POST /consultations/:id/artefacts
      const consultationId = url.split('/consultations/')[1].split('/artefacts')[0]
      const consultation = mockConsultations.find((c) => c.id === consultationId)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      if (!consultation.artefacts) {
        consultation.artefacts = []
      }
      const newArtefact: ConsultationArtefact = {
        id: crypto.randomUUID(),
        consultationId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      consultation.artefacts.push(newArtefact)
      consultation.updatedAt = new Date().toISOString()
      return { data: newArtefact as T }
    }

    if (url === '/consultations') {
      const now = new Date().toISOString()
      const staff = mockUsers.find((u) => u.id === data.staffId)
      if (!staff) {
        throw new Error('Staff member not found')
      }
      // Verify staff is clinical
      if (staff.roleCategory !== 'Clinical') {
        throw new Error('Consultations can only be created by clinical staff')
      }
      
      const newConsultation: Consultation = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        staffId: data.staffId,
        staffName: `${staff.firstName} ${staff.lastName}`,
        appointmentId: data.appointmentId || undefined,
        reasonCode: data.reasonCode,
        pregnancyId: data.pregnancyId,
        consultationDate: data.consultationDate,
        startTime: data.startTime || new Date().toTimeString().slice(0, 5),
        endTime: data.endTime,
        duration: data.duration,
        status: data.status || 'in-progress',
        reason: data.reason,
        notes: data.notes,
        diagnosis: data.diagnosis,
        plan: data.plan,
        followUpDate: data.followUpDate,
        followUpNotes: data.followUpNotes,
        // Structured terminology codes (ICPC-2 / ICD-10)
        codes: data.codes || [],
        artefacts: [],
        instructionsGiven: data.instructionsGiven || false,
        createdAt: now,
        updatedAt: now,
        createdBy: data.staffId,
      }
      mockConsultations.push(newConsultation)
      
      // If consultation is linked to an appointment, update appointment status to 'in-progress'
      if (data.appointmentId) {
        console.log('[Mock API] Updating appointment status for appointmentId:', data.appointmentId)
        const appointment = mockAppointments.find((a) => a.id === data.appointmentId)
        if (appointment) {
          console.log('[Mock API] Found appointment, updating status from', appointment.status, 'to in-progress')
          appointment.status = 'in-progress'
          appointment.updatedAt = now
          console.log('[Mock API] Appointment updated:', { id: appointment.id, status: appointment.status })
        } else {
          console.warn('[Mock API] Appointment not found for ID:', data.appointmentId)
          console.log('[Mock API] Available appointment IDs:', mockAppointments.map(a => a.id))
        }
      } else {
        console.log('[Mock API] No appointmentId provided in consultation data')
      }
      
      return { data: newConsultation as T }
    }

    // Vital Signs endpoints
    if (url === '/vital-signs') {
      const now = new Date().toISOString()
      const currentUserId = mockApi.getCurrentUserId() || '2'

      // Data is already in API format from service layer, so use it directly
      const newVitalSigns: VitalSigns = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        consultationId: data.consultationId || undefined,
        recordedAt: data.recordedAt || now,
        recordedBy: data.recordedBy || currentUserId,
        recordedByName: data.recordedByName || undefined,
        bloodPressure: data.bloodPressure || undefined,
        heartRate: data.heartRate || undefined,
        pulseRhythm: data.pulseRhythm || undefined,
        ejectionFraction: data.ejectionFraction || undefined,
        respiratoryRate: data.respiratoryRate || undefined,
        peakFlow: data.peakFlow || undefined,
        oxygenSaturation: data.oxygenSaturation || undefined,
        temperature: data.temperature || undefined,
        height: data.height || undefined,
        weight: data.weight || undefined,
        bmi: data.bmi || undefined,
        abdominalCircumference: data.abdominalCircumference || undefined,
        headCircumference: data.headCircumference || undefined,
        cholesterol: data.cholesterol || undefined,
        ldl: data.ldl || undefined,
        hdl: data.hdl || undefined,
        painSeverity: data.painSeverity || undefined,
        physicalExercise: data.physicalExercise || undefined,
        substanceMisuse: data.substanceMisuse || undefined,
        heightPercentile: data.heightPercentile || undefined,
        weightPercentile: data.weightPercentile || undefined,
        bmiPercentile: data.bmiPercentile || undefined,
        headCircumferencePercentile: data.headCircumferencePercentile || undefined,
        notes: data.notes || undefined,
        source: data.source || 'structured',
        createdAt: now,
        updatedAt: now,
        createdBy: currentUserId,
      }
      mockVitalSigns.push(newVitalSigns)
      console.log('[Mock API] Created vital signs:', newVitalSigns.id, 'for patient:', newVitalSigns.patientId)
      return { data: newVitalSigns as T }
    }

    // Lab Results endpoints
    if (url === '/lab-results') {
      const now = new Date().toISOString()
      const newLabResult: LabResult = {
        id: crypto.randomUUID(),
        patientId: data.patientId,
        type: data.type || 'blood',
        orderNumber: data.orderNumber,
        accessionNumber: data.accessionNumber,
        labName: data.labName || 'Unknown Laboratory',
        labCode: data.labCode,
        orderingPhysicianId: data.orderingPhysicianId,
        orderingPhysicianName: data.orderingPhysicianName,
        collectionDate: data.collectionDate || new Date().toISOString().split('T')[0],
        collectionTime: data.collectionTime,
        receivedDate: data.receivedDate,
        reportedDate: data.reportedDate || new Date().toISOString().split('T')[0],
        reportedTime: data.reportedTime,
        status: data.status || 'final',
        clinicalIndication: data.clinicalIndication,
        panels: data.panels || [],
        comments: data.comments,
        isAbnormal: data.isAbnormal || false,
        isCritical: data.isCritical || false,
        reviewStatus: data.reviewStatus || 'in-review',
        reviewedAt: data.reviewedAt,
        reviewedBy: data.reviewedBy,
        reviewedByName: data.reviewedByName,
        createdAt: now,
        updatedAt: now,
        source: data.source || 'manual',
        healthlinkMessageId: data.healthlinkMessageId,
      }
      mockLabResults.push(newLabResult)
      return { data: newLabResult as T }
    }

    if (url.includes('/validate')) {
      const id = url.split('/certificates/')[1]?.split('/')[0]
      if (id) {
        try {
          const certificate = await mockApi.getCertificateById(id) as any
          const now = new Date()
          const validTo = new Date(certificate.validTo)
          if (validTo < now) {
            await mockApi.updateCertificate(id, {
              lastValidatedAt: new Date().toISOString(),
              validationStatus: 'invalid',
              validationMessage: 'Certificate has expired.',
            })
            return { data: { valid: false, message: 'Certificate has expired' } as T }
          }

          await mockApi.updateCertificate(id, {
            lastValidatedAt: new Date().toISOString(),
            validationStatus: 'valid',
            validationStatusCode: 200,
            validationMessage: 'PCRS validation succeeded (HTTP 200).',
          })
          return { data: { valid: true, message: 'PCRS validation succeeded (HTTP 200).' } as T }
        } catch {
          return { data: { valid: false, message: 'Certificate not found' } as T }
        }
      }
    }


    // Tenant registration endpoints
    if (url === '/register' || url.startsWith('/register')) {
      const registrationData = data as {
        clinicName: string
        contactEmail: string
        subdomain: string
        contactPersonName?: string
        phoneNumber?: string
        termsAccepted?: boolean
        termsVersion?: string
        termsAcceptedAt?: string
        privacyAccepted?: boolean
        privacyVersion?: string
        privacyAcceptedAt?: string
        pcrsResponsibilityAccepted?: boolean
        pcrsResponsibilityVersion?: string
        pcrsResponsibilityAcceptedAt?: string
      }

      if (!registrationData.clinicName || !registrationData.contactEmail || !registrationData.subdomain) {
        throw new Error('Missing required fields: clinicName, contactEmail, subdomain')
      }

      if (
        registrationData.termsAccepted !== true ||
        registrationData.privacyAccepted !== true ||
        registrationData.pcrsResponsibilityAccepted !== true
      ) {
        throw new Error('You must accept Terms, Privacy Notice, and PCRS responsibility to register')
      }

      const normalizedSubdomain = registrationData.subdomain.toLowerCase().trim()

      // Check if subdomain already exists
      const existingTenant = mockTenants.find(t => t.subdomain === normalizedSubdomain)
      if (existingTenant) {
        throw new Error('Subdomain is already taken')
      }

      // Validate subdomain format (basic check)
      if (!/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(normalizedSubdomain)) {
        throw new Error('Invalid subdomain format')
      }

      // Check reserved subdomains
      const reserved = ['www', 'api', 'admin', 'app', 'staging', 'dev', 'test', 'mail', 'ftp', 'localhost']
      if (reserved.includes(normalizedSubdomain)) {
        throw new Error('This subdomain is reserved and cannot be used')
      }

      // Create new tenant
      const tenantId = `tenant-${normalizedSubdomain}-${Date.now()}`
      const now = new Date().toISOString()
      
      const newTenant: MockTenant = {
        subdomain: normalizedSubdomain,
        tenantId,
        clinicName: registrationData.clinicName.trim(),
        contactEmail: registrationData.contactEmail.toLowerCase().trim(),
        contactPersonName: registrationData.contactPersonName?.trim(),
        phoneNumber: registrationData.phoneNumber?.trim(),
        status: 'pending',
        termsAccepted: true,
        termsVersion: registrationData.termsVersion || '1.0',
        termsAcceptedAt: registrationData.termsAcceptedAt || now,
        privacyAccepted: true,
        privacyVersion: registrationData.privacyVersion || '1.0',
        privacyAcceptedAt: registrationData.privacyAcceptedAt || now,
        pcrsResponsibilityAccepted: true,
        pcrsResponsibilityVersion: registrationData.pcrsResponsibilityVersion || '1.0',
        pcrsResponsibilityAcceptedAt: registrationData.pcrsResponsibilityAcceptedAt || now,
        createdAt: now,
        updatedAt: now,
        stackName: `ClniclTenantStack-${normalizedSubdomain}`,
        fullDomain: `${normalizedSubdomain}.clnicl.ie`,
      }

      mockTenants.push(newTenant)

      // Simulate async provisioning (in real system, this would trigger SQS)
      setTimeout(() => {
        const tenant = mockTenants.find(t => t.subdomain === normalizedSubdomain)
        if (tenant && tenant.status === 'pending') {
          tenant.status = 'provisioning'
          tenant.updatedAt = new Date().toISOString()
          
          // Simulate provisioning completion after 5 seconds
          setTimeout(() => {
            const tenant2 = mockTenants.find(t => t.subdomain === normalizedSubdomain)
            if (tenant2 && tenant2.status === 'provisioning') {
              tenant2.status = 'provisioned'
              tenant2.provisionedAt = new Date().toISOString()
              tenant2.updatedAt = new Date().toISOString()
            }
          }, 5000)
        }
      }, 1000)

      return { data: {
        message: 'Registration successful. Your clinic instance is being provisioned.',
        tenantId,
        subdomain: normalizedSubdomain,
        status: 'pending',
        estimatedTime: '5-10 minutes',
      } as T }
    }

    // Appointment types endpoints
    if (url === '/settings/appointment-types') {
      const formData = data as {
        name: string
        code: string
        colors: {
          background: string
          border: string
          hoverBackground: string
          hoverBorder: string
        }
      }

      // Check if code already exists
      const existing = mockAppointmentTypes.find((t) => t.code === formData.code)
      if (existing) {
        throw new Error('An appointment type with this code already exists')
      }

      const now = new Date().toISOString()
      const newType: AppointmentTypeConfig = {
        id: `custom-${Date.now()}`,
        name: formData.name,
        code: formData.code,
        isDefault: false,
        isActive: true,
        colors: formData.colors,
        createdAt: now,
        updatedAt: now,
      }

      mockAppointmentTypes.push(newType)
      console.log('[Mock API] POST /settings/appointment-types - created appointment type:', newType)
      return { data: newType as T }
    }

    // Patient Files endpoints
    // POST /patient-files/upload/init - Initialize upload and get presigned POST URL
    if (url === '/patient-files/upload/init') {
      let patientId = config?.params?.patientId
      if (!patientId && url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        patientId = params.get('patientId') || undefined
      }
      if (!patientId) {
        throw new Error('Patient ID is required')
      }

      const { fileName, fileSize, fileType } = data as { fileName: string; fileSize: number; fileType: string }
      if (!fileName || !fileSize || !fileType) {
        throw new Error('fileName, fileSize, and fileType are required')
      }

      const fileId = crypto.randomUUID()
      const s3Key = `patient-files/${patientId}/${fileId}/${fileName}`

      // Return mock presigned POST data
      return {
        data: {
          fileId,
          s3Key,
          uploadUrl: 'https://mock-s3-upload.example.com/upload', // Mock S3 URL
          fields: {
            'Content-Type': fileType,
            'x-amz-meta-patient-id': patientId,
            'x-amz-meta-file-id': fileId,
            'x-amz-meta-original-filename': fileName,
          },
        } as T,
      }
    }

    // POST /patient-files/upload/complete - Complete upload and save metadata
    if (url === '/patient-files/upload/complete') {
      let patientId = config?.params?.patientId
      if (!patientId && url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        patientId = params.get('patientId') || undefined
      }
      if (!patientId) {
        throw new Error('Patient ID is required')
      }

      const { fileId, s3Key, fileName, fileType, fileSize, displayName, description, category, providerCustomNote } = data as {
        fileId: string
        s3Key: string
        fileName: string
        fileType: string
        fileSize: number
        displayName?: string
        description?: string
        category?: string
        providerCustomNote?: string
      }

      if (!fileId || !s3Key || !fileName || !fileType || !fileSize) {
        throw new Error('fileId, s3Key, fileName, fileType, and fileSize are required')
      }

      const now = new Date().toISOString()
      const currentUserId = mockApi.getCurrentUserId() || '1'
      const currentUser = mockUsers.find(u => u.id === currentUserId) || mockUsers[0]

      // Create a mock file URL (in production, this would be the actual uploaded file URL)
      const fileUrl = `https://example.com/patient-files/${fileId}/${fileName}`

      const newFile: PatientFile = {
        id: fileId,
        patientId,
        fileName,
        fileType,
        fileSize,
        fileUrl,
        s3Key,
        displayName: displayName?.trim() || undefined,
        description: description?.trim() || undefined,
        category: category || 'other',
        source: 'upload',
        uploadedBy: currentUserId,
        uploadedByName: `${currentUser.firstName} ${currentUser.lastName}`,
        uploadedAt: now,
        providerCustomNote: providerCustomNote?.trim() || undefined,
        createdAt: now,
        updatedAt: now,
      }

      mockPatientFiles.push(newFile)

      // Audit entry when providerCustomNote present
      if (providerCustomNote?.trim()) {
        const auditId = crypto.randomUUID()
        mockAuditEntries.push({
          id: auditId,
          patientId,
          timestamp: now,
          changeType: 'create',
          performedBy: currentUserId,
          performedByDisplayName: `${currentUser.firstName} ${currentUser.lastName}`,
          reason: 'Patient file created with provider custom note',
          entityType: 'patient-file',
          entityId: fileId,
          changes: [{ field: 'providerCustomNote', previousValue: '', newValue: providerCustomNote.trim() }],
          metadata: { tenantId: 'tenant-1', subdomain: 'demo', source: 'mock' },
        })
      }

      return { data: newFile as T }
    }

    // POST /patient-files/upload (legacy - kept for backward compatibility)
    if (url === '/patient-files/upload' || url.startsWith('/patient-files/upload')) {
      // Extract patientId from config params or URL query string
      let patientId = config?.params?.patientId
      if (!patientId && url.includes('?')) {
        const queryString = url.split('?')[1]
        const params = new URLSearchParams(queryString)
        patientId = params.get('patientId') || undefined
      }
      if (!patientId) {
        throw new Error('Patient ID is required')
      }

      // Handle FormData
      const formData = data as FormData
      const file = formData.get('file') as File
      const displayName = formData.get('displayName') as string || undefined
      const description = formData.get('description') as string || undefined
      const category = formData.get('category') as string || undefined

      if (!file) {
        throw new Error('File is required')
      }

      const now = new Date().toISOString()
      const currentUserId = mockApi.getCurrentUserId() || '1'
      const currentUser = mockUsers.find(u => u.id === currentUserId) || mockUsers[0]

      // Create a mock file URL (in production, this would be the actual uploaded file URL)
      const fileUrl = `https://example.com/patient-files/${crypto.randomUUID()}/${file.name}`

      const newFile: PatientFile = {
        id: crypto.randomUUID(),
        patientId,
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        fileSize: file.size,
        fileUrl,
        displayName: displayName?.trim() || undefined,
        description,
        category: category || 'other',
        source: 'upload',
        uploadedBy: currentUserId,
        uploadedByName: `${currentUser.firstName} ${currentUser.lastName}`,
        uploadedAt: now,
        createdAt: now,
        updatedAt: now,
      }

      mockPatientFiles.push(newFile)
      return { data: newFile as T }
    }

    // Create patient medication: POST /patients/:id/medications
    if (url.startsWith('/patients/') && url.endsWith('/medications')) {
      const patientId = url.split('/patients/')[1].split('/medications')[0]
      const now = new Date().toISOString()
      const id = `med-${mockPatientMedications.length + 1}`

      const newMedication: PatientMedication = {
        tenantId: 'demo-tenant',
        patientId,
        medicationId: id,
        referenceDrugId: data.referenceDrugId || null,
        name: data.name,
        dose: data.dose,
        doseText: data.doseText,
        route: data.route,
        frequency: data.frequency,
        quantity: data.quantity,
        startDate: data.startDate || now.split('T')[0],
        endDate: data.endDate || null,
        status: 'active',
        indication: data.indication || null,
        prescriberId: '2',
        prescriberName: 'Dr. Sarah O\'Brien',
        prescriberType: 'doctor',
        orderedFromList: !!data.orderedFromList,
        source: data.source || 'prescribed',
        isDoneInError: false,
        createdAt: now,
        createdBy: '2',
        updatedAt: now,
        updatedBy: '2',
      }

      mockPatientMedications.push(newMedication)
      return { data: newMedication as T }
    }

    // Create patient assessment: POST /patients/:id/assessments
    if (url.startsWith('/patients/') && url.includes('/assessments') && !url.match(/\/assessments\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const assessment = await mockApi.createAssessment({ ...data, patientId })
        return { data: assessment as T }
      }
    }

    // Care Plans POST endpoints
    // POST /care-plans/templates - Create care plan template
    if (url === '/care-plans/templates' || url.startsWith('/care-plans/templates')) {
      const templateData = {
        ...data,
        isTemplate: true,
        patientId: undefined, // Templates don't have patientId
        startDate: undefined,
        endDate: undefined,
        reviewDate: undefined,
      }
      const template = await mockApi.createCarePlan(templateData)
      return { data: template as T }
    }

    // POST /patients/{patientId}/care-plans/from-template/{templateId} - Create care plan from template
    if (url.startsWith('/patients/') && url.includes('/care-plans/from-template/')) {
      const match = url.match(/^\/patients\/([^\/]+)\/care-plans\/from-template\/([^\/]+)$/)
      if (match) {
        const patientId = match[1]
        const templateId = match[2]
        
        // Get the template
        const template = await mockApi.getCarePlan(templateId)
        if (!template || !template.isTemplate) {
          throw new Error('Template not found')
        }

        // Create new care plan based on template
        const now = new Date().toISOString()
        const startDate = data?.startDate || now.split('T')[0]
        
        const newPlan = {
          id: crypto.randomUUID(),
          patientId,
          conditionId: data?.conditionId || template.conditionId || undefined,
          assessmentId: data?.assessmentId || template.assessmentId || undefined,
          consultationId: data?.consultationId || template.consultationId || undefined,
          title: data?.title || template.title,
          description: data?.description !== undefined ? data.description : template.description,
          status: data?.status || 'active',
          goals: data?.goals || template.goals || [],
          interventions: data?.interventions || template.interventions || [],
          outcomes: data?.outcomes || template.outcomes || [],
          startDate,
          endDate: data?.endDate || undefined,
          reviewDate: data?.reviewDate || undefined,
          isTemplate: false, // This is a patient-specific plan
          templateCategory: undefined, // Not a template
          templateId: templateId, // Reference to source template
          suggestedBy: 'template',
          basedOnConditions: data?.basedOnConditions || template.basedOnConditions || [],
          notes: data?.notes !== undefined ? data.notes : template.notes,
          createdAt: now,
          updatedAt: now,
          createdBy: 'current-user-id',
          updatedBy: 'current-user-id',
        }
        
        mockCarePlans.push(newPlan)
        return { data: newPlan as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/care-plans') && !url.match(/\/care-plans\/[^\/]+$/) && !url.includes('/from-template/')) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const plan = await mockApi.createCarePlan({ ...data, patientId })
        return { data: plan as T }
      }
    }

    // Protocols POST endpoints
    if (url === '/protocols' || url.startsWith('/protocols?')) {
      const protocol = await mockApi.createProtocol(data)
      return { data: protocol as T }
    }

    if (url.startsWith('/patients/') && url.includes('/protocols') && !url.match(/\/protocols\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const enrollment = await mockApi.enrollPatientInProtocol(patientId, data.protocolId, data.notes)
        return { data: enrollment as T }
      }
    }

    // Drug Recalls POST endpoints
    if (url === '/drug-recalls' || url.startsWith('/drug-recalls?')) {
      const recall = await mockApi.createDrugRecall(data)
      return { data: recall as T }
    }

    // Research Studies POST endpoints
    if (url === '/research-studies' || url.startsWith('/research-studies?')) {
      const study = await mockApi.createResearchStudy(data)
      return { data: study as T }
    }

    if (url.startsWith('/patients/') && url.includes('/research-studies') && !url.match(/\/research-studies\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const enrollment = await mockApi.enrollPatientInStudy(patientId, data.studyId, data)
        return { data: enrollment as T }
      }
    }

    if (url.startsWith('/research-studies/') && url.includes('/protocol/versions')) {
      const id = url.split('/research-studies/')[1]?.split('/')[0]
      if (id) {
        const protocol = await mockApi.createProtocolVersion(id, data)
        return { data: protocol as T }
      }
    }

    // Patient Guidance POST endpoints
    if (url.startsWith('/patients/') && url.includes('/guidance') && !url.match(/\/guidance\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const guidance = await mockApi.createPatientGuidance({ ...data, patientId })
        return { data: guidance as T }
      }
    }

    if (url === '/reminder-rules') {
      const rule = await mockApi.createReminderRule(data)
      return { data: rule as T }
    }

    // Patient Reminders POST endpoints
    if (url.startsWith('/patients/') && url.includes('/reminders') && !url.match(/\/reminders\/[^\/]+$/)) {
      const patientId = url.split('/patients/')[1]?.split('/')[0]
      if (patientId) {
        const reminder = await mockApi.createPatientReminder({ ...data, patientId })
        return { data: reminder as T }
      }
    }



    // Create patient immunisation: POST /patients/:id/immunisations
    if (url.startsWith('/patients/') && url.endsWith('/immunisations')) {
      const patientId = url.split('/patients/')[1].split('/immunisations')[0]
      const now = new Date().toISOString()
      const id = `imm-${mockPatientImmunisations.length + 1}`

      const newImmunisation: Immunisation = {
        id,
        immunisationId: id,
        patientId,
        tenantId: 'demo-tenant',
        date: data.date,
        type: data.type,
        batchNumber: data.batchNumber,
        manufacturer: data.manufacturer,
        expiryDate: data.expiryDate,
        siteGiven: data.siteGiven,
        methodOfAdmin: data.methodOfAdmin,
        dose: data.dose,
        administeredBy: '2',
        administeredByName: 'Dr. Sarah O\'Brien',
        scheduledVaccineId: data.scheduledVaccineId,
        practiceVaccineId: data.practiceVaccineId,
        notes: data.notes,
        // Enhanced fields
        consentGiven: data.consentGiven ?? true,
        consentType: data.consentType,
        consentDate: data.consentDate,
        objectionToVaccination: data.objectionToVaccination ?? false,
        objectionReason: data.objectionReason,
        expiredVaccineUsed: new Date(data.expiryDate) < new Date(data.date),
        expiredVaccineWarningAcknowledged: data.expiredVaccineWarningAcknowledged ?? false,
        adverseReactionChecked: data.adverseReactionChecked ?? false,
        adverseReactionWarnings: data.adverseReactionWarnings,
        recommendedByGuideline: data.recommendedByGuideline ?? false,
        guidelineReference: data.guidelineReference,
        createdAt: now,
        createdBy: '2',
        updatedAt: now,
        updatedBy: '2',
      }

      mockPatientImmunisations.push(newImmunisation)
      return { data: newImmunisation as T }
    }

    // POST /vaccine-reference
    if (url === '/vaccine-reference') {
      const now = new Date().toISOString()
      const newReference: VaccineReference = {
        id: `vr-${mockVaccineReferences.length + 1}`,
        tenantId: 'demo-tenant',
        vaccineType: data.vaccineType,
        manufacturer: data.manufacturer,
        components: data.components || [],
        recommendations: data.recommendations || [],
        contraindications: data.contraindications || [],
        standardDose: data.standardDose,
        standardRoute: data.standardRoute,
        standardSite: data.standardSite,
        guidelineChapter: data.guidelineChapter,
        createdAt: now,
        updatedAt: now,
      }
      mockVaccineReferences.push(newReference)
      return { data: newReference as T }
    }

    // POST /practice-vaccines
    if (url === '/practice-vaccines') {
      const now = new Date().toISOString()
      const vaccineId = `${data.vaccineType}#${data.batchNumber}`
      const today = new Date().toISOString().split('T')[0]
      const isActive = data.expiryDate >= today
      const newVaccine: PracticeVaccine = {
        id: `pv-${mockPracticeVaccines.length + 1}`,
        tenantId: 'demo-tenant',
        vaccineId,
        vaccineType: data.vaccineType,
        batchNumber: data.batchNumber,
        manufacturer: data.manufacturer || '',
        expiryDate: data.expiryDate,
        quantity: data.quantity,
        isActive,
        location: data.location,
        notes: data.notes,
        createdAt: now,
        createdBy: '1',
        updatedAt: now,
        updatedBy: '1',
      }
      mockPracticeVaccines.push(newVaccine)
      return { data: newVaccine as T }
    }

    // POST /formulary - Add medication to formulary
    if (url === '/formulary') {
      // Reload from localStorage to ensure we have the latest data
      mockFormularyMedications = loadFormulary()
      
      const { medicationId, ...metadata } = data
      if (!medicationId) {
        throw new Error('medicationId is required')
      }

      // Check if already in formulary
      const existing = mockFormularyMedications.find((f) => f.medicationId === medicationId)
      if (existing) {
        return { data: existing as T }
      }

      const now = new Date().toISOString()
      const newFormularyEntry = {
        tenantId: 'demo-tenant',
        medicationId,
        addedAt: now,
        ...metadata,
      }
      mockFormularyMedications.push(newFormularyEntry)
      saveFormulary(mockFormularyMedications)
      return { data: newFormularyEntry as T }
    }

    // Mark medication error: POST /patients/:id/medications/:medId/mark-error
    if (url.startsWith('/patients/') && url.includes('/medications/') && url.endsWith('/mark-error')) {
      const parts = url.split('/patients/')[1].split('/medications/')
      const patientId = parts[0]
      const medAndAction = parts[1]
      const medicationId = medAndAction.split('/')[0]

      const med = mockPatientMedications.find(
        (m) => m.patientId === patientId && m.medicationId === medicationId,
      )
      if (!med) {
        throw new Error('Medication not found')
      }

      const now = new Date().toISOString()
      med.status = 'error'
      med.isDoneInError = true
      med.errorReason = data?.reason || 'Marked in error'
      med.errorMarkedBy = '2'
      med.errorMarkedAt = now
      med.updatedAt = now
      med.updatedBy = '2'

      return { data: med as T }
    }

    // Order from list: POST /patients/:id/medications/:medId/order
    if (url.startsWith('/patients/') && url.includes('/medications/') && url.endsWith('/order')) {
      const parts = url.split('/patients/')[1].split('/medications/')
      const patientId = parts[0]
      const medAndAction = parts[1]
      const medicationId = medAndAction.split('/')[0]

      const med = mockPatientMedications.find(
        (m) => m.patientId === patientId && m.medicationId === medicationId,
      )
      if (!med) {
        throw new Error('Medication not found')
      }

      const now = new Date().toISOString()
      med.orderedFromList = true
      med.updatedAt = now
      med.updatedBy = '2'

      return { data: med as T }
    }

    // Prescription endpoints - POST
    if (url === '/prescriptions') {
      const now = new Date().toISOString()
      const prescriptionId = `rx-${Date.now()}-${Math.random().toString(36).substring(7)}`
      const prescriptionDate = data.prescriptionDate || now.split('T')[0]

      const newPrescription: Prescription = {
        prescriptionId,
        tenantId: 'demo-tenant',
        patientId: data.patientId,
        prescriptionType: data.prescriptionType,
        scriptType: data.scriptType,
        prescriptionDate,
        prescriberId: '2', // Mock prescriber
        prescriberName: 'Dr. Sarah O\'Brien',
        prescriberMedicalCouncilNumber: 'MC-12345',
        medications: data.medications || [],
        instructions: data.instructions,
        isHandwritten: data.isHandwritten || false,
        handwrittenNotes: data.handwrittenNotes,
        reviewDate: data.reviewDate,
        maxRepeats: data.maxRepeats,
        currentRepeatCount: 0,
        status: 'draft',
        createdAt: now,
        createdBy: '2',
        updatedAt: now,
        updatedBy: '2',
        auditEvents: [
          {
            eventType: 'created',
            timestamp: now,
            userId: '2',
            userName: 'Dr. Sarah O\'Brien',
          },
        ],
      }

      mockPrescriptions.push(newPrescription)
      return { data: newPrescription as T }
    }

    if (url.startsWith('/prescriptions/') && url.includes('/issue')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('/issue')[0]
      const prescription = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!prescription) {
        throw new Error('Prescription not found')
      }
      if (prescription.status !== 'draft') {
        throw new Error('Only draft prescriptions can be issued')
      }

      const now = new Date().toISOString()
      prescription.status = 'issued'
      prescription.updatedAt = now
      prescription.updatedBy = '2'
      prescription.auditEvents.push({
        eventType: 'issued',
        timestamp: now,
        userId: '2',
        userName: 'Dr. Sarah O\'Brien',
        details: 'Prescription issued',
      })

      return { data: prescription as T }
    }

    if (url.startsWith('/prescriptions/') && url.includes('/print')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('/print')[0]
      const prescription = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!prescription) {
        throw new Error('Prescription not found')
      }

      const now = new Date().toISOString()
      prescription.printedAt = now
      prescription.printedBy = '2'
      prescription.updatedAt = now
      prescription.updatedBy = '2'
      prescription.auditEvents.push({
        eventType: 'printed',
        timestamp: now,
        userId: '2',
        userName: 'Dr. Sarah O\'Brien',
        details: 'Prescription printed',
      })

      return { data: prescription as T }
    }

    if (url.startsWith('/prescriptions/') && url.includes('/reorder')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('/reorder')[0]
      const original = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!original) {
        throw new Error('Prescription not found')
      }
      if (original.status !== 'issued') {
        throw new Error('Only issued prescriptions can be reordered')
      }

      const now = new Date().toISOString()
      const newPrescriptionId = `rx-${Date.now()}-${Math.random().toString(36).substring(7)}`

      const newPrescription: Prescription = {
        ...original,
        prescriptionId: newPrescriptionId,
        prescriptionDate: now.split('T')[0],
        currentRepeatCount: 0,
        status: 'draft',
        createdAt: now,
        createdBy: '2',
        updatedAt: now,
        updatedBy: '2',
        auditEvents: [
          {
            eventType: 'created',
            timestamp: now,
            userId: '2',
            userName: 'Dr. Sarah O\'Brien',
            details: `Reordered from ${original.prescriptionId}`,
          },
        ],
      }

      mockPrescriptions.push(newPrescription)

      // Update original
      original.updatedAt = now
      original.updatedBy = '2'
      original.auditEvents.push({
        eventType: 'reordered',
        timestamp: now,
        userId: '2',
        userName: 'Dr. Sarah O\'Brien',
        details: `Reordered as ${newPrescriptionId}`,
      })

      return { data: newPrescription as T }
    }

    if (url.startsWith('/prescriptions/') && url.includes('/cancel')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('/cancel')[0]
      const prescription = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!prescription) {
        throw new Error('Prescription not found')
      }

      const now = new Date().toISOString()
      prescription.status = 'cancelled'
      prescription.updatedAt = now
      prescription.updatedBy = '2'
      prescription.auditEvents.push({
        eventType: 'cancelled',
        timestamp: now,
        userId: '2',
        userName: 'Dr. Sarah O\'Brien',
        details: data?.reason || 'Prescription cancelled',
      })

      return { data: prescription as T }
    }

    if (url === '/prescriptions/check-allergies') {
      const { patientId, medications } = data || {}
      if (!patientId || !medications) {
        throw new Error('patientId and medications are required')
      }

      // Use allergyService to get patient allergies and check against medications
      // Import allergyService dynamically to avoid circular dependencies
      const { allergyService } = await import('../services/allergyService')
      const patientAllergies = await allergyService.listByPatient(patientId)

      // Filter to active drug allergies only
      const activeDrugAllergies = patientAllergies.filter(
        (a) => a.status === 'active' && a.triggerType === 'drug' && !a.isNKDA
      )

      // Check for NKDA
      const nkda = patientAllergies.find((a) => a.isNKDA && a.status === 'active')
      if (nkda) {
        const result: AllergyCheckResult = {
          hasAllergies: false,
          allergies: [],
        }
        return { data: result as T }
      }

      // Match medications against allergies
      const matches: AllergyCheckResult['allergies'] = []
      for (const medication of medications) {
        for (const allergy of activeDrugAllergies) {
          const medicationNameLower = (medication.name || '').toLowerCase()
          const allergyDisplayLower = (allergy.triggerDisplay || '').toLowerCase()

          // Match by name (substring matching)
          if (
            medicationNameLower.includes(allergyDisplayLower) ||
            allergyDisplayLower.includes(medicationNameLower) ||
            // Match by trigger code if medication has referenceDrugId
            (medication.referenceDrugId && allergy.triggerCode === medication.referenceDrugId) ||
            // Match by ATC codes if available
            (medication.atcCodes &&
              allergy.triggerCode &&
              medication.atcCodes.some((atc: string) => atc === allergy.triggerCode))
          ) {
            matches.push({
              allergyId: allergy.id,
              triggerDisplay: allergy.triggerDisplay,
              triggerCode: allergy.triggerCode,
              category: allergy.category,
              severity: allergy.reaction?.severity,
              medication: {
                name: medication.name,
                referenceDrugId: medication.referenceDrugId,
              },
            })
          }
        }
      }

      const result: AllergyCheckResult = {
        hasAllergies: matches.length > 0,
        allergies: matches,
      }
      return { data: result as T }
    }

    if (url === '/prescriptions/check-interactions') {
      const { medications } = data || {}
      if (!medications) {
        throw new Error('medications are required')
      }

      // Mock interaction check - if patientId provided, also check against current medications
      // For now, return empty (can be enhanced with mock interaction data)
      const result: InteractionCheckResult = {
        hasInteractions: false,
        interactions: [],
      }
      return { data: result as T }
    }

    // Dosage validation endpoint
    if (url === '/dosage-validation/validate') {
      const { medications, patientId } = data || {}
      if (!medications || !patientId) {
        throw new Error('medications and patientId are required')
      }

      // Mock dosage validation - return empty warnings for now
      // Can be enhanced with mock dosage range data
      const result = {
        hasWarnings: false,
        warnings: [],
        infoMessages: [],
        medicationResults: medications.map((med: any) => ({
          medication: med,
          valid: true,
          warnings: [],
          infoMessages: [],
        })),
      }
      return { data: result as T }
    }

    // Maternity routes - POST
    if (url === '/pregnancies') {
      return { data: await mockApi.createPregnancy(data) as T }
    }

    if (url.match(/\/pregnancies\/([^/?]+)\/outcome/)) {
      const match = url.match(/\/pregnancies\/([^/?]+)\/outcome/)
      const pregnancyId = match?.[1]?.split('?')[0] || ''
      if (pregnancyId) {
        return { data: await mockApi.recordOutcome(pregnancyId, data) as T }
      }
    }

    if (url.match(/\/pregnancies\/([^/?]+)\/claim-form/)) {
      const match = url.match(/\/pregnancies\/([^/?]+)\/claim-form/)
      const pregnancyId = match?.[1]?.split('?')[0] || ''
      if (pregnancyId) {
        return { data: await mockApi.generateClaimForm(pregnancyId) as T }
      }
    }

    if (url === '/maternity-visits') {
      return { data: await mockApi.createVisit(data) as T }
    }

    // OLD Formulary POST endpoint - REMOVED (using new implementation with localStorage above)

    console.log('[Mock API] ✗ No matching POST route found for:', url)
    throw new Error(`Not found: ${url}`)
  },

  put: async <T>(url: string, data?: any): Promise<{ data: T }> => {
    await delay(300)
    console.log('[Mock API] PUT handler called with:', { url, dataType: typeof data, hasData: !!data })

    if (url.startsWith('/leave')) {
      const leaveData = await handleLeaveMock('PUT', url, data)
      return { data: leaveData as T }
    }

    if (url.startsWith('/ppt-patients/') && !url.includes('?')) {
      const id = url.split('/ppt-patients/')[1]?.split('/')[0]
      const idx = mockPptPatients.findIndex((patient) => patient.id === id)
      if (idx === -1) throw new Error('PPT patient not found')

      const payload = (data || {}) as {
        displayName?: string
        dateOfBirth?: string
        gmsOrDvcNumber?: string
        ppsn?: string
      }
      const has = (key: string) => Object.prototype.hasOwnProperty.call(payload, key)
      const nextDisplayNameRaw = has('displayName') ? String(payload.displayName || '') : String(mockPptPatients[idx].displayName || '')
      const nextDobRaw = has('dateOfBirth') ? String(payload.dateOfBirth || '') : String(mockPptPatients[idx].dateOfBirth || '')
      const nextCardRaw = has('gmsOrDvcNumber') ? String(payload.gmsOrDvcNumber || '') : String(mockPptPatients[idx].gmsOrDvcNumber || '')
      const nextPpsnRaw = has('ppsn') ? String(payload.ppsn || '') : String(mockPptPatients[idx].ppsn || '')

      const parsedName = normalizeMockPptPatientName(nextDisplayNameRaw)
      if (!parsedName.displayName) throw new Error('displayName is required')
      const dateOfBirth = normalizeMockPptDob(nextDobRaw)
      const gmsOrDvcNumber = normalizeMockPptCard(nextCardRaw)
      const ppsn = normalizeMockPpsn(nextPpsnRaw)
      const patientKey = normalizeMockPptKey(parsedName.displayName, dateOfBirth)
      if (!gmsOrDvcNumber && !patientKey) throw new Error('Either GMS/DVC number or Name+DateOfBirth key is required')

      const duplicateByCard = gmsOrDvcNumber
        ? mockPptPatients.some((patient) => patient.id !== id && patient.gmsOrDvcNumber === gmsOrDvcNumber)
        : false
      const duplicateByKey = !gmsOrDvcNumber && patientKey
        ? mockPptPatients.some((patient) => patient.id !== id && patient.patientKey === patientKey)
        : false
      if (duplicateByCard || duplicateByKey) throw new Error('Duplicate patient record detected')

      const updated: MockPptPatient = {
        ...mockPptPatients[idx],
        firstName: parsedName.firstName || undefined,
        middleName: parsedName.middleName || undefined,
        lastName: parsedName.lastName || undefined,
        displayName: parsedName.displayName,
        dateOfBirth: dateOfBirth || undefined,
        gmsOrDvcNumber: gmsOrDvcNumber || undefined,
        ppsn: ppsn || undefined,
        patientKey: patientKey || undefined,
        updatedAt: new Date().toISOString(),
      }
      mockPptPatients[idx] = updated
      return { data: updated as T }
    }

    // PUT /stc-records/:id - partial update (e.g. submit claim -> status: 'to_pay')
    if ((url.startsWith('/stc-records/') || url.startsWith('/ppt-stc-records/')) && !url.includes('?')) {
      const isPptStcPath = url.startsWith('/ppt-stc-records/')
      const id = url.replace(/^\/(?:ppt-)?stc-records\//, '').split('/')[0]
      const idx = mockStcRecords.findIndex((r) => r.id === id)
      if (idx === -1) throw new Error('STC record not found')
      if (mockStcRecords[idx].lockBatchRunId) {
        throw new Error('Claim is locked while batch processing is in progress')
      }
      const patch = data as Partial<MockStcRecord>
      const prev = mockStcRecords[idx]
      const updated: MockStcRecord = {
        ...prev,
        ...patch,
        id: prev.id,
      }
      if (prev.status === 'failed' && updated.status === 'signed') {
        delete (updated as { lastSubmissionAttemptAt?: unknown }).lastSubmissionAttemptAt
        delete (updated as { lastSubmissionResult?: unknown }).lastSubmissionResult
        delete (updated as { lastSubmissionErrorMessage?: unknown }).lastSubmissionErrorMessage
        delete (updated as { lastSubmissionHttpStatus?: unknown }).lastSubmissionHttpStatus
        delete (updated as { lastSubmissionDocNum?: unknown }).lastSubmissionDocNum
        delete (updated as { lastSubmissionDocNam?: unknown }).lastSubmissionDocNam
        delete (updated as { lastSubmissionClaimSummaryUrl?: unknown }).lastSubmissionClaimSummaryUrl
      }
      mockStcRecords[idx] = updated
      if (isPptStcPath) upsertMockPptPatientFromStc(updated)
      return { data: updated as T }
    }

    if (url.startsWith('/billing')) {
      const segments = url.split('?')[0].split('/').filter(Boolean)

      if (segments[1] === 'accounts' && segments[2]) {
        const accountIndex = mockBillingAccounts.findIndex((entry) => entry.id === segments[2])
        if (accountIndex === -1) throw new Error('Billing account not found')
        const updated = {
          ...mockBillingAccounts[accountIndex],
          ...data,
          id: segments[2],
          updatedAt: new Date().toISOString(),
        }
        mockBillingAccounts[accountIndex] = updated
        return { data: updated as T }
      }

      if (segments[1] === 'items' && segments[2]) {
        const itemIndex = mockBillableItems.findIndex((entry) => entry.id === segments[2])
        if (itemIndex === -1) throw new Error('Billable item not found')
        const existing = mockBillableItems[itemIndex]
        const updated = {
          ...existing,
          ...data,
          id: segments[2],
          updatedAt: new Date().toISOString(),
        }
        mockBillableItems[itemIndex] = updated
        return { data: updated as T }
      }
    }

    // Custom phrases endpoints - PUT /custom-phrases/{id}
    if (url.startsWith('/custom-phrases/') && url.match(/^\/custom-phrases\/[^/]+$/)) {
      const id = url.split('/custom-phrases/')[1]?.split('?')[0]
      const currentUserId = mockApi.getCurrentUserId() || '1'
      const phrase = mockCustomPhrases.find((p) => p.id === id && p.userId === currentUserId)
      
      if (!phrase) {
        throw new Error('Phrase not found')
      }
      
      const updated = {
        ...phrase,
        text: data?.text !== undefined ? data.text.trim() : phrase.text,
        trigger: data?.trigger !== undefined ? data.trigger.trim() : phrase.trigger,
        sectionType: data?.sectionType !== undefined ? data.sectionType : phrase.sectionType,
        usageCount: data?.incrementUsage ? (phrase.usageCount || 0) + 1 : phrase.usageCount,
        updatedAt: new Date().toISOString(),
      }
      
      const index = mockCustomPhrases.findIndex((p) => p.id === id)
      mockCustomPhrases[index] = updated
      saveCustomPhrases(mockCustomPhrases)

      return { data: updated as T }
    }

    if (url.startsWith('/communication-logs/') && url.match(/^\/communication-logs\/[^/]+$/)) {
      const id = url.split('/communication-logs/')[1]?.split('?')[0]
      const logIndex = mockCommunicationLogs.findIndex((log) => log.id === id)
      if (logIndex === -1) {
        throw new Error('Communication log not found')
      }
      const existing = mockCommunicationLogs[logIndex]
      const updated = {
        ...existing,
        ...data,
        updatedAt: new Date().toISOString(),
      }
      mockCommunicationLogs[logIndex] = updated
      return { data: updated as T }
    }

    // Template endpoints
    if (url.startsWith('/templates/') && !url.includes('/generate') && !url.includes('/preview')) {
      const id = url.split('/templates/')[1]
      const template = mockTemplates.find((t) => t.id === id)
      if (!template) {
        throw new Error('Template not found')
      }
      const templateData: Partial<TemplateFormData> = data
      console.log('[Mock API] Updating template:', id)
      console.log('[Mock API] Update data keys:', Object.keys(templateData))

      const newS3Key = templateData.s3Key
      const hasNewFile = typeof newS3Key === 'string' && newS3Key !== template.s3Key
      if (hasNewFile) {
        if (!newS3Key.startsWith(TEMPLATE_S3_PREFIX)) {
          throw new Error(`s3Key must start with ${TEMPLATE_S3_PREFIX}`)
        }
        if (!templateData.fileName || !templateData.fileSize) {
          throw new Error('fileName and fileSize are required when updating template file')
        }
        if (!mockTemplateFiles[newS3Key]) {
          mockTemplateFiles[newS3Key] = letterTemplateOttBase64
        }
      }

      // Auto-extract variables if file was updated and variables not explicitly provided
      let variables = templateData.variables !== undefined ? templateData.variables : template.variables
      if (hasNewFile && templateData.variables === undefined) {
        try {
          const templateContent = mockTemplateFiles[newS3Key] || letterTemplateOttBase64
          const extracted = await extractVariablesFromContent(templateContent, true)
          variables = await enrichVariables(extracted)
          console.log('[Mock API] Auto-extracted', extracted.length, 'variables from updated template')
        } catch (error) {
          console.error('[Mock API] Error auto-extracting variables:', error)
          // Keep existing variables if extraction fails
        }
      }
      
      const updated: WordTemplate = {
        ...template,
        name: templateData.name !== undefined ? templateData.name : template.name,
        description: templateData.description !== undefined ? templateData.description : template.description,
        category: templateData.category !== undefined ? templateData.category : template.category,
        format: templateData.format !== undefined ? templateData.format : template.format || 'ott',
        mimeType: templateData.mimeType !== undefined ? templateData.mimeType : template.mimeType || 'application/vnd.oasis.opendocument.text-template',
        s3Key: templateData.s3Key !== undefined ? templateData.s3Key : template.s3Key,
        fileName: templateData.fileName !== undefined ? templateData.fileName : template.fileName,
        fileSize: templateData.fileSize !== undefined ? templateData.fileSize : template.fileSize,
        variables,
        updatedAt: new Date().toISOString(),
      }
      const index = mockTemplates.findIndex((t) => t.id === id)
      mockTemplates[index] = updated
      console.log('[Mock API] Template updated successfully. Updated format:', updated.format, 'mimeType:', updated.mimeType)
      return { data: updated as T }
    }

    // PUT /vaccine-reference/{vaccineType}
    if (url.startsWith('/vaccine-reference/')) {
      const vaccineType = decodeURIComponent(url.split('/vaccine-reference/')[1]?.split('?')[0] || '')
      const index = mockVaccineReferences.findIndex((v) => v.vaccineType === vaccineType)
      if (index === -1) {
        throw new Error('Vaccine reference not found')
      }
      const updated = {
        ...mockVaccineReferences[index],
        ...data,
        vaccineType, // Don't allow changing vaccine type
        updatedAt: new Date().toISOString(),
      }
      mockVaccineReferences[index] = updated
      return { data: updated as T }
    }

    // Maternity routes - PUT
    if (url.match(/\/pregnancies\/([^/]+)$/)) {
      const match = url.match(/\/pregnancies\/([^/]+)$/)
      return { data: await mockApi.updatePregnancy(match?.[1] || '', data) as T }
    }

    if (url.match(/\/maternity-visits\/([^/?]+)/)) {
      const match = url.match(/\/maternity-visits\/([^/?]+)/)
      const visitId = match?.[1]?.split('?')[0] || ''
      if (visitId) {
        return { data: await mockApi.updateVisit(visitId, data) as T }
      }
    }

    // PUT /practice-vaccines/{vaccineId}
    if (url.startsWith('/practice-vaccines/') && !url.includes('/type/') && !url.includes('/expired')) {
      const vaccineId = decodeURIComponent(url.split('/practice-vaccines/')[1]?.split('?')[0] || '')
      const index = mockPracticeVaccines.findIndex((v) => v.vaccineId === vaccineId)
      if (index === -1) {
        throw new Error('Practice vaccine not found')
      }
      const today = new Date().toISOString().split('T')[0]
      const expiryDate = data.expiryDate || mockPracticeVaccines[index].expiryDate
      const isActive = expiryDate >= today && (data.isActive !== false)
      const updated = {
        ...mockPracticeVaccines[index],
        ...data,
        vaccineId, // Don't allow changing ID
        expiryDate,
        isActive,
        updatedAt: new Date().toISOString(),
        updatedBy: '1',
      }
      mockPracticeVaccines[index] = updated
      return { data: updated as T }
    }

    // Settings endpoints
    if (url === '/settings/clinic-hours') {
      const updated: ClinicHours = {
        ...mockClinicHours,
        ...data,
        updatedAt: new Date().toISOString(),
      }
      mockClinicHours = updated
      return { data: updated as T }
    }

    if (url === '/settings/practice-location') {
      // Explicitly handle healthcareOrganisationIdentifier - treat empty string as undefined
      const hoiValue = data.healthcareOrganisationIdentifier
        ? (data.healthcareOrganisationIdentifier.trim() || undefined)
        : undefined
      
      const updated: PracticeLocation = {
        id: mockPracticeLocation.id,
        practiceName: data.practiceName,
        healthcareOrganisationIdentifier: hoiValue,
        addresses: data.addresses,
        contacts: data.contacts,
        primaryCareTeam: data.primaryCareTeam || undefined,
        createdAt: mockPracticeLocation.createdAt,
        updatedAt: new Date().toISOString(),
      }
      mockPracticeLocation = updated
      console.log('[Mock API] PUT /settings/practice-location - updated practice location:', updated)
      return { data: updated as T }
    }

    // Appointment types endpoints
    if (url.startsWith('/settings/appointment-types/') && url.includes('/toggle-active')) {
      const id = url.split('/settings/appointment-types/')[1].split('/toggle-active')[0]
      const existing = mockAppointmentTypes.find((t) => t.id === id)
      if (!existing) {
        throw new Error('Appointment type not found')
      }

      const updated: AppointmentTypeConfig = {
        ...existing,
        isActive: data.isActive !== undefined ? data.isActive : !existing.isActive,
        updatedAt: new Date().toISOString(),
      }
      const index = mockAppointmentTypes.findIndex((t) => t.id === id)
      mockAppointmentTypes[index] = updated
      console.log('[Mock API] PUT /settings/appointment-types - toggled active status:', updated)
      return { data: updated as T }
    }

    if (url.startsWith('/settings/appointment-types/') && !url.includes('/toggle-active')) {
      const id = url.split('/settings/appointment-types/')[1]
      const existing = mockAppointmentTypes.find((t) => t.id === id)
      if (!existing) {
        throw new Error('Appointment type not found')
      }

      if (existing.isDefault && data.code && data.code !== existing.code) {
        throw new Error('Cannot change code of default appointment types')
      }

      const updated: AppointmentTypeConfig = {
        ...existing,
        ...data,
        updatedAt: new Date().toISOString(),
      }
      const index = mockAppointmentTypes.findIndex((t) => t.id === id)
      mockAppointmentTypes[index] = updated
      console.log('[Mock API] PUT /settings/appointment-types - updated appointment type:', updated)
      return { data: updated as T }
    }

    // Appointment endpoints
    if (url.startsWith('/appointments/') && url.includes('/cancel')) {
      const id = url.split('/appointments/')[1].split('/cancel')[0]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      appointment.status = 'cancelled'
      appointment.cancelledAt = new Date().toISOString()
      appointment.cancellationReason = data?.reason
      appointment.updatedAt = new Date().toISOString()
      return { data: appointment as T }
    }

    if (url.startsWith('/appointments/') && url.includes('/dna')) {
      const id = url.split('/appointments/')[1].split('/dna')[0]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      
      // Mark appointment as DNA
      const now = new Date().toISOString()
      appointment.status = 'dna'
      appointment.dnaAt = now
      appointment.dnaBy = '2' // Mock user ID
      appointment.dnaReason = data?.reason
      appointment.updatedAt = now
      
      // Update patient DNA count and history
      const patient = mockPatients.find((p) => p.id === appointment.patientId)
      if (patient) {
        patient.dnaCount = (patient.dnaCount || 0) + 1
        patient.dnaHistory = patient.dnaHistory || []
        patient.dnaHistory.unshift({
          appointmentId: appointment.id,
          appointmentDate: appointment.appointmentDate,
          dnaAt: now,
          dnaReason: data?.reason,
        })
        // Keep only last 20 entries
        if (patient.dnaHistory.length > 20) {
          patient.dnaHistory = patient.dnaHistory.slice(0, 20)
        }
        patient.updatedAt = now
      }
      
      return { data: appointment as T }
    }

    if (url.startsWith('/appointments/') && url.includes('/arrived') && !url.includes('/revert-arrived')) {
      const id = url.split('/appointments/')[1].split('/arrived')[0]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      
      // Mark appointment as arrived
      const now = new Date().toISOString()
      appointment.status = 'arrived'
      appointment.arrivedAt = now
      appointment.arrivedBy = '2' // Mock user ID
      appointment.updatedAt = now
      
      return { data: appointment as T }
    }

    if (url.startsWith('/appointments/') && url.includes('/revert-arrived')) {
      const id = url.split('/appointments/')[1].split('/revert-arrived')[0]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      
      // Only allow reverting if status is 'arrived'
      if (appointment.status !== 'arrived') {
        throw new Error('Appointment is not in arrived status')
      }
      
      // Revert to scheduled status and remove arrived fields
      const now = new Date().toISOString()
      appointment.status = 'scheduled'
      appointment.arrivedAt = undefined
      appointment.arrivedBy = undefined
      appointment.updatedAt = now
      
      return { data: appointment as T }
    }

    if (url.startsWith('/appointments/') && !url.includes('/cancel') && !url.includes('/dna') && !url.includes('/arrived') && !url.includes('/available-slots')) {
      const id = url.split('/appointments/')[1]
      const appointment = mockAppointments.find((a) => a.id === id)
      if (!appointment) {
        throw new Error('Appointment not found')
      }
      
      // Update appointment fields
      if (data.patientId !== undefined) appointment.patientId = data.patientId
      if (data.staffId !== undefined) {
        const staff = mockUsers.find((u) => u.id === data.staffId)
        if (!staff) {
          throw new Error('Staff member not found')
        }
        if (staff.roleCategory !== 'Clinical') {
          throw new Error('Appointments can only be scheduled with clinical staff')
        }
        appointment.staffId = data.staffId
        appointment.staffRole = staff.role
      }
      if (data.appointmentDate !== undefined) appointment.appointmentDate = data.appointmentDate
      if (data.startTime !== undefined) appointment.startTime = data.startTime
      if (data.endTime !== undefined) appointment.endTime = data.endTime
      if (data.duration !== undefined) appointment.duration = data.duration
      if (data.type !== undefined) appointment.type = data.type
      if (data.status !== undefined) appointment.status = data.status
      if (data.reason !== undefined) appointment.reason = data.reason
      if (data.reasonCode !== undefined) appointment.reasonCode = data.reasonCode
      if (data.pregnancyId !== undefined) appointment.pregnancyId = data.pregnancyId
      if (data.notes !== undefined) appointment.notes = data.notes
      if (data.stcSuggested !== undefined) appointment.stcSuggested = data.stcSuggested
      if (data.stcSuggestedAt !== undefined) appointment.stcSuggestedAt = data.stcSuggestedAt
      if (data.stcSuggestedReasons !== undefined) appointment.stcSuggestedReasons = data.stcSuggestedReasons
      if (data.stcSuggestedConsultationId !== undefined) appointment.stcSuggestedConsultationId = data.stcSuggestedConsultationId
      if (data.stcRaised !== undefined) appointment.stcRaised = data.stcRaised
      if (data.stcRaisedAt !== undefined) appointment.stcRaisedAt = data.stcRaisedAt
      appointment.updatedAt = new Date().toISOString()
      
      return { data: appointment as T }
    }

    // Consultation endpoints
    if (url.startsWith('/consultations/') && url.includes('/complete')) {
      const id = url.split('/consultations/')[1].split('/complete')[0]
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      consultation.status = 'completed'
      if (data?.reason !== undefined) consultation.reason = data.reason
      if (data?.notes !== undefined) consultation.notes = data.notes
      if (data?.diagnosis !== undefined) consultation.diagnosis = data.diagnosis
      if (data?.plan !== undefined) consultation.plan = data.plan
      if (data?.codes !== undefined) consultation.codes = data.codes
      if (!consultation.endTime) {
        const now = new Date()
        consultation.endTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        if (consultation.startTime) {
          const [startHour, startMinute] = consultation.startTime.split(':').map(Number)
          const startDate = new Date()
          startDate.setHours(startHour, startMinute, 0, 0)
          consultation.duration = Math.round((now.getTime() - startDate.getTime()) / (1000 * 60))
        }
      }
      consultation.updatedAt = new Date().toISOString()

      // If consultation is linked to an appointment, update status and run simple STC detection for mock
      if (consultation.appointmentId) {
        const appointment = mockAppointments.find((a) => a.id === consultation.appointmentId)
        if (appointment) {
          appointment.status = 'completed'
          appointment.updatedAt = new Date().toISOString()
          const combined = [
            consultation.reason,
            consultation.notes,
            Array.isArray(consultation.diagnosis) ? consultation.diagnosis.join(' ') : '',
            consultation.plan,
            Array.isArray(consultation.codes) ? consultation.codes.map((c: { display?: string }) => c?.display ?? '').join(' ') : '',
          ].filter(Boolean).join(' ').toLowerCase()
          const stcPhrases: { phrase: string; label: string }[] = [
            { phrase: 'nebuliser', label: 'Nebuliser treatment' },
            { phrase: 'nebulizer', label: 'Nebuliser treatment' },
            { phrase: 'asthma', label: 'Nebuliser treatment (acute asthmatic attack)' },
            { phrase: 'copd', label: 'Nebuliser treatment (acute asthmatic attack)' },
            { phrase: 'sats low', label: 'Nebuliser treatment (acute asthmatic attack)' },
            { phrase: 'saturations', label: 'Nebuliser treatment (acute asthmatic attack)' },
            { phrase: 'larc', label: 'LARC' },
            { phrase: 'coil', label: 'LARC coil' },
            { phrase: 'implant', label: 'LARC implant' },
            { phrase: 'suturing', label: 'Suturing cuts and lacerations' },
            { phrase: 'laceration', label: 'Suturing cuts and lacerations' },
            { phrase: 'foreign body', label: 'Removal of foreign bodies' },
            { phrase: 'special service', label: 'Special service (mock match)' },
            { phrase: 'involuntary admission', label: 'Involuntary admission' },
          ]
          const matched = stcPhrases.filter((p) => combined.includes(p.phrase)).map((p) => p.label)
          if (matched.length > 0) {
            appointment.stcSuggested = true
            appointment.stcSuggestedAt = new Date().toISOString()
            appointment.stcSuggestedReasons = [...new Set(matched)]
            appointment.stcSuggestedConsultationId = consultation.id
          }
        }
      }

      return { data: consultation as T }
    }

    if (url.startsWith('/consultations/') && url.includes('/cancel')) {
      const id = url.split('/consultations/')[1].split('/cancel')[0]
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        throw new Error('Consultation not found')
      }
      consultation.status = 'cancelled'
      consultation.updatedAt = new Date().toISOString()
      return { data: consultation as T }
    }

    if (url.startsWith('/consultations/') && !url.includes('/artefacts') && !url.includes('/complete') && !url.includes('/cancel')) {
      const id = url.split('/consultations/')[1].split('?')[0] // Remove query params if any
      console.log('[Mock API] PUT /consultations/:id - Updating consultation:', id)
      console.log('[Mock API] Update data:', data)
      console.log('[Mock API] Available consultations:', mockConsultations.map(c => ({ id: c.id, status: c.status })))
      
      const consultation = mockConsultations.find((c) => c.id === id)
      if (!consultation) {
        console.error('[Mock API] Consultation not found:', id)
        throw new Error('Consultation not found')
      }
      
      console.log('[Mock API] Found consultation, updating fields...')
      
      // Update consultation fields
      if (data.patientId !== undefined) consultation.patientId = data.patientId
      if (data.appointmentId !== undefined) consultation.appointmentId = data.appointmentId
      if (data.reasonCode !== undefined) consultation.reasonCode = data.reasonCode
      if (data.pregnancyId !== undefined) consultation.pregnancyId = data.pregnancyId
      if (data.staffId !== undefined) {
        const staff = mockUsers.find((u) => u.id === data.staffId)
        if (!staff) {
          throw new Error('Staff member not found')
        }
        if (staff.roleCategory !== 'Clinical') {
          throw new Error('Consultations can only be assigned to clinical staff')
        }
        consultation.staffId = data.staffId
        consultation.staffName = `${staff.firstName} ${staff.lastName}`
      }
      if (data.consultationDate !== undefined) consultation.consultationDate = data.consultationDate
      if (data.startTime !== undefined) consultation.startTime = data.startTime
      if (data.endTime !== undefined) consultation.endTime = data.endTime
      if (data.duration !== undefined) consultation.duration = data.duration
      if (data.status !== undefined) consultation.status = data.status
      if (data.reason !== undefined) consultation.reason = data.reason
      if (data.notes !== undefined) {
        consultation.notes = data.notes
        console.log('[Mock API] Updated notes (length):', data.notes?.length || 0)
      }
      if (data.noteHighlights !== undefined) {
        consultation.noteHighlights = data.noteHighlights
        console.log('[Mock API] Updated noteHighlights (count):', data.noteHighlights?.length || 0)
      }
      if (data.diagnosis !== undefined) consultation.diagnosis = data.diagnosis
      if (data.plan !== undefined) consultation.plan = data.plan
      if (data.followUpDate !== undefined) consultation.followUpDate = data.followUpDate
      if (data.followUpNotes !== undefined) consultation.followUpNotes = data.followUpNotes
      if (data.codes !== undefined) consultation.codes = data.codes
      consultation.updatedAt = new Date().toISOString()
      
      console.log('[Mock API] Consultation updated successfully')
      return { data: consultation as T }
    }

    // Vital Signs endpoints - PUT
    if (url.startsWith('/vital-signs/') && !url.includes('/patient/')) {
      const id = url.split('/vital-signs/')[1].split('?')[0] // Remove query params if any
      const index = mockVitalSigns.findIndex((v) => v.id === id)
      if (index === -1) {
        throw new Error('Vital signs record not found')
      }

      const existing = mockVitalSigns[index]
      const updated: VitalSigns = {
        ...existing,
        ...data,
        updatedAt: new Date().toISOString(),
      }
      mockVitalSigns[index] = updated
      return { data: updated as T }
    }

    // Lab Results endpoints
    if (url.startsWith('/lab-results/') && !url.includes('/sync-healthlink') && !url.includes('/summaries')) {
      const id = url.split('/lab-results/')[1]
      const result = mockLabResults.find((r) => r.id === id)
      if (!result) {
        throw new Error('Lab result not found')
      }
      
      // Update lab result fields
      if (data.patientId !== undefined) result.patientId = data.patientId
      if (data.orderNumber !== undefined) result.orderNumber = data.orderNumber
      if (data.accessionNumber !== undefined) result.accessionNumber = data.accessionNumber
      if (data.labName !== undefined) result.labName = data.labName
      if (data.labCode !== undefined) result.labCode = data.labCode
      if (data.orderingPhysicianId !== undefined) result.orderingPhysicianId = data.orderingPhysicianId
      if (data.orderingPhysicianName !== undefined) result.orderingPhysicianName = data.orderingPhysicianName
      if (data.collectionDate !== undefined) result.collectionDate = data.collectionDate
      if (data.collectionTime !== undefined) result.collectionTime = data.collectionTime
      if (data.receivedDate !== undefined) result.receivedDate = data.receivedDate
      if (data.reportedDate !== undefined) result.reportedDate = data.reportedDate
      if (data.reportedTime !== undefined) result.reportedTime = data.reportedTime
      if (data.status !== undefined) result.status = data.status
      if (data.clinicalIndication !== undefined) result.clinicalIndication = data.clinicalIndication
      if (data.panels !== undefined) result.panels = data.panels
      if (data.comments !== undefined) result.comments = data.comments
      if (data.isAbnormal !== undefined) result.isAbnormal = data.isAbnormal
      if (data.isCritical !== undefined) result.isCritical = data.isCritical
      result.updatedAt = new Date().toISOString()
      
      return { data: result as T }
    }

    // Referrals endpoints - PUT
    if (url.startsWith('/referrals/') && !url.includes('/send') && !url.includes('/overview')) {
      const id = url.split('/referrals/')[1].split('?')[0]
      const referral = mockReferrals.find((r) => r.id === id)
      if (!referral) {
        throw new Error('Referral not found')
      }
      
      // Update referral fields
      if (data.referringProviderId !== undefined) referral.referringProviderId = data.referringProviderId
      if (data.referringProviderName !== undefined) referral.referringProviderName = data.referringProviderName
      if (data.referredToProviderId !== undefined) referral.referredToProviderId = data.referredToProviderId
      if (data.referredToProviderName !== undefined) referral.referredToProviderName = data.referredToProviderName
      if (data.referredToProviderType !== undefined) referral.referredToProviderType = data.referredToProviderType
      if (data.providerDetails !== undefined) referral.providerDetails = data.providerDetails
      if (data.referralDate !== undefined) referral.referralDate = data.referralDate
      if (data.priority !== undefined) referral.priority = data.priority
      if (data.status !== undefined) referral.status = data.status
      if (data.reason !== undefined) referral.reason = data.reason
      if (data.clinicalDetails !== undefined) referral.clinicalDetails = data.clinicalDetails
      if (data.insuranceInformation !== undefined) referral.insuranceInformation = data.insuranceInformation
      if (data.templateId !== undefined) referral.templateId = data.templateId
      if (data.templateName !== undefined) referral.templateName = data.templateName
      if (data.documentContent !== undefined) referral.documentContent = data.documentContent
      if (data.communicationMethod !== undefined) referral.communicationMethod = data.communicationMethod
      if (data.communicationStatus !== undefined) referral.communicationStatus = data.communicationStatus
      if (data.followUpRequired !== undefined) referral.followUpRequired = data.followUpRequired
      if (data.followUpDate !== undefined) referral.followUpDate = data.followUpDate
      if (data.notes !== undefined) referral.notes = data.notes
      if (data.providerCustomNote !== undefined) referral.providerCustomNote = data.providerCustomNote
      referral.updatedAt = new Date().toISOString()
      
      return { data: referral as T }
    }

    // Providers endpoints - PUT
    if (url.startsWith('/providers/')) {
      const id = url.split('/providers/')[1].split('?')[0]
      const provider = mockProviders.find((p) => p.id === id)
      if (!provider) {
        throw new Error('Provider not found')
      }
      
      // Update provider fields
      if (data.type !== undefined) provider.type = data.type
      if (data.name !== undefined) provider.name = data.name
      if (data.firstName !== undefined) provider.firstName = data.firstName
      if (data.lastName !== undefined) provider.lastName = data.lastName
      if (data.fullName !== undefined) provider.fullName = data.fullName
      if (data.title !== undefined) provider.title = data.title
      if (data.organization !== undefined) provider.organization = data.organization
      if (data.specialty !== undefined) provider.specialty = data.specialty
      if (data.address !== undefined) provider.address = data.address
      if (data.phone !== undefined) provider.phone = data.phone
      if (data.email !== undefined) provider.email = data.email
      if (data.healthmailAddress !== undefined) provider.healthmailAddress = data.healthmailAddress
      if (data.isActive !== undefined) provider.isActive = data.isActive
      if (data.notes !== undefined) provider.notes = data.notes
      if (data.firstName !== undefined || data.lastName !== undefined) {
        const recomputedFullName = [provider.firstName, provider.lastName].filter(Boolean).join(' ') || undefined
        provider.fullName = recomputedFullName
        if (!data.name && recomputedFullName) {
          provider.name = recomputedFullName
        }
      }
      provider.updatedAt = new Date().toISOString()
      
      return { data: provider as T }
    }

    // Family endpoints
    if (url.startsWith('/families/') && !url.includes('/members')) {
      const id = url.split('/families/')[1]
      const family = mockFamilies.find((f) => f.id === id)
      if (!family) {
        throw new Error('Family not found')
      }
      // Update family fields
      if (data.name !== undefined) family.name = data.name
      if (data.description !== undefined) family.description = data.description
      if (data.primaryContactId !== undefined) family.primaryContactId = data.primaryContactId
      if (data.address !== undefined) family.address = data.address
      if (data.phone !== undefined) family.phone = data.phone
      if (data.email !== undefined) family.email = data.email
      if (data.isActive !== undefined) family.isActive = data.isActive
      if (data.notes !== undefined) family.notes = data.notes
      family.updatedAt = new Date().toISOString()
      return { data: family as T }
    }

    // Handle FormData for certificate updates
    let processedData = data
    if (data instanceof FormData) {
      processedData = {
        name: data.get('name') as string || undefined,
        description: data.get('description') as string || undefined,
        password: data.get('password') as string || undefined,
        notes: data.get('notes') as string || undefined,
        certificateData: 'mock-certificate-data', // In real implementation, would read from file
      }
    }

    // Clinical history endpoints - PUT
    const clinicalHistoryPutPath = stripMockRequestPath(url)
    if (clinicalHistoryPutPath.startsWith('/patients/') && clinicalHistoryPutPath.includes('/clinical-history')) {
      const parts = clinicalHistoryPutPath.split('/patients/')[1].split('/clinical-history')
      const patientId = parts[0].split('/')[0].split('?')[0]
      
      // Find or create clinical history
      let history = mockClinicalHistory.find(h => h.patientId === patientId)
      if (!history) {
        history = {
          patientId,
          conditions: [],
          familyHistory: [],
          preferences: {
            recordedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          consents: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        mockClinicalHistory.push(history)
      }
      
      // PUT /patients/:id/clinical-history (update entire history)
      if (parts[1] === '' || parts[1] === '/') {
        if (data.conditions !== undefined) history.conditions = data.conditions
        if (data.familyHistory !== undefined) history.familyHistory = data.familyHistory
        if (data.preferences !== undefined) {
          history.preferences = { ...history.preferences, ...data.preferences, updatedAt: new Date().toISOString() }
        }
        if (data.consents !== undefined) history.consents = data.consents
        history.updatedAt = new Date().toISOString()
        return { data: history as T }
      }
      
      // PUT /patients/:id/clinical-history/conditions/:conditionId
      const conditionMatch = parts[1]?.match(/^\/conditions\/(.+)$/)
      if (conditionMatch) {
        const conditionId = conditionMatch[1]
        const condition = history.conditions.find(c => c.id === conditionId)
        if (!condition) {
          throw new Error('Condition not found')
        }
        Object.assign(condition, data, { updatedAt: new Date().toISOString() })
        history.updatedAt = new Date().toISOString()
        return { data: condition as T }
      }
      
      // PUT /patients/:id/clinical-history/family-history/:entryId
      const fhMatch = parts[1]?.match(/^\/family-history\/(.+)$/)
      if (fhMatch) {
        const entryId = fhMatch[1]
        const entry = history.familyHistory.find(fh => fh.id === entryId)
        if (!entry) {
          throw new Error('Family history entry not found')
        }
        Object.assign(entry, data)
        history.updatedAt = new Date().toISOString()
        return { data: entry as T }
      }
      
      // PUT /patients/:id/clinical-history/preferences
      if (parts[1] === '/preferences') {
        history.preferences = { ...history.preferences, ...data, updatedAt: new Date().toISOString() }
        history.updatedAt = new Date().toISOString()
        return { data: history.preferences as T }
      }
      
      // PUT /patients/:id/clinical-history/consents/:consentId
      const consentMatch = parts[1]?.match(/^\/consents\/(.+)$/)
      if (consentMatch) {
        const consentId = consentMatch[1]
        const consent = history.consents.find(c => c.id === consentId)
        if (!consent) {
          throw new Error('Consent not found')
        }
        Object.assign(consent, data, { updatedAt: new Date().toISOString() })
        history.updatedAt = new Date().toISOString()
        return { data: consent as T }
      }
    }

    // Update patient immunisation: PUT /patients/:id/immunisations/:immunisationId
    if (url.startsWith('/patients/') && url.includes('/immunisations/')) {
      const parts = url.split('/patients/')[1].split('/immunisations/')
      const patientId = parts[0]
      const immunisationId = parts[1]?.split('?')[0] // Remove query params if any

      const imm = mockPatientImmunisations.find(
        (i) => i.patientId === patientId && i.immunisationId === immunisationId,
      )
      if (!imm) {
        throw new Error('Immunisation not found')
      }

      const now = new Date().toISOString()
      // Update immunisation with provided data
      if (data.date !== undefined) imm.date = data.date
      if (data.type !== undefined) imm.type = data.type
      if (data.batchNumber !== undefined) imm.batchNumber = data.batchNumber
      if (data.manufacturer !== undefined) imm.manufacturer = data.manufacturer
      if (data.expiryDate !== undefined) imm.expiryDate = data.expiryDate
      if (data.siteGiven !== undefined) imm.siteGiven = data.siteGiven
      if (data.methodOfAdmin !== undefined) imm.methodOfAdmin = data.methodOfAdmin
      if (data.dose !== undefined) imm.dose = data.dose
      if (data.scheduledVaccineId !== undefined) imm.scheduledVaccineId = data.scheduledVaccineId
      if (data.practiceVaccineId !== undefined) imm.practiceVaccineId = data.practiceVaccineId
      if (data.notes !== undefined) imm.notes = data.notes
      
      imm.updatedAt = now
      imm.updatedBy = '2'

      return { data: imm as T }
    }

    // Update patient medication: PUT /patients/:id/medications/:medicationId
    // This must come BEFORE the general patient update handler
    if (url.startsWith('/patients/') && url.includes('/medications/') && !url.includes('/mark-error') && !url.includes('/order')) {
      const parts = url.split('/patients/')[1].split('/medications/')
      const patientId = parts[0]
      const medicationId = parts[1]?.split('?')[0] // Remove query params if any

      const med = mockPatientMedications.find(
        (m) => m.patientId === patientId && m.medicationId === medicationId,
      )
      if (!med) {
        throw new Error('Medication not found')
      }

      const now = new Date().toISOString()
      // Update medication with provided data
      if (data.status !== undefined) med.status = data.status
      if (data.endDate !== undefined) med.endDate = data.endDate
      if (data.startDate !== undefined) med.startDate = data.startDate
      if (data.name !== undefined) med.name = data.name
      if (data.dose !== undefined) med.dose = data.dose
      if (data.doseText !== undefined) med.doseText = data.doseText
      if (data.route !== undefined) med.route = data.route
      if (data.frequency !== undefined) med.frequency = data.frequency
      if (data.quantity !== undefined) med.quantity = data.quantity
      if (data.indication !== undefined) med.indication = data.indication
      if (data.orderedFromList !== undefined) med.orderedFromList = data.orderedFromList
      if (data.source !== undefined) med.source = data.source
      
      med.updatedAt = now
      med.updatedBy = '2'

      return { data: med as T }
    }

    // PUT /patients/{id}/mark-inactive - Mark patient as inactive
    if (url.startsWith('/patients/') && url.includes('/mark-inactive')) {
      const id = url.split('/patients/')[1].split('/mark-inactive')[0]
      const patient = mockPatients.find((p) => p.id === id)
      if (!patient) {
        throw new Error('Patient not found')
      }
      patient.isActive = false
      patient.updatedAt = new Date().toISOString()
      return { data: patient as T }
    }

    // PUT /patients/{id}/activate - Activate inactive patient
    if (url.startsWith('/patients/') && url.includes('/activate')) {
      const id = url.split('/patients/')[1].split('/activate')[0]
      const patient = mockPatients.find((p) => p.id === id)
      if (!patient) {
        throw new Error('Patient not found')
      }
      patient.isActive = true
      patient.updatedAt = new Date().toISOString()
      return { data: patient as T }
    }

    if (url.startsWith('/patients/') && !url.includes('/clinical-history') && !url.includes('/medications/') && !url.includes('/mark-inactive') && !url.includes('/activate') && !url.includes('/set-primary')) {
      const id = url.split('/patients/')[1]
      const existingPatient = mockPatients.find((p) => p.id === id)
      if (!existingPatient) {
        throw new Error('Patient not found')
      }

      const updatedPatient = transformFormDataToPatient(processedData as PatientFormData, existingPatient)
      const index = mockPatients.findIndex((p) => p.id === id)
      mockPatients[index] = updatedPatient
      return { data: updatedPatient as T }
    }

    // Task endpoints
    if (url.startsWith('/tasks/')) {
      // Extract ID from URL, handling query parameters and additional path segments
      const pathAfterTasks = url.split('/tasks/')[1]
      const id = pathAfterTasks.split('?')[0].split('/')[0] // Get ID before query params or additional path segments
      const task = await mockApi.updateTask(id, data)
      return { data: task as T }
    }

    // Automation rules endpoints
    if (url.startsWith('/task-automation/rules/') && url.includes('/toggle')) {
      const id = url.split('/task-automation/rules/')[1].split('/toggle')[0]
      const { enabled } = data as { enabled: boolean }
      const rule = await mockApi.toggleAutomationRule(id, enabled)
      return { data: rule as T }
    }

    if (url.startsWith('/task-automation/rules/') && !url.includes('/toggle') && !url.includes('/test')) {
      const id = url.split('/task-automation/rules/')[1]
      const rule = await mockApi.updateAutomationRule(id, data as Partial<AutomationRuleFormData>)
      return { data: rule as T }
    }

    if (url.startsWith('/users/') && !url.includes('/activate') && !url.includes('/deactivate') && !url.includes('/2fa/') && !url.includes('/reset-password') && !url.includes('/record-login')) {
      const id = url.split('/users/')[1]
      const updated = await mockApi.updateUser(id, processedData as Partial<UserFormData>)
      return { data: updated as T }
    }

    // Prescription endpoints - PUT
    if (url.startsWith('/prescriptions/') && !url.includes('/issue') && !url.includes('/print') && !url.includes('/reorder') && !url.includes('/cancel')) {
      const prescriptionId = url.split('/prescriptions/')[1].split('?')[0]
      const prescription = mockPrescriptions.find((p) => p.prescriptionId === prescriptionId)
      if (!prescription) {
        throw new Error('Prescription not found')
      }

      const now = new Date().toISOString()
      const updates = processedData as Partial<CreatePrescriptionInput>

      if (updates.prescriptionType !== undefined) prescription.prescriptionType = updates.prescriptionType
      if (updates.scriptType !== undefined) prescription.scriptType = updates.scriptType
      if (updates.prescriptionDate !== undefined) prescription.prescriptionDate = updates.prescriptionDate
      if (updates.medications !== undefined) prescription.medications = updates.medications
      if (updates.instructions !== undefined) prescription.instructions = updates.instructions
      if (updates.isHandwritten !== undefined) prescription.isHandwritten = updates.isHandwritten
      if (updates.handwrittenNotes !== undefined) prescription.handwrittenNotes = updates.handwrittenNotes
      if (updates.reviewDate !== undefined) prescription.reviewDate = updates.reviewDate
      if (updates.maxRepeats !== undefined) prescription.maxRepeats = updates.maxRepeats

      prescription.updatedAt = now
      prescription.updatedBy = '2'
      prescription.auditEvents.push({
        eventType: 'updated',
        timestamp: now,
        userId: '2',
        userName: 'Dr. Sarah O\'Brien',
        details: 'Prescription updated',
      })

      return { data: prescription as T }
    }

    if (url.startsWith('/role-permissions/') && !url.includes('/apply-template')) {
      // Handle confidentiality-policy POST
      if (url === '/role-permissions/confidentiality-policy') {
        const policy = processedData as any
        const updated = await mockApi.setConfidentialityPolicy(policy)
        return { data: updated as T }
      }
      
      // Handle role-specific permission updates
      const role = url.split('/role-permissions/')[1] as UserRole
      const data = processedData as { permissions: Permission[] }
      if (!data.permissions) {
        throw new Error('Permissions array is required')
      }
      const updated = await mockApi.updateRolePermission(role, data.permissions)
      return { data: updated as T }
    }

    // Emergency override endpoints
    if (url.startsWith('/emergency-override/')) {
      if (url === '/emergency-override/request') {
        const data = processedData as { patientId: string; fields: string[]; reason: string }
        const override = await mockApi.requestEmergencyOverride(data)
        return { data: override as T }
      }
      
      if (url === '/emergency-override/approve') {
        const data = processedData as { overrideId: string; expiresInHours?: number }
        const override = await mockApi.approveOverride(data)
        return { data: override as T }
      }
      
      if (url === '/emergency-override/reject') {
        const data = processedData as { overrideId: string; rejectionReason?: string }
        const override = await mockApi.rejectOverride(data)
        return { data: override as T }
      }
    }

    if (url === '/tenant/settings') {
      const settings = processedData as Partial<{ sessionTimeoutMinutes: number }>
      const updated = await mockApi.updateTenantSettings(settings)
      return { data: updated as T }
    }

    if (url === '/certificates/pcrs-credentials') {
      const { username, password } = processedData as { username?: string; password?: string }
      if (username && password) {
        mockPcrsCredentialsConfigured = true
        return { data: { success: true } as T }
      }
      throw new Error('username and password are required')
    }

    if (url.startsWith('/certificates/')) {
      const id = url.split('/certificates/')[1]
      const updated = await mockApi.updateCertificate(id, processedData)
      const { certificateData, privateKey, password, ...response } = updated
      return { data: response as T }
    }

    // Patient Files endpoints - PUT /patient-files/{id}
    if (url.startsWith('/patient-files/') && !url.includes('/download') && !url.includes('/upload')) {
      const id = url.split('/patient-files/')[1]
      const index = mockPatientFiles.findIndex(f => f.id === id)
      if (index === -1) {
        throw new Error('Patient file not found')
      }
      const existing = mockPatientFiles[index]
      const updateData = processedData as Partial<Pick<PatientFile, 'displayName' | 'description' | 'category'>>
      const updated: PatientFile = {
        ...existing,
        ...updateData,
        updatedAt: new Date().toISOString(),
      }
      mockPatientFiles[index] = updated
      return { data: updated as T }
    }

    // Care Plans PUT endpoints
    if (url.startsWith('/care-plans/')) {
      const id = url.split('/care-plans/')[1]?.split('/')[0]
      if (id) {
        const plan = await mockApi.updateCarePlan(id, data)
        return { data: plan as T }
      }
    }

    // Protocols PUT endpoints
    if (url.startsWith('/protocols/') && !url.includes('/compliance') && !url.includes('/patients/')) {
      const id = url.split('/protocols/')[1]?.split('/')[0]
      if (id) {
        const protocol = await mockApi.updateProtocol(id, data)
        return { data: protocol as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/protocols/') && url.includes('/exclude')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const protocolId = parts[4]
      const enrollment = await mockApi.excludePatientFromProtocol(patientId, protocolId, data.reason)
      return { data: enrollment as T }
    }

    // Drug Recalls PUT endpoints
    if (url.startsWith('/drug-recalls/') && !url.includes('/affected-patients') && !url.includes('/notifications')) {
      const id = url.split('/drug-recalls/')[1]?.split('/')[0]
      if (id) {
        const recall = await mockApi.updateDrugRecall(id, data)
        return { data: recall as T }
      }
    }

    // Research Studies PUT endpoints
    if (url.startsWith('/research-studies/') && !url.includes('/patients/') && !url.includes('/protocol')) {
      const id = url.split('/research-studies/')[1]?.split('/')[0]
      if (id) {
        const study = await mockApi.updateResearchStudy(id, data)
        return { data: study as T }
      }
    }

    if (url.startsWith('/patients/') && url.includes('/research-studies/') && url.includes('/withdraw')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const studyId = parts[4]
      const enrollment = await mockApi.withdrawPatientFromStudy(patientId, studyId, data.reason)
      return { data: enrollment as T }
    }

    // Patient Guidance PUT endpoints
    if (url.startsWith('/patients/') && url.includes('/guidance/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const guidanceId = parts[4]
      if (patientId && guidanceId) {
        const guidance = await mockApi.updatePatientGuidance(patientId, guidanceId, data)
        return { data: guidance as T }
      }
    }

    // Patient Reminders PUT endpoints
    if (url.startsWith('/patients/') && url.includes('/reminders/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const reminderId = parts[4]
      if (patientId && reminderId) {
        const reminder = await mockApi.updatePatientReminder(patientId, reminderId, data)
        return { data: reminder as T }
      }
    }

    // Assessments PUT endpoints
    if (url.startsWith('/assessments/')) {
      const id = url.split('/assessments/')[1]
      const assessment = await mockApi.updateAssessment(id, data)
      return { data: assessment as T }
    }

    // PUT /patients/{id}/set-primary - Change primary designation
    if (url.startsWith('/patients/') && url.includes('/set-primary')) {
      const id = url.split('/patients/')[1].split('/set-primary')[0]
      const { newPrimaryId } = data || {}
      
      if (!newPrimaryId) {
        throw new Error('New primary ID is required')
      }

      const currentPrimary = mockPatients.find((p) => p.id === id)
      const newPrimary = mockPatients.find((p) => p.id === newPrimaryId)
      
      if (!currentPrimary || !newPrimary) {
        throw new Error('One or both patients not found')
      }

      const currentIsPrimary = currentPrimary.isPrimary ?? true
      if (!currentIsPrimary) {
        throw new Error('Current patient is not a primary record')
      }

      const linkedIds = currentPrimary.linkedPatientIds || []
      if (!linkedIds.includes(newPrimaryId)) {
        throw new Error('New primary patient is not linked to current primary')
      }

      // Update new primary
      const newLinkedIds = [id, ...linkedIds.filter((lid) => lid !== newPrimaryId)]
      newPrimary.isPrimary = true
      newPrimary.linkedPatientIds = newLinkedIds
      newPrimary.primaryPatientId = undefined
      newPrimary.updatedAt = new Date().toISOString()

      // Update old primary
      currentPrimary.isPrimary = false
      currentPrimary.primaryPatientId = newPrimaryId
      currentPrimary.linkedPatientIds = undefined
      currentPrimary.updatedAt = new Date().toISOString()

      // Update all other linked patients
      linkedIds
        .filter((lid) => lid !== newPrimaryId)
        .forEach((lid) => {
          const linked = mockPatients.find((p) => p.id === lid)!
          linked.primaryPatientId = newPrimaryId
          linked.updatedAt = new Date().toISOString()
        })

      return { data: { message: 'Primary designation changed successfully' } as T }
    }

    console.log('[Mock API] ✗ No matching PUT route found for:', url)
    console.log('[Mock API] PUT data:', data)
    throw new Error(`Not found: ${url}`)
  },

  delete: async <T = void>(url: string, _config?: any): Promise<{ data: T } | void> => {
    await delay(300)

    if (url.startsWith('/leave')) {
      const data = await handleLeaveMock('DELETE', url)
      return { data: data as T }
    }

    if (url.startsWith('/ppt-patients/') && !url.includes('?')) {
      const id = url.split('/ppt-patients/')[1]?.split('/')[0]
      const index = mockPptPatients.findIndex((patient) => patient.id === id)
      if (index === -1) throw new Error('PPT patient not found')
      mockPptPatients.splice(index, 1)
      return { data: { deleted: true, id } as T }
    }
    
    // DELETE /batch-documents/:fileId
    if (url.startsWith('/batch-documents/') && !url.includes('/upload') && !url.includes('/extract') && !url.includes('/accept-match') && !url.includes('/link') && !url.includes('/preview')) {
      const fileId = url.replace('/batch-documents/', '').split('/')[0]
      const index = mockBatchDocuments.findIndex((d) => d.fileId === fileId)
      if (index === -1) throw new Error('Batch document not found')
      mockBatchDocuments.splice(index, 1)
      return
    }

    if (url.startsWith('/billing')) {
      const segments = url.split('?')[0].split('/').filter(Boolean)
      if (segments[1] === 'items' && segments[2]) {
        const index = mockBillableItems.findIndex((entry) => entry.id === segments[2])
        if (index === -1) throw new Error('Billable item not found')
        const [deleted] = mockBillableItems.splice(index, 1)
        const account = mockBillingAccounts.find((entry) => entry.id === deleted.accountId)
        if (account) {
          account.balance -= deleted.amount
          account.updatedAt = new Date().toISOString()
        }
        return { data: { message: 'Billable item deleted' } as T }
      }
      if (segments[1] === 'payment-methods' && segments[2]) {
        const index = mockBillingPaymentMethods.findIndex((entry) => entry.id === segments[2])
        if (index === -1) throw new Error('Payment method not found')
        mockBillingPaymentMethods.splice(index, 1)
        return { data: { message: 'Payment method deleted' } as T }
      }
      if (segments[1] === 'consultations' && segments[2] && segments[3] === 'items' && segments[4]) {
        const item = mockBillableItems.find((entry) => entry.id === segments[4])
        if (!item) throw new Error('Billable item not found')
        item.consultationId = undefined
        item.updatedAt = new Date().toISOString()
        return { data: item as T }
      }
    }

    // Referrals endpoints - DELETE (soft delete: set status to cancelled)
    if (url.startsWith('/referrals/') && !url.includes('/send') && !url.includes('/overview')) {
      const id = url.split('/referrals/')[1].split('?')[0]
      const referral = mockReferrals.find((r) => r.id === id)
      if (!referral) {
        throw new Error('Referral not found')
      }
      
      // Soft delete: set status to cancelled
      referral.status = 'cancelled'
      referral.updatedAt = new Date().toISOString()
      return { data: referral as T }
    }

    if (url.startsWith('/communication-logs/')) {
      const id = url.split('/communication-logs/')[1]?.split('?')[0]
      const index = mockCommunicationLogs.findIndex((log) => log.id === id)
      if (index === -1) {
        throw new Error('Communication log not found')
      }
      mockCommunicationLogs.splice(index, 1)
      return
    }

    // Providers endpoints - DELETE (soft delete: set isActive to false)
    if (url.startsWith('/providers/')) {
      const id = url.split('/providers/')[1].split('?')[0]
      const provider = mockProviders.find((p) => p.id === id)
      if (!provider) {
        throw new Error('Provider not found')
      }
      
      // Soft delete: set isActive to false
      provider.isActive = false
      provider.updatedAt = new Date().toISOString()
      return { data: provider as T }
    }
    
    // Delete patient immunisation: DELETE /patients/:id/immunisations/:immunisationId
    if (url.startsWith('/patients/') && url.includes('/immunisations/')) {
      const parts = url.split('/patients/')[1].split('/immunisations/')
      const patientId = parts[0]
      const immunisationId = parts[1]?.split('?')[0] // Remove query params if any

      const index = mockPatientImmunisations.findIndex(
        (i) => i.patientId === patientId && i.immunisationId === immunisationId,
      )
      if (index === -1) {
        throw new Error('Immunisation not found')
      }

      mockPatientImmunisations.splice(index, 1)
      return
    }

    // DELETE /vaccine-reference/{vaccineType}
    if (url.startsWith('/vaccine-reference/')) {
      const vaccineType = decodeURIComponent(url.split('/vaccine-reference/')[1]?.split('?')[0] || '')
      const index = mockVaccineReferences.findIndex((v) => v.vaccineType === vaccineType)
      if (index === -1) {
        throw new Error('Vaccine reference not found')
      }
      mockVaccineReferences.splice(index, 1)
      return
    }

    // DELETE /practice-vaccines/{vaccineId}
    if (url.startsWith('/practice-vaccines/') && !url.includes('/type/') && !url.includes('/expired')) {
      const vaccineId = decodeURIComponent(url.split('/practice-vaccines/')[1]?.split('?')[0] || '')
      const index = mockPracticeVaccines.findIndex((v) => v.vaccineId === vaccineId)
      if (index === -1) {
        throw new Error('Practice vaccine not found')
      }
      // Mark as inactive rather than deleting
      mockPracticeVaccines[index].isActive = false
      mockPracticeVaccines[index].updatedAt = new Date().toISOString()
      return
    }

    // DELETE /formulary/{medicationId} - Remove medication from formulary
    if (url.startsWith('/formulary/') && !url.includes('/check/')) {
      // Reload from localStorage to ensure we have the latest data
      mockFormularyMedications = loadFormulary()
      const medicationId = decodeURIComponent(url.split('/formulary/')[1]?.split('?')[0] || '')
      const index = mockFormularyMedications.findIndex((f) => f.medicationId === medicationId)
      if (index === -1) {
        throw new Error('Medication not found in formulary')
      }
      mockFormularyMedications.splice(index, 1)
      saveFormulary(mockFormularyMedications)
      return
    }

    // Custom phrases endpoints - DELETE /custom-phrases/{id}
    if (url.startsWith('/custom-phrases/')) {
      const id = url.split('/custom-phrases/')[1]?.split('?')[0]
      const currentUserId = mockApi.getCurrentUserId() || '1'
      const index = mockCustomPhrases.findIndex((p) => p.id === id && p.userId === currentUserId)
      
      if (index === -1) {
        throw new Error('Phrase not found')
      }

      mockCustomPhrases.splice(index, 1)
      saveCustomPhrases(mockCustomPhrases)
      return
    }

    // Template endpoints
    if (url.startsWith('/templates/') && !url.includes('/generate') && !url.includes('/preview')) {
      const id = url.split('/templates/')[1]
      const template = mockTemplates.find((t) => t.id === id)
      if (!template) {
        throw new Error('Template not found')
      }
      // Soft delete (archive)
      template.isArchived = true
      template.updatedAt = new Date().toISOString()
      return
    }

    // Appointment endpoints
    if (url.startsWith('/appointments/')) {
      const id = url.split('/appointments/')[1]
      const index = mockAppointments.findIndex((a) => a.id === id)
      if (index === -1) {
        throw new Error('Appointment not found')
      }
      mockAppointments.splice(index, 1)
      return
    }

    // Patient Files endpoints
    if (url.startsWith('/patient-files/') && !url.includes('/download')) {
      const id = url.split('/patient-files/')[1]
      const index = mockPatientFiles.findIndex(f => f.id === id)
      if (index === -1) {
        throw new Error('Patient file not found')
      }
      mockPatientFiles.splice(index, 1)
      return
    }

    // Consultation endpoints
    if (url.startsWith('/consultations/') && url.includes('/artefacts/')) {
      const parts = url.split('/')
      const consultationId = parts[2]
      const artefactId = parts[4]
      const consultation = mockConsultations.find((c) => c.id === consultationId)
      if (!consultation || !consultation.artefacts) {
        throw new Error('Consultation or artefact not found')
      }
      consultation.artefacts = consultation.artefacts.filter((a) => a.id !== artefactId)
      consultation.updatedAt = new Date().toISOString()
      return
    }

    if (url.startsWith('/consultations/')) {
      const id = url.split('/consultations/')[1]
      const index = mockConsultations.findIndex((c) => c.id === id)
      if (index === -1) {
        throw new Error('Consultation not found')
      }
      mockConsultations.splice(index, 1)
      return
    }

    // Family endpoints
    if (url.startsWith('/families/') && !url.includes('/members')) {
      const id = url.split('/families/')[1]
      const index = mockFamilies.findIndex((f) => f.id === id)
      if (index === -1) {
        throw new Error('Family not found')
      }
      const family = mockFamilies[index]
      // Remove familyId from all member patients
      family.memberIds.forEach((patientId) => {
        const patient = mockPatients.find((p) => p.id === patientId)
        if (patient && patient.familyId === id) {
          delete patient.familyId
        }
      })
      mockFamilies.splice(index, 1)
      return
    }

    if (url.startsWith('/families/') && url.includes('/members/')) {
      const parts = url.split('/families/')[1].split('/members/')
      const familyId = parts[0]
      const patientId = parts[1]
      const family = mockFamilies.find((f) => f.id === familyId)
      if (!family) {
        throw new Error('Family not found')
      }
      family.memberIds = family.memberIds.filter((id) => id !== patientId)
      family.updatedAt = new Date().toISOString()
      // Remove familyId from patient
      const patient = mockPatients.find((p) => p.id === patientId)
      if (patient && patient.familyId === familyId) {
        delete patient.familyId
      }
      return
    }

    // Clinical history endpoints - DELETE
    const clinicalHistoryDeletePath = stripMockRequestPath(url)
    if (clinicalHistoryDeletePath.startsWith('/patients/') && clinicalHistoryDeletePath.includes('/clinical-history')) {
      const parts = clinicalHistoryDeletePath.split('/patients/')[1].split('/clinical-history')
      const patientId = parts[0].split('/')[0].split('?')[0]
      
      let history = mockClinicalHistory.find((h) => h.patientId === patientId)

      // DELETE /patients/:id/clinical-history/conditions/:conditionId
      const conditionMatch = parts[1]?.match(/^\/conditions\/([^/?]+)/)
      if (conditionMatch) {
        const conditionId = conditionMatch[1]
        if (!history) {
          throw new Error('Condition not found')
        }
        const index = history.conditions.findIndex((c) => c.id === conditionId)
        if (index === -1) {
          throw new Error('Condition not found')
        }
        history.conditions.splice(index, 1)
        history.updatedAt = new Date().toISOString()
        return
      }

      if (!history) {
        throw new Error('Clinical history not found')
      }

      // DELETE /patients/:id/clinical-history/family-history/:entryId
      const fhMatch = parts[1]?.match(/^\/family-history\/(.+)$/)
      if (fhMatch) {
        const entryId = fhMatch[1]
        const index = history.familyHistory.findIndex(fh => fh.id === entryId)
        if (index === -1) {
          throw new Error('Family history entry not found')
        }
        history.familyHistory.splice(index, 1)
        history.updatedAt = new Date().toISOString()
        return
      }
      
      // DELETE /patients/:id/clinical-history/consents/:consentId
      const consentMatch = parts[1]?.match(/^\/consents\/(.+)$/)
      if (consentMatch) {
        const consentId = consentMatch[1]
        const index = history.consents.findIndex(c => c.id === consentId)
        if (index === -1) {
          throw new Error('Consent not found')
        }
        history.consents.splice(index, 1)
        history.updatedAt = new Date().toISOString()
        return
      }
    }


    // Task endpoints
    if (url.startsWith('/tasks/')) {
      // Extract ID from URL, handling query parameters and additional path segments
      const pathAfterTasks = url.split('/tasks/')[1]
      const id = pathAfterTasks.split('?')[0].split('/')[0] // Get ID before query params or additional path segments
      await mockApi.deleteTask(id)
      return
    }

    // Automation rules endpoints
    if (url.startsWith('/task-automation/rules/')) {
      const id = url.split('/task-automation/rules/')[1]
      await mockApi.deleteAutomationRule(id)
      return
    }

    // DELETE /users/{id}/signature
    if (url.includes('/users/') && url.endsWith('/signature')) {
      const id = url.split('/users/')[1]?.split('/')[0]
      if (id) {
        await mockApi.deleteSignature(id)
        return { data: { message: 'Signature deleted successfully' } as T }
      }
    }

    if (url.startsWith('/users/') && !url.includes('/signature')) {
      const id = url.split('/users/')[1]
      await mockApi.deleteUser(id)
      return
    }

    if (url.startsWith('/certificates/')) {
      const id = url.split('/certificates/')[1]
      await mockApi.deleteCertificate(id)
      return undefined
    }

    // OLD Formulary DELETE endpoint - REMOVED (using new implementation with localStorage)

    // Appointment types endpoints
    if (url.startsWith('/settings/appointment-types/')) {
      const id = url.split('/settings/appointment-types/')[1]
      const type = mockAppointmentTypes.find((t) => t.id === id)
      if (!type) {
        throw new Error('Appointment type not found')
      }
      if (type.isDefault) {
        throw new Error('Cannot delete default appointment types')
      }
      const index = mockAppointmentTypes.findIndex((t) => t.id === id)
      mockAppointmentTypes.splice(index, 1)
      console.log('[Mock API] DELETE /settings/appointment-types - deleted appointment type:', id)
      return
    }

    // Care Plans DELETE endpoints
    if (url.startsWith('/care-plans/')) {
      const id = url.split('/care-plans/')[1]?.split('/')[0]
      if (id) {
        await mockApi.deleteCarePlan(id)
        return
      }
    }

    // Protocols DELETE endpoints
    if (url.startsWith('/protocols/') && !url.includes('/compliance') && !url.includes('/patients/')) {
      const id = url.split('/protocols/')[1]?.split('/')[0]
      if (id) {
        await mockApi.deleteProtocol(id)
        return
      }
    }

    // Drug Recalls DELETE endpoints
    if (url.startsWith('/drug-recalls/') && !url.includes('/affected-patients') && !url.includes('/notifications')) {
      const id = url.split('/drug-recalls/')[1]?.split('/')[0]
      if (id) {
        await mockApi.deleteDrugRecall(id)
        return
      }
    }

    // Research Studies DELETE endpoints
    if (url.startsWith('/research-studies/') && !url.includes('/patients/') && !url.includes('/protocol')) {
      const id = url.split('/research-studies/')[1]?.split('/')[0]
      if (id) {
        await mockApi.deleteResearchStudy(id)
        return
      }
    }

    // Assessments DELETE endpoints
    if (url.startsWith('/assessments/')) {
      const id = url.split('/assessments/')[1]
      await mockApi.deleteAssessment(id)
      return
    }

    // Patient Guidance DELETE endpoints
    if (url.startsWith('/patients/') && url.includes('/guidance/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const guidanceId = parts[4]
      if (patientId && guidanceId) {
        await mockApi.deletePatientGuidance(patientId, guidanceId)
        return
      }
    }

    // Patient Reminders DELETE endpoints
    if (url.startsWith('/patients/') && url.includes('/reminders/')) {
      const parts = url.split('/')
      const patientId = parts[2]
      const reminderId = parts[4]
      if (patientId && reminderId) {
        await mockApi.deletePatientReminder(patientId, reminderId)
        return
      }
    }

    throw new Error('Not found')
  },
  
  // Certificates endpoints
  getCertificates: async () => {
    await delay(200)
    return [...mockCertificates]
  },

  getCertificateById: async (_id: string) => {
    await delay(200)
    const certificate = mockCertificates.find((item) => item.id === _id)
    if (!certificate) {
      throw new Error('Certificate not found')
    }
    return { ...certificate }
  },

  createCertificate: async (data: any) => {
    await delay(300)
    const now = new Date().toISOString()
    const certificate = {
      id: crypto.randomUUID(),
      type: data.type,
      name: data.name,
      description: data.description,
      serialNumber: crypto.randomUUID().replace(/-/g, '').slice(0, 16),
      issuer: 'Mock Certificate Authority',
      status: 'pending' as const,
      isActive: false,
      validFrom: new Date().toISOString(),
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
      createdAt: now,
      updatedAt: now,
    }
    mockCertificates.push(certificate)
    return { ...certificate }
  },

  updateCertificate: async (id: string, data: any) => {
    await delay(300)
    const now = new Date().toISOString()
    const index = mockCertificates.findIndex((item) => item.id === id)
    if (index === -1) {
      const certificate = {
        id,
        ...data,
        createdAt: now,
        updatedAt: now,
      }
      mockCertificates.push(certificate)
      return { ...certificate }
    }

    mockCertificates[index] = {
      ...mockCertificates[index],
      ...data,
      updatedAt: now,
    }
    return { ...mockCertificates[index] }
  },

  deleteCertificate: async (_id: string) => {
    await delay(200)
    const index = mockCertificates.findIndex((item) => item.id === _id)
    if (index !== -1) {
      mockCertificates.splice(index, 1)
    }
  },

  // GMS endpoints
  checkEligibility: async (schemeId: string, _patientId?: string) => {
    await delay(400)
    const trimmed = (schemeId || '').trim()
    const found = trimmed.length >= 7 && /[A-Za-z]$/.test(trimmed) && trimmed.toUpperCase().endsWith('B')
    if (!found) {
      return { found: false, schemeId: trimmed || undefined }
    }
    const start = new Date()
    start.setFullYear(start.getFullYear() - 2)
    const end = new Date()
    end.setFullYear(end.getFullYear() + 1)
    return {
      found: true,
      schemeId: trimmed,
      schemeType: 'GMS',
      eligibilityStatus: 'Valid',
      doctorNumber: '54343',
      eligibilityStartDate: start.toISOString().slice(0, 10),
      eligibilityEndDate: end.toISOString().slice(0, 10),
    }
  },

  getGMSStatus: async (patientId: string) => {
    await delay(200)
    // Return mock GMS status
    return {
      patientId,
      gmsNumber: `GMS${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
      status: Math.random() > 0.5 ? 'eligible' : 'not_eligible',
      eligibilityStartDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      eligibilityEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      cardType: 'medical_card',
      lastChecked: new Date().toISOString(),
    }
  },

  checkGMSStatus: async (patientId: string) => {
    await delay(500)
    return mockApi.getGMSStatus(patientId)
  },

  checkDPSDrugs: async (patientId: string, drugCodes: string[]) => {
    await delay(500)
    const drugs = drugCodes.map((code) => ({
      drugCode: code,
      drugName: `Drug ${code}`,
      isCovered: Math.random() > 0.3,
      maxPayment: Math.random() > 0.3 ? Math.random() * 50 : undefined,
      notes: Math.random() > 0.7 ? 'Subject to DPS limits' : undefined,
    }))
    return {
      patientId,
      drugs,
      totalPayment: drugs
        .filter((d) => d.isCovered && d.maxPayment)
        .reduce((sum, d) => sum + (d.maxPayment || 0), 0),
      checkedAt: new Date().toISOString(),
    }
  },

  searchDrug: async (searchTerm: string) => {
    await delay(300)
    const mockDrugs = [
      { drugCode: 'DRUG001', drugName: 'Paracetamol 500mg', isCovered: true, maxPayment: 5.0, packSize: 24 },
      { drugCode: 'DRUG002', drugName: 'Ibuprofen 400mg', isCovered: true, maxPayment: 7.5, packSize: 30 },
      { drugCode: 'DRUG003', drugName: 'Aspirin 75mg', isCovered: false, packSize: 28 },
    ]
    if (!searchTerm) return []
    return mockDrugs.filter(
      (d) =>
        d.drugName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.drugCode.toLowerCase().includes(searchTerm.toLowerCase())
    )
  },

  // Drugs endpoints
  drugs: [] as any[],

  getDrugs: async (filters?: any) => {
    await delay(200)
    console.log('getDrugs called with filters:', filters)
    console.log('mockApi.drugs length:', mockApi.drugs.length)
    let drugs = [...mockApi.drugs]
    
    if (filters?.search) {
      const search = filters.search.toLowerCase()
      drugs = drugs.filter(
        (d) =>
          d.productName?.toLowerCase().includes(search) ||
          d.drugIdPK?.toLowerCase().includes(search) ||
          d.licenceNumber?.toLowerCase().includes(search)
      )
    }
    
    if (filters?.productType) {
      drugs = drugs.filter((d) => d.productType === filters.productType)
    }
    
    if (filters?.marketInfo) {
      drugs = drugs.filter((d) => d.marketInfo === filters.marketInfo)
    }
    
    if (filters?.isWithdrawn !== undefined) {
      drugs = drugs.filter((d) => d.isWithdrawn === filters.isWithdrawn)
    }
    
    if (filters?.isInterchangeable !== undefined) {
      drugs = drugs.filter((d) => d.isInterchangeable === filters.isInterchangeable)
    }
    
    console.log('getDrugs returning:', drugs.length, 'drugs')
    return drugs
  },

  getDrugById: async (id: string) => {
    await delay(200)
    const drug = mockApi.drugs.find((d) => d.id === id)
    if (!drug) {
      throw new Error('Drug not found')
    }
    return drug
  },

  getDrugByDrugIdPK: async (drugIdPK: string) => {
    await delay(200)
    const drug = mockApi.drugs.find((d) => d.drugIdPK === drugIdPK)
    if (!drug) {
      throw new Error('Drug not found')
    }
    return drug
  },

  searchDrugs: async (searchTerm: string) => {
    await delay(200)
    if (!searchTerm) return []
    const term = searchTerm.toLowerCase()
    return mockApi.drugs.filter(
      (d) =>
        d.productName?.toLowerCase().includes(term) ||
        d.drugIdPK?.toLowerCase().includes(term) ||
        d.licenceNumber?.toLowerCase().includes(term)
    )
  },

  getDrugSyncStatus: async () => {
    await delay(200)
    return {
      lastSyncDate: mockApi.drugs.length > 0 ? new Date().toISOString() : undefined,
      totalProducts: mockApi.drugs.length,
      totalWithdrawn: mockApi.drugs.filter((d) => d.isWithdrawn).length,
      totalInterchangeable: mockApi.drugs.filter((d) => d.isInterchangeable).length,
      syncInProgress: false,
    }
  },

  syncDrugsFromHPRA: async () => {
    try {
      console.log('Starting HPRA sync...')
      // Fetch XML files from local storage (served as static assets)
      // Helper to safely check if we're in development
      const isDev = (() => {
        try {
          // @ts-ignore
          if (typeof globalThis.import !== 'undefined' && globalThis.import.meta?.env) {
            // @ts-ignore
            return globalThis.import.meta.env.DEV === true
          }
        } catch {
          // Ignore errors
        }
        return typeof window !== 'undefined' && window.location.hostname === 'localhost'
      })()
      
      const baseUrl = isDev 
        ? '/data/hpra' 
        : 'https://assets.hpra.ie/products/xml'
      
      console.log(`Fetching XML files from: ${baseUrl}`)
      
      const [latestProducts, withdrawnProducts, interchangeableProducts] = await Promise.all([
        fetchAndParseHPRAXML(`${baseUrl}/latestHumanlist.xml`).catch((err) => {
          console.error('Error fetching latest list:', err)
          return []
        }),
        fetchAndParseHPRAXML(`${baseUrl}/withdrawnHumanlist.xml`).catch((err) => {
          console.error('Error fetching withdrawn list:', err)
          return []
        }),
        fetchAndParseHPRAXML(`${baseUrl}/latestInterchangeableslist.xml`).catch((err) => {
          console.error('Error fetching interchangeable list:', err)
          return []
        }),
      ])
      
      console.log(`Parsed ${latestProducts.length} latest products, ${withdrawnProducts.length} withdrawn, ${interchangeableProducts.length} interchangeable`)

      // Create sets for quick lookup
      const withdrawnSet = new Set(withdrawnProducts.map((p) => p.drugIdPK))
      const interchangeableSet = new Set([
        ...interchangeableProducts.map((p) => p.drugIdPK),
        ...interchangeableProducts.map((p) => p.interchangeableListCode).filter(Boolean) as string[],
      ])

      // Combine all products and mark withdrawn/interchangeable status
      const allProducts = [...latestProducts, ...withdrawnProducts]
      const drugRecords = allProducts.map((product) => {
        const drugId = product.drugIdPK.replace(/[^a-zA-Z0-9]/g, '_')
        const isWithdrawn = withdrawnSet.has(product.drugIdPK)
        const isInterchangeable = 
          interchangeableSet.has(product.drugIdPK) || 
          (product.interchangeableListCode && interchangeableSet.has(product.interchangeableListCode))

        return {
          id: drugId,
          ...product,
          isWithdrawn,
          isInterchangeable,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      })

      // Remove duplicates based on drugIdPK
      const uniqueDrugs = new Map<string, any>()
      drugRecords.forEach((drug) => {
        const key = drug.drugIdPK
        if (!uniqueDrugs.has(key) || !drug.isWithdrawn) {
          // Prefer non-withdrawn versions
          uniqueDrugs.set(key, drug)
        }
      })

      mockApi.drugs = Array.from(uniqueDrugs.values())

      console.log('✅ Sync completed. Total drugs in mockApi.drugs:', mockApi.drugs.length)
      if (mockApi.drugs.length > 0) {
        console.log('Sample drugs:', mockApi.drugs.slice(0, 3).map(d => ({ 
          id: d.id, 
          productName: d.productName, 
          drugIdPK: d.drugIdPK 
        })))
      } else {
        console.warn('⚠️ No drugs were synced! Check XML parsing.')
      }

      return {
        message: `Successfully synced ${mockApi.drugs.length} drugs`,
        status: 'completed',
      }
    } catch (error: any) {
      console.error('Error in syncDrugsFromHPRA:', error)
      throw new Error(`Failed to sync drugs from HPRA: ${error?.message || 'Unknown error'}`)
    }
  },

  // User endpoints
  getUsers: async () => {
    await delay(200)
    // Load dev signature for practitioners if file exists
    const signature = await loadDevSignature()
    return mockUsers.map(user => {
      // Add signatureS3Key to clinical users if available (simulate S3 key)
      if (signature && (user.roleCategory === 'Clinical' || user.role === 'doctor' || user.role === 'nurse')) {
        return { ...user, signatureS3Key: `signatures/${user.id}/signature.png` }
      }
      return user
    })
  },

  getUserById: async (id: string) => {
    await delay(200)
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    // Load dev signature for practitioners if file exists (simulate S3 key)
    const signature = await loadDevSignature()
    if (signature && (user.roleCategory === 'Clinical' || user.role === 'doctor' || user.role === 'nurse')) {
      return { ...user, signatureS3Key: `signatures/${user.id}/signature.png` }
    }
    return user
  },

  createUser: async (data: UserFormData) => {
    await delay(300)
    const now = new Date().toISOString()
    const newUser: User = {
      id: crypto.randomUUID(),
      username: data.username,
      email: data.email,
      salutation: data.salutation,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      role: data.role,
      roleCategory: data.roleCategory,
      permissions: data.permissions || [],
      isActive: data.isActive !== undefined ? data.isActive : true,
      twoFactorEnabled: data.twoFactorEnabled !== undefined ? data.twoFactorEnabled : true,
      phone: data.phone,
      mobile: data.mobile,
      address: data.address,
      hpi: data.hpi,
      professionalBodyRegistrationNumber: data.professionalBodyRegistrationNumber,
      prescriberIdentifierNumber: data.prescriberIdentifierNumber,
      gmsNumber: data.gmsNumber,
      contractualArrangements: data.contractualArrangements,
      signatureS3Key: undefined, // Signatures uploaded separately
      createdAt: now,
      updatedAt: now,
    }
    mockUsers.push(newUser)
    return newUser
  },

  updateUser: async (id: string, data: Partial<UserFormData>) => {
    await delay(300)
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    const updated: User = {
      ...user,
      ...data,
      roleCategory: data.roleCategory || user.roleCategory,
      updatedAt: new Date().toISOString(),
    }
    const index = mockUsers.findIndex((u) => u.id === id)
    mockUsers[index] = updated
    return updated
  },

  deleteUser: async (id: string) => {
    await delay(300)
    const index = mockUsers.findIndex((u) => u.id === id)
    if (index === -1) {
      throw new Error('User not found')
    }
    mockUsers.splice(index, 1)
  },

  activateUser: async (id: string) => {
    await delay(300)
    return mockApi.updateUser(id, { isActive: true })
  },

  deactivateUser: async (id: string) => {
    await delay(300)
    return mockApi.updateUser(id, { isActive: false })
  },

  enableUser2fa: async (id: string) => {
    await delay(300)
    return mockApi.updateUser(id, { twoFactorEnabled: true })
  },

  disableUser2fa: async (id: string) => {
    await delay(300)
    return mockApi.updateUser(id, { twoFactorEnabled: false })
  },

  resetUserPassword: async (id: string, _newPassword: string) => {
    await delay(300)
    // In a real implementation, this would hash and store the password
    // For mock, we just verify the user exists
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    return
  },

  recordLastLogin: async (id: string) => {
    await delay(50)
    const user = mockUsers.find((u) => u.id === id)
    if (user) {
      user.lastLoginAt = new Date().toISOString()
    }
  },

  // Signature management methods
  getSignatureUrl: async (id: string): Promise<string> => {
    await delay(200)
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    // In mock mode, return a data URL for the dev signature if available
    const signature = await loadDevSignature()
    if (signature) {
      return `data:image/png;base64,${signature}`
    }
    // If no dev signature, return a placeholder or throw error
    throw new Error('Signature not found')
  },

  uploadSignature: async (id: string, file: File): Promise<void> => {
    await delay(300)
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    // In mock mode, store the S3 key in the user object
    const s3Key = `signatures/${id}/${file.name}`
    const index = mockUsers.findIndex((u) => u.id === id)
    mockUsers[index] = { ...user, signatureS3Key: s3Key }
  },

  deleteSignature: async (id: string): Promise<void> => {
    await delay(300)
    const user = mockUsers.find((u) => u.id === id)
    if (!user) {
      throw new Error('User not found')
    }
    // In mock mode, remove the S3 key from the user object
    const index = mockUsers.findIndex((u) => u.id === id)
    mockUsers[index] = { ...user, signatureS3Key: undefined }
  },

  // Role permission endpoints
  getRolePermissions: async (): Promise<RolePermission[]> => {
    await delay(200)
    return [...mockRolePermissions]
  },

  getRolePermission: async (role: UserRole): Promise<RolePermission> => {
    await delay(200)
    const rolePermission = mockRolePermissions.find((rp) => rp.role === role)
    if (!rolePermission) {
      // If not found, create a default one
      const now = new Date().toISOString()
      const defaultPermission: RolePermission = {
        role,
        roleCategory: getRoleCategory(role),
        permissions: DEFAULT_ROLE_PERMISSIONS[role] || [],
        createdAt: now,
        updatedAt: now,
      }
      mockRolePermissions.push(defaultPermission)
      return defaultPermission
    }
    return rolePermission
  },

  updateRolePermission: async (role: UserRole, permissions: Permission[]): Promise<RolePermission> => {
    await delay(300)
    const index = mockRolePermissions.findIndex((rp) => rp.role === role)
    const now = new Date().toISOString()
    
    if (index === -1) {
      // Create new role permission if it doesn't exist
      const newRolePermission: RolePermission = {
        role,
        roleCategory: getRoleCategory(role),
        permissions,
        createdAt: now,
        updatedAt: now,
      }
      mockRolePermissions.push(newRolePermission)
      return newRolePermission
    }
    
    const updated: RolePermission = {
      ...mockRolePermissions[index],
      permissions,
      updatedAt: now,
    }
    mockRolePermissions[index] = updated
    return updated
  },

  getPermissionTemplates: async (): Promise<PermissionTemplate[]> => {
    await delay(200)
    return [...PERMISSION_TEMPLATES]
  },

  getScopeTemplates: async (): Promise<PermissionTemplate[]> => {
    await delay(200)
    // Scope templates are the same as permission templates for now
    return [...PERMISSION_TEMPLATES]
  },

  applyTemplateToRole: async (role: UserRole, templateName: string): Promise<RolePermission> => {
    await delay(300)
    const template = PERMISSION_TEMPLATES.find((t) => t.name === templateName || t.id === templateName)
    if (!template) {
      throw new Error(`Template "${templateName}" not found`)
    }
    
    return mockApi.updateRolePermission(role, template.permissions)
  },

  // Tenant settings
  getTenantSettings: async (): Promise<{
    sessionTimeoutMinutes: number
    logoUrl?: string | null
    posProvider?: 'none' | 'square'
    squareEnvironment?: 'sandbox' | 'production'
    squareAppId?: string | null
    squareAccessToken?: string | null
    squareAccessTokenLast4?: string | null
    hasSquareAccessToken?: boolean
    smsEnabled?: boolean
    smsSenderId?: string | null
    smsOriginationNumber?: string | null
    smsQuietHoursStart?: string | null
    smsQuietHoursEnd?: string | null
    stcGoogleDriveFolderId?: string | null
    stcGoogleClientId?: string | null
    stcWacomLicenceId?: string | null
    stcWacomLicenceKey?: string | null
    hasStcWacomLicence?: boolean
    modules?: { leave?: boolean }
  }> => {
    await delay(200)
    // Default to 30 minutes if not set
    const stored = typeof window !== 'undefined'
      ? window.localStorage.getItem('clnicl_tenant_settings')
      : null

    if (stored) {
      try {
        const parsed = JSON.parse(stored) as {
          sessionTimeoutMinutes?: number
          logoUrl?: string | null
          posProvider?: 'none' | 'square'
          squareEnvironment?: 'sandbox' | 'production'
          squareAppId?: string | null
          squareAccessToken?: string | null
          squareAccessTokenLast4?: string | null
          hasSquareAccessToken?: boolean
          smsEnabled?: boolean
          smsSenderId?: string | null
          smsOriginationNumber?: string | null
          smsQuietHoursStart?: string | null
          smsQuietHoursEnd?: string | null
          stcGoogleDriveFolderId?: string | null
          stcGoogleClientId?: string | null
          stcWacomLicenceId?: string | null
          stcWacomLicenceKey?: string | null
          hasStcWacomLicence?: boolean
          modules?: { leave?: boolean }
        }
        if (parsed.sessionTimeoutMinutes && Number.isFinite(parsed.sessionTimeoutMinutes)) {
          return {
            sessionTimeoutMinutes: parsed.sessionTimeoutMinutes,
            logoUrl: parsed.logoUrl ?? null,
            posProvider: parsed.posProvider ?? 'none',
            squareEnvironment: parsed.squareEnvironment ?? 'sandbox',
            squareAppId: parsed.squareAppId ?? null,
            squareAccessToken: parsed.squareAccessToken ?? null,
            squareAccessTokenLast4: parsed.squareAccessTokenLast4 ?? null,
            hasSquareAccessToken: parsed.hasSquareAccessToken ?? false,
            smsEnabled: parsed.smsEnabled ?? false,
            smsSenderId: parsed.smsSenderId ?? null,
            smsOriginationNumber: parsed.smsOriginationNumber ?? null,
            smsQuietHoursStart: parsed.smsQuietHoursStart ?? '21:00',
            smsQuietHoursEnd: parsed.smsQuietHoursEnd ?? '08:00',
            stcGoogleDriveFolderId: parsed.stcGoogleDriveFolderId ?? null,
            stcGoogleClientId: parsed.stcGoogleClientId ?? null,
            stcWacomLicenceId: parsed.stcWacomLicenceId ?? null,
            stcWacomLicenceKey: parsed.stcWacomLicenceKey ?? null,
            hasStcWacomLicence: !!(parsed.stcWacomLicenceKey && parsed.stcWacomLicenceKey.length > 0),
            modules: { leave: parsed.modules?.leave === true },
          }
        }
      } catch {
        // ignore parse errors and fall back to default
      }
    }

    return {
      sessionTimeoutMinutes: 30,
      logoUrl: null,
      posProvider: 'none',
      squareEnvironment: 'sandbox',
      squareAppId: null,
      squareAccessToken: null,
      squareAccessTokenLast4: null,
      hasSquareAccessToken: false,
      smsEnabled: false,
      smsSenderId: null,
      smsOriginationNumber: null,
      smsQuietHoursStart: '21:00',
      smsQuietHoursEnd: '08:00',
      stcGoogleDriveFolderId: null,
      stcGoogleClientId: null,
      stcWacomLicenceId: null,
      stcWacomLicenceKey: null,
      hasStcWacomLicence: false,
      modules: { leave: false },
    }
  },

  updateTenantSettings: async (data: Partial<{
    sessionTimeoutMinutes: number
    logoUrl?: string | null
    posProvider?: 'none' | 'square'
    squareEnvironment?: 'sandbox' | 'production'
    squareAppId?: string | null
    squareAccessToken?: string | null
    smsEnabled?: boolean
    smsSenderId?: string | null
    smsOriginationNumber?: string | null
    smsQuietHoursStart?: string | null
    smsQuietHoursEnd?: string | null
    stcGoogleDriveFolderId?: string | null
    stcGoogleClientId?: string | null
    stcWacomLicenceId?: string | null
    stcWacomLicenceKey?: string | null
    modules?: { leave?: boolean }
  }>): Promise<{
    sessionTimeoutMinutes: number
    logoUrl?: string | null
    posProvider?: 'none' | 'square'
    squareEnvironment?: 'sandbox' | 'production'
    squareAppId?: string | null
    squareAccessToken?: string | null
    squareAccessTokenLast4?: string | null
    hasSquareAccessToken?: boolean
    smsEnabled?: boolean
    smsSenderId?: string | null
    smsOriginationNumber?: string | null
    smsQuietHoursStart?: string | null
    smsQuietHoursEnd?: string | null
    stcGoogleDriveFolderId?: string | null
    stcGoogleClientId?: string | null
    stcWacomLicenceId?: string | null
    stcWacomLicenceKey?: string | null
    hasStcWacomLicence?: boolean
    modules?: { leave?: boolean }
  }> => {
    await delay(200)
    const current = await mockApi.getTenantSettings()
    const token = data.squareAccessToken?.trim()
    const hasNewToken = !!token
    let stcWacomKey: string | null = null
    if (typeof data.stcWacomLicenceKey === 'string' && data.stcWacomLicenceKey.trim().length > 0) {
      stcWacomKey = data.stcWacomLicenceKey.trim()
    } else if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('clnicl_tenant_settings')
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as { stcWacomLicenceKey?: string }
          if (typeof parsed.stcWacomLicenceKey === 'string' && parsed.stcWacomLicenceKey.length > 0) {
            stcWacomKey = parsed.stcWacomLicenceKey
          }
        } catch {
          // ignore
        }
      }
    }
    const storedForPersist = {
      sessionTimeoutMinutes: typeof data.sessionTimeoutMinutes === 'number'
        ? data.sessionTimeoutMinutes
        : current.sessionTimeoutMinutes,
      logoUrl: Object.prototype.hasOwnProperty.call(data, 'logoUrl') ? (data.logoUrl ?? null) : (current.logoUrl ?? null),
      posProvider: data.posProvider ?? current.posProvider ?? 'none',
      squareEnvironment: data.squareEnvironment ?? current.squareEnvironment ?? 'sandbox',
      squareAppId: Object.prototype.hasOwnProperty.call(data, 'squareAppId') ? (data.squareAppId ?? null) : (current.squareAppId ?? null),
      squareAccessToken: hasNewToken ? token : (current as { squareAccessToken?: string }).squareAccessToken ?? null,
      squareAccessTokenLast4: hasNewToken ? token?.slice(-4) ?? null : (current.squareAccessTokenLast4 ?? null),
      hasSquareAccessToken: hasNewToken ? true : (current.hasSquareAccessToken ?? false),
      smsEnabled: Object.prototype.hasOwnProperty.call(data, 'smsEnabled') ? !!data.smsEnabled : (current.smsEnabled ?? false),
      smsSenderId: Object.prototype.hasOwnProperty.call(data, 'smsSenderId') ? (data.smsSenderId ?? null) : (current.smsSenderId ?? null),
      smsOriginationNumber: Object.prototype.hasOwnProperty.call(data, 'smsOriginationNumber') ? (data.smsOriginationNumber ?? null) : (current.smsOriginationNumber ?? null),
      smsQuietHoursStart: Object.prototype.hasOwnProperty.call(data, 'smsQuietHoursStart') ? (data.smsQuietHoursStart ?? null) : (current.smsQuietHoursStart ?? '21:00'),
      smsQuietHoursEnd: Object.prototype.hasOwnProperty.call(data, 'smsQuietHoursEnd') ? (data.smsQuietHoursEnd ?? null) : (current.smsQuietHoursEnd ?? '08:00'),
      stcGoogleDriveFolderId: Object.prototype.hasOwnProperty.call(data, 'stcGoogleDriveFolderId') ? (data.stcGoogleDriveFolderId ?? null) : (current.stcGoogleDriveFolderId ?? null),
      stcGoogleClientId: Object.prototype.hasOwnProperty.call(data, 'stcGoogleClientId') ? (data.stcGoogleClientId ?? null) : (current.stcGoogleClientId ?? null),
      stcWacomLicenceId: Object.prototype.hasOwnProperty.call(data, 'stcWacomLicenceId') ? (data.stcWacomLicenceId ?? null) : (current.stcWacomLicenceId ?? null),
      stcWacomLicenceKey: stcWacomKey,
      modules: {
        leave: Object.prototype.hasOwnProperty.call(data, 'modules')
          ? data.modules?.leave === true
          : current.modules?.leave === true,
      },
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('clnicl_tenant_settings', JSON.stringify(storedForPersist))
    }
    const hasStcWacomLicence = !!(stcWacomKey && stcWacomKey.length > 0)
    return {
      ...storedForPersist,
      squareAccessToken: undefined,
      hasStcWacomLicence,
    } as {
      sessionTimeoutMinutes: number
      logoUrl?: string | null
      posProvider?: 'none' | 'square'
      squareEnvironment?: 'sandbox' | 'production'
      squareAppId?: string | null
      squareAccessToken?: string | null
      squareAccessTokenLast4?: string | null
      hasSquareAccessToken?: boolean
      smsEnabled?: boolean
      smsSenderId?: string | null
      smsOriginationNumber?: string | null
      smsQuietHoursStart?: string | null
      smsQuietHoursEnd?: string | null
      stcGoogleDriveFolderId?: string | null
      stcGoogleClientId?: string | null
      stcWacomLicenceId?: string | null
      hasStcWacomLicence?: boolean
      modules?: { leave?: boolean }
    }
  },

  // Task endpoints
  createTask: async (data: TaskFormData): Promise<Task> => {
    await delay(300)
    const now = new Date().toISOString()
    const newTask: Task = {
      id: crypto.randomUUID(),
      title: data.title,
      description: data.description,
      status: data.status || 'pending',
      priority: data.priority || 'medium',
      assignedTo: data.assignedTo,
      assignedBy: (data as any).assignedBy,
      dueDate: data.dueDate,
      createdAt: now,
      updatedAt: now,
      relatedEntityType: data.relatedEntityType,
      relatedEntityId: data.relatedEntityId,
      tags: data.tags || [],
    }
    mockTasks.push(newTask)
    return newTask
  },

  // Helper to get current user ID from localStorage
  getCurrentUserId: (): string | null => {
    try {
      const userStr = localStorage.getItem('clnicl_user')
      if (userStr) {
        const user = JSON.parse(userStr)
        return user.id
      }
    } catch {
      // Ignore errors
    }
    return null
  },

  // Chat endpoints
  getAllMessages: async (): Promise<Message[]> => {
    await delay(200)
    const currentUserId = mockApi.getCurrentUserId() || '1'
    return mockMessages.filter(
      (msg) => msg.isTeamMessage || msg.senderId === currentUserId || msg.recipientId === currentUserId
    )
  },

  getTeamMessages: async (): Promise<Message[]> => {
    await delay(200)
    return mockMessages.filter((msg) => msg.isTeamMessage)
  },

  getDirectMessages: async (userId: string): Promise<Message[]> => {
    await delay(200)
    const currentUserId = mockApi.getCurrentUserId() || '1'
    return mockMessages.filter(
      (msg) =>
        !msg.isTeamMessage &&
        ((msg.senderId === currentUserId && msg.recipientId === userId) ||
          (msg.senderId === userId && msg.recipientId === currentUserId))
    )
  },

  sendMessage: async (data: ChatFormData): Promise<Message> => {
    await delay(300)
    const currentUserId = mockApi.getCurrentUserId() || '1'
    const now = new Date().toISOString()
    const newMessage: Message = {
      id: crypto.randomUUID(),
      content: data.content,
      senderId: currentUserId,
      recipientId: data.recipientId,
      isTeamMessage: !data.recipientId,
      createdAt: now,
    }
    mockMessages.push(newMessage)
    return newMessage
  },

  markMessagesAsRead: async (messageIds: string[]): Promise<void> => {
    await delay(200)
    const now = new Date().toISOString()
    messageIds.forEach((id) => {
      const message = mockMessages.find((m) => m.id === id)
      if (message && !message.readAt) {
        message.readAt = now
      }
    })
  },

  getUnreadMessageCount: async (): Promise<number> => {
    await delay(200)
    const currentUserId = mockApi.getCurrentUserId() || '1'
    return mockMessages.filter(
      (msg) => msg.senderId !== currentUserId && !msg.readAt && (msg.isTeamMessage || msg.recipientId === currentUserId)
    ).length
  },

  // Maternity endpoints
  getPregnancies: async (patientId: string): Promise<Pregnancy[]> => {
    await delay(200)
    return mockPregnancies.filter(p => p.patientId === patientId)
  },

  getPregnancy: async (pregnancyId: string): Promise<Pregnancy | null> => {
    await delay(200)
    return mockPregnancies.find(p => p.pregnancyId === pregnancyId) || null
  },

  createPregnancy: async (data: PregnancyFormData): Promise<Pregnancy> => {
    await delay(300)
    const pregnancy: Pregnancy = {
      pregnancyId: crypto.randomUUID(),
      patientId: data.patientId,
      lastMenstrualPeriod: data.lastMenstrualPeriod,
      estimatedDueDate: calculateDueDate(data.lastMenstrualPeriod).toISOString().split('T')[0],
      pregnancyNumber: mockPregnancies.filter(p => p.patientId === data.patientId).length + 1,
      status: 'active',
      babies: [],
      notes: data.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: mockApi.getCurrentUserId() || '1',
    }
    mockPregnancies.push(pregnancy)
    return pregnancy
  },

  updatePregnancy: async (pregnancyId: string, data: Partial<PregnancyFormData>): Promise<Pregnancy> => {
    await delay(300)
    const index = mockPregnancies.findIndex(p => p.pregnancyId === pregnancyId)
    if (index === -1) throw new Error('Pregnancy not found')
    const existing = mockPregnancies[index]
    const updated: Pregnancy = {
      ...existing,
      ...data,
      updatedAt: new Date().toISOString(),
      updatedBy: mockApi.getCurrentUserId() || '1',
    }
    // Recalculate EDD if LMP changed
    if (data.lastMenstrualPeriod && data.lastMenstrualPeriod !== existing.lastMenstrualPeriod) {
      updated.estimatedDueDate = calculateDueDate(data.lastMenstrualPeriod).toISOString().split('T')[0]
    }
    mockPregnancies[index] = updated
    return updated
  },

  recordOutcome: async (pregnancyId: string, outcomeData: any): Promise<Pregnancy> => {
    await delay(300)
    const index = mockPregnancies.findIndex(p => p.pregnancyId === pregnancyId)
    if (index === -1) throw new Error('Pregnancy not found')
    const existing = mockPregnancies[index]
    const outcomeDate = outcomeData.outcomeDate || new Date().toISOString().split('T')[0]
    const gestationalAge = calculateGestationalAge(existing.lastMenstrualPeriod, new Date(outcomeDate))
    const updated: Pregnancy = {
      ...existing,
      ...outcomeData,
      gestationalAgeAtOutcome: gestationalAge.display,
      status: 'completed',
      updatedAt: new Date().toISOString(),
      updatedBy: mockApi.getCurrentUserId() || '1',
    }
    mockPregnancies[index] = updated
    return updated
  },

  getVisits: async (pregnancyId: string): Promise<MaternityVisit[]> => {
    await delay(200)
    return mockMaternityVisits.filter(v => v.pregnancyId === pregnancyId)
  },

  getVisit: async (visitId: string): Promise<MaternityVisit | null> => {
    await delay(200)
    return mockMaternityVisits.find(v => v.visitId === visitId) || null
  },

  createVisit: async (data: MaternityVisitFormData): Promise<MaternityVisit> => {
    await delay(300)
    const pregnancy = mockPregnancies.find(p => p.pregnancyId === data.pregnancyId)
    if (!pregnancy) throw new Error('Pregnancy not found')
    const visitDate = new Date(data.visitDate)
    const gestationalAge = calculateGestationalAge(pregnancy.lastMenstrualPeriod, visitDate)
    const now = new Date().toISOString()
    const visit: MaternityVisit = {
      visitId: crypto.randomUUID(),
      pregnancyId: data.pregnancyId,
      patientId: data.patientId,
      visitDate: data.visitDate,
      visitType: data.visitType,
      consultationId: data.consultationId,
      gestationalAge: gestationalAge.display,
      bloodPressure: data.bloodPressure,
      weight: data.weight,
      fundalHeight: data.fundalHeight,
      fetalHeartRate: data.fetalHeartRate,
      urineTest: data.urineTest,
      presentation: data.presentation,
      cervicalDilation: data.cervicalDilation,
      maternalMood: data.maternalMood,
      customFields: data.customFields,
      interventions: (data.interventions || []).map(i => ({
        id: crypto.randomUUID(),
        ...i,
        performedBy: mockApi.getCurrentUserId() || '1',
        performedAt: now,
      })),
      notes: data.notes,
      createdAt: now,
      updatedAt: now,
      createdBy: mockApi.getCurrentUserId() || '1',
    }
    mockMaternityVisits.push(visit)
    return visit
  },

  updateVisit: async (visitId: string, data: Partial<MaternityVisitFormData>): Promise<MaternityVisit> => {
    await delay(300)
    const index = mockMaternityVisits.findIndex(v => v.visitId === visitId)
    if (index === -1) throw new Error('Visit not found')
    const existing = mockMaternityVisits[index]
    
    // Merge the data, ensuring all required fields are preserved
    const updated: MaternityVisit = {
      ...existing,
      visitDate: data.visitDate ?? existing.visitDate,
      visitType: data.visitType ?? existing.visitType,
      gestationalAge: data.gestationalAge ?? existing.gestationalAge,
      consultationId: data.consultationId !== undefined ? data.consultationId : existing.consultationId,
      bloodPressure: data.bloodPressure !== undefined ? data.bloodPressure : existing.bloodPressure,
      weight: data.weight !== undefined ? data.weight : existing.weight,
      fundalHeight: data.fundalHeight !== undefined ? data.fundalHeight : existing.fundalHeight,
      fetalHeartRate: data.fetalHeartRate !== undefined ? data.fetalHeartRate : existing.fetalHeartRate,
      urineTest: data.urineTest !== undefined ? data.urineTest : existing.urineTest,
      presentation: data.presentation !== undefined ? data.presentation : existing.presentation,
      cervicalDilation: data.cervicalDilation !== undefined ? data.cervicalDilation : existing.cervicalDilation,
      maternalMood: data.maternalMood !== undefined ? data.maternalMood : existing.maternalMood,
      notes: data.notes !== undefined ? data.notes : existing.notes,
      updatedAt: new Date().toISOString(),
      updatedBy: mockApi.getCurrentUserId() || '1',
    }
    mockMaternityVisits[index] = updated
    return updated
  },

  generateClaimForm: async (pregnancyId: string): Promise<{ content: string; fileName: string; mimeType: string }> => {
    await delay(300)
    const pregnancy = mockPregnancies.find(p => p.pregnancyId === pregnancyId)
    if (!pregnancy) throw new Error('Pregnancy not found')
    const visits = mockMaternityVisits.filter(v => v.pregnancyId === pregnancyId)
    const visitsChronological = [...visits].sort((a, b) => {
      const ta = new Date(a.visitDate).getTime()
      const tb = new Date(b.visitDate).getTime()
      return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb)
    })
    const patient = mockPatients.find(p => p.id === pregnancy.patientId) ?? null
    const html = buildMaternityClaimFormHtml({
      patient,
      pregnancy,
      visits: visitsChronological,
      generatedAt: new Date().toISOString(),
      preparedBy: 'Demo clinician',
    })
    return {
      content: encodeUtf8Base64(html),
      fileName: `maternity-claim-${pregnancyId}.html`,
      mimeType: 'text/html; charset=utf-8',
    }
  },

  updateTask: async (id: string, data: Partial<TaskFormData>): Promise<Task> => {
    await delay(300)
    const task = mockTasks.find((t) => t.id === id)
    if (!task) {
      throw new Error('Task not found')
    }
    const now = new Date().toISOString()
    const updated: Task = {
      ...task,
      ...data,
      updatedAt: now,
      // Set timestamps based on status changes
      startedAt: data.status === 'in-progress' && task.status !== 'in-progress' ? now : (task.startedAt || undefined),
      completedAt: data.status === 'completed' || data.status === 'resolved' ? now : (task.completedAt || undefined),
      resolvedAt: data.status === 'resolved' ? now : (task.resolvedAt || undefined),
      cancelledAt: data.status === 'cancelled' ? now : (task.cancelledAt || undefined),
    }
    const index = mockTasks.findIndex((t) => t.id === id)
    mockTasks[index] = updated
    return updated
  },

  deleteTask: async (id: string): Promise<void> => {
    await delay(300)
    const index = mockTasks.findIndex((t) => t.id === id)
    if (index === -1) {
      throw new Error('Task not found')
    }
    mockTasks.splice(index, 1)
  },

  // Automation rules endpoints (mock implementation)
  getAllAutomationRules: async (): Promise<AutomationRule[]> => {
    await delay(200)
    return [...mockAutomationRules]
  },

  getAutomationRule: async (id: string): Promise<AutomationRule> => {
    await delay(150)
    const rule = mockAutomationRules.find((r) => r.id === id)
    if (!rule) {
      throw new Error('Automation rule not found')
    }
    return { ...rule }
  },

  createAutomationRule: async (data: AutomationRuleFormData): Promise<AutomationRule> => {
    await delay(250)
    const now = new Date().toISOString()
    const newRule: AutomationRule = {
      id: crypto.randomUUID(),
      name: data.name,
      description: data.description,
      triggerType: data.triggerType,
      enabled: data.enabled,
      condition: data.condition,
      action: data.action,
      createdAt: now,
      updatedAt: now,
    }
    mockAutomationRules.push(newRule)
    return { ...newRule }
  },

  updateAutomationRule: async (
    id: string,
    data: Partial<AutomationRuleFormData>
  ): Promise<AutomationRule> => {
    await delay(250)
    const index = mockAutomationRules.findIndex((r) => r.id === id)
    if (index === -1) {
      throw new Error('Automation rule not found')
    }
    const existing = mockAutomationRules[index]
    const updated: AutomationRule = {
      ...existing,
      ...data,
      condition: data.condition ?? existing.condition,
      action: data.action ?? existing.action,
      updatedAt: new Date().toISOString(),
    }
    mockAutomationRules[index] = updated
    return { ...updated }
  },

  deleteAutomationRule: async (id: string): Promise<void> => {
    await delay(150)
    const index = mockAutomationRules.findIndex((r) => r.id === id)
    if (index === -1) {
      throw new Error('Automation rule not found')
    }
    mockAutomationRules.splice(index, 1)
  },

  toggleAutomationRule: async (id: string, enabled: boolean): Promise<AutomationRule> => {
    await delay(150)
    const index = mockAutomationRules.findIndex((r) => r.id === id)
    if (index === -1) {
      throw new Error('Automation rule not found')
    }
    const updated: AutomationRule = {
      ...mockAutomationRules[index],
      enabled,
      updatedAt: new Date().toISOString(),
    }
    mockAutomationRules[index] = updated
    return { ...updated }
  },

  testAutomationRule: async (
    ruleData: AutomationRuleFormData,
    sampleData: any
  ): Promise<{ matches: boolean; wouldCreateTask: any }> => {
    await delay(150)

    // Simple condition evaluation mirroring backend logic
    const condition = ruleData.condition
    const fieldValue = sampleData ? sampleData[condition.field] : undefined
    let matches = false

    if (fieldValue !== undefined) {
      switch (condition.operator) {
        case 'equals':
          matches = fieldValue === condition.value
          break
        case 'notEquals':
          matches = fieldValue !== condition.value
          break
        case 'contains':
          matches = String(fieldValue).includes(condition.value)
          break
        case 'lessThanOrEqual':
          if (condition.value === 'now') {
            matches = new Date(fieldValue) <= new Date()
          } else {
            matches = new Date(fieldValue) <= new Date(condition.value)
          }
          break
        case 'greaterThanOrEqual':
          if (condition.value === 'now') {
            matches = new Date(fieldValue) >= new Date()
          } else {
            matches = new Date(fieldValue) >= new Date(condition.value)
          }
          break
        default:
          matches = false
      }
    }

    const wouldCreateTask = matches
      ? {
          ...ruleData.action,
          isAutomated: true,
          automationRuleId: 'test-rule',
        }
      : null

    return { matches, wouldCreateTask }
  },

  // Dashboard summary endpoint (role-aware)
  getDashboardSummary: async (role?: DashboardRoleGroup): Promise<DashboardSummary> => {
    await delay(200)

    const resolveRoleGroup = (value?: string): DashboardRoleGroup => {
      if (!value) return 'admin'
      const normalized = value.toLowerCase()

      if (['clinician', 'doctor', 'physician-assistant', 'therapist'].includes(normalized)) return 'clinician'
      if (['nurse'].includes(normalized)) return 'nurse'
      if (['reception', 'receptionist'].includes(normalized)) return 'reception'
      if (['manager', 'office-manager', 'practice-manager'].includes(normalized)) return 'manager'
      if (['system', 'system-admin', 'it-support'].includes(normalized)) return 'system'
      return 'admin'
    }

    const roleGroup = resolveRoleGroup(role)
    const now = new Date()
    const todayIso = now.toISOString().slice(0, 10)

    const appointmentsToday = mockAppointments.filter((appt) => appt.appointmentDate === todayIso)
    const upcomingAppointments = mockAppointments.filter((appt) => appt.appointmentDate > todayIso)
    const openTasks = mockTasks.filter(
      (task) => !['completed', 'resolved', 'cancelled', 'denied'].includes(task.status)
    )
    const reviewLabResults = mockLabResults.filter((result) => result.reviewStatus === 'in-review')
    const criticalLabResults = mockLabResults.filter((result) => result.isCritical)
    const activePatients = mockPatients.filter((patient) => patient.isActive && !patient.isArchived)

    const appointmentQueue = appointmentsToday.slice(0, 3).map((appt) => ({
      id: appt.id,
      label: `${appt.startTime} appointment`,
      meta: `${appt.type} • ${appt.status}`,
      badge: appt.type === 'emergency' ? 'Priority' : undefined,
      to: '/appointments',
    }))

    const taskQueue = openTasks.slice(0, 3).map((task) => ({
      id: task.id,
      label: task.title,
      meta: task.dueDate ? `Due ${task.dueDate}` : 'No due date',
      badge: task.priority === 'urgent' ? 'Urgent' : task.priority === 'high' ? 'High' : undefined,
      to: '/tasks',
    }))

    const reviewQueue = reviewLabResults.slice(0, 3).map((result) => ({
      id: result.id,
      label: `${result.labName} result`,
      meta: result.reportedDate ? `Reported ${result.reportedDate}` : 'Needs review',
      badge: result.isCritical ? 'Critical' : result.isAbnormal ? 'Abnormal' : undefined,
      to: '/lab-results',
    }))

    const roleLabelMap: Record<DashboardRoleGroup, string> = {
      clinician: 'Clinician',
      nurse: 'Nurse',
      reception: 'Reception',
      admin: 'Admin',
      manager: 'Practice manager',
      system: 'System',
    }

    const roleDescriptionMap: Record<DashboardRoleGroup, string> = {
      clinician: 'Start consults quickly and stay ahead of results and follow-ups.',
      nurse: 'Keep vitals, protocols, and patient flow running smoothly.',
      reception: 'Manage arrivals, calls, and schedule changes fast.',
      admin: 'Keep operations, billing, and users running efficiently.',
      manager: 'Balance staffing, compliance, and service performance.',
      system: 'Monitor system health, security, and data integrity.',
    }

    const quickActions: DashboardSummary['quickActions'] = [
      { id: 'new-patient', label: 'New patient', description: 'Create a patient record', to: '/patients/new', tone: 'primary' },
      { id: 'schedule', label: 'Schedule appointment', description: 'Book an appointment', to: '/appointments' },
      { id: 'tasks', label: 'Open tasks', description: 'Track assigned work', to: '/tasks' },
      { id: 'reports', label: 'View reports', description: 'Practice insights', to: '/reports' },
    ]

    const roleActions: Record<DashboardRoleGroup, DashboardSummary['quickActions']> = {
      clinician: [
        { id: 'clinician-schedule', label: 'Open today schedule', description: 'Jump to patient list', to: '/appointments', tone: 'primary' },
        { id: 'clinician-results', label: 'Review lab results', description: 'Sign off and message', to: '/lab-results' },
        { id: 'clinician-referral', label: 'Create referral', description: 'Send to specialist', to: '/referrals/new' },
      ],
      nurse: [
        { id: 'nurse-vitals', label: 'Record vitals', description: 'Start intake flow', to: '/patients', tone: 'primary' },
        { id: 'nurse-protocols', label: 'Protocol checklists', description: 'Vaccines + triage', to: '/protocols' },
        { id: 'nurse-results', label: 'Lab results queue', description: 'Prepare for clinician', to: '/lab-results' },
      ],
      reception: [
        { id: 'reception-checkin', label: 'Check in patient', description: 'Arrivals and queue', to: '/appointments', tone: 'primary' },
        { id: 'reception-book', label: 'Book appointment', description: 'Schedule visit', to: '/appointments' },
        { id: 'reception-search', label: 'Find patient', description: 'Search records', to: '/patients' },
      ],
      admin: [
        { id: 'admin-reports', label: 'Run reports', description: 'Weekly and monthly', to: '/reports', tone: 'primary' },
        { id: 'admin-templates', label: 'Manage templates', description: 'Update forms', to: '/templates' },
        { id: 'admin-tenants', label: 'Tenant settings', description: 'Billing + access', to: '/admin/tenants' },
      ],
      manager: [
        { id: 'manager-performance', label: 'View performance', description: 'Weekly KPIs', to: '/reports', tone: 'primary' },
        { id: 'manager-audit', label: 'Audit review', description: 'Compliance checks', to: '/audit' },
        { id: 'manager-protocols', label: 'Update protocols', description: 'Clinical workflows', to: '/protocols' },
      ],
      system: [
        { id: 'system-audit', label: 'System audit', description: 'Review access logs', to: '/audit', tone: 'primary' },
        { id: 'system-settings', label: 'Settings overview', description: 'Environment status', to: '/settings' },
        { id: 'system-tenants', label: 'Tenant controls', description: 'Provisioning', to: '/admin/tenants' },
      ],
    }

    const capacity = 20
    const scheduleGaps = Math.max(0, capacity - appointmentsToday.length)

    const kpisByRole: Record<DashboardRoleGroup, DashboardSummary['kpis']> = {
      clinician: [
        { id: 'clinician-appointments', label: 'Consultations today', value: String(appointmentsToday.length), trend: `${upcomingAppointments.length} upcoming` },
        { id: 'clinician-results', label: 'Results to review', value: String(reviewLabResults.length), trend: `${criticalLabResults.length} critical` },
        { id: 'clinician-followups', label: 'Follow-ups pending', value: String(openTasks.length), trend: 'Across clinic' },
      ],
      nurse: [
        { id: 'nurse-vitals', label: 'Vitals due', value: String(openTasks.length), trend: 'Today' },
        { id: 'nurse-protocols', label: 'Protocols in progress', value: String(Math.max(0, Math.round(openTasks.length * 0.4))), trend: 'Pending sign-off' },
        { id: 'nurse-results', label: 'Results to review', value: String(reviewLabResults.length), trend: `${criticalLabResults.length} critical` },
      ],
      reception: [
        { id: 'reception-arrivals', label: 'Arrivals today', value: String(appointmentsToday.length), trend: `${upcomingAppointments.length} upcoming` },
        { id: 'reception-callbacks', label: 'Pending callbacks', value: String(Math.max(0, Math.round(openTasks.length * 0.6))), trend: 'Return by 3pm' },
        { id: 'reception-gaps', label: 'Schedule gaps', value: String(scheduleGaps), trend: 'Open slots' },
      ],
      admin: [
        { id: 'admin-active', label: 'Active patients', value: String(activePatients.length), trend: `${mockPatients.length} total` },
        { id: 'admin-tasks', label: 'Open tasks', value: String(openTasks.length), trend: 'Across clinic' },
        { id: 'admin-results', label: 'Results to review', value: String(reviewLabResults.length), trend: `${criticalLabResults.length} critical` },
      ],
      manager: [
        { id: 'manager-capacity', label: 'Capacity today', value: `${Math.round((appointmentsToday.length / Math.max(1, capacity)) * 100)}%`, trend: 'Booked' },
        { id: 'manager-waiting', label: 'Appointments today', value: String(appointmentsToday.length), trend: `${scheduleGaps} slots open` },
        { id: 'manager-risks', label: 'Open tasks', value: String(openTasks.length), trend: 'Needs review' },
      ],
      system: [
        { id: 'system-alerts', label: 'Critical results', value: String(criticalLabResults.length), trend: 'Today' },
        { id: 'system-open', label: 'Open tasks', value: String(openTasks.length), trend: 'Across clinic' },
        { id: 'system-patients', label: 'Active patients', value: String(activePatients.length), trend: 'Protected' },
      ],
    }

    const highlights = [
      { id: 'highlight-appointments', label: `${appointmentsToday.length} appointments today`, meta: `${upcomingAppointments.length} upcoming`, to: '/appointments' },
      { id: 'highlight-tasks', label: `${openTasks.length} open tasks`, meta: 'Assigned across team', badge: openTasks.length > 0 ? 'Priority' : undefined, to: '/tasks' },
      { id: 'highlight-results', label: `${reviewLabResults.length} lab results to review`, meta: 'Awaiting sign-off', to: '/lab-results' },
    ]

    const insights = [
      { id: 'insight-labs', label: `${reviewLabResults.length} lab results need review`, meta: `${criticalLabResults.length} critical`, to: '/lab-results' },
      { id: 'insight-tasks', label: `${openTasks.length} tasks need attention`, meta: 'Across teams', to: '/tasks' },
      { id: 'insight-appointments', label: `${upcomingAppointments.length} upcoming appointments`, meta: 'Next 7 days', to: '/appointments' },
    ]

    const alerts = [
      { id: 'alert-critical', label: `${criticalLabResults.length} critical lab results`, meta: 'Review immediately', badge: criticalLabResults.length ? 'Clinical' : 'Info', to: '/lab-results' },
      { id: 'alert-tasks', label: `${openTasks.length} open tasks`, meta: 'Follow up today', badge: openTasks.length > 0 ? 'Priority' : 'Info', to: '/tasks' },
    ]

    const shortcuts = [
      { id: 'shortcut-patients', label: 'Patient search', meta: 'Find records fast', to: '/patients' },
      { id: 'shortcut-templates', label: 'Templates', meta: 'Letters + forms', to: '/templates' },
      { id: 'shortcut-audit', label: 'Audit log', meta: 'Recent access', to: '/audit' },
      { id: 'shortcut-settings', label: 'Settings', meta: 'Practice setup', to: '/settings' },
    ]

    const queue = roleGroup === 'clinician' || roleGroup === 'nurse' || roleGroup === 'reception'
      ? [...appointmentQueue, ...reviewQueue].slice(0, 3)
      : taskQueue

    return {
      role: roleGroup,
      roleLabel: roleLabelMap[roleGroup],
      roleDescription: roleDescriptionMap[roleGroup],
      updatedAt: new Date().toISOString(),
      quickActions: [...quickActions, ...roleActions[roleGroup]],
      highlights,
      queue,
      kpis: kpisByRole[roleGroup],
      insights,
      alerts,
      shortcuts,
    }
  },

  // Dashboard stats endpoint
  getDashboardStats: async () => {
    await delay(200)
    
    const activePatients = mockPatients.filter(p => p.isActive && !p.isArchived)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
    
    return {
      totalPatients: mockPatients.length,
      activePatients: activePatients.length,
      archivedPatients: mockPatients.filter(p => p.isArchived).length,
      todayAppointments: 0, // Placeholder until appointments are implemented
      upcomingAppointments: 0, // Placeholder until appointments are implemented
      recentPatients: mockPatients.filter(p => {
        const createdAt = new Date(p.createdAt)
        return createdAt >= sevenDaysAgo
      }).length,
      patientsWithAllergies: mockPatients.filter(p => 
        p.allergies && p.allergies.length > 0
      ).length,
      patientsWithInsurance: mockPatients.filter(p => 
        p.insurance && p.insurance.provider
      ).length,
    }
  },

  getPptDashboardQueueSnapshot: async (): Promise<PptQueueSnapshot> => {
    await delay(120)
    const signed = mockStcRecords.filter((record) => record.status === 'signed').length
    const queued = mockStcRecords.filter((record) => record.status === 'queued').length
    const toPay = mockStcRecords.filter((record) => record.status === 'to_pay').length
    const paid = mockStcRecords.filter((record) => record.status === 'paid').length

    return {
      signed,
      queued,
      toPay,
      paid,
      updatedAt: new Date().toISOString(),
    }
  },

  getPptDashboardPipeline: async (): Promise<PptPipeline> => {
    await delay(120)
    const counts: Omit<PptPipeline, 'signedToToPayRate' | 'updatedAt'> = {
      signed: 0,
      queued: 0,
      toPay: 0,
      paid: 0,
      rejected: 0,
      failed: 0,
    }

    mockStcRecords.forEach((record) => {
      const normalizedStatus = record.status === 'to_pay'
        ? 'toPay'
        : record.status === 'paid'
          ? 'paid'
          : record.status
      if (Object.prototype.hasOwnProperty.call(counts, normalizedStatus)) {
        counts[normalizedStatus as keyof typeof counts] += 1
      }
    })

    const signedToToPayRate = counts.signed === 0 ? 0 : Math.round((counts.toPay / counts.signed) * 100)
    return {
      ...counts,
      signedToToPayRate,
      updatedAt: new Date().toISOString(),
    }
  },

  getPptDashboardBatchReadiness: async (): Promise<PptBatchReadiness> => {
    await delay(120)
    const signedCount = mockStcRecords.filter((record) => record.status === 'signed').length
    const queuedCount = mockStcRecords.filter((record) => record.status === 'queued').length
    const recommendedBatchSize = 25
    return {
      signedCount,
      queuedCount,
      recommendedBatchSize,
      estimatedBatches: Math.ceil(queuedCount / recommendedBatchSize),
      updatedAt: new Date().toISOString(),
    }
  },

  getPptDashboardExceptions: async (limit = 25): Promise<PptExceptions> => {
    await delay(120)
    const failed = mockStcRecords
      .filter((record) => record.status === 'failed')
      .sort((a, b) => String(b.processedAt || b.signedAt || b.createdAt).localeCompare(String(a.processedAt || a.signedAt || a.createdAt)))

    const items = failed.slice(0, Math.max(1, Math.min(limit, 100))).map((record) => ({
      id: record.id,
      label: record.patientName || 'Patient',
      meta: `${record.formType} • failed • ${record.createdAt.slice(0, 10)}`,
      badge: 'Failed',
      to: '/reports/stc-status',
    }))

    return {
      totalFailed: failed.length,
      items,
      updatedAt: new Date().toISOString(),
    }
  },

  // Assessment endpoints
  getAssessments: async (patientId: string): Promise<Assessment[]> => {
    await delay(200)
    return mockAssessments.filter((a) => a.patientId === patientId)
  },

  getAssessment: async (id: string): Promise<Assessment> => {
    await delay(150)
    const assessment = mockAssessments.find((a) => a.id === id)
    if (!assessment) {
      throw new Error('Assessment not found')
    }
    return { ...assessment }
  },

  createAssessment: async (data: AssessmentFormData): Promise<Assessment> => {
    await delay(300)
    const now = new Date().toISOString()
    const newAssessment: Assessment = {
      id: crypto.randomUUID(),
      patientId: data.patientId,
      consultationId: data.consultationId,
      assessmentDate: data.assessmentDate,
      assessedBy: 'current-user-id',
      assessedByName: 'Current User',
      chiefComplaint: data.chiefComplaint,
      historyOfPresentIllness: data.historyOfPresentIllness,
      systemsReview: data.systemsReview || [],
      physicalExam: data.physicalExam,
      functionalAssessment: data.functionalAssessment,
      assessment: data.assessment,
      plan: data.plan,
      status: data.status || 'completed',
      notes: data.notes,
      createdAt: now,
      updatedAt: now,
      createdBy: 'current-user-id',
      updatedBy: 'current-user-id',
    }
    mockAssessments.push(newAssessment)
    return { ...newAssessment }
  },

  updateAssessment: async (id: string, data: Partial<AssessmentFormData>): Promise<Assessment> => {
    await delay(250)
    const index = mockAssessments.findIndex((a) => a.id === id)
    if (index === -1) {
      throw new Error('Assessment not found')
    }
    const existing = mockAssessments[index]
    const updated: Assessment = {
      ...existing,
      ...data,
      updatedAt: new Date().toISOString(),
      updatedBy: 'current-user-id',
    }
    mockAssessments[index] = updated
    return { ...updated }
  },

  deleteAssessment: async (id: string): Promise<void> => {
    await delay(200)
    const index = mockAssessments.findIndex((a) => a.id === id)
    if (index === -1) {
      throw new Error('Assessment not found')
    }
    mockAssessments.splice(index, 1)
  },

  // Care Plans functions
  getCarePlans: async (patientId: string): Promise<any[]> => {
    await delay(200)
    return mockCarePlans.filter((cp) => cp.patientId === patientId)
  },

  getCarePlan: async (id: string): Promise<any> => {
    await delay(200)
    const plan = mockCarePlans.find((cp) => cp.id === id)
    if (!plan) {
      throw new Error('Care plan not found')
    }
    return plan
  },

  createCarePlan: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newPlan = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockCarePlans.push(newPlan)
    return newPlan
  },

  updateCarePlan: async (id: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockCarePlans.findIndex((cp) => cp.id === id)
    if (index === -1) {
      throw new Error('Care plan not found')
    }
    const updated = {
      ...mockCarePlans[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockCarePlans[index] = updated
    return updated
  },

  deleteCarePlan: async (id: string): Promise<void> => {
    await delay(200)
    const index = mockCarePlans.findIndex((cp) => cp.id === id)
    if (index === -1) {
      throw new Error('Care plan not found')
    }
    mockCarePlans.splice(index, 1)
  },

  getCarePlanSuggestions: async (_patientId: string): Promise<any[]> => {
    await delay(200)
    return []
  },

  // Protocols functions
  getProtocols: async (): Promise<any[]> => {
    await delay(200)
    return mockProtocols
  },

  getProtocol: async (id: string): Promise<any> => {
    await delay(200)
    const protocol = mockProtocols.find((p) => p.id === id)
    if (!protocol) {
      throw new Error('Protocol not found')
    }
    return protocol
  },

  createProtocol: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newProtocol = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockProtocols.push(newProtocol)
    return newProtocol
  },

  updateProtocol: async (id: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockProtocols.findIndex((p) => p.id === id)
    if (index === -1) {
      throw new Error('Protocol not found')
    }
    const updated = {
      ...mockProtocols[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockProtocols[index] = updated
    return updated
  },

  deleteProtocol: async (id: string): Promise<void> => {
    await delay(200)
    const index = mockProtocols.findIndex((p) => p.id === id)
    if (index === -1) {
      throw new Error('Protocol not found')
    }
    mockProtocols.splice(index, 1)
  },

  getPatientProtocols: async (patientId: string): Promise<any[]> => {
    await delay(200)
    return mockProtocolEnrollments.filter((e) => e.patientId === patientId)
  },

  enrollPatientInProtocol: async (patientId: string, protocolId: string, notes?: string): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const enrollment = {
      id: crypto.randomUUID(),
      patientId: patientId,
      protocolId: protocolId,
      status: 'active',
      enrolledAt: now,
      notes: notes,
      createdAt: now,
      updatedAt: now,
    }
    mockProtocolEnrollments.push(enrollment)
    return enrollment
  },

  excludePatientFromProtocol: async (patientId: string, protocolId: string, reason?: string): Promise<any> => {
    await delay(300)
    const enrollment = mockProtocolEnrollments.find(
      (e) => e.patientId === patientId && e.protocolId === protocolId
    )
    if (!enrollment) {
      throw new Error('Protocol enrollment not found')
    }
    const updated = {
      ...enrollment,
      status: 'excluded',
      excludedAt: new Date().toISOString(),
      exclusionReason: reason,
      updatedAt: new Date().toISOString(),
    }
    const index = mockProtocolEnrollments.indexOf(enrollment)
    mockProtocolEnrollments[index] = updated
    return updated
  },

  checkProtocolEligibility: async (_patientId: string, protocolId: string): Promise<any> => {
    await delay(200)
    const protocol = mockProtocols.find((p) => p.id === protocolId)
    return {
      eligible: true,
      protocolId,
      protocolName: protocol?.name ?? 'Unknown',
      matchedCriteria: [],
      unmatchedCriteria: [],
      reason: 'Patient meets all eligibility criteria',
    }
  },

  screenEligiblePatients: async (protocolId: string): Promise<any> => {
    await delay(300)
    const protocol = mockProtocols.find((p) => p.id === protocolId)
    const enrolledIds = new Set(
      mockProtocolEnrollments
        .filter((e) => e.protocolId === protocolId && e.status === 'enrolled')
        .map((e) => e.patientId)
    )
    const eligible = mockPatients
      .filter((p) => !enrolledIds.has(p.id))
      .slice(0, 10)
      .map((p) => ({
        patientId: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        dateOfBirth: p.dateOfBirth,
        gender: p.gender,
      }))
    return {
      protocolId,
      protocolName: protocol?.name ?? 'Unknown',
      eligiblePatients: eligible,
      totalScreened: mockPatients.length,
      alreadyEnrolled: enrolledIds.size,
    }
  },

  getProtocolCompliance: async (protocolId: string): Promise<any> => {
    await delay(200)
    const protocol = mockProtocols.find((p) => p.id === protocolId)
    return {
      protocolId,
      protocolName: protocol?.name ?? 'Unknown',
      totalEnrolled: 0,
      compliantCount: 0,
      nonCompliantCount: 0,
      averageCompliance: 0,
      complianceByRule: [],
    }
  },

  getProtocolComplianceReport: async (protocolId: string): Promise<any> => {
    await delay(200)
    const protocol = mockProtocols.find((p) => p.id === protocolId)
    const metrics = await mockApi.getProtocolCompliance(protocolId)
    return {
      protocolId,
      protocolName: protocol?.name ?? 'Unknown',
      generatedAt: new Date().toISOString(),
      period: {
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
      },
      metrics,
      patientCompliance: [],
    }
  },

  // Drug Recalls functions
  getDrugRecalls: async (): Promise<any[]> => {
    await delay(200)
    return mockDrugRecalls
  },

  getDrugRecall: async (id: string): Promise<any> => {
    await delay(200)
    const recall = mockDrugRecalls.find((r) => r.id === id)
    if (!recall) {
      throw new Error('Drug recall not found')
    }
    return recall
  },

  createDrugRecall: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newRecall = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockDrugRecalls.push(newRecall)
    return newRecall
  },

  updateDrugRecall: async (id: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockDrugRecalls.findIndex((r) => r.id === id)
    if (index === -1) {
      throw new Error('Drug recall not found')
    }
    const updated = {
      ...mockDrugRecalls[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockDrugRecalls[index] = updated
    return updated
  },

  deleteDrugRecall: async (id: string): Promise<void> => {
    await delay(200)
    const index = mockDrugRecalls.findIndex((r) => r.id === id)
    if (index === -1) {
      throw new Error('Drug recall not found')
    }
    mockDrugRecalls.splice(index, 1)
  },

  getAffectedPatients: async (_recallId: string): Promise<any[]> => {
    await delay(200)
    return []
  },

  searchPatientsByMedication: async (_drugId: string): Promise<any[]> => {
    await delay(200)
    return []
  },

  // Research Studies functions
  getResearchStudies: async (): Promise<any[]> => {
    await delay(200)
    return mockResearchStudies
  },

  getResearchStudy: async (id: string): Promise<any> => {
    await delay(200)
    const study = mockResearchStudies.find((s) => s.id === id)
    if (!study) {
      throw new Error('Research study not found')
    }
    return study
  },

  createResearchStudy: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newStudy = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockResearchStudies.push(newStudy)
    return newStudy
  },

  updateResearchStudy: async (id: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockResearchStudies.findIndex((s) => s.id === id)
    if (index === -1) {
      throw new Error('Research study not found')
    }
    const updated = {
      ...mockResearchStudies[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockResearchStudies[index] = updated
    return updated
  },

  deleteResearchStudy: async (id: string): Promise<void> => {
    await delay(200)
    const index = mockResearchStudies.findIndex((s) => s.id === id)
    if (index === -1) {
      throw new Error('Research study not found')
    }
    mockResearchStudies.splice(index, 1)
  },

  getPatientResearchStudies: async (patientId: string): Promise<any[]> => {
    await delay(200)
    return mockResearchEnrollments.filter((e) => e.patientId === patientId)
  },

  enrollPatientInStudy: async (patientId: string, studyId: string, data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const enrollment = {
      id: crypto.randomUUID(),
      patientId,
      studyId,
      status: 'active',
      enrolledAt: now,
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockResearchEnrollments.push(enrollment)
    return enrollment
  },

  withdrawPatientFromStudy: async (patientId: string, studyId: string, reason?: string): Promise<any> => {
    await delay(300)
    const enrollment = mockResearchEnrollments.find(
      (e) => e.patientId === patientId && e.studyId === studyId
    )
    if (!enrollment) {
      throw new Error('Research study enrollment not found')
    }
    const updated = {
      ...enrollment,
      status: 'withdrawn',
      withdrawnAt: new Date().toISOString(),
      withdrawalReason: reason,
      updatedAt: new Date().toISOString(),
    }
    const index = mockResearchEnrollments.indexOf(enrollment)
    mockResearchEnrollments[index] = updated
    return updated
  },

  getStudyProtocol: async (studyId: string, version?: string): Promise<any> => {
    await delay(200)
    return {
      id: crypto.randomUUID(),
      studyId,
      version: version || '1.0',
      content: '',
      createdAt: new Date().toISOString(),
    }
  },

  createProtocolVersion: async (studyId: string, data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    return {
      id: crypto.randomUUID(),
      studyId,
      version: data.version || '1.0',
      ...data,
      createdAt: now,
    }
  },

  // Patient Guidance functions
  getPatientGuidance: async (patientId: string): Promise<any[]> => {
    await delay(200)
    return mockPatientGuidance.filter((g) => g.patientId === patientId)
  },

  getPatientGuidanceById: async (patientId: string, guidanceId: string): Promise<any> => {
    await delay(200)
    const guidance = mockPatientGuidance.find(
      (g) => g.patientId === patientId && g.id === guidanceId
    )
    if (!guidance) {
      throw new Error('Patient guidance not found')
    }
    return guidance
  },

  createPatientGuidance: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newGuidance = {
      id: crypto.randomUUID(),
      ...data,
      createdAt: now,
      updatedAt: now,
    }
    mockPatientGuidance.push(newGuidance)
    return newGuidance
  },

  updatePatientGuidance: async (patientId: string, guidanceId: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockPatientGuidance.findIndex(
      (g) => g.patientId === patientId && g.id === guidanceId
    )
    if (index === -1) {
      throw new Error('Patient guidance not found')
    }
    const updated = {
      ...mockPatientGuidance[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockPatientGuidance[index] = updated
    return updated
  },

  deletePatientGuidance: async (patientId: string, guidanceId: string): Promise<void> => {
    await delay(200)
    const index = mockPatientGuidance.findIndex(
      (g) => g.patientId === patientId && g.id === guidanceId
    )
    if (index === -1) {
      throw new Error('Patient guidance not found')
    }
    mockPatientGuidance.splice(index, 1)
  },

  // Patient Reminders functions
  getReminderRules: async (): Promise<any[]> => {
    await delay(200)
    return [...mockReminderRules]
  },

  createReminderRule: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const rule = {
      id: crypto.randomUUID(),
      name: data.name,
      description: data.description,
      trigger: data.trigger,
      message: data.message,
      deliveryMethod: data.deliveryMethod,
      frequency: data.frequency || 'once',
      enabled: data.enabled !== undefined ? data.enabled : true,
      createdAt: now,
      updatedAt: now,
    }
    mockReminderRules.push(rule)
    return rule
  },

  getAllPatientReminders: async (): Promise<any[]> => {
    await delay(200)
    return [...mockPatientReminders]
  },

  getPatientReminders: async (patientId: string): Promise<any[]> => {
    await delay(200)
    return mockPatientReminders.filter((r) => r.patientId === patientId)
  },

  getPatientReminderById: async (patientId: string, reminderId: string): Promise<any> => {
    await delay(200)
    const reminder = mockPatientReminders.find(
      (r) => r.patientId === patientId && r.id === reminderId
    )
    if (!reminder) {
      throw new Error('Patient reminder not found')
    }
    return reminder
  },

  createPatientReminder: async (data: any): Promise<any> => {
    await delay(300)
    const now = new Date().toISOString()
    const newReminder = {
      id: crypto.randomUUID(),
      ...data,
      status: data.status || 'pending',
      sendAttempts: data.sendAttempts || 0,
      createdAt: now,
      updatedAt: now,
    }
    mockPatientReminders.push(newReminder)
    return newReminder
  },

  updatePatientReminder: async (patientId: string, reminderId: string, data: any): Promise<any> => {
    await delay(300)
    const index = mockPatientReminders.findIndex(
      (r) => r.patientId === patientId && r.id === reminderId
    )
    if (index === -1) {
      throw new Error('Patient reminder not found')
    }
    const updated = {
      ...mockPatientReminders[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    mockPatientReminders[index] = updated
    return updated
  },

  deletePatientReminder: async (patientId: string, reminderId: string): Promise<void> => {
    await delay(200)
    const index = mockPatientReminders.findIndex(
      (r) => r.patientId === patientId && r.id === reminderId
    )
    if (index === -1) {
      throw new Error('Patient reminder not found')
    }
    mockPatientReminders.splice(index, 1)
  },

  // Confidentiality policy functions
  getConfidentialityPolicy: async (): Promise<any> => {
    await delay(200)
    // Return default policy or stored policy from localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_confidentiality_policy')
        if (stored) {
          return JSON.parse(stored)
        }
      }
    } catch (error) {
      console.error('[Mock API] Error loading confidentiality policy:', error)
    }
    
    // Default policy
    return {
      fieldPolicies: {},
      defaultConfidentialityLevel: 'restricted',
      allowEmergencyOverride: true,
    }
  },

  setConfidentialityPolicy: async (policy: any): Promise<any> => {
    await delay(300)
    const updated = {
      ...policy,
      updatedAt: new Date().toISOString(),
    }
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('clnicl_confidentiality_policy', JSON.stringify(updated))
      }
    } catch (error) {
      console.error('[Mock API] Error saving confidentiality policy:', error)
    }
    return updated
  },

  // Emergency override functions
  requestEmergencyOverride: async (data: { patientId: string; fields: string[]; reason: string }): Promise<any> => {
    await delay(300)
    const now = new Date()
    const overrideId = crypto.randomUUID()
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000) // 24 hours
    const currentUserId = mockApi.getCurrentUserId() || '1'
    
    const override = {
      id: overrideId,
      userId: currentUserId,
      patientId: data.patientId,
      fields: data.fields,
      reason: data.reason,
      status: 'pending',
      requestedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    }
    
    // Store in localStorage for mock
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        overrides.push(override)
        window.localStorage.setItem('clnicl_emergency_overrides', JSON.stringify(overrides))
      }
    } catch (error) {
      console.error('[Mock API] Error saving override:', error)
    }
    
    return override
  },

  approveOverride: async (data: { overrideId: string; expiresInHours?: number }): Promise<any> => {
    await delay(300)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        const index = overrides.findIndex((o: any) => o.id === data.overrideId)
        
        if (index === -1) {
          throw new Error('Override request not found')
        }
        
        const existing = overrides[index]
        if (existing.status !== 'pending') {
          throw new Error(`Override request is already ${existing.status}`)
        }
        
        const now = new Date()
        const expiresAt = new Date(now.getTime() + (data.expiresInHours || 24) * 60 * 60 * 1000)
        const currentUserId = mockApi.getCurrentUserId() || '1'
        
        const updated = {
          ...existing,
          status: 'approved',
          approvedBy: currentUserId,
          approvedAt: now.toISOString(),
          expiresAt: expiresAt.toISOString(),
          updatedAt: now.toISOString(),
        }
        
        overrides[index] = updated
        window.localStorage.setItem('clnicl_emergency_overrides', JSON.stringify(overrides))
        return updated
      }
    } catch (error) {
      console.error('[Mock API] Error approving override:', error)
      throw error
    }
    throw new Error('Failed to approve override')
  },

  rejectOverride: async (data: { overrideId: string; rejectionReason?: string }): Promise<any> => {
    await delay(300)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        const index = overrides.findIndex((o: any) => o.id === data.overrideId)
        
        if (index === -1) {
          throw new Error('Override request not found')
        }
        
        const existing = overrides[index]
        if (existing.status !== 'pending') {
          throw new Error(`Override request is already ${existing.status}`)
        }
        
        const now = new Date()
        const currentUserId = mockApi.getCurrentUserId() || '1'
        const updated = {
          ...existing,
          status: 'rejected',
          rejectedBy: currentUserId,
          rejectedAt: now.toISOString(),
          rejectionReason: data.rejectionReason,
          updatedAt: now.toISOString(),
        }
        
        overrides[index] = updated
        window.localStorage.setItem('clnicl_emergency_overrides', JSON.stringify(overrides))
        return updated
      }
    } catch (error) {
      console.error('[Mock API] Error rejecting override:', error)
      throw error
    }
    throw new Error('Failed to reject override')
  },

  getActiveOverrides: async (patientId?: string, fields?: string): Promise<any[]> => {
    await delay(200)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        const currentUserId = mockApi.getCurrentUserId()
        
        const now = new Date().toISOString()
        return overrides.filter((o: any) => {
          // Only return overrides for the current user
          if (currentUserId && o.userId !== currentUserId) return false
          if (o.status !== 'approved') return false
          if (o.expiresAt && o.expiresAt < now) return false
          if (patientId && o.patientId !== patientId) return false
          if (fields) {
            const fieldArray = fields.split(',')
            return fieldArray.some((f) => o.fields.includes(f))
          }
          return true
        })
      }
    } catch (error) {
      console.error('[Mock API] Error loading active overrides:', error)
    }
    return []
  },

  getPendingOverrides: async (): Promise<any[]> => {
    await delay(200)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        return overrides.filter((o: any) => o.status === 'pending')
      }
    } catch (error) {
      console.error('[Mock API] Error loading pending overrides:', error)
    }
    return []
  },

  getOverrideById: async (overrideId: string): Promise<any> => {
    await delay(200)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('clnicl_emergency_overrides') || '[]'
        const overrides = JSON.parse(stored)
        const override = overrides.find((o: any) => o.id === overrideId)
        if (!override) {
          throw new Error('Override not found')
        }
        return override
      }
    } catch (error) {
      console.error('[Mock API] Error loading override:', error)
      throw error
    }
    throw new Error('Override not found')
  },
}

